// The SPA's main script. Was an ~11,400-line inline `text/babel` block in
// index.html until 2026-10-11; moved out so it can be precompiled and cached.
// index.html is served no-store (it names the versioned URLs), so while this
// lived inline its 650 KB was re-downloaded on every single page load.
//
// Authored as JSX. The browser never sees this file — `node scripts/precompile-spa.js`
// compiles it to app.compiled.js, which is what index.html loads. Edit this one.

// `var`, not `const`: every text/babel script shares one global scope, so a second
// `const` binding of these names is a redeclaration SyntaxError now that Babel compiles
// JSX only (data-presets="react") instead of downlevelling to ES5.
var {useState,useEffect,useCallback,useRef,useMemo}=React;

/* ===== ADAPTER REGISTRY ===== */
const ADAPTER_CATEGORIES=[
  {id:'database',label:'Database'},
  {id:'erp',label:'ERP / Finance'},
  {id:'asp',label:'ASP / Tax'},
  {id:'crm',label:'CRM / Marketing'},
  {id:'cloud',label:'Cloud Storage'},
  {id:'messaging',label:'Messaging'},
  {id:'protocol',label:'Protocol / API'},
  {id:'file',label:'File / Email'},
  {id:'edi',label:'EDI'},
];

const ADAPTERS={
  /* ── DATABASE ── */
  'oracle-db':{label:'Oracle DB',cat:'database',icon:'https://cdn.simpleicons.org/oracle',fields:[
    {k:'host',label:'Hostname / IP',type:'text',req:true,ph:'oracle-db.example.com'},
    {k:'port',label:'Port',type:'number',req:true,def:'1521'},
    {k:'connectType',label:'Connect By',type:'select',req:true,opts:[{v:'sid',l:'SID'},{v:'service',l:'Service Name'},{v:'tns',l:'TNS Alias'}]},
    {k:'sid',label:'SID',type:'text',ph:'ORCL',sw:{f:'connectType',v:'sid'}},
    {k:'serviceName',label:'Service Name',type:'text',ph:'ORCLPDB1',sw:{f:'connectType',v:'service'}},
    {k:'tnsAlias',label:'TNS Alias',type:'text',ph:'MYDB',sw:{f:'connectType',v:'tns'}},
    {k:'username',label:'Username',type:'text',req:true,ph:'SYSTEM'},
    {k:'password',label:'Password',type:'password',req:true},
    {k:'walletPath',label:'Wallet Path (optional)',type:'text',ph:'/opt/oracle/wallet'},
  ]},
  'mysql':{label:'MySQL',cat:'database',icon:'https://cdn.simpleicons.org/mysql',fields:[
    {k:'host',label:'Host',type:'text',req:true,ph:'mysql.example.com'},
    {k:'port',label:'Port',type:'number',req:true,def:'3306'},
    {k:'database',label:'Database',type:'text',req:true,ph:'mydb'},
    {k:'username',label:'Username',type:'text',req:true},
    {k:'password',label:'Password',type:'password',req:true},
    {k:'ssl',label:'Use SSL',type:'select',opts:[{v:'false',l:'No'},{v:'true',l:'Yes'}]},
  ]},
  'postgresql':{label:'PostgreSQL',cat:'database',icon:'https://cdn.simpleicons.org/postgresql',fields:[
    {k:'host',label:'Host',type:'text',req:true,ph:'pg.example.com'},
    {k:'port',label:'Port',type:'number',req:true,def:'5432'},
    {k:'database',label:'Database',type:'text',req:true},
    {k:'username',label:'Username',type:'text',req:true},
    {k:'password',label:'Password',type:'password',req:true},
    {k:'sslMode',label:'SSL Mode',type:'select',opts:[{v:'disable',l:'Disable'},{v:'require',l:'Require'},{v:'verify-full',l:'Verify Full'}]},
  ]},
  'mssql':{label:'SQL Server',cat:'database',icon:'https://cdn.simpleicons.org/microsoftsqlserver',fields:[
    {k:'host',label:'Host',type:'text',req:true,ph:'sqlserver.example.com'},
    {k:'port',label:'Port',type:'number',req:true,def:'1433'},
    {k:'database',label:'Database',type:'text',req:true},
    {k:'username',label:'Username',type:'text',req:true},
    {k:'password',label:'Password',type:'password',req:true},
    {k:'integratedSecurity',label:'Integrated Security',type:'select',opts:[{v:'false',l:'No'},{v:'true',l:'Yes'}]},
    {k:'encrypt',label:'Encrypt Connection',type:'select',opts:[{v:'true',l:'Yes'},{v:'false',l:'No'}]},
  ]},
  'mongodb':{label:'MongoDB',cat:'database',icon:'https://cdn.simpleicons.org/mongodb',fields:[
    {k:'connectionString',label:'Connection String',type:'text',req:true,ph:'mongodb://user:pass@host:27017/db'},
    {k:'database',label:'Database',type:'text',req:true},
    {k:'authSource',label:'Auth Source',type:'text',def:'admin'},
  ]},
  'snowflake':{label:'Snowflake',cat:'database',icon:'https://cdn.simpleicons.org/snowflake',fields:[
    {k:'account',label:'Account Identifier',type:'text',req:true,ph:'myorg-myaccount'},
    {k:'username',label:'Username',type:'text',req:true},
    {k:'password',label:'Password',type:'password',req:true},
    {k:'warehouse',label:'Warehouse',type:'text',req:true,ph:'COMPUTE_WH'},
    {k:'database',label:'Database',type:'text',req:true},
    {k:'schema',label:'Schema',type:'text',def:'PUBLIC'},
    {k:'role',label:'Role (optional)',type:'text',ph:'SYSADMIN'},
  ]},
  'redis':{label:'Redis',cat:'database',icon:'https://cdn.simpleicons.org/redis',fields:[
    {k:'host',label:'Host',type:'text',req:true,def:'localhost'},
    {k:'port',label:'Port',type:'number',req:true,def:'6379'},
    {k:'password',label:'Password (optional)',type:'password'},
    {k:'database',label:'Database Index',type:'number',def:'0'},
    {k:'ssl',label:'TLS/SSL',type:'select',opts:[{v:'false',l:'No'},{v:'true',l:'Yes'}]},
  ]},
  'db2':{label:'IBM Db2',cat:'database',icon:'https://cdn.simpleicons.org/ibm',fields:[
    {k:'host',label:'Host',type:'text',req:true},
    {k:'port',label:'Port',type:'number',req:true,def:'50000'},
    {k:'database',label:'Database',type:'text',req:true},
    {k:'username',label:'Username',type:'text',req:true},
    {k:'password',label:'Password',type:'password',req:true},
  ]},

  /* ── ERP ── */
  'oracle-fusion':{label:'Oracle Fusion',cat:'erp',letter:'OF',color:'#f80000',fields:[
    {k:'baseUrl',label:'Base URL',type:'text',req:true,ph:'https://xxx.oraclecloud.com'},
    {k:'authType',label:'Authentication Type',type:'select',req:true,
      opts:[{v:'basic',l:'Basic Authentication'},{v:'oauth2',l:'OAuth 2.0 (Recommended)'}],def:'basic'},
    /* Basic Auth fields */
    {k:'username',label:'Username',type:'text',req:true,ph:'john.doe@company.com',sw:{f:'authType',v:'basic'}},
    {k:'password',label:'Password',type:'password',req:true,sw:{f:'authType',v:'basic'}},
    /* OAuth2 fields */
    {k:'clientId',label:'Client ID',type:'text',req:true,sw:{f:'authType',v:'oauth2'}},
    {k:'clientSecret',label:'Client Secret',type:'password',req:true,sw:{f:'authType',v:'oauth2'}},
    {k:'tokenUrl',label:'Token URL',type:'text',req:true,ph:'https://xxx.oraclecloud.com/oauth/token',sw:{f:'authType',v:'oauth2'}},
    {k:'scope',label:'Scope (optional)',type:'text',ph:'urn:opc:resource:consumer::all',sw:{f:'authType',v:'oauth2'}},
    /* Common fields */
    {k:'module',label:'Module',type:'select',opts:[{v:'financials',l:'Financials'},{v:'scm',l:'SCM'},{v:'hcm',l:'HCM'},{v:'procurement',l:'Procurement'},{v:'projects',l:'Projects'}]},
    {k:'apiVersion',label:'REST API Version',type:'text',def:'v1'},
    {k:'faBusinessUnit',label:'Business Unit (optional)',type:'text'},
  ]},
  'oracle-ebs':{label:'Oracle EBS',cat:'erp',icon:'https://cdn.simpleicons.org/oracle',fields:[
    {k:'host',label:'EBS Host',type:'text',req:true},
    {k:'port',label:'Port',type:'number',def:'8000'},
    {k:'username',label:'Username',type:'text',req:true},
    {k:'password',label:'Password',type:'password',req:true},
    {k:'responsibility',label:'Responsibility Key',type:'text'},
    {k:'orgId',label:'Org ID',type:'text'},
  ]},
  'sap-s4hana':{label:'SAP S/4HANA',cat:'erp',icon:'https://cdn.simpleicons.org/sap',fields:[
    {k:'baseUrl',label:'Base URL',type:'text',req:true,ph:'https://myXXXXXX.s4hana.ondemand.com'},
    {k:'clientId',label:'OAuth Client ID',type:'text',req:true},
    {k:'clientSecret',label:'Client Secret',type:'password',req:true},
    {k:'tokenUrl',label:'Token URL',type:'text',req:true,ph:'https://myXXXXXX.authentication.eu10.hana.ondemand.com/oauth/token'},
    {k:'apiCatalog',label:'API',type:'select',opts:[{v:'invoice',l:'AP Invoice'},{v:'gl',l:'G/L Journal'},{v:'po',l:'Purchase Order'},{v:'vendor',l:'Vendor Master'},{v:'material',l:'Material Master'}]},
    {k:'systemId',label:'System ID (optional)',type:'text'},
  ]},
  'sap-ecc':{label:'SAP ECC',cat:'erp',icon:'https://cdn.simpleicons.org/sap',fields:[
    {k:'host',label:'SAP Host',type:'text',req:true},
    {k:'systemNumber',label:'System Number',type:'text',req:true,ph:'00'},
    {k:'client',label:'Client',type:'text',req:true,ph:'100'},
    {k:'username',label:'Username',type:'text',req:true},
    {k:'password',label:'Password',type:'password',req:true},
    {k:'language',label:'Language',type:'text',def:'EN'},
  ]},
  'sap-s4hana-cpi':{label:'SAP S/4HANA via CPI',cat:'erp',icon:'https://cdn.simpleicons.org/sap',fields:[
    {k:'baseUrl',label:'CPI Base URL',type:'text',req:true,ph:'https://{subaccount}.it-cpi001-rt.cfapps.{region}.hana.ondemand.com'},
    {k:'username',label:'iFlow Username',type:'text',req:true,ph:'CPI iFlow user'},
    {k:'password',label:'iFlow Password',type:'password',req:true},
  ]},
  'dynamics365':{label:'Dynamics 365',cat:'erp',icon:'https://cdn.simpleicons.org/microsoftazure',fields:[
    {k:'tenantId',label:'Tenant ID',type:'text',req:true},
    {k:'clientId',label:'Client ID',type:'text',req:true},
    {k:'clientSecret',label:'Client Secret',type:'password',req:true},
    {k:'resourceUrl',label:'Resource URL',type:'text',req:true,ph:'https://mycompany.crm.dynamics.com'},
    {k:'environment',label:'Environment',type:'text',ph:'production'},
  ]},
  /* Dynamics 365 Business Central (online) — Azure AD OAuth2 client_credentials,
   * company-scoped REST API v2.0. Distinct from the CRM-flavoured 'dynamics365'
   * adapter above (Dataverse) and from F&O. The base URL MUST include the
   * companies({id}) segment so the route appends /salesInvoices, /customers(…),
   * /companyInformation. Targeted by the 'dynamics-365-bc-ar-invoice-to-asp'
   * recipe. clientId/clientSecret/tokenUrl/scope land on flat Connection columns
   * (resolved at runtime by ConnectionPropertiesSource); baseUrl → host/basePath. */
  'dynamics365-bc':{label:'Dynamics 365 Business Central',cat:'erp',icon:'https://cdn.simpleicons.org/microsoftazure',fields:[
    {k:'baseUrl',label:'Base URL (company-scoped)',type:'text',req:true,ph:'https://api.businesscentral.dynamics.com/v2.0/{tenant}/{env}/api/v2.0/companies({companyId})'},
    {k:'tenantId',label:'Tenant ID',type:'text',req:true,ph:'contoso.onmicrosoft.com'},
    {k:'environment',label:'Environment',type:'text',ph:'Production'},
    {k:'companyId',label:'Company ID (GUID — also in the Base URL)',type:'text'},
    {k:'clientId',label:'Client ID (Azure AD app)',type:'text',req:true},
    {k:'clientSecret',label:'Client Secret',type:'password',req:true},
    {k:'tokenUrl',label:'Token URL',type:'text',req:true,ph:'https://login.microsoftonline.com/{tenant}/oauth2/v2.0/token'},
    {k:'scope',label:'Scope',type:'text',req:true,def:'https://api.businesscentral.dynamics.com/.default'},
  ]},
  /* NetSuite REST Web Services — OAuth2 client-credentials (M2M). NetSuite's
   * M2M flow has NO client secret: the client signs a PS256 JWT assertion with
   * an uploaded certificate (RS256 was deprecated). The connection therefore
   * carries Client ID (consumer key) + Certificate ID (the cert-mapping id /
   * JWT kid) + a PEM private key. NetSuiteTokenManager reads these at runtime.
   * The 'netsuite-ar-invoice-to-asp' recipe targets this adapter. */
  'netsuite':{label:'NetSuite',cat:'erp',icon:'https://cdn.simpleicons.org/oracle',fields:[
    {k:'baseUrl',label:'Account host',type:'text',req:true,ph:'https://1234567.suitetalk.api.netsuite.com'},
    {k:'clientId',label:'Client ID (Consumer Key)',type:'text',req:true},
    {k:'certificateId',label:'Certificate ID (OAuth2 M2M mapping — JWT kid)',type:'text',req:true},
    {k:'privateKey',label:'Private Key (PEM · PKCS#8)',type:'textarea',req:true,ph:'-----BEGIN PRIVATE KEY-----'},
    {k:'scope',label:'Scope',type:'text',def:'rest_webservices'},
    {k:'tokenUrl',label:'Token URL (optional — derived from host)',type:'text',ph:'https://1234567.suitetalk.api.netsuite.com/services/rest/auth/oauth2/v1/token'},
  ]},
  'workday':{label:'Workday',cat:'erp',icon:'https://cdn.simpleicons.org/workday',fields:[
    {k:'tenant',label:'Tenant',type:'text',req:true,ph:'mycompany_preview1'},
    {k:'baseUrl',label:'Base URL',type:'text',req:true,ph:'https://wd2-impl-services1.workday.com/ccx/service'},
    {k:'username',label:'Username',type:'text',req:true},
    {k:'password',label:'Password',type:'password',req:true},
    {k:'version',label:'API Version',type:'text',def:'v42.0'},
  ]},
  'infor':{label:'Infor LN/CloudSuite',cat:'erp',letter:'I',color:'#AC0C2C',fields:[
    {k:'baseUrl',label:'Base URL',type:'text',req:true},
    {k:'clientId',label:'Client ID',type:'text',req:true},
    {k:'clientSecret',label:'Client Secret',type:'password',req:true},
    {k:'tenantId',label:'Tenant ID',type:'text',req:true},
    {k:'ionApiFile',label:'ION API Key File (path)',type:'text'},
  ]},
  'sage-intacct':{label:'Sage Intacct',cat:'erp',icon:'https://cdn.simpleicons.org/sage',fields:[
    {k:'senderId',label:'Sender ID',type:'text',req:true},
    {k:'senderPassword',label:'Sender Password',type:'password',req:true},
    {k:'companyId',label:'Company ID',type:'text',req:true},
    {k:'userId',label:'User ID',type:'text',req:true},
    {k:'userPassword',label:'User Password',type:'password',req:true},
    {k:'entityId',label:'Entity ID (optional)',type:'text'},
  ]},
  'epicor':{label:'Epicor ERP',cat:'erp',letter:'E',color:'#E31837',fields:[
    {k:'baseUrl',label:'Base URL',type:'text',req:true,ph:'https://myepicor.example.com/EpicorAPI'},
    {k:'username',label:'Username',type:'text',req:true},
    {k:'password',label:'Password',type:'password',req:true},
    {k:'company',label:'Company ID',type:'text',req:true},
    {k:'plant',label:'Plant (optional)',type:'text'},
  ]},
  'odoo':{label:'Odoo',cat:'erp',icon:'https://cdn.simpleicons.org/odoo',fields:[
    {k:'url',label:'Odoo URL',type:'text',req:true,ph:'https://mycompany.odoo.com'},
    {k:'database',label:'Database',type:'text',req:true},
    {k:'username',label:'Username (email)',type:'text',req:true},
    {k:'apiKey',label:'API Key',type:'password',req:true},
    {k:'version',label:'API Version',type:'number',def:'16'},
  ]},

  /* ── ASP / TAX ── */
  'taxlab':{label:'TaxLab.ai',cat:'asp',letter:'T',color:'#ff5630',fields:[
    {k:'baseUrl',label:'API Base URL',type:'text',req:true,def:'https://api.taxlab.ai/v1'},
    {k:'apiKey',label:'API Key',type:'password',req:true,ph:'tlk_xxxxxxxxxxxx'},
    {k:'tenantId',label:'Tenant ID',type:'text',req:true},
    {k:'environment',label:'Environment',type:'select',opts:[{v:'production',l:'Production'},{v:'sandbox',l:'Sandbox / UAT'}]},
    {k:'country',label:'Country',type:'select',opts:[{v:'MY',l:'Malaysia'},{v:'SA',l:'Saudi Arabia'},{v:'AE',l:'UAE'},{v:'SG',l:'Singapore'},{v:'IN',l:'India'}]},
    {k:'timeout',label:'Timeout (seconds)',type:'number',def:'30'},
  ]},
  /* TaxLab ASP, reached THROUGH THE AXON GATEWAY (since 2026-10-29). The base
   * URL is this platform's own gateway (axon.taxlab.ai / sandbox-axon.taxlab.ai)
   * and the credential is an Axon key of the customer's organisation (API
   * Management -> Organisations -> Keys), never the TaxLab client secret: the
   * gateway swaps the key's token for the organisation's TaxLab credential and
   * adds the signed X-Axon-Caller. The field keys (baseUrl/clientId/clientSecret)
   * are unchanged on purpose -- installed connections and the recipes'
   * {{slot.asp.*}} references read them. A direct TaxLab host still works for a
   * tenant holding its own TaxLab credential, but bypasses all of the above.
   * The AR accelerators (Fusion, NetSuite, Business Central, SAP) target this
   * adapter. */
  'taxlab-asp':{label:'TaxLab ASP',cat:'asp',letter:'TL',color:'#ff5630',fields:[
    {k:'baseUrl',label:'Axon gateway URL',type:'text',req:true,ph:'https://axon.taxlab.ai',def:'https://axon.taxlab.ai'},
    {k:'clientId',label:'Axon key id',type:'text',req:true,ph:'axon-app-7f3a9c'},
    {k:'clientSecret',label:'Axon key secret',type:'password',req:true},
    {k:'tokenUrl',label:'Token URL',type:'text',req:false,ph:'https://axon.taxlab.ai/api/external-auth/token'},
    {k:'scope',label:'Scope',type:'text',def:'documents:read documents:write'},
    {k:'country',label:'Country',type:'select',def:'AE',opts:[{v:'AE',l:'UAE'},{v:'SA',l:'Saudi Arabia'},{v:'MY',l:'Malaysia'}]},
    /* ── Inbound webhook (TaxLab Acknowledgement spec §3.3) ──
       Used when this Connection is bound to a webhook-receiver integration
       (e.g. `taxlab-ack-receiver` recipe). The encryption key is HS256 (32+
       bytes). It's stored encrypted at rest and never written into route YAML. */
    {k:'webhookSection',label:'━━━ Inbound Webhook (Acknowledgements) ━━━',type:'header'},
    {k:'webhookAuthType',label:'Webhook Auth Type',type:'select',def:'jwt',
      opts:[{v:'jwt',l:'JWT (HS256) — recommended'},{v:'basic',l:'Basic'},{v:'none',l:'None (testing only)'}]},
    {k:'webhookEncryptionKey',label:'Webhook Encryption Key (HS256 shared secret, 32+ bytes)',type:'password',sw:{f:'webhookAuthType',v:'jwt'}},
    {k:'webhookAllowedAudience',label:'Allowed Audience (your public hostname)',type:'text',ph:'acme-prod.example.com',sw:{f:'webhookAuthType',v:'jwt'}},
    {k:'webhookBasicUsername',label:'Webhook Username',type:'text',sw:{f:'webhookAuthType',v:'basic'}},
    {k:'webhookBasicPassword',label:'Webhook Password',type:'password',sw:{f:'webhookAuthType',v:'basic'}},
  ]},
  'avalara':{label:'Avalara',cat:'asp',letter:'A',color:'#E0182D',fields:[
    {k:'accountId',label:'Account ID',type:'text',req:true},
    {k:'licenseKey',label:'License Key',type:'password',req:true},
    {k:'environment',label:'Environment',type:'select',req:true,opts:[{v:'production',l:'Production'},{v:'sandbox',l:'Sandbox'}]},
    {k:'companyCode',label:'Company Code',type:'text',req:true},
  ]},
  'vertex':{label:'Vertex',cat:'asp',letter:'V',color:'#C4162A',fields:[
    {k:'baseUrl',label:'Vertex Cloud URL',type:'text',req:true,ph:'https://calc.vertexsmb.com/vertex-ws/services'},
    {k:'username',label:'Username',type:'text',req:true},
    {k:'password',label:'Password',type:'password',req:true},
    {k:'trustedId',label:'Trusted ID',type:'text'},
    {k:'companyCode',label:'Company Code',type:'text',req:true},
  ]},
  'myinvois':{label:'LHDN MyInvois',cat:'asp',letter:'M',color:'#003087',fields:[
    {k:'clientId',label:'Client ID',type:'text',req:true,ph:'XXXXXXXXXXXXXXXX'},
    {k:'clientSecret',label:'Client Secret',type:'password',req:true},
    {k:'environment',label:'Environment',type:'select',req:true,opts:[{v:'production',l:'Production'},{v:'sandbox',l:'Sandbox (Preprod)'}]},
    {k:'tin',label:'TIN (Tax Identification No.)',type:'text',req:true},
    {k:'idType',label:'ID Type',type:'select',opts:[{v:'BRN',l:'Business Registration No.'},{v:'NRIC',l:'NRIC'},{v:'PASSPORT',l:'Passport'},{v:'ARMY',l:'Army'}]},
    {k:'idValue',label:'ID Value',type:'text',req:true},
  ]},
  'zatca':{label:'ZATCA (Saudi)',cat:'asp',letter:'Z',color:'#006400',fields:[
    {k:'environment',label:'Environment',type:'select',req:true,opts:[{v:'production',l:'Production'},{v:'simulation',l:'Simulation'},{v:'sandbox',l:'Sandbox'}]},
    {k:'vatNumber',label:'VAT Registration No.',type:'text',req:true},
    {k:'certificatePem',label:'Certificate (PEM)',type:'textarea',req:true,ph:'-----BEGIN CERTIFICATE-----\n...'},
    {k:'privateKeyPem',label:'Private Key (PEM)',type:'password',req:true},
    {k:'secretKey',label:'Secret Key',type:'password',req:true},
    {k:'deviceSerial',label:'Device Serial No.',type:'text',req:true},
  ]},

  /* ── CRM ── */
  'salesforce':{label:'Salesforce',cat:'crm',icon:'https://cdn.simpleicons.org/salesforce',fields:[
    {k:'instanceUrl',label:'Instance URL',type:'text',req:true,ph:'https://mycompany.my.salesforce.com'},
    {k:'clientId',label:'Connected App Client ID',type:'text',req:true},
    {k:'clientSecret',label:'Client Secret',type:'password',req:true},
    {k:'username',label:'Username',type:'text',req:true},
    {k:'password',label:'Password + Security Token',type:'password',req:true},
    {k:'apiVersion',label:'API Version',type:'text',def:'v59.0'},
  ]},
  'hubspot':{label:'HubSpot',cat:'crm',icon:'https://cdn.simpleicons.org/hubspot',fields:[
    {k:'apiKey',label:'Private App Token',type:'password',req:true,ph:'pat-na1-xxxx'},
    {k:'portalId',label:'Portal ID (optional)',type:'text'},
  ]},
  'zoho-crm':{label:'Zoho CRM',cat:'crm',icon:'https://cdn.simpleicons.org/zoho',fields:[
    {k:'clientId',label:'Client ID',type:'text',req:true},
    {k:'clientSecret',label:'Client Secret',type:'password',req:true},
    {k:'refreshToken',label:'Refresh Token',type:'password',req:true},
    {k:'dataCenter',label:'Data Center',type:'select',opts:[{v:'com',l:'US (zoho.com)'},{v:'eu',l:'EU (zoho.eu)'},{v:'in',l:'IN (zoho.in)'},{v:'com.au',l:'AU (zoho.com.au)'}]},
  ]},

  /* ── CLOUD STORAGE ── */
  's3':{label:'Amazon S3',cat:'cloud',icon:'https://cdn.simpleicons.org/amazons3',fields:[
    {k:'region',label:'Region',type:'text',req:true,ph:'ap-southeast-1'},
    {k:'bucket',label:'Bucket Name',type:'text',req:true},
    {k:'accessKeyId',label:'Access Key ID',type:'text',req:true},
    {k:'secretAccessKey',label:'Secret Access Key',type:'password',req:true},
    {k:'sessionToken',label:'Session Token (optional)',type:'password'},
    {k:'pathPrefix',label:'Key Prefix (optional)',type:'text',ph:'data/incoming/'},
  ]},
  'azure-blob':{label:'Azure Blob',cat:'cloud',icon:'https://cdn.simpleicons.org/microsoftazure',fields:[
    {k:'connectionString',label:'Connection String',type:'password',req:true,ph:'DefaultEndpointsProtocol=https;AccountName=...'},
    {k:'container',label:'Container Name',type:'text',req:true},
  ]},
  'gcs':{label:'Google Cloud Storage',cat:'cloud',icon:'https://cdn.simpleicons.org/googlecloud',fields:[
    {k:'projectId',label:'Project ID',type:'text',req:true},
    {k:'bucket',label:'Bucket Name',type:'text',req:true},
    {k:'serviceAccountJson',label:'Service Account JSON',type:'textarea',req:true,ph:'{"type":"service_account",...}'},
  ]},
  'sharepoint':{label:'SharePoint',cat:'cloud',icon:'https://cdn.simpleicons.org/microsoftsharepoint',fields:[
    {k:'tenantId',label:'Tenant ID',type:'text',req:true},
    {k:'clientId',label:'Client ID',type:'text',req:true},
    {k:'clientSecret',label:'Client Secret',type:'password',req:true},
    {k:'siteUrl',label:'Site URL',type:'text',req:true,ph:'https://company.sharepoint.com/sites/MySite'},
    {k:'libraryName',label:'Document Library',type:'text',def:'Documents'},
  ]},
  'box':{label:'Box',cat:'cloud',icon:'https://cdn.simpleicons.org/box',fields:[
    {k:'clientId',label:'Client ID',type:'text',req:true},
    {k:'clientSecret',label:'Client Secret',type:'password',req:true},
    {k:'enterpriseId',label:'Enterprise ID',type:'text',req:true},
    {k:'jwtKeyId',label:'JWT Key ID',type:'text'},
    {k:'jwtPrivateKey',label:'JWT Private Key',type:'textarea'},
  ]},

  /* ── MESSAGING ── */
  'kafka':{label:'Apache Kafka',cat:'messaging',icon:'https://cdn.simpleicons.org/apachekafka',fields:[
    {k:'brokers',label:'Bootstrap Servers',type:'text',req:true,ph:'broker1:9092,broker2:9092'},
    {k:'securityProtocol',label:'Security Protocol',type:'select',opts:[{v:'PLAINTEXT',l:'PLAINTEXT'},{v:'SSL',l:'SSL'},{v:'SASL_PLAINTEXT',l:'SASL_PLAINTEXT'},{v:'SASL_SSL',l:'SASL_SSL'}]},
    {k:'saslMechanism',label:'SASL Mechanism',type:'select',opts:[{v:'PLAIN',l:'PLAIN'},{v:'SCRAM-SHA-256',l:'SCRAM-SHA-256'},{v:'SCRAM-SHA-512',l:'SCRAM-SHA-512'},{v:'GSSAPI',l:'GSSAPI'}],sw:{f:'securityProtocol',v:'SASL_SSL'}},
    {k:'saslUsername',label:'SASL Username',type:'text',sw:{f:'securityProtocol',v:'SASL_SSL'}},
    {k:'saslPassword',label:'SASL Password',type:'password',sw:{f:'securityProtocol',v:'SASL_SSL'}},
    {k:'groupId',label:'Consumer Group ID',type:'text',ph:'my-consumer-group'},
  ]},
  'rabbitmq':{label:'RabbitMQ',cat:'messaging',icon:'https://cdn.simpleicons.org/rabbitmq',fields:[
    {k:'host',label:'Host',type:'text',req:true,def:'localhost'},
    {k:'port',label:'Port',type:'number',req:true,def:'5672'},
    {k:'username',label:'Username',type:'text',req:true,def:'guest'},
    {k:'password',label:'Password',type:'password',req:true},
    {k:'virtualHost',label:'Virtual Host',type:'text',def:'/'},
    {k:'tls',label:'TLS/SSL',type:'select',opts:[{v:'false',l:'No'},{v:'true',l:'Yes'}]},
  ]},
  'activemq':{label:'ActiveMQ',cat:'messaging',letter:'A',color:'#D22128',fields:[
    {k:'brokerUrl',label:'Broker URL',type:'text',req:true,ph:'tcp://localhost:61616'},
    {k:'username',label:'Username',type:'text'},
    {k:'password',label:'Password',type:'password'},
    {k:'destinationType',label:'Destination Type',type:'select',opts:[{v:'queue',l:'Queue'},{v:'topic',l:'Topic'}]},
  ]},
  'azure-servicebus':{label:'Azure Service Bus',cat:'messaging',icon:'https://cdn.simpleicons.org/microsoftazure',fields:[
    {k:'connectionString',label:'Connection String',type:'password',req:true,ph:'Endpoint=sb://xxx.servicebus.windows.net/;SharedAccessKeyName=...'},
    {k:'entityName',label:'Queue / Topic Name',type:'text',req:true},
    {k:'subscriptionName',label:'Subscription Name (if Topic)',type:'text'},
  ]},
  'aws-sqs':{label:'Amazon SQS',cat:'messaging',icon:'https://cdn.simpleicons.org/amazonsqs',fields:[
    {k:'region',label:'Region',type:'text',req:true,ph:'us-east-1'},
    {k:'queueUrl',label:'Queue URL',type:'text',req:true,ph:'https://sqs.us-east-1.amazonaws.com/123456789/my-queue'},
    {k:'accessKeyId',label:'Access Key ID',type:'text',req:true},
    {k:'secretAccessKey',label:'Secret Access Key',type:'password',req:true},
  ]},

  /* ── PROTOCOL ── */
  'rest-api':{label:'REST API',cat:'protocol',letter:'R',color:'#4CAF50',fields:[
    {k:'baseUrl',label:'Base URL',type:'text',req:true,ph:'https://api.example.com/v1'},
    {k:'authType',label:'Auth Type',type:'select',opts:[{v:'none',l:'None'},{v:'bearer',l:'Bearer Token'},{v:'basic',l:'Basic Auth'},{v:'apikey',l:'API Key'},{v:'oauth2',l:'OAuth 2.0'}]},
    {k:'bearerToken',label:'Bearer Token',type:'password',sw:{f:'authType',v:'bearer'}},
    {k:'basicUser',label:'Username',type:'text',sw:{f:'authType',v:'basic'}},
    {k:'basicPass',label:'Password',type:'password',sw:{f:'authType',v:'basic'}},
    {k:'apiKeyHeader',label:'API Key Header',type:'text',def:'X-API-Key',sw:{f:'authType',v:'apikey'}},
    {k:'apiKeyValue',label:'API Key Value',type:'password',sw:{f:'authType',v:'apikey'}},
    {k:'oauthTokenUrl',label:'Token URL',type:'text',sw:{f:'authType',v:'oauth2'}},
    {k:'oauthClientId',label:'Client ID',type:'text',sw:{f:'authType',v:'oauth2'}},
    {k:'oauthClientSecret',label:'Client Secret',type:'password',sw:{f:'authType',v:'oauth2'}},
    {k:'sslVerify',label:'Verify SSL',type:'select',opts:[{v:'true',l:'Yes'},{v:'false',l:'No'}]},
    {k:'timeout',label:'Timeout (seconds)',type:'number',def:'30'},
  ]},
  'soap':{label:'SOAP',cat:'protocol',letter:'S',color:'#005A9C',fields:[
    {k:'wsdlUrl',label:'WSDL URL',type:'text',req:true,ph:'http://service.example.com/MyService?wsdl'},
    {k:'serviceEndpoint',label:'Service Endpoint',type:'text'},
    {k:'soapAction',label:'SOAPAction (optional)',type:'text'},
    {k:'username',label:'Username (optional)',type:'text'},
    {k:'password',label:'Password (optional)',type:'password'},
  ]},
  'graphql':{label:'GraphQL',cat:'protocol',icon:'https://cdn.simpleicons.org/graphql',fields:[
    {k:'endpoint',label:'GraphQL Endpoint',type:'text',req:true,ph:'https://api.example.com/graphql'},
    {k:'authType',label:'Auth Type',type:'select',opts:[{v:'none',l:'None'},{v:'bearer',l:'Bearer Token'},{v:'apikey',l:'API Key'}]},
    {k:'token',label:'Token / API Key',type:'password',sw:{f:'authType',v:'bearer'}},
    {k:'apiKeyHeader',label:'Header Name',type:'text',sw:{f:'authType',v:'apikey'}},
    {k:'apiKeyValue',label:'API Key Value',type:'password',sw:{f:'authType',v:'apikey'}},
    {k:'introspectionEnabled',label:'Enable Introspection',type:'select',opts:[{v:'true',l:'Yes'},{v:'false',l:'No'}]},
  ]},

  /* ── FILE / EMAIL ── */
  'sftp':{label:'SFTP',cat:'file',letter:'S',color:'#2196F3',fields:[
    {k:'host',label:'Host',type:'text',req:true},
    {k:'port',label:'Port',type:'number',req:true,def:'22'},
    {k:'username',label:'Username',type:'text',req:true},
    {k:'authMode',label:'Auth Mode',type:'select',opts:[{v:'password',l:'Password'},{v:'key',l:'Private Key'}]},
    {k:'password',label:'Password',type:'password',sw:{f:'authMode',v:'password'}},
    {k:'privateKey',label:'Private Key (PEM)',type:'textarea',sw:{f:'authMode',v:'key'}},
    {k:'passphrase',label:'Key Passphrase',type:'password',sw:{f:'authMode',v:'key'}},
    {k:'directory',label:'Remote Directory',type:'text',def:'/'},
  ]},
  'ftp':{label:'FTP / FTPS',cat:'file',letter:'F',color:'#FF9800',fields:[
    {k:'host',label:'Host',type:'text',req:true},
    {k:'port',label:'Port',type:'number',req:true,def:'21'},
    {k:'username',label:'Username',type:'text',req:true},
    {k:'password',label:'Password',type:'password',req:true},
    {k:'protocol',label:'Protocol',type:'select',opts:[{v:'ftp',l:'FTP'},{v:'ftps',l:'FTPS (Explicit)'},{v:'ftps-implicit',l:'FTPS (Implicit)'}]},
    {k:'directory',label:'Remote Directory',type:'text',def:'/'},
    {k:'passiveMode',label:'Passive Mode',type:'select',opts:[{v:'true',l:'Yes'},{v:'false',l:'No'}]},
  ]},
  'smtp':{label:'SMTP / Email',cat:'file',icon:'https://cdn.simpleicons.org/maildotru',fields:[
    {k:'host',label:'SMTP Host',type:'text',req:true,ph:'smtp.gmail.com'},
    {k:'port',label:'Port',type:'number',req:true,def:'587'},
    {k:'username',label:'Username / Email',type:'text',req:true},
    {k:'password',label:'Password / App Password',type:'password',req:true},
    {k:'security',label:'Security',type:'select',opts:[{v:'STARTTLS',l:'STARTTLS'},{v:'SSL',l:'SSL/TLS'},{v:'NONE',l:'None'}]},
    {k:'fromAddress',label:'Default From Address',type:'text'},
  ]},

  /* ── EDI ── */
  'as2':{label:'AS2',cat:'edi',letter:'A',color:'#607D8B',fields:[
    {k:'as2Id',label:'Your AS2 ID',type:'text',req:true},
    {k:'partnerId',label:'Partner AS2 ID',type:'text',req:true},
    {k:'partnerUrl',label:'Partner AS2 URL',type:'text',req:true},
    {k:'signingAlg',label:'Signing Algorithm',type:'select',opts:[{v:'SHA256',l:'SHA-256'},{v:'SHA1',l:'SHA-1'}]},
    {k:'certificate',label:'Signing Certificate (PEM)',type:'textarea'},
    {k:'privateKey',label:'Private Key (PEM)',type:'password'},
  ]},
  'edifact':{label:'EDIFACT',cat:'edi',letter:'E',color:'#795548',fields:[
    {k:'version',label:'EDIFACT Version',type:'select',opts:[{v:'D01B',l:'D01B'},{v:'D96A',l:'D96A'},{v:'D93A',l:'D93A'}]},
    {k:'senderId',label:'Sender ID',type:'text',req:true},
    {k:'senderQualifier',label:'Sender Qualifier',type:'text',def:'14'},
    {k:'recipientId',label:'Recipient ID',type:'text',req:true},
    {k:'recipientQualifier',label:'Recipient Qualifier',type:'text',def:'14'},
    {k:'transport',label:'Transport',type:'select',opts:[{v:'sftp',l:'SFTP'},{v:'as2',l:'AS2'},{v:'http',l:'HTTP'}]},
  ]},
};

/* ===== TRIGGER TYPES ===== */
const TRIGGER_TYPES=[
  {id:'scheduled',label:'Scheduled',desc:'Run on cron or fixed interval'},
  {id:'rest',label:'REST / Webhook',desc:'Expose an HTTP endpoint'},
  {id:'app-event',label:'App Event',desc:'Trigger on source system event'},
  {id:'file-watch',label:'File / Storage Watch',desc:'React to new files'},
  {id:'message-queue',label:'Message Queue',desc:'Consume from Kafka, RabbitMQ, SQS…'},
  {id:'db-poll',label:'Database Poll',desc:'Poll a database table / view'},
  {id:'manual',label:'Manual / On-Demand',desc:'Trigger via Run Now button'},
];

const TRIGGER_CONFIGS={
  'scheduled':[
    {k:'scheduleType',label:'Schedule Type',type:'select',def:'interval',opts:[{v:'interval',l:'Fixed Interval'},{v:'cron',l:'Cron Expression'}]},
    {k:'intervalValue',label:'Interval Value',type:'number',def:'5',sw:{f:'scheduleType',v:'interval'}},
    {k:'intervalUnit',label:'Unit',type:'select',def:'MINUTES',opts:[{v:'SECONDS',l:'Seconds'},{v:'MINUTES',l:'Minutes'},{v:'HOURS',l:'Hours'},{v:'DAYS',l:'Days'}],sw:{f:'scheduleType',v:'interval'}},
    {k:'cronExpression',label:'Cron Expression',type:'text',ph:'0 0/5 * * * ?',sw:{f:'scheduleType',v:'cron'}},
    {k:'timezone',label:'Timezone',type:'text',def:'UTC'},
    {k:'startDate',label:'Start Date (optional)',type:'text',ph:'2025-01-01T00:00:00'},
  ],
  'rest':[
    {k:'httpMethod',label:'HTTP Method',type:'select',def:'POST',opts:[{v:'POST',l:'POST'},{v:'GET',l:'GET'},{v:'PUT',l:'PUT'},{v:'PATCH',l:'PATCH'}]},
    {k:'path',label:'Webhook Path',type:'text',req:true,ph:'/webhooks/my-flow'},
    {k:'authRequired',label:'Require Auth',type:'select',def:'true',opts:[{v:'true',l:'Yes (API Key)'},{v:'false',l:'No (Public)'}]},
    {k:'responseMode',label:'Response Mode',type:'select',def:'async',opts:[{v:'async',l:'Async (202 Accepted)'},{v:'sync',l:'Sync (wait for result)'}]},
    {k:'payloadFormat',label:'Payload Format',type:'select',def:'json',opts:[{v:'json',l:'JSON'},{v:'xml',l:'XML'},{v:'form',l:'Form Data'}]},
  ],
  'app-event':[
    {k:'eventSource',label:'Event Source Adapter',type:'text',req:true,ph:'oracle-fusion'},
    {k:'eventType',label:'Event Type',type:'text',req:true,ph:'INVOICE_CREATED'},
    {k:'filterExpression',label:'Filter Expression (optional)',type:'text',ph:'status == "APPROVED"'},
    {k:'pollingInterval',label:'Polling Interval (sec)',type:'number',def:'30'},
  ],
  'file-watch':[
    {k:'connectionId',label:'Storage Connection',type:'text',req:true,ph:'Select a connection'},
    {k:'directory',label:'Watch Directory / Prefix',type:'text',req:true,ph:'/incoming'},
    {k:'filePattern',label:'File Pattern',type:'text',def:'*',ph:'*.xml'},
    {k:'pollInterval',label:'Poll Interval (sec)',type:'number',def:'60'},
    {k:'moveAfterRead',label:'Move After Read',type:'select',def:'true',opts:[{v:'true',l:'Yes'},{v:'false',l:'No'}]},
    {k:'moveDirectory',label:'Move To Directory',type:'text',ph:'/processed'},
  ],
  'message-queue':[
    {k:'connectionId',label:'Broker Connection',type:'text',req:true,ph:'Select a connection'},
    {k:'topic',label:'Topic / Queue Name',type:'text',req:true},
    {k:'consumerGroup',label:'Consumer Group',type:'text',ph:'my-integration-group'},
    {k:'maxMessages',label:'Max Messages per Poll',type:'number',def:'100'},
    {k:'autoCommit',label:'Auto Commit Offset',type:'select',def:'true',opts:[{v:'true',l:'Yes'},{v:'false',l:'No'}]},
  ],
  'db-poll':[
    {k:'connectionId',label:'DB Connection',type:'text',req:true,ph:'Select a connection'},
    {k:'pollQuery',label:'Poll SQL Query',type:'textarea',req:true,ph:'SELECT * FROM staging_table WHERE processed = 0'},
    {k:'markProcessedQuery',label:'Mark Processed Query',type:'textarea',ph:'UPDATE staging_table SET processed=1 WHERE id=?'},
    {k:'pollInterval',label:'Poll Interval (sec)',type:'number',def:'60'},
    {k:'maxRows',label:'Max Rows per Poll',type:'number',def:'500'},
  ],
  'manual':[
    {k:'description',label:'Description',type:'textarea',ph:'Describe when / how to trigger manually'},
    {k:'allowRerun',label:'Allow Re-run',type:'select',def:'true',opts:[{v:'true',l:'Yes'},{v:'false',l:'No'}]},
  ],
};

/* ===== HELPERS ===== */
function genId(){return Date.now().toString(36)+Math.random().toString(36).slice(2,7)}
function fmt(n){return n==null?'0':Number(n).toLocaleString()}
function fmtDate(s){if(!s)return'—';try{return new Date(s).toLocaleString();}catch{return s}}
function fmtDur(ms){if(ms==null||ms===undefined)return'—';if(ms===0)return'<1ms';if(ms<1000)return ms+'ms';if(ms<60000)return(ms/1000).toFixed(1)+'s';return(ms/60000).toFixed(1)+'m'}
function adapterMeta(id){return ADAPTERS[id]||{label:id,icon:'',fields:[]}}
function adapterLabel(id){return adapterMeta(id).label}
function triggerMeta(id){return TRIGGER_TYPES.find(t=>t.id===id)||{id,label:id}}

/* ===== VALIDATION ===== */
// `saved`: the secret fields the server says hold a value (Connection.secretsSet). The
// API never returns a secret, so an edit form shows them blank -- and a blank saved secret
// is kept on save, so it is not "missing".
function validateConnForm(adapter,values,saved){
  const errs={};
  const kept=new Set(saved||[]);
  if(!adapter){errs._adapter='Please select an adapter type';return errs;}
  const meta=ADAPTERS[adapter];
  if(!meta)return errs;
  (meta.fields||[]).forEach(f=>{
    if(!f.req)return;
    if(f.sw){
      const dep=values[f.sw.f];
      if(dep!==f.sw.v)return;
    }
    const val=values[f.k];
    if(!val||String(val).trim()===''){
      if(kept.has(f.k))return;
      errs[f.k]=`${f.label} is required`;
    }
  });
  if(!values.name||!values.name.trim())errs.name='Connection name is required';
  return errs;
}

function cleanName(raw){
  return raw.trim().replace(/[^\w\s\-\.\,]/g,'').replace(/\s+/g,' ').trim();
}
function validateIntegrationForm(values){
  const errs={};
  const raw=values.name||'';
  if(!raw.trim())errs.name='Integration name is required';
  else{
    const c=cleanName(raw);
    if(c!==raw)errs.name='Name contains special characters — use letters, numbers, spaces, - . , only';
    if(c.length<2)errs.name='Name must be at least 2 characters after cleaning';
  }
  if(!values.triggerType)errs.triggerType='Select a trigger type';
  return errs;
}

/* ===== ADAPTER→CONNECTION PAYLOAD BUILDER ===== */
const ADAPTER_TYPE_MAP={
  'oracle-db':'DATABASE','mysql':'DATABASE','postgresql':'DATABASE','mssql':'DATABASE',
  'mongodb':'MONGODB','snowflake':'REST','oracle-fusion':'ORACLE_FUSION','oracle-ebs':'REST',
  'sap-s4hana':'SAP','sap-s4hana-cpi':'SAP','sap-ecc':'SAP','dynamics-365':'DYNAMICS','dynamics365-bc':'DYNAMICS','salesforce':'SALESFORCE',
  'servicenow':'SERVICENOW','workday':'WORKDAY','netsuite':'REST','infor':'REST',
  'epicor':'REST','hubspot':'REST','salesforce-mc':'REST','marketo':'REST','zoho-crm':'REST',
  'taxlab':'REST','myinvois':'REST','zatca':'REST','avalara':'REST','gs1':'REST',
  'google-drive':'REST','aws-s3':'AWS_S3','azure-blob':'AZURE_BLOB','sharepoint':'REST','box':'REST',
  'rabbitmq':'RABBITMQ','kafka':'KAFKA','jms':'JMS','activemq':'JMS','mqtt':'JMS','azure-sb':'JMS',
  'rest':'REST','soap':'SOAP','graphql':'REST','grpc':'REST',
  'sftp':'SFTP','ftp':'FTP','as2':'AS2','email':'EMAIL','smtp':'EMAIL',
  'edifact':'FILE','x12':'FILE','ansi':'FILE','csv':'FILE',
};
const AUTH_TYPE_MAP={
  'basic':'BASIC','oauth2':'OAUTH2_CLIENT_CREDENTIALS','bearer':'BEARER_TOKEN',
  'apikey':'API_KEY','api_key':'API_KEY','none':'NONE',
};
/* Strip accidentally pasted JSON wrappers from secret fields, e.g.
   `  "client_secret": "abc123"` → `abc123` */
function stripSecretWrapper(v){
  if(!v)return v;
  const m=v.match(/^\s*"?[\w_-]+"?\s*:\s*"([^"]+)"\s*$/);
  return m?m[1]:v;
}
function buildConnectionPayload(adapter,config,name,description){
  const host=config.baseUrl||config.host||config.instanceUrl||config.brokers||
              config.connectionString||config.account||config.endpoint||'';
  const port=config.port?parseInt(config.port,10):null;
  const rawAuth=(config.authType||'').toLowerCase();
  const authType=AUTH_TYPE_MAP[rawAuth]||null;
  // Sanitize secrets before serialising — guards against pasted JSON snippets
  const sanitized={...config,
    clientSecret:stripSecretWrapper(config.clientSecret),
    password:stripSecretWrapper(config.password),
    apiKey:stripSecretWrapper(config.apiKey),
    webhookEncryptionKey:stripSecretWrapper(config.webhookEncryptionKey),
    webhookBasicPassword:stripSecretWrapper(config.webhookBasicPassword),
  };
  // extra fields stored as JSON for display/edit round-trip.
  // Strip credential fields — they are never shown when editing and are
  // handled separately by the top-level payload. Keeping them out of
  // additionalConfig avoids plaintext secrets in the JSON body.
  const {password:_,clientSecret:__,apiKey:___,privateKey:____,webhookEncryptionKey:_____,webhookBasicPassword:______,...extraConfig}=sanitized;
  const additionalConfig=JSON.stringify(extraConfig);
  return{
    name,description,adapter,
    type:ADAPTER_TYPE_MAP[adapter]||'REST',
    // Write the same URL into BOTH host and basePath so connections created
    // through the manual form interop with recipes (which read basePath) and
    // vice versa. Without this, recipe-installed connections re-opened in
    // the manual edit form lose their URL on first save.
    host,basePath:host,port:port||null,
    authType,
    username:sanitized.username||null,
    ...(sanitized.password?{password:sanitized.password}:null),
    ...(sanitized.clientId?{clientId:sanitized.clientId}:null),
    ...(sanitized.clientSecret?{clientSecret:sanitized.clientSecret}:null),
    // NetSuite OAuth2 M2M (JWT client-assertion) — no client secret.
    certificateId:sanitized.certificateId||null,
    ...(sanitized.privateKey?{privateKey:sanitized.privateKey}:null),
    tokenUrl:sanitized.tokenUrl||null,
    scope:sanitized.scope||null,
    ...(sanitized.apiKey?{apiKey:sanitized.apiKey}:null),
    apiKeyHeader:sanitized.apiKeyHeader||null,
    // Inbound webhook auth (TaxLab Acknowledgement spec)
    webhookAuthType:sanitized.webhookAuthType||null,
    ...(sanitized.webhookEncryptionKey?{webhookEncryptionKey:sanitized.webhookEncryptionKey}:null),
    ...(sanitized.webhookBasicUsername?{webhookBasicUsername:sanitized.webhookBasicUsername}:null),
    ...(sanitized.webhookBasicPassword?{webhookBasicPassword:sanitized.webhookBasicPassword}:null),
    webhookAllowedAudience:sanitized.webhookAllowedAudience||null,
    additionalConfig,
    // status intentionally omitted — backend sets DRAFT on create; pass separately for updates
  };
}
/* Restore config map from saved connection (round-trip) */
function loadConfigFromConnection(conn){
  if(!conn)return{};
  if(conn.additionalConfig){
    try{
      const parsed=JSON.parse(conn.additionalConfig);
      // Connections created by the recipe install wizard store the URL on
      // conn.basePath (not in additionalConfig). Hydrate baseUrl back into
      // the form when additionalConfig was saved without it.
      if(!parsed.baseUrl && (conn.basePath||conn.host)) parsed.baseUrl = conn.basePath || conn.host;
      // Webhook fields live on flat Connection columns — overlay them on
      // top of additionalConfig so the form always shows current values.
      if(conn.webhookAuthType) parsed.webhookAuthType = conn.webhookAuthType;
      if(conn.webhookAllowedAudience) parsed.webhookAllowedAudience = conn.webhookAllowedAudience;
      if(conn.webhookBasicUsername) parsed.webhookBasicUsername = conn.webhookBasicUsername;
      // No secret is hydrated: the API never returns one (secretsSet says which
      // are saved). The user sees an empty field and either types a new value
      // (rotation) or leaves it blank, which the server keeps as it was.
      return parsed;
    }catch(e){}
  }
  // fallback: reconstruct from flat fields. basePath is the canonical URL slot
  // (recipe wizard); host is the legacy slot (manual connection form).
  const url = conn.basePath || conn.host || '';
  return{
    baseUrl:url, host:url,
    username:conn.username||'',password:conn.password||'',
    authType:conn.authType?conn.authType.toLowerCase().replace('oauth2_client_credentials','oauth2'):'',
    clientId:conn.clientId||'',clientSecret:conn.clientSecret||'',
    tokenUrl:conn.tokenUrl||'',scope:conn.scope||'',
    apiKey:conn.apiKey||'',
    webhookAuthType:conn.webhookAuthType||'',
    webhookAllowedAudience:conn.webhookAllowedAudience||'',
    webhookBasicUsername:conn.webhookBasicUsername||'',
  };
}

/* ===== API (proxy-auth aware) ===== */
const PROXY_PATHS=['/api/external-auth','/api/invoice-endpoints'];
const API_BASE=location.origin;

function isProxyPath(p){
  return PROXY_PATHS.some(x=>p===x||p.startsWith(x+'/'));
}

// ── Token management ──────────────────────────────────────────────────────
function parseJwt(t){try{const p=t.split('.')[1];return JSON.parse(atob(p.replace(/-/g,'+').replace(/_/g,'/')));}catch{return{};}}
function parseAuthUser(){try{const t=localStorage.getItem('kc_token');if(!t)return null;const u=localStorage.getItem('kc_user');return{token:t,refreshToken:localStorage.getItem('kc_refresh'),username:u?JSON.parse(u).preferred_username:null};}catch{return null;}}

function saveAuthData(data){
  localStorage.setItem('kc_token',data.access_token);
  if(data.refresh_token) localStorage.setItem('kc_refresh',data.refresh_token);
  const user=parseJwt(data.access_token);
  localStorage.setItem('kc_user',JSON.stringify(user));
  window.__authData={token:data.access_token,refreshToken:data.refresh_token||localStorage.getItem('kc_refresh'),username:user.preferred_username,fullName:(user.name||user.preferred_username||'User'),email:user.email||'',roles:user.realm_access&&user.realm_access.roles?user.realm_access.roles:[]};
}

function clearAuthData(){
  localStorage.removeItem('kc_token');localStorage.removeItem('kc_refresh');localStorage.removeItem('kc_user');
  localStorage.removeItem('kc_auth');localStorage.removeItem('kc_client');
  window.__authData=null;
}

async function ensureValidToken(){
  const a=window.__authData;
  if(!a||!a.token)return null;
  const payload=parseJwt(a.token);
  const expiresIn=(payload.exp||0)-Date.now()/1000;
  if(expiresIn>60)return a.token; // still valid
  if(!a.refreshToken){clearAuthData();return null;}
  try{
    const resp=await fetch('/auth/realms/axon/protocol/openid-connect/token',{
      method:'POST',headers:{'Content-Type':'application/x-www-form-urlencoded'},
      body:new URLSearchParams({grant_type:'refresh_token',client_id:authClientId(),refresh_token:a.refreshToken})
    });
    if(!resp.ok){clearAuthData();return null;}
    const data=await resp.json();
    saveAuthData(data);
    return data.access_token;
  }catch{clearAuthData();return null;}
}

// Does the RUNNING server accept this token? `exp` is a claim the token makes
// about itself, not proof the server will honour it: a token signed by a key
// Keycloak has since rotated, or minted against a realm that was re-imported,
// still carries a future exp. Trusting exp alone is what let a dead session
// render the whole app and then 401 on every page.
//
// /api/auth/me is permitAll, so it answers without a token at all:
//   no token      -> 200 {"authenticated":false}
//   good token    -> 200 {"authenticated":true,...}
//   rejected token-> 401
// Anything other than authenticated:true means the session is dead.
async function serverAcceptsToken(token){
  try{
    const r=await fetch('/api/auth/me',{
      method:'GET',
      headers:{Accept:'application/json',Authorization:'Bearer '+token},
      credentials:'omit'
    });
    if(!r.ok)return false;
    const body=await r.json();
    return !!body.authenticated;
  }catch{ return false; }
}

async function loginWithCredentials(username,password){
  const resp=await fetch('/auth/realms/axon/protocol/openid-connect/token',{
    method:'POST',headers:{'Content-Type':'application/x-www-form-urlencoded'},
    body:new URLSearchParams({grant_type:'password',client_id:'axon-spa',username,password,scope:'openid profile email roles'})
  });
  if(!resp.ok){const e=await resp.json().catch(()=>({error_description:'Login failed'}));throw new Error(e.error_description||e.error||'Login failed');}
  const data=await resp.json();
  saveAuthData(data);
  try{localStorage.setItem('kc_client','axon-spa');}catch{}
  return data;
}

// Local login: the login form's credentials, checked by Axon itself (BreakglassService) --
// no Keycloak involved, so it works when Keycloak is down. Same response shape as a Keycloak
// token call; no refresh token, so the session simply ends when the token expires.
async function loginBreakglass(username,password){
  const resp=await fetch('/api/auth/breakglass',{
    method:'POST',headers:{'Content-Type':'application/json'},
    body:JSON.stringify({username,password})
  });
  const data=await resp.json().catch(()=>({}));
  if(!resp.ok)throw new Error(data.error_description||data.error||('Break-glass sign-in failed ('+resp.status+')'));
  saveAuthData(data);
  try{localStorage.setItem('kc_client','axon-spa');}catch{}
  return data;
}

// ── "Sign in with Keycloak" ────────────────────────────────────────────────
// The realm's own login page, beside the username/password form: authorization
// code + PKCE (S256) on axon-spa. The browser goes to Keycloak only for the page
// itself; discovery and the code exchange go through Axon's /auth proxy like the
// password grant does, so Keycloak needs no web origin for Axon. The verifier and
// state live in sessionStorage for the one round trip and are removed on return.
const SSO_PENDING_KEY='axon_sso_pending';
// Which Keycloak client this session belongs to: axon-spa (the staff console) or
// axon-portal (customers' developer portal, 2026-10-17). Token refresh and sign-out must
// use the client that issued the token. A portal session renders the customer portal,
// never the console (see App) -- the token, not the user, decides.
function authClientId(){try{return localStorage.getItem('kc_client')||'axon-spa';}catch{return 'axon-spa';}}
function isCustomerSession(){const a=window.__authData;return !!(a&&a.token)&&parseJwt(a.token).azp==='axon-portal';}
function b64url(bytes){
  let s='';bytes.forEach(b=>{s+=String.fromCharCode(b);});
  return btoa(s).replace(/\+/g,'-').replace(/\//g,'_').replace(/=+$/,'');
}
async function startKeycloakLogin(clientId){
  clientId=clientId||'axon-spa';
  const r=await fetch('/auth/realms/axon/.well-known/openid-configuration');
  if(!r.ok)throw new Error('Keycloak is not reachable ('+r.status+')');
  const disc=await r.json();
  const verifier=b64url(crypto.getRandomValues(new Uint8Array(32)));
  const challenge=b64url(new Uint8Array(await crypto.subtle.digest('SHA-256',new TextEncoder().encode(verifier))));
  const state=b64url(crypto.getRandomValues(new Uint8Array(16)));
  const redirectUri=location.origin+'/';
  sessionStorage.setItem(SSO_PENDING_KEY,JSON.stringify({verifier,state,redirectUri,clientId}));
  const q=new URLSearchParams({client_id:clientId,response_type:'code',
    scope:clientId==='axon-portal'?'openid profile email':'openid profile email roles',
    redirect_uri:redirectUri,state,code_challenge:challenge,code_challenge_method:'S256'});
  location.assign(disc.authorization_endpoint+'?'+q.toString());
}
// On page load: if Keycloak has just sent the browser back here, finish the sign-in.
// Returns the token response, null when this load is not a return from Keycloak, or
// throws with a message for the login page.
async function completeKeycloakLogin(){
  const p=new URLSearchParams(location.search);
  if(!p.has('state')||!(p.has('code')||p.has('error')))return null;
  const raw=sessionStorage.getItem(SSO_PENDING_KEY);
  sessionStorage.removeItem(SSO_PENDING_KEY);
  history.replaceState(null,'',location.pathname+location.hash);  // the code is single-use; keep it out of history
  if(p.get('error'))throw new Error(p.get('error_description')||p.get('error'));
  const pending=raw?JSON.parse(raw):null;
  if(!pending||pending.state!==p.get('state'))throw new Error('That sign-in was not started from this tab. Please try again.');
  const resp=await fetch('/auth/realms/axon/protocol/openid-connect/token',{
    method:'POST',headers:{'Content-Type':'application/x-www-form-urlencoded'},
    body:new URLSearchParams({grant_type:'authorization_code',client_id:pending.clientId||'axon-spa',code:p.get('code'),
      redirect_uri:pending.redirectUri,code_verifier:pending.verifier})
  });
  if(!resp.ok){const e=await resp.json().catch(()=>({}));throw new Error(e.error_description||e.error||'Keycloak sign-in failed');}
  const data=await resp.json();
  saveAuthData(data);
  try{localStorage.setItem('kc_client',pending.clientId||'axon-spa');}catch{}
  return data;
}

function logout(){
  // End the Keycloak session too, not just this tab's tokens: otherwise "Sign in
  // with Keycloak" walks straight back in on Keycloak's cookie. Best effort.
  const rt=(window.__authData&&window.__authData.refreshToken)||localStorage.getItem('kc_refresh');
  if(rt){
    fetch('/auth/realms/axon/protocol/openid-connect/logout',{method:'POST',
      headers:{'Content-Type':'application/x-www-form-urlencoded'},
      body:new URLSearchParams({client_id:authClientId(),refresh_token:rt})}).catch(()=>{});
  }
  // Drop the observability cookie too, or Grafana/Zipkin stay open in this browser
  // for the rest of its 10 minutes after sign-out.
  fetch('/api/auth/tools-session',{method:'DELETE'}).catch(()=>{});
  clearAuthData();
  if(window.__onLogout)window.__onLogout();
}

// The management/control-plane API. Mirrors MANAGEMENT_API in SecurityConfig —
// every path here is .authenticated() server-side, so the SPA must attach a
// token or the call 401s.
//
// Deliberately NOT "everything under /api/": that namespace is shared with
// Camel platform-http integration ingress (/api/cmk/..., /api/sap/...,
// /api/webhooks/...), which is open and authenticates per-route. Listing the
// control plane explicitly is what keeps the two apart on both sides.
//
// Keep this list and SecurityConfig.MANAGEMENT_API in step. Adding a path here
// but not there leaves an endpoint open to the internet; adding it there but
// not here makes the page 401 and bounce the user to the login modal.
const MANAGEMENT_API_PATHS=['/api/integrations','/api/connections','/api/projects','/api/routes','/api/lookups','/api/recipes','/api/executions','/api/metrics','/api/catalog','/api/scheduled','/api/readiness','/api/apisix-admin','/api/portal'];

// Open under /api/ even though they look like control plane. /api/auth/me is
// the session probe (it must answer before a token exists); the rest are
// customer-facing or gateway-internal and are permitAll server-side.
//
// NOTE: deliberately does NOT list PROXY_PATHS (/api/external-auth,
// /api/invoice-endpoints). Those are permitAll in SecurityConfig only because
// Axon forwards them to APISIX, and APISIX's openid-connect plugin validates a
// JWT itself — so the SPA must still send one. Listing them here would make
// isProxyPath() unreachable and every gateway call 401 at the gateway.
// /api/platform was here and is not any more: unauthenticated it served the exact Camel,
// Spring Boot and Java versions to the internet, which is a CVE shopping list. Nothing
// in the SPA calls it, so gating it server-side costs nothing.
// /api/portal is NOT open any more (2026-10-17): only its catalogue GETs are public
// server-side, and sending a token there is harmless, so every portal call carries one.
const OPEN_API_PATHS=['/api/auth/me','/api/oauth','/api/internal','/api/webhooks'];

function needsAuthFor(path){
  if(OPEN_API_PATHS.some(p=>path===p||path.startsWith(p+'/')||path.startsWith(p+'?')))return false;
  if(isProxyPath(path))return true;
  if(path.startsWith('/api/admin/'))return true;
  return MANAGEMENT_API_PATHS.some(p=>path===p||path.startsWith(p+'/')||path.startsWith(p+'?'));
}

// A few call sites use raw fetch() instead of API() — the APISIX admin CRUD
// helper, the perf-summary poller and the builder's Java-source loader. They
// still need a token now that those paths are authenticated. Spread the result
// into a fetch init's headers; it is empty when there is no session, so an
// open path keeps working unchanged.
async function authHeaders(){
  const t=await ensureValidToken();
  return t?{Authorization:'Bearer '+t}:{};
}

/* The session is gone and the login modal is taking over.
 *
 * This used to `return new Promise(()=>{})` so the caller would simply stop. But a
 * promise that never settles cannot be caught and never resolves, so ANY Promise.all
 * containing one hangs forever and skips every setter after it. That is how Platform
 * Overview came to show 0 connections / 0 integrations / 0 executions while the API
 * held 12 integrations: one 401 inside the App's four-call load, and the page
 * rendered its initial state as though it were data. A `.catch()` on the call could
 * not help — there was nothing to catch.
 *
 * Rejecting instead keeps the login modal and lets callers finish and render. The
 * flag lets a caller tell "signed out" apart from a real failure. */
function authRequiredError(path){
  const e=new Error('Your session has expired — sign in to continue.');
  e.authRequired=true; e.path=path;
  return e;
}

const API=async(method,path,body)=>{
  const needsAuth=needsAuthFor(path);
  let token=null;
  if(needsAuth){
    token=await ensureValidToken();
    if(!token){
      // Session is gone and could not be refreshed. Send the user to the
      // sign-in wall rather than a modal floating over a page whose data never
      // arrives — every page funnels through here, so one dead session used to
      // pop one modal per in-flight call.
      if(window.__sessionExpired)window.__sessionExpired();
      return new Promise(()=>{});
    }
  }
  let url=path;
  const opts={method,headers:{'Content-Type':'application/json'}};
  if(token)opts.headers['Authorization']='Bearer '+token;
  if(method==='GET'||method==='HEAD'){
    if(body){
      const qs=new URLSearchParams();
      Object.entries(body).forEach(([k,v])=>{if(v!==undefined&&v!==null&&v!=='')qs.set(k,String(v));});
      const sep=url.includes('?')?'&':'?';
      url=url+sep+qs.toString();
    }
  }else{
    if(body)opts.body=JSON.stringify(body);
  }
  const r=await fetch(url,opts);
  // 401 from any secured endpoint — prompt login and resolve to "no-op"
  if(r.status===401&&needsAuth){
    clearAuthData();
    if(window.__sessionExpired)window.__sessionExpired();
    return new Promise(()=>{});
  }
  // 403 = authenticated but unauthorized — surface a toast instead of hanging
  if(r.status===403&&needsAuth){
    throw new Error('You do not have permission to perform this action');
  }
  if(!r.ok){const e=await r.json().catch(()=>({error:r.statusText}));throw new Error(e.error||r.statusText);}
  return r.json();
};

/* Load several independent resources without letting one failure blank the page.
 *
 * `Promise.all` rejects on the first failure, so a page that loaded two resources
 * together ran NEITHER setter when one of them failed — every tile rendered its
 * initial zero and the screen read as a healthy platform with no data in it. That
 * is worse than an error state, because it looks like a fact. It happened for real:
 * an nginx 403 on /api/connections showed Platform Overview as 0 connections,
 * 0 integrations and 0 executions while /api/integrations was returning 12 records.
 *
 * Each entry is [label, promise, fallback]. A rejected call yields its fallback and
 * is named in a single toast, so the resources that did load still render.
 *
 * A signed-out failure (authRequired) is not toasted: the login modal is already
 * the signal, and one toast per in-flight call on session expiry is just noise.
 */
async function loadAll(entries,showToast){
  const rs=await Promise.all(entries.map(([label,p,fb])=>
    Promise.resolve(p).then(v=>({label,value:v}),e=>({label,value:fb,error:e}))));
  const bad=rs.filter(r=>r.error&&!r.error.authRequired);
  if(bad.length&&showToast){
    showToast(bad.map(r=>`${r.label}: ${(r.error&&r.error.message)||'failed to load'}`).join(' · '),'error');
  }
  return rs.map(r=>r.value);
}

/* ===== SHARED COMPONENTS ===== */
function TaxLabMark({size=32}){
  const s=size;
  return(
    <svg width={s} height={s} viewBox="0 0 32 32" fill="none">
      <defs><linearGradient id="tlgrad" x1="0" y1="0" x2="32" y2="32" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#ff5630"/>
        <stop offset="100%" stopColor="#ffab00"/>
      </linearGradient></defs>
      <rect x="8" y="6" width="16" height="3.5" rx="1.5" fill="url(#tlgrad)"/>
      <rect x="18" y="6" width="3.5" height="13" rx="1.5" fill="url(#tlgrad)"/>
      <rect x="7" y="14" width="3.5" height="12" rx="1.5" fill="url(#tlgrad)"/>
      <rect x="7" y="22.5" width="11" height="3.5" rx="1.5" fill="url(#tlgrad)"/>
      <circle cx="21" cy="25" r="1.6" fill="url(#tlgrad)"/>
    </svg>
  );
}

function Toast({msg,type,onClose}){
  useEffect(()=>{const t=setTimeout(onClose,3500);return()=>clearTimeout(t)},[onClose]);
  const icons={success:'✓',error:'✕',info:'ℹ',danger:'⚠'};
  return <div className={`toast toast-${type}`}><span>{icons[type]||'ℹ'}</span>{msg}</div>;
}

function ConfirmModal({open,message,onConfirm,onCancel,confirmLabel='Delete'}){
  useEffect(()=>{
    if(!open) return;
    const onKey=(e)=>{ if(e.key==='Escape') onCancel(); };
    window.addEventListener('keydown',onKey);
    return()=>window.removeEventListener('keydown',onKey);
  },[open,onCancel]);
  if(!open) return null;
  return(
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal modal-sm" onClick={e=>e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-heading">
            <div className="modal-heading-icon" style={{background:'rgba(239,68,68,.1)',color:'var(--error)'}}>⚠</div>
            <div>
              <div className="modal-title">Confirm</div>
            </div>
          </div>
          <button className="modal-close-btn" onClick={onCancel} aria-label="Close"><CloseIcon size={16}/></button>
        </div>
        <div className="modal-body">
          <div className="modal-body-scroll">
            <div style={{fontSize:14,color:'var(--text2)',lineHeight:1.55}}>{message}</div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="btn btn-danger" onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

function Spinner(){return <span className="spin" style={{display:'inline-block',fontSize:16}}>⟳</span>}

function AdapterIcon({id,size=28}){
  const meta=ADAPTERS[id];
  // Declared above the early returns below: hooks must run in the same order on
  // every render. Callers resolve the adapter asynchronously — connAdapter()
  // returns '' until connections have loaded — so the same instance renders
  // first with no adapter (early return, no hooks) and then with one (this
  // hook). Below the returns that is a 0-then-1 hook count, which React rejects
  // with "rendered more hooks than during the previous render".
  const [errored,setErrored]=React.useState(false);
  if(!meta){
    return(
      <div style={{width:size,height:size,borderRadius:size*.25,background:'#e0e4f0',display:'flex',alignItems:'center',justifyContent:'center',fontSize:size*.45,fontWeight:700,color:'#6b7280',flexShrink:0}}>?</div>
    );
  }
  if(meta.letter){
    return(
      <div style={{width:size,height:size,borderRadius:size*.25,background:meta.color+'18',border:`1.5px solid ${meta.color}30`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:size*.45,fontWeight:800,color:meta.color,flexShrink:0,letterSpacing:'-0.5px'}}>
        {meta.letter}
      </div>
    );
  }
  if(errored||!meta.icon){
    const fb=meta.label?meta.label[0]:'?';
    const col=meta.color||'#6366f1';
    return(
      <div style={{width:size,height:size,borderRadius:size*.25,background:col+'18',border:`1.5px solid ${col}30`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:size*.45,fontWeight:800,color:col,flexShrink:0}}>
        {fb}
      </div>
    );
  }
  return(
    <img src={meta.icon} alt={meta.label} width={size} height={size}
      style={{borderRadius:size*.18,objectFit:'contain',display:'block'}}
      onError={()=>setErrored(true)}
    />
  );
}

function AdapterCategoryIcon({id,size=15,color='currentColor'}){
  const common={width:size,height:size,viewBox:'0 0 24 24','aria-hidden':'true',style:{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}};
  switch(id){
    case 'all':
      return <svg {...common}><circle cx="12" cy="12" r="9" fill="currentColor" opacity=".28"/><path fill="currentColor" d="M7 7h4v4H7zm6 0h4v4h-4zM7 13h4v4H7zm6 0h4v4h-4z"/></svg>;
    case 'database':
      return <svg {...common}><path fill="currentColor" d="M4 6c0-2 3.6-3 8-3s8 1 8 3v12c0 2-3.6 3-8 3s-8-1-8-3z" opacity=".3"/><path fill="currentColor" d="M12 3c4.4 0 8 1.3 8 3s-3.6 3-8 3s-8-1.3-8-3s3.6-3 8-3m-8 8c1.8 1.1 4.7 1.6 8 1.6s6.2-.5 8-1.6v2c-1.8 1.1-4.7 1.6-8 1.6S5.8 14.1 4 13zm0 5c1.8 1.1 4.7 1.6 8 1.6s6.2-.5 8-1.6v2c-1.8 1.1-4.7 1.6-8 1.6S5.8 19.1 4 18z"/></svg>;
    case 'erp':
      return <svg {...common}><path fill="currentColor" d="M3 21V9l6 3V7l6 3V3h6v18z" opacity=".3"/><path fill="currentColor" d="M5 19v-7l6 3v-5l6 3V5h2v14h-3v-3h-2v3h-3v-2H9v2zm1-5h2v2H6z"/></svg>;
    case 'asp':
      return <svg {...common}><path fill="currentColor" d="M5 3h14v18l-3-2-4 2-4-2-3 2z" opacity=".3"/><path fill="currentColor" d="M8 7h8v2H8zm0 4h8v2H8zm0 4h5v2H8z"/></svg>;
    case 'crm':
      return <svg {...common}><circle cx="9" cy="9" r="4" fill="currentColor" opacity=".35"/><circle cx="17" cy="10" r="3" fill="currentColor" opacity=".35"/><path fill="currentColor" d="M2 20c.4-4 2.7-6 7-6s6.6 2 7 6zm12.5-5.2c3.9-.8 6.5 1 7.5 5.2h-4.3a7.7 7.7 0 0 0-3.2-5.2"/></svg>;
    case 'cloud':
      return <svg {...common}><path fill="currentColor" d="M7 19a5 5 0 0 1-.5-10A6 6 0 0 1 18 10a4.5 4.5 0 0 1-.5 9z" opacity=".32"/><path fill="currentColor" d="m12 9 4 4h-3v4h-2v-4H8z"/></svg>;
    case 'messaging':
      return <svg {...common}><path fill="currentColor" d="M3 4h18v14H8l-5 3z" opacity=".3"/><path fill="currentColor" d="M7 8h10v2H7zm0 4h7v2H7z"/></svg>;
    case 'protocol':
      return <svg {...common}><path fill="currentColor" d="M8 3h8v6h2a3 3 0 0 1 3 3v3h-3v6h-5v-6h-2v6H6v-6H3v-3a3 3 0 0 1 3-3h2z" opacity=".32"/><path fill="currentColor" d="M10 3h2v5h-2zm3 0h2v5h-2zM6 11h12v2H6z"/></svg>;
    case 'file':
      return <svg {...common}><path fill="currentColor" d="M3 6a2 2 0 0 1 2-2h5l2 2h7a2 2 0 0 1 2 2v11H3z" opacity=".32"/><path fill="currentColor" d="M3 9h18l-2 10H5z"/></svg>;
    case 'edi':
      return <svg {...common}><path fill="currentColor" d="M6 2h9l4 4v16H6z" opacity=".3"/><path fill="currentColor" d="M15 2v5h4M9 10h7v2H9zm0 4h7v2H9zm0 4h5v2H9z"/></svg>;
    default:
      return <ConnectionPlugIcon size={size} color={color}/>;
  }
}

function TriggerTypeIcon({id,size=15,color='currentColor'}){
  if(id==='scheduled') return <ClockIcon size={size} color={color}/>;
  if(id==='rest') return <LinkChainIcon size={size} color={color}/>;
  if(id==='app-event') return <BoltActionIcon size={size} color={color}/>;
  if(id==='file-watch') return <FolderStackIcon size={size} color={color}/>;
  if(id==='db-poll') return <AdapterCategoryIcon id="database" size={size} color={color}/>;
  if(id==='manual') return <BuilderTestIcon size={size}/>;
  if(id==='message-queue'){
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
        <path fill="currentColor" d="M3 5h18v12H8l-5 4z" opacity=".3"></path>
        <path fill="currentColor" d="M7 9h10v2H7zm0 4h7v2H7z"></path>
      </svg>
    );
  }
  return <IntegrationFlowIcon size={size} color={color}/>;
}

function IconDropdown({value,onChange,options,allLabel,renderIcon,style}){
  const [open,setOpen]=useState(false);
  const ref=useRef(null);
  const selected=options.find(o=>o.value===value);
  useEffect(()=>{
    if(!open)return;
    const close=e=>{if(ref.current&&!ref.current.contains(e.target))setOpen(false);};
    document.addEventListener('mousedown',close);
    return()=>document.removeEventListener('mousedown',close);
  },[open]);
  const choose=v=>{onChange(v);setOpen(false);};
  return (
    <div className={`icon-dropdown${open?' open':''}`} style={style} ref={ref}>
      <button type="button" className="icon-dropdown-trigger" onClick={()=>setOpen(v=>!v)} aria-haspopup="listbox" aria-expanded={open}>
        <span className="icon-dropdown-leading">{renderIcon(value)}</span>
        <span className="icon-dropdown-label">{(selected||{}).label||allLabel}</span>
        <ChevronDownIcon size={15}/>
      </button>
      {open&&(
        <div className="icon-dropdown-menu" role="listbox">
          <button type="button" className={!value?'selected':''} onClick={()=>choose('')} role="option" aria-selected={!value}>
            {renderIcon('')}<span>{allLabel}</span>{!value&&<CheckCircleIcon size={14}/>}
          </button>
          {options.map(o=>(
            <button type="button" key={o.value} className={value===o.value?'selected':''} onClick={()=>choose(o.value)} role="option" aria-selected={value===o.value}>
              {renderIcon(o.value)}<span>{o.label}</span>{value===o.value&&<CheckCircleIcon size={14}/>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ThemeModeIcon({theme,size=18,color='currentColor'}){
  const common={width:size,height:size,viewBox:'0 0 24 24','aria-hidden':'true',style:{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}};
  if(theme==='dark'||theme==='midnight'){
    return <svg {...common}><path fill="currentColor" d="M20.5 15.2A8.5 8.5 0 0 1 8.8 3.5A9 9 0 1 0 20.5 15.2" opacity=".9"/>{theme==='midnight'&&<path fill="currentColor" d="m17 3 .7 1.8L20 5.5l-2.3.7L17 8l-.7-1.8-2.3-.7 2.3-.7z"/>}</svg>;
  }
  if(theme==='high-contrast'){
    return <svg {...common}><circle cx="12" cy="12" r="9" fill="currentColor" opacity=".25"/><path fill="currentColor" d="M12 3a9 9 0 0 1 0 18z"/></svg>;
  }
  return <svg {...common}><circle cx="12" cy="12" r="4" fill="currentColor"/><path d="M12 2v3m0 14v3M2 12h3m14 0h3M5 5l2 2m10 10 2 2M19 5l-2 2M7 17l-2 2" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg>;
}

function FieldInput({field,value,onChange,error,allValues,isEditing,secretSaved}){
  const [showPw,setShowPw]=useState(false);
  // Set the moment the user types into a password field, so the input can stop
  // being pinned empty and start showing their input. See pwValue below.
  const [pwDirty,setPwDirty]=useState(false);
  // Declared here, above the conditional return below, because hooks must run in
  // the same order on every render. Its value is filled in once field.type is
  // known; see hasStoredSecret.
  const pwInitial = useRef(null);
  if(field.sw){
    const depVal=allValues[field.sw.f];
    if(depVal!==field.sw.v)return null;
  }
  const cls=`form-input${error?' error':''}`;
  let input;
  // Assigned only in the password branch below, but read unconditionally in the
  // returned JSX. Without this declaration every non-password field threw
  // ReferenceError, which unmounted the whole app (see PageErrorBoundary).
  let helperHint = null;
  const isPw = field.type === 'password';
  // Whether a secret was actually stored when this form opened. Captured once,
  // before any typing, because `value` becomes the user's input as they type.
  // Being in edit mode is NOT the same as having a secret: a connection can be
  // saved with its password unset, and claiming "saved" on an empty field tells
  // the user to leave it alone — which is how a connection stays broken.
  if(pwInitial.current === null) pwInitial.current = (isPw && isEditing) ? (value || '') : '';
  // secretSaved comes from the server's secretsSet: the API no longer returns secrets,
  // so `value` is empty even when one is stored. pwInitial covers forms fed a value.
  const hasStoredSecret = isPw && isEditing && (!!secretSaved || !!pwInitial.current);
  // Never pre-fill the stored secret into the DOM — that is the shoulder-surfing
  // risk this blanking exists for. But the input is controlled, so pinning it to
  // '' for the whole edit meant every keystroke was wiped by the next render and
  // a password could not be typed at all. Blank it only until the user types.
  const pwValue = (isPw && isEditing && !pwDirty) ? '' : (value || '');
  const pwPlaceholder = (hasStoredSecret && !pwDirty) ? '•••••••••• (saved — type to replace)' : '';
  if(field.type==='header'){
    return(
      <div className="form-group form-section-header" style={{
        gridColumn:'1 / -1', marginTop:18, marginBottom:6,
        fontSize:12, fontWeight:600, letterSpacing:'.04em',
        color:'var(--text3)', textTransform:'uppercase',
        borderTop:'1px solid var(--border)', paddingTop:14
      }}>{field.label}</div>
    );
  }
  if(field.type==='select'){
    input=<select className={`form-select${error?' error':''}`} value={value||field.def||''} onChange={e=>onChange(field.k,e.target.value)}>
      {!field.def&&<option value="">— Select —</option>}
      {(field.opts||[]).map(o=><option key={o.v} value={o.v}>{o.l}</option>)}
    </select>;
  } else if(field.type==='textarea'){
    input=<textarea className={cls} value={value||''} onChange={e=>onChange(field.k,e.target.value)} placeholder={field.ph||''} rows={3}/>;
  } else if(field.type==='password'){
    input=<div className="input-wrap">
      <input className={cls} type={showPw?'text':'password'}
             value={pwValue}
             onChange={e=>{ if(!pwDirty) setPwDirty(true); onChange(field.k,e.target.value); }}
             placeholder={pwPlaceholder||field.ph||''}
             autoComplete="new-password"/>
      <button className="eye-btn" type="button" onClick={()=>setShowPw(p=>!p)}
              aria-label={showPw?'Hide password':'Show password'}
              title={showPw?'Hide password':'Show password'}>
        <EyeIcon open={!showPw}/>
      </button>
    </div>;
    if(hasStoredSecret && !pwDirty){
      helperHint = (
        <div style={{fontSize:11,color:'var(--text3)',marginTop:4,display:'flex',alignItems:'center',gap:4}}>
          <span aria-hidden style={{display:'inline-flex'}}><LockIcon size={11}/></span>
          <span>Stored secret — type a new value to replace it, leave as-is to keep it</span>
        </div>
      );
    }
  } else {
    input=<input className={cls} type={field.type||'text'} value={value||field.def||''} onChange={e=>onChange(field.k,e.target.value)} placeholder={field.ph||''}/>;
  }
  return(
    <div className="form-group">
      <label className="form-label">{field.label}{field.req&&<span className="req">*</span>}</label>
      {input}
      {helperHint}
      {error&&<div className="field-error"><WarningIcon/> {error}</div>}
    </div>
  );
}

function StatusDot({status}){
  const map={ACTIVE:'success',RUNNING:'success',SUCCESS:'success',INACTIVE:'gray',STOPPED:'gray',ERROR:'error',FAILED:'error',WARNING:'warning',TESTING:'info'};
  const c=map[String(status).toUpperCase()]||'gray';
  return <span className={`dot dot-${c}`} style={{marginRight:5}}/>;
}

function StatusBadge({status}){
  const map={ACTIVE:'success',RUNNING:'success',SUCCESS:'success',INACTIVE:'gray',STOPPED:'gray',ERROR:'error',FAILED:'error',WARNING:'warning',TESTING:'info',CONNECTED:'success',DISCONNECTED:'error',UNKNOWN:'warning'};
  const s=String(status||'').toUpperCase();
  const c=map[s]||'gray';
  return <span className={`badge badge-${c}`}><StatusDot status={status}/>{status}</span>;
}
function WarningIcon({size=14,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <path fill="currentColor" d="M13 3.5a1.15 1.15 0 0 0-2 0l-8.5 15A1.15 1.15 0 0 0 3.5 20h17a1.15 1.15 0 0 0 1-1.75z" opacity=".28"></path>
      <path fill="currentColor" d="M12 9a.75.75 0 0 1 .75.75v4a.75.75 0 0 1-1.5 0v-4A.75.75 0 0 1 12 9m0 8a1 1 0 1 0 0-2a1 1 0 0 0 0 2"></path>
    </svg>
  );
}
function CheckCircleIcon({size=14,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <circle cx="12" cy="12" r="9" fill="currentColor" opacity=".25"></circle>
      <path d="m8 12 2.6 2.6L16.5 9" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
    </svg>
  );
}
function ExternalLinkIcon({size=14,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <path d="M13 5h6v6M19 5l-9 9M18 14v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"></path>
    </svg>
  );
}
function EyeIcon({open=true,size=16,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <path fill="currentColor" d="M12 6.5c4.91 0 8.91 3.19 10.3 5.5c-1.39 2.31-5.39 5.5-10.3 5.5S3.09 14.31 1.7 12c1.39-2.31 5.39-5.5 10.3-5.5" opacity=".28"></path>
      <circle cx="12" cy="12" r="2.65" fill="currentColor"></circle>
      {!open && <path fill="currentColor" d="m4.53 4.53l14.94 14.94l-1.06 1.06L3.47 5.59z"></path>}
    </svg>
  );
}
function SearchIcon({size=14,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <circle cx="11" cy="11" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.9"></circle>
      <path d="m16 16 4 4" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"></path>
    </svg>
  );
}
function ConnectionPlugIcon({size=18,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <path fill="currentColor" d="M7 3h2v4h6V3h2v4h1a1 1 0 0 1 1 1v3a5 5 0 0 1-5 5h-1v4h-2v-4h-1a5 5 0 0 1-5-5V8a1 1 0 0 1 1-1h1z" opacity=".35"></path>
      <path fill="currentColor" d="M8 9h8v2a4 4 0 0 1-8 0z"></path>
    </svg>
  );
}
function IntegrationFlowIcon({size=18,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <circle cx="5.5" cy="7" r="2.5" fill="currentColor" opacity=".35"></circle>
      <circle cx="18.5" cy="7" r="2.5" fill="currentColor" opacity=".35"></circle>
      <circle cx="12" cy="17" r="2.7" fill="currentColor"></circle>
      <path d="M8 8.5h8M7.5 9.5 10.5 14M16.5 9.5 13.5 14" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"></path>
    </svg>
  );
}
function FolderStackIcon({size=18,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <path fill="currentColor" d="M3 7a2 2 0 0 1 2-2h5l2 2h7a2 2 0 0 1 2 2v1H3z" opacity=".35"></path>
      <path fill="currentColor" d="M3 10h18l-1.2 7.2A2 2 0 0 1 17.82 19H6.18a2 2 0 0 1-1.98-1.8z"></path>
    </svg>
  );
}
function AuditTrailIcon({size=18,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <path fill="currentColor" d="M6 3h9l4 4v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2" opacity=".34"></path>
      <path fill="currentColor" d="M15 3v4h4M8 11h8v1.5H8zm0 3h8v1.5H8z"></path>
    </svg>
  );
}
function UsersGroupIcon({size=18,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <path fill="currentColor" d="M12 12a3.2 3.2 0 1 0 0-6.4a3.2 3.2 0 0 0 0 6.4" opacity=".9"></path>
      <path fill="currentColor" d="M5 9a2.4 2.4 0 1 0 0-4.8A2.4 2.4 0 0 0 5 9m14 0a2.4 2.4 0 1 0 0-4.8A2.4 2.4 0 0 0 19 9" opacity=".35"></path>
      <path fill="currentColor" d="M12 13.2c-3.2 0-5.8 1.56-5.8 3.5S8.8 20.2 12 20.2s5.8-1.56 5.8-3.5s-2.6-3.5-5.8-3.5" opacity=".95"></path>
    </svg>
  );
}
function ShieldRoleIcon({size=16,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <path fill="currentColor" d="M12 2 4 5v5c0 5.1 3.4 9.7 8 11c4.6-1.3 8-5.9 8-11V5z" opacity=".35"></path>
      <path fill="currentColor" d="m10.8 13.8-2.2-2.2 1.3-1.3 1 1 3.1-3.1 1.3 1.3z"></path>
    </svg>
  );
}
function MaskRoleIcon({size=16,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <path fill="currentColor" d="M3 9c0 6 3.5 10 9 10s9-4 9-10c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2" opacity=".35"></path>
      <circle cx="9" cy="12" r="1.3" fill="currentColor"></circle>
      <circle cx="15" cy="12" r="1.3" fill="currentColor"></circle>
      <path d="M9 15c1 .8 5 .8 6 0" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"></path>
    </svg>
  );
}
function CloseIcon({size=16,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <g fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="12" cy="12" r="10"></circle>
        <path strokeLinecap="round" d="m14.5 9.5-5 5m0-5 5 5"></path>
      </g>
    </svg>
  );
}
function ChevronDownIcon({size=14,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="m19 9-7 6-7-6"></path>
    </svg>
  );
}
function JvmChipIcon({size=20,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <rect x="6" y="6" width="12" height="12" rx="1.5" fill="currentColor" opacity=".32"></rect>
      <rect x="9" y="9" width="6" height="6" rx="1" fill="currentColor"></rect>
      <path fill="currentColor" d="M9 2h1.5v3H9zm4.5 0H15v3h-1.5zM9 19h1.5v3H9zm4.5 0H15v3h-1.5zM2 9h3v1.5H2zm0 4.5h3V15H2zM19 9h3v1.5h-3zm0 4.5h3V15h-3z"></path>
    </svg>
  );
}
function CpuChipIcon({size=20,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <rect x="5" y="5" width="14" height="14" rx="2.5" fill="currentColor" opacity=".3"></rect>
      <path fill="currentColor" d="M9 8h6a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1m1.5 2.5v3h3v-3z"></path>
      <path fill="currentColor" d="M9 1h1.5v3H9zm4.5 0H15v3h-1.5zM9 20h1.5v3H9zm4.5 0H15v3h-1.5zM1 9h3v1.5H1zm0 4.5h3V15H1zM20 9h3v1.5h-3zm0 4.5h3V15h-3z"></path>
    </svg>
  );
}
function RoutesMapIcon({size=20,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <path fill="currentColor" d="M4 6a2 2 0 1 1 4 0v1h2.5A4.5 4.5 0 0 1 15 11.5v1h1a2 2 0 1 1 0 2h-1v1A4.5 4.5 0 0 1 10.5 20H8v1a2 2 0 1 1-2-2v-1a2 2 0 0 1 2-2h2.5a2.5 2.5 0 0 0 2.5-2.5v-1A2.5 2.5 0 0 0 10.5 10H8v1a2 2 0 1 1-2-2V8a2 2 0 0 1-2-2" opacity=".95"></path>
    </svg>
  );
}
function PaletteIcon({size=14,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <path fill="currentColor" d="M12 2C6.5 2 2 6.2 2 11.4c0 2.7 1.7 4.2 4 4.2h1.4c.8 0 1.4.6 1.4 1.4v.7c0 1.6 1.3 2.9 2.9 2.9H12c5.5 0 10-4.5 10-10S17.5 2 12 2" opacity=".35"></path>
      <circle cx="7.8" cy="10.2" r="1.15" fill="currentColor"></circle>
      <circle cx="10.9" cy="7.7" r="1.15" fill="currentColor"></circle>
      <circle cx="14.8" cy="7.9" r="1.15" fill="currentColor"></circle>
      <circle cx="17.1" cy="11" r="1.15" fill="currentColor"></circle>
    </svg>
  );
}
function KeyBadgeIcon({size=16,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <path fill="currentColor" d="M14 3a7 7 0 0 0-6.6 9.4L2 17.8V21h3.2l1.2-1.2h1.8l1.2-1.2v-1.8l.8-.8A7 7 0 1 0 14 3" opacity=".35"></path>
      <circle cx="14" cy="10" r="1.5" fill="currentColor"></circle>
    </svg>
  );
}
function BoltActionIcon({size=14,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <path fill="currentColor" d="M13 2 5 13h5l-1 9 8-11h-5z"></path>
    </svg>
  );
}
function BeakerActionIcon({size=14,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <path fill="currentColor" d="M9 3h6v2l-1 1v3.2l4.4 7.1A3 3 0 0 1 15.8 21H8.2a3 3 0 0 1-2.6-4.7L10 9.2V6L9 5z" opacity=".35"></path>
      <path fill="currentColor" d="M8.7 14h6.6l1.3 2.2a1.4 1.4 0 0 1-1.2 2.1H8.6a1.4 1.4 0 0 1-1.2-2.1z"></path>
    </svg>
  );
}
function PencilIcon({size=14,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <path fill="currentColor" d="m4 15 8.9-8.9 4 4L8 19H4z" opacity=".35"></path>
      <path fill="currentColor" d="m14.3 4.3 1.4-1.4a2 2 0 0 1 2.8 0l1.2 1.2a2 2 0 0 1 0 2.8l-1.4 1.4z"></path>
    </svg>
  );
}
function TrashIcon({size=14,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <path fill="currentColor" d="M9 3h6l1 2h4v2H4V5h4z" opacity=".45"></path>
      <path fill="currentColor" d="M6 8h12l-1 11a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2z"></path>
    </svg>
  );
}
function LinkChainIcon({size=14,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <path fill="currentColor" d="M10.8 13.2a3.5 3.5 0 0 1 0-5l2.4-2.4a3.5 3.5 0 1 1 5 5l-1.2 1.2" opacity=".35"></path>
      <path fill="currentColor" d="M13.2 10.8a3.5 3.5 0 0 1 0 5l-2.4 2.4a3.5 3.5 0 0 1-5-5l1.2-1.2"></path>
    </svg>
  );
}
function ClockIcon({size=14,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <circle cx="12" cy="12" r="9" fill="currentColor" opacity=".3"></circle>
      <path d="M12 7.5v5l3 2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"></path>
    </svg>
  );
}
function FileDocIcon({size=14,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <path fill="currentColor" d="M6 2h8l4 4v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2" opacity=".3"></path>
      <path fill="currentColor" d="M14 2v4h4M8 12h8v1.5H8zm0 3h6v1.5H8z"></path>
    </svg>
  );
}
function ClipboardListIcon({size=24,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <path fill="currentColor" d="M21 15.998v-6c0-2.828 0-4.242-.879-5.121C19.353 4.109 18.175 4.012 16 4H8c-2.175.012-3.353.109-4.121.877C3 5.756 3 7.17 3 9.998v6c0 2.829 0 4.243.879 5.122c.878.878 2.293.878 5.121.878h6c2.828 0 4.243 0 5.121-.878c.879-.88.879-2.293.879-5.122" opacity=".5"></path>
      <path fill="currentColor" d="M8 3.5A1.5 1.5 0 0 1 9.5 2h5A1.5 1.5 0 0 1 16 3.5v1A1.5 1.5 0 0 1 14.5 6h-5A1.5 1.5 0 0 1 8 4.5z"></path>
      <path fill="currentColor" fillRule="evenodd" d="M6.25 10.5A.75.75 0 0 1 7 9.75h.5a.75.75 0 0 1 0 1.5H7a.75.75 0 0 1-.75-.75m3.5 0a.75.75 0 0 1 .75-.75H17a.75.75 0 0 1 0 1.5h-6.5a.75.75 0 0 1-.75-.75M6.25 14a.75.75 0 0 1 .75-.75h.5a.75.75 0 0 1 0 1.5H7a.75.75 0 0 1-.75-.75m3.5 0a.75.75 0 0 1 .75-.75H17a.75.75 0 0 1 0 1.5h-6.5a.75.75 0 0 1-.75-.75" clipRule="evenodd"></path>
    </svg>
  );
}
function LookupBarsIcon({size=24,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <path fill="currentColor" d="M12 22c-4.714 0-7.071 0-8.536-1.465C2 19.072 2 16.714 2 12s0-7.071 1.464-8.536C4.93 2 7.286 2 12 2s7.071 0 8.535 1.464C22 4.93 22 7.286 22 12s0 7.071-1.465 8.535C19.072 22 16.714 22 12 22" opacity=".5"></path>
      <path fill="currentColor" d="M12 5.25a.75.75 0 0 1 .75.75v12a.75.75 0 0 1-1.5 0V6a.75.75 0 0 1 .75-.75m-5 3a.75.75 0 0 1 .75.75v9a.75.75 0 0 1-1.5 0V9A.75.75 0 0 1 7 8.25m10 4a.75.75 0 0 1 .75.75v5a.75.75 0 0 1-1.5 0v-5a.75.75 0 0 1 .75-.75"></path>
    </svg>
  );
}
function AssessmentClipIcon({size=24,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <path fill="currentColor" d="M9 3.75A1.75 1.75 0 0 1 10.75 2h2.5A1.75 1.75 0 0 1 15 3.75V5h3.25A1.75 1.75 0 0 1 20 6.75v12.5A1.75 1.75 0 0 1 18.25 21H5.75A1.75 1.75 0 0 1 4 19.25V6.75A1.75 1.75 0 0 1 5.75 5H9z" opacity=".35"></path>
      <path fill="currentColor" d="M10.75 3.5h2.5a.25.25 0 0 1 .25.25V5h-3V3.75a.25.25 0 0 1 .25-.25"></path>
      <path fill="currentColor" fillRule="evenodd" d="M8.47 11.47a.75.75 0 0 1 1.06 0L11 12.94l3.47-3.47a.75.75 0 0 1 1.06 1.06l-4 4a.75.75 0 0 1-1.06 0l-2-2a.75.75 0 0 1 0-1.06m0 5a.75.75 0 0 1 1.06 0L11 17.94l3.47-3.47a.75.75 0 1 1 1.06 1.06l-4 4a.75.75 0 0 1-1.06 0l-2-2a.75.75 0 0 1 0-1.06" clipRule="evenodd"></path>
    </svg>
  );
}
function GlobeEndpointIcon({size=24,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <circle cx="12" cy="12" r="9" fill="currentColor" opacity=".28"></circle>
      <ellipse cx="12" cy="12" rx="4" ry="9" fill="none" stroke="currentColor" strokeWidth="1.6"></ellipse>
      <path d="M3 12h18M5.2 7.5h13.6M5.2 16.5h13.6" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"></path>
    </svg>
  );
}
const EMPTY_ICON_TONES={
  teal:   {background:'rgba(8,145,178,.16)',  color:'rgb(14,116,144)'},
  green:  {background:'rgba(34,197,94,.16)',  color:'rgb(17,141,87)'},
  indigo: {background:'rgba(99,102,241,.16)', color:'rgb(79,70,229)'},
  violet: {background:'rgba(139,92,246,.16)', color:'rgb(109,40,217)'},
  sky:    {background:'rgba(14,165,233,.16)', color:'rgb(3,105,161)'},
  amber:  {background:'rgba(245,158,11,.16)', color:'rgb(180,83,9)'},
  orange: {background:'rgba(249,115,22,.16)', color:'rgb(194,65,12)'},
  rose:   {background:'rgba(244,63,94,.16)',  color:'rgb(190,18,60)'},
  slate:  {background:'rgba(100,116,139,.16)',color:'rgb(51,65,85)'},
  emerald:{background:'rgba(16,185,129,.16)', color:'rgb(4,120,87)'},
  pink:   {background:'rgba(236,72,153,.16)', color:'rgb(190,24,93)'},
  purple: {background:'rgba(124,58,237,.16)', color:'rgb(91,33,182)'},
};
function EmptyStateIcon({tone='teal', children}){
  const t=EMPTY_ICON_TONES[tone]||EMPTY_ICON_TONES.teal;
  const child=React.isValidElement(children)
    ? React.cloneElement(children,{size:24,color:'currentColor'})
    : children;
  return <div className="empty-icon" style={t}>{child}</div>;
}
function LockIcon({size=14,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <rect x="4" y="10" width="16" height="11" rx="2.2" fill="currentColor" opacity=".35"></rect>
      <path d="M8 10V7.8A4 4 0 0 1 12 4a4 4 0 0 1 4 3.8V10" fill="none" stroke="currentColor" strokeWidth="1.7"></path>
    </svg>
  );
}

/* ===== DASHBOARD ===== */
/* ─────────────────────────────────────────────────────────────────────────────
 * Dashboard primitives.
 *
 * Hand-rolled SVG: this SPA has no build step and no chart library, and pulling one
 * in over CDN is blocked by the same constraints that keep everything else inline.
 * Every colour is a theme token — the platform ships five themes and two brand ramps,
 * so a hard-coded hex looks correct in exactly one of them.
 * ───────────────────────────────────────────────────────────────────────────── */

/* Delta pill: "▲ 2.4%". Direction is explicit rather than inferred from sign, because
   "failures down" is good and "success down" is bad — the caller knows which. */
function AxDelta({value,dir,suffix}){
  if(value==null) return null;
  const good = dir==='up';
  const col = good?'var(--success)':'var(--error)';
  const bg  = good?'var(--success-bg)':'var(--error-bg)';
  const bd  = good?'var(--success-border)':'var(--error-border)';
  return(
    <span style={{display:'inline-flex',alignItems:'center',gap:3,fontSize:11,fontWeight:700,
                  color:col,background:bg,border:'1px solid '+bd,borderRadius:20,padding:'2px 7px'}}>
      {good?'▲':'▼'}{value}{suffix||''}
    </span>
  );
}

function AxStatCard({label,value,sub,delta,deltaDir,deltaSuffix,tint,icon,loading}){
  return(
    <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',
                 padding:'16px 18px',display:'flex',flexDirection:'column',gap:10,minHeight:112}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <span style={{fontSize:13,fontWeight:600,color:'var(--text2)'}}>{label}</span>
        <span style={{width:34,height:34,borderRadius:10,display:'inline-flex',alignItems:'center',
                      justifyContent:'center',background:tint||'var(--surface3)',fontSize:15}}>{icon}</span>
      </div>
      <div style={{display:'flex',alignItems:'baseline',gap:8,flexWrap:'wrap'}}>
        <span style={{fontSize:28,fontWeight:800,letterSpacing:'-.5px',color:'var(--text)',lineHeight:1}}>
          {loading?'—':value}
        </span>
        {!loading&&<AxDelta value={delta} dir={deltaDir} suffix={deltaSuffix}/>}
      </div>
      <div style={{fontSize:11.5,color:'var(--text3)'}}>{loading?'loading…':sub}</div>
    </div>
  );
}

/* Area chart over evenly spaced points, with a hover rail. */
function AxArea({points,height=150,color='var(--brand-start)',labels}){
  const [hover,setHover]=useState(null);
  const n=points.length;
  if(!n) return <div style={{height,display:'flex',alignItems:'center',justifyContent:'center',color:'var(--text3)',fontSize:12}}>No data</div>;
  const W=100, H=height, pad=6;
  const max=Math.max(1,...points);
  const x=(i)=>n<2?W/2:(i/(n-1))*W;
  const y=(v)=>pad+(1-v/max)*(H-pad*2);
  const line=points.map((v,i)=>(i?'L':'M')+x(i).toFixed(2)+','+y(v).toFixed(2)).join(' ');
  const area=line+' L'+W+','+H+' L0,'+H+' Z';
  return(
    <div style={{position:'relative'}}>
      <svg viewBox={'0 0 '+W+' '+H} preserveAspectRatio="none" style={{width:'100%',height:height,display:'block'}}
           onMouseLeave={()=>setHover(null)}
           onMouseMove={(e)=>{const r=e.currentTarget.getBoundingClientRect();
                              const i=Math.round(((e.clientX-r.left)/r.width)*(n-1));
                              setHover(Math.max(0,Math.min(n-1,i)));}}>
        <defs>
          <linearGradient id="axfill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.28"/>
            <stop offset="100%" stopColor={color} stopOpacity="0"/>
          </linearGradient>
        </defs>
        {[0.25,0.5,0.75].map(f=>(
          <line key={f} x1="0" x2={W} y1={pad+f*(H-pad*2)} y2={pad+f*(H-pad*2)}
                stroke="var(--border)" strokeWidth="0.4" strokeDasharray="1.5 1.5" vectorEffect="non-scaling-stroke"/>
        ))}
        <path d={area} fill="url(#axfill)"/>
        <path d={line} fill="none" stroke={color} strokeWidth="2" vectorEffect="non-scaling-stroke"
              strokeLinejoin="round" strokeLinecap="round"/>
        {hover!=null&&(
          <g>
            <line x1={x(hover)} x2={x(hover)} y1="0" y2={H} stroke="var(--border2)" strokeWidth="0.6" vectorEffect="non-scaling-stroke"/>
            <circle cx={x(hover)} cy={y(points[hover])} r="2.5" fill={color} stroke="var(--surface)" strokeWidth="1.2" vectorEffect="non-scaling-stroke"/>
          </g>
        )}
      </svg>
      {hover!=null&&(
        <div style={{position:'absolute',top:4,left:'50%',transform:'translateX(-50%)',background:'var(--text)',
                     color:'var(--surface)',fontSize:11,fontWeight:600,padding:'4px 9px',borderRadius:6,whiteSpace:'nowrap',pointerEvents:'none'}}>
          {(labels&&labels[hover])||('#'+hover)} · {points[hover]}
        </div>
      )}
    </div>
  );
}

/* Column chart; the peak column takes the brand colour, the rest stay neutral. */
function AxBars({items,height=130}){
  if(!items.length) return null;
  const max=Math.max(1,...items.map(i=>i.value));
  const peak=items.reduce((b,i,ix)=>i.value>items[b].value?ix:b,0);
  return(
    <div style={{display:'flex',alignItems:'flex-end',gap:6,height,paddingTop:18}}>
      {items.map((it,ix)=>{
        const h=Math.max(3,(it.value/max)*(height-30));
        const on=ix===peak;
        return(
          <div key={ix} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:5}} title={it.label+': '+it.value}>
            {on&&<div style={{fontSize:11,fontWeight:700,color:'var(--text)'}}>{it.value}</div>}
            <div style={{width:'100%',height:h,borderRadius:'6px 6px 3px 3px',
                         background:on?'var(--brand-start)':'var(--surface3)',
                         border:on?'none':'1px solid var(--border)'}}/>
            <div style={{fontSize:9.5,color:on?'var(--text)':'var(--text3)',fontWeight:on?700:500}}>{it.label}</div>
          </div>
        );
      })}
    </div>
  );
}

/* Semicircular gauge built from discrete ticks — reads as a dial without a chart lib. */
function AxGauge({pct,caption,tone}){
  const TICKS=44, lit=Math.round((Math.max(0,Math.min(100,pct))/100)*TICKS);
  const col=tone||'var(--success)';
  return(
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',padding:'6px 0 2px'}}>
      <svg viewBox="0 0 200 112" style={{width:'100%',maxWidth:250}}>
        {Array.from({length:TICKS}).map((_,i)=>{
          const a=Math.PI*(i/(TICKS-1)), cx=100, cy=104, r1=68, r2=90;
          const x1=cx-Math.cos(a)*r1, y1=cy-Math.sin(a)*r1;
          const x2=cx-Math.cos(a)*r2, y2=cy-Math.sin(a)*r2;
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} strokeWidth="3.4" strokeLinecap="round"
                       stroke={i<lit?col:'var(--surface3)'}/>;
        })}
        <text x="100" y="86" textAnchor="middle" fontSize="30" fontWeight="800" fill="var(--text)">
          {pct==null?'—':Math.round(pct*10)/10+'%'}
        </text>
      </svg>
      <div style={{fontSize:11.5,color:'var(--text3)',textAlign:'center',marginTop:-4}}>{caption}</div>
    </div>
  );
}

/* Ranked rows with a proportional bar — the "values by source" pattern. */
function AxBreakdown({rows,colors}){
  if(!rows.length) return <div style={{fontSize:12,color:'var(--text3)'}}>Nothing to show</div>;
  const total=rows.reduce((a,r)=>a+r.value,0)||1;
  const palette=colors||['var(--brand-start)','var(--info)','var(--success)','var(--warning)','var(--error)','var(--text3)'];
  return(
    <div style={{display:'flex',flexDirection:'column',gap:13}}>
      {rows.map((r,i)=>{
        const pct=Math.round((r.value/total)*100);
        return(
          <div key={r.label}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:5}}>
              <span style={{fontSize:12.5,color:'var(--text)',fontWeight:600}}>{r.label}</span>
              <span style={{fontSize:11.5,color:'var(--text3)'}}>
                <b style={{color:'var(--text2)'}}>{pct}%</b> &nbsp;{r.value}
              </span>
            </div>
            <div style={{height:4,borderRadius:3,background:'var(--surface3)',overflow:'hidden'}}>
              <div style={{width:pct+'%',height:'100%',borderRadius:3,background:palette[i%palette.length]}}/>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AxPanel({title,action,children,pad=18}){
  return(
    <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',padding:pad}}>
      {(title||action)&&(
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
          <div style={{fontSize:14,fontWeight:700,color:'var(--text)'}}>{title}</div>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

/* Compact ring for a 0-100 readiness score — same visual language as the dashboard
   gauge, sized for a card header. Band colour is passed in so the ring and the band
   pill can never disagree. */
function AxScoreDial({score,color,size=44}){
  const pct=Math.max(0,Math.min(100,Number(score)||0));
  const r=(size-7)/2, c=2*Math.PI*r, off=c*(1-pct/100);
  return(
    <svg width={size} height={size} viewBox={'0 0 '+size+' '+size} style={{flexShrink:0}}>
      <circle cx={size/2} cy={size/2} r={r} fill="var(--surface)" stroke="var(--surface3)" strokeWidth="4"/>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="4" strokeLinecap="round"
              strokeDasharray={c} strokeDashoffset={off}
              transform={'rotate(-90 '+(size/2)+' '+(size/2)+')'}/>
      <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central"
            fontSize={size*0.33} fontWeight="800" fill={color}>{score}</text>
    </svg>
  );
}

function Dashboard({connections,integrations,executions,metrics,loaded=true}){
  const m=metrics||{};
  const hourly=Array.isArray(m.hourlyStats)?m.hourlyStats:[];
  const counts=hourly.map(h=>h.count||0);
  const hourLabels=hourly.map(h=>h.hour+':00');

  const todayTotal=m.totalToday!=null?m.totalToday:executions.length;
  const todayOk=m.successToday||0, todayBad=m.failedToday||0;
  const rateToday=m.successRate!=null?m.successRate:null;
  const rateAll=m.successRateAll!=null?m.successRateAll:null;
  /* Today vs all-time is the only comparison the API actually supports — there is no
     yesterday bucket, so inventing "vs last period" would be fiction. */
  const rateDelta=(rateToday!=null&&rateAll!=null)?Math.round((rateToday-rateAll)*100)/100:null;

  const activeInts=m.activeIntegrations!=null?m.activeIntegrations:integrations.filter(i=>i.status==='ACTIVE').length;
  const totalInts=m.totalIntegrations!=null?m.totalIntegrations:integrations.length;
  const totalConns=m.totalConnections!=null?m.totalConnections:connections.length;
  const connOk=connections.filter(c=>c.status==='ACTIVE').length;
  const avgMs=m.avgDurationAll;

  const byTrigger={};
  integrations.forEach(i=>{if(i.triggerType)byTrigger[i.triggerType]=(byTrigger[i.triggerType]||0)+1;});
  const triggerRows=Object.entries(byTrigger).map(([k,v])=>({label:k.replace(/_/g,' '),value:v})).sort((a,b)=>b.value-a.value);

  const byAdapter={};
  integrations.forEach(i=>{
    if(i.sourceAdapter)byAdapter[i.sourceAdapter]=(byAdapter[i.sourceAdapter]||0)+1;
    if(i.targetAdapter)byAdapter[i.targetAdapter]=(byAdapter[i.targetAdapter]||0)+1;
  });
  const adapterRows=Object.entries(byAdapter).map(([k,v])=>({label:k.replace(/_/g,' '),value:v})).sort((a,b)=>b.value-a.value).slice(0,6);

  const busiest=hourly.map(h=>({label:h.hour,value:h.count||0}));
  const errors=Array.isArray(m.recentErrors)?m.recentErrors:[];
  const writer=m.writer||{};

  return(
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Platform Overview</h1>
          <div className="page-subtitle">Axon — real-time integration health</div>
        </div>
        <div className="flex gap-3" style={{alignItems:'center'}}>
          <span className="badge badge-success">● Platform Online</span>
          <span className="text-sm text-muted">{new Date().toLocaleString()}</span>
        </div>
      </div>

      {/* KPI row */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(215px,1fr))',gap:14,marginBottom:14}}>
        <AxStatCard loading={!loaded} label="Executions Today" value={(todayTotal||0).toLocaleString()}
          sub={loaded?todayOk.toLocaleString()+' succeeded · '+todayBad+' failed':''}
          tint="rgba(255,86,48,.12)" icon="⚡"/>
        <AxStatCard loading={!loaded} label="Success Rate" value={rateToday!=null?rateToday+'%':'—'}
          delta={rateDelta!=null?Math.abs(rateDelta):null} deltaDir={rateDelta>=0?'up':'down'} deltaSuffix="pt"
          sub={rateAll!=null?'vs '+rateAll+'% all time':''}
          tint="rgba(22,163,74,.12)" icon="✓"/>
        <AxStatCard loading={!loaded} label="Active Integrations" value={activeInts}
          sub={'of '+totalInts+' total · '+Math.max(0,totalInts-activeInts)+' idle'}
          tint="rgba(37,99,235,.12)" icon="◉"/>
        <AxStatCard loading={!loaded} label="Connections" value={totalConns}
          sub={connOk+' healthy · '+Math.max(0,totalConns-connOk)+' need attention'}
          tint="rgba(217,119,6,.12)" icon="⚯"/>
      </div>

      {/* Throughput + gauge */}
      <div style={{display:'grid',gridTemplateColumns:'minmax(0,2fr) minmax(0,1fr)',gap:14,marginBottom:14}}>
        <AxPanel title="Throughput — last 24 hours"
          action={<span style={{fontSize:11.5,color:'var(--text3)'}}>{counts.reduce((a,b)=>a+b,0).toLocaleString()} executions</span>}>
          <div style={{display:'flex',alignItems:'baseline',gap:10,marginBottom:6}}>
            <span style={{fontSize:30,fontWeight:800,letterSpacing:'-.6px',color:'var(--text)'}}>
              {loaded?(todayTotal||0).toLocaleString():'—'}
            </span>
            <span style={{fontSize:12,color:'var(--text3)'}}>today</span>
            {loaded&&avgMs!=null&&<span style={{fontSize:11.5,color:'var(--text3)',marginLeft:'auto'}}>avg {avgMs} ms</span>}
          </div>
          <AxArea points={counts} labels={hourLabels}/>
          <div style={{display:'flex',justifyContent:'space-between',fontSize:10,color:'var(--text3)',marginTop:4}}>
            {hourLabels.filter((_,i)=>i%4===0).map(l=><span key={l}>{l}</span>)}
          </div>
        </AxPanel>

        <AxPanel title="Success Rate">
          <AxGauge pct={rateToday} tone={rateToday!=null&&rateToday<95?'var(--warning)':'var(--success)'}
            caption={loaded?todayOk.toLocaleString()+' of '+(todayTotal||0).toLocaleString()+' succeeded today':'loading…'}/>
          <div style={{display:'flex',gap:8,marginTop:12}}>
            <div style={{flex:1,background:'var(--success-bg)',border:'1px solid var(--success-border)',borderRadius:8,padding:'8px 10px'}}>
              <div style={{fontSize:16,fontWeight:800,color:'var(--success)'}}>{loaded?todayOk.toLocaleString():'—'}</div>
              <div style={{fontSize:10.5,color:'var(--text3)'}}>succeeded</div>
            </div>
            <div style={{flex:1,background:'var(--error-bg)',border:'1px solid var(--error-border)',borderRadius:8,padding:'8px 10px'}}>
              <div style={{fontSize:16,fontWeight:800,color:'var(--error)'}}>{loaded?todayBad:'—'}</div>
              <div style={{fontSize:10.5,color:'var(--text3)'}}>failed</div>
            </div>
          </div>
        </AxPanel>
      </div>

      {/* Busiest hours + breakdowns */}
      <div style={{display:'grid',gridTemplateColumns:'minmax(0,2fr) minmax(0,1fr)',gap:14,marginBottom:14}}>
        <AxPanel title="Busiest hours" action={<span style={{fontSize:11.5,color:'var(--text3)'}}>executions per hour</span>}>
          <AxBars items={busiest}/>
        </AxPanel>
        <AxPanel title="Triggers">
          <AxBreakdown rows={triggerRows}/>
        </AxPanel>
      </div>

      {/* Errors + adapters */}
      <div style={{display:'grid',gridTemplateColumns:'minmax(0,2fr) minmax(0,1fr)',gap:14}}>
        <AxPanel title="Recent failures"
          action={<span style={{fontSize:11.5,color:'var(--text3)'}}>{errors.length} shown</span>} pad={0}>
          <div style={{padding:'0 18px 16px'}}>
            {errors.length===0?(
              <div style={{padding:'26px 0',textAlign:'center',color:'var(--text3)',fontSize:12.5}}>
                {loaded?'No failures recorded — nothing to investigate.':'Loading…'}
              </div>
            ):(
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead>
                  <tr style={{textAlign:'left',color:'var(--text3)',fontSize:10.5,textTransform:'uppercase',letterSpacing:'.6px'}}>
                    <th style={{padding:'6px 0',fontWeight:700}}>Integration</th>
                    <th style={{padding:'6px 0',fontWeight:700}}>Error</th>
                    <th style={{padding:'6px 0',fontWeight:700,textAlign:'right'}}>When</th>
                  </tr>
                </thead>
                <tbody>
                  {errors.slice(0,6).map((e,i)=>(
                    <tr key={e.id||i} style={{borderTop:'1px solid var(--border)'}}>
                      <td style={{padding:'9px 10px 9px 0',fontWeight:600,color:'var(--text)',whiteSpace:'nowrap'}}>
                        {e.integrationName||'—'}
                      </td>
                      <td style={{padding:'9px 10px 9px 0',color:'var(--error)',maxWidth:380,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}
                          title={e.errorMessage||''}>
                        {e.errorMessage||'—'}
                      </td>
                      <td style={{padding:'9px 0',color:'var(--text3)',textAlign:'right',whiteSpace:'nowrap'}}>
                        {e.startedAt?new Date(e.startedAt).toLocaleTimeString():''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </AxPanel>

        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          <AxPanel title="Adapters in use">
            <AxBreakdown rows={adapterRows} colors={['var(--info)','var(--brand-start)','var(--success)','var(--warning)','var(--text3)','var(--error)']}/>
          </AxPanel>
          <AxPanel title="Log pipeline">
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,fontSize:12}}>
              <div><div style={{fontSize:17,fontWeight:800,color:'var(--text)'}}>{writer.queueSize!=null?writer.queueSize:'—'}</div><div style={{fontSize:10.5,color:'var(--text3)'}}>queued</div></div>
              <div><div style={{fontSize:17,fontWeight:800,color:writer.dropped?'var(--error)':'var(--text)'}}>{writer.dropped!=null?writer.dropped:'—'}</div><div style={{fontSize:10.5,color:'var(--text3)'}}>dropped</div></div>
              <div><div style={{fontSize:17,fontWeight:800,color:'var(--text)'}}>{writer.rowsWritten!=null?Number(writer.rowsWritten).toLocaleString():'—'}</div><div style={{fontSize:10.5,color:'var(--text3)'}}>rows written</div></div>
              <div><div style={{fontSize:17,fontWeight:800,color:'var(--text)'}}>{m.totalAll!=null?Number(m.totalAll).toLocaleString():'—'}</div><div style={{fontSize:10.5,color:'var(--text3)'}}>all-time runs</div></div>
            </div>
          </AxPanel>
        </div>
      </div>
    </div>
  );
}

function fmtRelative(iso){
  if(!iso)return null;
  let t;try{t=new Date(iso).getTime();}catch{return null;}
  if(!t||isNaN(t))return null;
  const diff=Math.floor((Date.now()-t)/1000);
  if(diff<5)return'just now';
  if(diff<60)return diff+'s ago';
  if(diff<3600)return Math.floor(diff/60)+' min ago';
  if(diff<86400)return Math.floor(diff/3600)+' hr ago';
  if(diff<2592000)return Math.floor(diff/86400)+' days ago';
  return new Date(t).toLocaleDateString();
}

/* Observability -> Logs: two sources, served by LogViewController at /api/admin/logs/*.
   - app:     Axon's own log, Camel routes included (logback, logs/taxlab-einvoice*.log)
   - gateway: APISIX's error and access logs (file copies under /home/opc/apisix/data/logs)
   To follow one gateway call end to end, use Distributed Tracing (Zipkin). */
const LOG_SOURCES={app:'Axon & Camel routes',gateway:'API Gateway (APISIX)'};
const LOG_APP_ENTRY=/^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}\.\d{3}) +(TRACE|DEBUG|INFO|WARN|ERROR)\b\s*(.*)$/;
const LOG_GW_ERROR=/^(\d{4}\/\d{2}\/\d{2} \d{2}:\d{2}:\d{2}) \[(\w+)\] (.*)$/;
const LOG_GW_ACCESS=/^(\S+) \S+ \S+ \[([^\]]+)\] (.*)$/;
const NGINX_LEVELS={debug:'DEBUG',info:'INFO',notice:'INFO',warn:'WARN',error:'ERROR',crit:'ERROR',alert:'ERROR',emerg:'ERROR'};
function parseLogLine(source,line){
  if(source==='gateway'){
    let m=LOG_GW_ERROR.exec(line);
    if(m)return {timestamp:m[1].replace(/\//g,'-'),level:NGINX_LEVELS[m[2]]||m[2].toUpperCase(),message:m[3]};
    m=LOG_GW_ACCESS.exec(line);
    if(m){
      const st=/" (\d{3}) /.exec(m[3]);const code=st?Number(st[1]):0;
      return {timestamp:m[2],level:code>=500?'ERROR':code>=400?'WARN':'INFO',message:m[1]+' '+m[3]};
    }
    return null;
  }
  const m=LOG_APP_ENTRY.exec(line);
  return m?{timestamp:m[1],level:m[2],message:m[3]}:null;
}
function groupLogLines(source,raw){
  /* Axon's pattern spans lines, and so does a Lua traceback in the gateway's error log:
     a line that does not start an entry continues the one above */
  const out=[];
  for(const line of raw){
    const e=parseLogLine(source,line);
    if(e)out.push(e);
    else if(out.length)out[out.length-1].message+=' '+line.trim();
    else if(line.trim())out.push({timestamp:'',level:'',message:line});
  }
  return out;
}

function LogViewer({showToast}){
  const [files,setFiles]=useState([]);
  const [selected,setSelected]=useState('');   // "source:name"
  const [entries,setEntries]=useState([]);
  const [meta,setMeta]=useState(null);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState(null);
  const [tail,setTail]=useState(2000);
  const [levelFilter,setLevelFilter]=useState('ALL');
  const [searchText,setSearchText]=useState('');

  const loadFiles=async()=>{
    try{
      const r=await API('GET','/api/admin/logs/files');
      const fs=r.files||[];
      setFiles(fs);
      const current=fs.find(f=>f.source==='app'&&f.name==='taxlab-einvoice.log')||fs[0];
      if(current)setSelected(current.source+':'+current.name);else setLoading(false);
    }catch(e){setError(e.message);setLoading(false);}
  };
  useEffect(()=>{loadFiles();},[]);

  const loadContent=async()=>{
    if(!selected)return;
    const i=selected.indexOf(':');const source=selected.slice(0,i),file=selected.slice(i+1);
    setLoading(true);setError(null);
    try{
      const qs=new URLSearchParams({source,file,tail:String(tail),maxLines:String(tail)}).toString();
      const r=await API('GET','/api/admin/logs/content?'+qs);
      setEntries(groupLogLines(source,r.lines||[]).reverse());
      setMeta({total:r.totalLines,shown:(r.lines||[]).length});
    }catch(e){setError(e.message);}
    setLoading(false);
  };
  useEffect(()=>{loadContent();},[selected,tail]);

  const filtered=entries.filter(l=>{
    if(levelFilter!=='ALL'&&l.level!==levelFilter)return false;
    if(searchText){const s=searchText.toLowerCase();return (l.timestamp+' '+l.level+' '+l.message).toLowerCase().includes(s);}
    return true;
  });

  const levelBadge=(lv)=>{
    const colors={ERROR:'#d32f2f',WARN:'#f57c00',INFO:'#1976d2',DEBUG:'#388e3c',TRACE:'#757575'};
    const c=colors[lv]||'#666';
    return <span style={{color:c,fontWeight:600,fontSize:'11px',display:'inline-block',minWidth:42}}>{lv}</span>;
  };

  return(
    <div className="page-container">
      <div className="page-header">
        <div><h1 className="page-title">Logs</h1><div className="page-subtitle">Axon's application log and the gateway's logs, newest first</div></div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <select value={tail} onChange={e=>setTail(Number(e.target.value))} className="form-select" title="How many lines to read from the end of the file">
            {[500,2000].map(n=><option key={n} value={n}>Last {n} lines</option>)}
          </select>
          <button className="btn btn-primary" onClick={loadContent} disabled={loading}>
            {loading?'Loading...':'Refresh'}
          </button>
        </div>
      </div>
      <div style={{fontSize:13,color:'var(--text-muted,#666)',marginBottom:12}}>
        Newest first. <b>{LOG_SOURCES.app}</b>: Axon's application log, every Camel route included.
        {' '}<b>{LOG_SOURCES.gateway}</b>: the gateway's error log (plugin and upstream failures) and access log (one line per call).
        {' '}To follow one call end to end, use <b>Distributed Tracing</b>.
      </div>
      {error&&<div className="error-banner">{error}</div>}
      <div style={{display:'flex',gap:8,marginBottom:12,flexWrap:'wrap',alignItems:'center'}}>
        <select value={selected} onChange={e=>setSelected(e.target.value)} className="form-select" style={{minWidth:250}}>
          {Object.keys(LOG_SOURCES).map(src=>{
            const fs=files.filter(f=>f.source===src);
            return <optgroup key={src} label={LOG_SOURCES[src]}>
              {fs.length===0?<option disabled value="">(no log files)</option>:
                fs.map(f=><option key={src+':'+f.name} value={src+':'+f.name}>{f.name} {f.sizeHuman?`(${f.sizeHuman})`:''}</option>)}
            </optgroup>;
          })}
        </select>
        <select value={levelFilter} onChange={e=>setLevelFilter(e.target.value)} className="form-select">
          <option value="ALL">All Levels</option>
          {['ERROR','WARN','INFO','DEBUG','TRACE'].map(l=><option key={l} value={l}>{l}</option>)}
        </select>
        <input type="text" placeholder="Search..." value={searchText} onChange={e=>setSearchText(e.target.value)} className="form-input" style={{minWidth:180}}/>
      </div>
      {loading?<div className="loading-spinner">Loading...</div>:(
        <div className="log-viewer-container">
          {meta&&<div style={{padding:'4px 8px',background:'var(--surface-2,#f5f5f5)',borderBottom:'1px solid var(--border,#e0e0e0)',fontSize:'12px',color:'var(--text-muted,#666)'}}>
            {`${filtered.length} of ${entries.length} entries · last ${meta.shown} of ${meta.total} lines`}
          </div>}
          <div className="log-viewer-lines">
            {filtered.length===0?<div style={{padding:20,color:'#999'}}>No log lines</div>:filtered.map((l,i)=>
              <div key={i} className="log-line" style={{padding:'2px 8px',fontFamily:'monospace',fontSize:'12px',borderBottom:'1px solid var(--border,#f0f0f0)',wordBreak:'break-word'}}>
                <span style={{color:'#999',marginRight:8}}>{l.timestamp}</span>
                {levelBadge(l.level)}
                <span style={{marginLeft:8}}>{l.message}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ConnectionsPage({showToast,confirm}){
  const [connections,setConnections]=useState([]);
  const [integrations,setIntegrations]=useState([]);
  const [loading,setLoading]=useState(true);
  const [showModal,setShowModal]=useState(false);
  const [editing,setEditing]=useState(null);
  const [searchQ,setSearchQ]=useState('');
  const [filterCat,setFilterCat]=useState('');
  const [filterAdapter,setFilterAdapter]=useState('');
  const [filterStatus,setFilterStatus]=useState('');
  /* per-row inline test state — { [id]: {testing,ok,message,latencyMs,lastTestedAt} } */
  const [testState,setTestState]=useState({});

  /* Map { connectionId -> [{ id, name, status }, ...] } built from the
     integration list. Drives the "used by N integrations" chip on each
     card so operators see adoption + can avoid breaking live wires. */
  const usageByConn=React.useMemo(()=>{
    const m={};
    for(const it of integrations){
      for(const cid of [it.sourceConnectionId,it.targetConnectionId]){
        if(!cid) continue;
        if(!m[cid]) m[cid]=[];
        m[cid].push({id:it.id,name:it.name||it.id,status:it.status});
      }
    }
    return m;
  },[integrations]);

  const load=useCallback(async()=>{
    try{
      setLoading(true);
      const [c,i]=await loadAll([
        ['Connections',API('GET','/api/connections'),[]],
        ['Integrations',API('GET','/api/integrations'),[]],
      ],showToast);
      setConnections(c);
      setIntegrations(Array.isArray(i)?i:[]);
    }
    catch(e){showToast(e.message,'error');}
    finally{setLoading(false);}
  },[showToast]);
  useEffect(()=>{load();},[load]);

  const del=async(id,name)=>{
    const _c=await confirm(`Delete connection "${name}"?`);if(!_c)return;
    try{await API('DELETE',`/api/connections/${id}`);showToast('Connection deleted','success');load();}
    catch(e){showToast(e.message,'error');}
  };

  /* Inline test — updates the pill/last-tested in place, no full reload. */
  const inlineTest=useCallback(async(c)=>{
    setTestState(s=>({...s,[c.id]:{...(s[c.id]||{}),testing:true}}));
    try{
      const r=await API('POST',`/api/connections/${c.id}/test`);
      const ok=!!(r.ok||r.success);
      const lastTestedAt=new Date().toISOString();
      setTestState(s=>({...s,[c.id]:{testing:false,ok,message:r.message,latencyMs:r.latencyMs,lastTestedAt}}));
      /* Patch the connection in-place so the status pill updates without refetch. */
      setConnections(list=>list.map(x=>x.id===c.id?{...x,
        status:ok?'ACTIVE':'ERROR',
        lastTestedAt,lastTestResult:r.message,lastTestLatencyMs:r.latencyMs
      }:x));
      showToast(`${c.name}: ${r.message||(ok?'OK':'Failed')}`,ok?'success':'error');
    }catch(e){
      setTestState(s=>({...s,[c.id]:{testing:false,ok:false,message:e.message,lastTestedAt:new Date().toISOString()}}));
      setConnections(list=>list.map(x=>x.id===c.id?{...x,status:'ERROR'}:x));
      showToast('Test failed: '+e.message,'error');
    }
  },[showToast]);

  /* Unique adapter list (just the ones actually in use). */
  const adapterOpts=useMemo(()=>{
    const seen=new Set();
    connections.forEach(c=>{if(c.adapter)seen.add(c.adapter);});
    return Array.from(seen).sort();
  },[connections]);

  const filtered=connections.filter(c=>{
    const q=searchQ.toLowerCase();
    if(q&&!(c.name||'').toLowerCase().includes(q)&&!(c.adapter||'').toLowerCase().includes(q)
        &&!(c.description||'').toLowerCase().includes(q))return false;
    if(filterCat&&(ADAPTERS[c.adapter]||{}).cat!==filterCat)return false;
    if(filterAdapter&&c.adapter!==filterAdapter)return false;
    if(filterStatus&&String(c.status||'').toUpperCase()!==filterStatus)return false;
    return true;
  });

  return(
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Connections</h1>
          <div className="page-subtitle">Manage all your system connections and credentials</div>
        </div>
      </div>

      <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:20,flexWrap:'wrap'}}>
        <div className="search-field" style={{width:300,flex:'0 0 auto'}}>
          <span className="search-field-icon"><SearchIcon size={20} color="rgb(99,115,129)"/></span>
          <input className="search-field-input" placeholder="Search connections…" value={searchQ} onChange={e=>setSearchQ(e.target.value)}/>
        </div>
        <IconDropdown
          value={filterCat}
          onChange={setFilterCat}
          allLabel="All Categories"
          options={ADAPTER_CATEGORIES.map(c=>({value:c.id,label:c.label}))}
          renderIcon={id=><AdapterCategoryIcon id={id||'all'} size={16}/>}
          style={{width:190,flex:'0 0 auto'}}
        />
        <select className="form-select" style={{maxWidth:170,flex:'0 0 auto'}} value={filterAdapter} onChange={e=>setFilterAdapter(e.target.value)}>
          <option value="">All Adapters</option>
          {adapterOpts.map(a=><option key={a} value={a}>{(ADAPTERS[a]||{}).label||a}</option>)}
        </select>
        <select className="form-select" style={{maxWidth:140,flex:'0 0 auto'}} value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="DRAFT">Draft</option>
          <option value="ACTIVE">Active</option>
          <option value="ERROR">Error</option>
          <option value="TESTING">Testing</option>
        </select>
        <button className="btn btn-refresh" onClick={load} style={{...TAXLAB_LIGHT_STYLE,flexShrink:0}}><BuilderRefreshIcon size={18}/> Refresh</button>
        <button className="btn btn-refresh" title="Run health-check on every active connection now" style={{...TAXLAB_LIGHT_STYLE,flexShrink:0}}
          onClick={async()=>{
            try{
              const r=await API('POST','/api/connections/health/run');
              showToast(`Checked ${r.checked} · ${r.recovered} recovered · ${r.failed} failed`,
                        r.failed>0?'warning':'success');
              load();
            }catch(e){showToast('Health-check failed: '+e.message,'error');}
          }}><ConnectionPlugIcon size={14}/> Run Health Check</button>
        <div style={{flex:1}}/>
        <button className="btn btn-primary" style={TAXLAB_CTA_STYLE} onClick={()=>{setEditing(null);setShowModal(true);}}>
          <AddCircleIcon/> New Connection
        </button>
      </div>

      {loading?<div style={{textAlign:'center',padding:40,color:'var(--text2)'}}>Loading connections…</div>:
       filtered.length===0?
         <div className="empty-state">
           <EmptyStateIcon tone="sky"><ConnectionPlugIcon/></EmptyStateIcon>
           <div className="empty-title">{connections.length===0?'No connections yet':'No connections match your filters'}</div>
           {connections.length===0?<>
             <p style={{marginBottom:20}}>Connect to your first system to start building integrations.</p>
             <button className="btn btn-primary" onClick={()=>setShowModal(true)}><AddCircleIcon/> New Connection</button>
           </>:<button className="btn btn-secondary btn-sm" onClick={()=>{setSearchQ('');setFilterCat('');setFilterAdapter('');setFilterStatus('');}}>Clear filters</button>}
         </div>:
      <div className="connection-grid">
        {filtered.map(c=>(
          <ConnectionCard
            key={c.id}
            conn={c}
            testState={testState[c.id]}
            usedBy={usageByConn[c.id]||[]}
            onTest={()=>inlineTest(c)}
            onEdit={()=>{setEditing(c);setShowModal(true);}}
            onDelete={()=>del(c.id,c.name)}
          />
        ))}
      </div>}

      {showModal&&<ConnectionModal
        initial={editing}
        onClose={()=>setShowModal(false)}
        onSaved={()=>{setShowModal(false);load();showToast(editing?'Connection updated':'Connection created','success');}}
        showToast={showToast}
        confirm={confirm}
      />}
    </div>
  );
}

function ConnectionCard({conn,testState,onTest,onEdit,onDelete,usedBy=[]}){
  const meta=ADAPTERS[conn.adapter]||{};
  const catMeta=ADAPTER_CATEGORIES.find(ac=>ac.id===meta.cat)||{};
  const effectiveStatus=(testState||{}).testing?'TESTING':String(conn.status||'DRAFT').toUpperCase();
  const lastTestedAt=(testState||{}).lastTestedAt||conn.lastTestedAt;
  const relTime=fmtRelative(lastTestedAt);
  const latency=(testState||{}).latencyMs||conn.lastTestLatencyMs;
  const lastMsg=(testState||{}).message||conn.lastTestResult;
  const url=conn.basePath||conn.host;

  return(
    <div className="conn-card" onClick={onEdit} title="Click to edit connection">
      <div className="conn-card-head">
        <div className="conn-card-logo"><AdapterIcon id={conn.adapter} size={36}/></div>
        <div className="conn-card-headtext">
          <div className="conn-card-name" title={conn.name}>{conn.name}</div>
          <div className="conn-card-sub">
            <span className="pill pill-mini"><AdapterCategoryIcon id={catMeta.id||meta.cat} size={12}/> {catMeta.label||'—'}</span>
            <span className="conn-card-sub-sep">•</span>
            <span className="conn-card-sub-adapter">{meta.label||conn.adapter}</span>
          </div>
        </div>
        <StatusBadge status={effectiveStatus}/>
      </div>

      {url&&<div className="conn-card-url" title={url}>{url}</div>}

      <div className="conn-card-foot">
        <div className="conn-card-tested">
          {relTime?<>
            <span style={{color:'var(--text3)'}}>Tested </span>
            <span style={{color:'var(--text2)',fontWeight:600}}>{relTime}</span>
            {latency!=null&&<span style={{color:'var(--text3)',marginLeft:6}}>· {fmtDur(latency)}</span>}
          </>:<span style={{color:'var(--text3)'}}>Not tested yet</span>}
          {/* Usage chip — bullet-separated from the Tested time. Hover
              reveals the full list of integration names so operators
              can see exactly what they'd break by editing or deleting
              this connection. "Unused" stays soft-grey to flag safe-
              to-delete connections without being alarming. */}
          {' · '}
          {usedBy.length>0
            ? <span title={'Used by:\n• '+usedBy.map(u=>u.name+(u.status?' ('+u.status+')':'')).join('\n• ')}
                style={{color:'var(--brand-start)',fontWeight:600,cursor:'help'}}>
                <LinkChainIcon size={13}/> {usedBy.length} integration{usedBy.length===1?'':'s'}
              </span>
            : <span title="Not used by any integration yet — safe to delete"
                style={{color:'var(--text3)',fontStyle:'italic'}}>unused</span>}
        </div>
        <div className="conn-card-actions" onClick={ev=>ev.stopPropagation()}>
          <button
            className={(testState||{}).ok===false?'int-row-btn int-row-btn-danger':'int-row-btn int-row-btn-success'}
            onClick={onTest}
            disabled={!!(testState||{}).testing}
            title={lastMsg||'Test connection now'}>
            {(testState||{}).testing?<><Spinner/> Testing…</>:<><BoltActionIcon size={17}/> Test</>}
          </button>
          <button className="int-row-btn int-row-btn-grey" onClick={onEdit} title="Edit"><PencilIcon size={17}/> Edit</button>
          <button className="btn btn-danger row-icon-action" onClick={onDelete} title="Delete" aria-label="Delete"><TrashIcon size={17}/></button>
        </div>
      </div>

      {lastMsg&&!(testState||{}).testing&&<div className={`conn-card-msg${(testState||{}).ok===false||(testState===undefined&&effectiveStatus==='ERROR')?' err':''}`} title={lastMsg}>
        {(testState||{}).ok===false||effectiveStatus==='ERROR'?<WarningIcon size={13}/>:<span aria-hidden>✓</span>} {lastMsg}
      </div>}
    </div>
  );
}

/* Kept for back-compat with any older callsites. */
function TestConnButton({connId,showToast,onDone}){
  const [testing,setTesting]=useState(false);
  const test=async()=>{
    setTesting(true);
    try{
      const r=await API('POST',`/api/connections/${connId}/test`);
      showToast(r.message||'Connection successful!','success');
      if(onDone)onDone();
    }catch(e){showToast('Test failed: '+e.message,'error');}
    finally{setTesting(false);}
  };
  return <button className="btn btn-success btn-sm" onClick={test} disabled={testing}>{testing?<Spinner/>:<><BoltActionIcon size={13}/> Test</>}</button>;
}

/* Collapsible field group used inside ConnectionModal. The header is a
   clickable row showing the section icon, label, and optional metadata
   (field count / required count). When `collapsible=false` the chevron and
   click handler are suppressed (used for Identity, which is always open).
   `hasError` adds a red accent so the user can spot a section that
   auto-opened due to validation errors. */
function CollapsibleSection({id, icon, label, meta, open, onToggle, children, collapsible=true, hasError=false}) {
  return (
    <section className={`conn-section${open?' is-open':''}${hasError?' has-error':''}`}>
      <header className="conn-section-head"
        onClick={collapsible ? () => onToggle(id) : undefined}
        style={{cursor: collapsible ? 'pointer' : 'default'}}>
        <span className="conn-section-icon" aria-hidden>{icon}</span>
        <span className="conn-section-label-text">{label}</span>
        {meta && <span className="conn-section-meta">{meta}</span>}
        {hasError && <span className="conn-section-err-dot" title="Section has validation errors">●</span>}
        {collapsible && (
          <span className="conn-section-chevron" aria-hidden>{open ? '▴' : '▾'}</span>
        )}
      </header>
      {open && <div className="conn-section-body">{children}</div>}
    </section>
  );
}

/* Classify an adapter field key into a UI group. Adapter definitions don't
   carry a `group` hint, so we infer one from the key. Keeps the form readable
   for connectors with 10+ fields (Oracle Fusion, NetSuite, Workday…). */
const CONN_FIELD_GROUPS = [
  {id:'endpoint', label:'Endpoint',       icon:<LinkChainIcon size={15}/>, defaultOpen:true},
  {id:'auth',     label:'Authentication', icon:<KeyBadgeIcon size={15}/>, defaultOpen:true},
  {id:'advanced', label:'Advanced',       icon:<BuilderToolsIcon size={15}/>, defaultOpen:false},
];
function classifyConnField(field){
  const k=(field.k||'').toLowerCase();
  const label=(field.label||'').toLowerCase();
  // Auth-related keys first (most specific)
  if(/^(auth|username|password|clientid|clientsecret|apikey|tokenid|tokensecret|consumerkey|consumersecret|tenantid|tokenurl|scope|tenant)$/.test(k)
     || /token|secret|password|client.id|client.secret/.test(k)) return 'auth';
  // Endpoint / connection-target keys
  if(/^(baseurl|host|port|server|resourceurl|accountid|restleturl|brokers|topic|database|connectionstring|path|url)$/.test(k)
     || /url$|host$|port$/.test(k)) return 'endpoint';
  // Everything else (version selectors, modules, optional org hints) → advanced
  if(/optional/.test(label) || /version|module|environment|language|systemid|orgid|responsibility|businessunit|apicatalog|apiversion|country|region/.test(k)) return 'advanced';
  // Required-but-unclassified → keep with the endpoint block (safest default
  // for "must-fill" fields a user shouldn't have to expand a section to find)
  return field.req ? 'endpoint' : 'advanced';
}

function ConnectionModal({initial,onClose,onSaved,showToast,confirm}){
  const [step,setStep]=useState(initial?2:1);
  const [adapter,setAdapter]=useState((initial||{}).adapter||'');
  const [catFilter,setCatFilter]=useState('');
  // Load config: from additionalConfig JSON (round-trip) or fallback flat fields
  const [values,setValues]=useState(()=>loadConfigFromConnection(initial));
  const [name,setName]=useState((initial||{}).name||'');
  const [description,setDescription]=useState((initial||{}).description||'');
  const [errors,setErrors]=useState({});
  const [saving,setSaving]=useState(false);
  const [testing,setTesting]=useState(false);
  // Dirty tracking: flips true on first user edit, resets on save/test
  // success. Esc/backdrop click consult this — clean form dismisses
  // immediately; dirty form prompts so the user doesn't lose changes.
  const [dirty,setDirty]=useState(false);
  // Internal setters that mark the form dirty in addition to updating state.
  const setNameD=v=>{setName(v);setDirty(true);};
  const setDescriptionD=v=>{setDescription(v);setDirty(true);};
  // Section collapse state — keyed by group id. Identity is always open
  // (just Name + Description, no real reason to hide). Advanced starts
  // closed so the form fits the modal at common screen sizes.
  const [openGroups,setOpenGroups]=useState(()=>{
    const o={identity:true};
    CONN_FIELD_GROUPS.forEach(g=>{o[g.id]=g.defaultOpen;});
    return o;
  });
  const toggleGroup=(id)=>setOpenGroups(p=>({...p,[id]:!p[id]}));
  const expandAll=()=>{
    const o={identity:true};
    CONN_FIELD_GROUPS.forEach(g=>{o[g.id]=true;});
    setOpenGroups(o);
  };
  const collapseAll=()=>{
    const o={identity:true};  // keep identity always visible
    CONN_FIELD_GROUPS.forEach(g=>{o[g.id]=false;});
    setOpenGroups(o);
  };
  const allOpen=CONN_FIELD_GROUPS.every(g=>openGroups[g.id]);

  // Safe-dismiss: Esc and backdrop-click route through here. Clean form
  // closes immediately; dirty form prompts to avoid silent data loss
  // (the iPaaS form-dirty UX standard — Workato/Boomi/Tray all do this).
  const tryClose=React.useCallback(()=>{
    if (!dirty) { onClose(); return; }
    confirm('You have unsaved changes. Discard and close?').then(ok=>{
      if (ok) onClose();
    });
  }, [dirty, onClose, confirm]);

  // Esc-to-dismiss — listens at the document level so the listener fires
  // even when focus is inside an input that doesn't itself handle Escape.
  React.useEffect(()=>{
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); tryClose(); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [tryClose]);

  // Impact analysis — load dependents when editing an existing connection
  const [dependents,setDependents]=useState(null);
  React.useEffect(()=>{
    if(!(initial||{}).id) return;
    let alive=true;
    API('GET',`/api/connections/${initial.id}/dependents`).then(d=>{ if(alive) setDependents(d); }).catch(()=>{});
    return()=>{ alive=false; };
  },[(initial||{}).id]);

  // Initialize field defaults when adapter is chosen (fixes sw-condition for selects with `def`)
  const {useEffect}=React;
  useEffect(()=>{
    if(!adapter)return;
    const meta=ADAPTERS[adapter];
    if(!meta)return;
    setValues(prev=>{
      const defaults={};
      (meta.fields||[]).forEach(f=>{
        if(f.def!==undefined&&prev[f.k]===undefined){defaults[f.k]=String(f.def);}
      });
      return Object.keys(defaults).length?{...defaults,...prev}:prev;
    });
  },[adapter]);

  const setVal=(k,v)=>{setValues(p=>({...p,[k]:v}));if(errors[k])setErrors(p=>({...p,[k]:undefined}));setDirty(true);};

  const doValidate=()=>{
    const allVals={...values,name};
    const errs=validateConnForm(adapter,allVals,(initial||{}).secretsSet);
    setErrors(errs);
    return Object.keys(errs).length===0;
  };

  const save=async()=>{
    if(!doValidate())return;
    setSaving(true);
    try{
      const payload=buildConnectionPayload(adapter,values,name,description);
      if(initial){
        payload.status=initial.status||'DRAFT';
        await API('PUT',`/api/connections/${initial.id}`,payload);
      } else {
        await API('POST','/api/connections',payload);
      }
      setDirty(false);          // saved cleanly — Esc will dismiss without prompt
      onSaved();showToast('Connection saved!','success');
    }catch(e){showToast(e.message,'error');}
    finally{setSaving(false);}
  };

  // Test Connection stays in the modal: persist the form (so the backend
  // has the values to test against), hit /test, show the result inline,
  // and leave the user where they are. Previously this called onSaved()
  // which closed the modal — fine for "save and test" but surprising for
  // anyone iterating on credentials.
  const [testResult,setTestResult]=useState(null);  // { ok, message, ts }
  const testDirect=async()=>{
    if(!doValidate())return;
    setTesting(true);
    setTestResult(null);
    try{
      const payload=buildConnectionPayload(adapter,values,name,description);
      let id=(initial||{}).id;
      if(!id){const r=await API('POST','/api/connections',payload);id=r.id;}
      else{payload.status=initial.status||'DRAFT';await API('PUT',`/api/connections/${id}`,payload);}
      const r=await API('POST',`/api/connections/${id}/test`);
      setDirty(false);          // values are persisted at this point
      // The /test endpoint returns {ok, success, message, ...} — `ok` is
      // the truth value. Even when HTTP 200 the credentials may still
      // have failed (401/403 from the upstream API), so we must read
      // the body, not assume success from the API call completing.
      const ok = (r && (r.ok === true || r.success === true));
      setTestResult({ok, message:r.message||(ok?'Connection test successful!':'Test failed'), ts:Date.now()});
      showToast(r.message||(ok?'Connection test successful!':'Test failed'), ok?'success':'error');
    }catch(e){
      setTestResult({ok:false,message:'Test failed: '+e.message,ts:Date.now()});
      showToast('Test failed: '+e.message,'error');
    }
    finally{setTesting(false);}
  };

  const meta=ADAPTERS[adapter];

  const catLabel=(ADAPTER_CATEGORIES.find(c=>c.id===(meta||{}).cat)||{}).label||(meta||{}).cat||'';

  return(
    <div className="modal-overlay" onClick={e=>{if(e.target===e.currentTarget)tryClose();}}>
      <div className="modal modal-lg">

        {/* ── Header ── */}
        <div className="modal-header">
          <div>
            <div className="modal-title">{initial?'Edit Connection':'New Connection'}</div>
            {step===2&&meta&&<div className="modal-subtitle">{meta.label}{catLabel?' · '+catLabel:''}</div>}
            {step===1&&<div className="modal-subtitle">Select an adapter to get started</div>}
          </div>
          <button className="modal-close-btn" onClick={tryClose} aria-label="Close"><CloseIcon size={16}/></button>
        </div>

        {/* ── Body ── */}
        <div className="modal-body">

          {/* Step 1 — adapter picker */}
          {step===1&&(
            <div className="modal-body-scroll">
              {!initial&&(
                <div style={{display:'flex',gap:4,flexWrap:'nowrap',overflowX:'auto',marginBottom:16,paddingBottom:2,scrollbarWidth:'none'}}>
                  {[{id:'',label:'All'},...ADAPTER_CATEGORIES].map(c=>(
                    <button key={c.id} onClick={()=>setCatFilter(c.id)} title={c.label||'All'}
                      style={{padding:'4px 12px',borderRadius:20,fontSize:11,fontWeight:600,cursor:'pointer',border:'1.5px solid',transition:'all .15s',whiteSpace:'nowrap',flexShrink:0,lineHeight:1.7,display:'inline-flex',alignItems:'center',gap:6,
                        background:catFilter===c.id?'var(--brand-grad)':'var(--surface2)',
                        color:catFilter===c.id?'#fff':'var(--text2)',
                        borderColor:catFilter===c.id?'transparent':'var(--border)',
                        boxShadow:catFilter===c.id?'0 2px 8px rgba(255,86,48,.25)':'none'
                      }}>
                      <AdapterCategoryIcon id={c.id||'all'} size={14}/> {c.id===''?'All':c.label}
                    </button>
                  ))}
                </div>
              )}
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(108px,1fr))',gap:10}}>
                {Object.entries(ADAPTERS)
                  .filter(([id,a])=>!catFilter||a.cat===catFilter)
                  .map(([id,a])=>(
                    <div key={id} onClick={()=>{setAdapter(id);setStep(2);}}
                      style={{background:adapter===id?'linear-gradient(135deg,rgba(255,86,48,.08),rgba(255,171,0,.05))':'var(--surface)',
                        border:`1.5px solid ${adapter===id?'var(--brand-start)':'var(--border)'}`,
                        borderRadius:12,padding:'14px 8px',textAlign:'center',cursor:'pointer',transition:'all .18s',
                        boxShadow:adapter===id?'0 4px 14px rgba(255,86,48,.14)':'0 1px 3px rgba(0,0,0,.04)'
                      }}
                      onMouseEnter={e=>{if(adapter!==id){e.currentTarget.style.borderColor='rgba(255,86,48,.5)';e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.boxShadow='0 6px 18px rgba(255,86,48,.1)';}}}
                      onMouseLeave={e=>{if(adapter!==id){e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.transform='none';e.currentTarget.style.boxShadow='0 1px 3px rgba(0,0,0,.04)';}}}
                    >
                      <div style={{display:'flex',justifyContent:'center',marginBottom:8}}><AdapterIcon id={id} size={32}/></div>
                      <div style={{fontSize:11,fontWeight:600,color:adapter===id?'var(--brand-start)':'var(--text2)',lineHeight:1.3}}>{a.label}</div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Step 2 — two-panel configure */}
          {step===2&&meta&&(
            <div className="conn-modal-panels">

              {/* Left sidebar */}
              <div className="conn-modal-sidebar">
                <div className="conn-adapter-badge">
                  <div className="conn-adapter-badge-icon"><AdapterIcon id={adapter} size={28}/></div>
                  <div>
                    <div className="conn-adapter-badge-name">{meta.label}</div>
                    {catLabel&&<div className="conn-adapter-badge-cat">{catLabel}</div>}
                  </div>
                  {!initial&&(
                    <button onClick={()=>setStep(1)}
                      style={{fontSize:11,fontWeight:600,color:'var(--text3)',background:'none',border:'1px solid var(--border)',borderRadius:6,padding:'3px 10px',cursor:'pointer',transition:'all .15s'}}
                      onMouseEnter={e=>{e.currentTarget.style.color='var(--text)';e.currentTarget.style.borderColor='var(--text3)';}}
                      onMouseLeave={e=>{e.currentTarget.style.color='var(--text3)';e.currentTarget.style.borderColor='var(--border)';}}>
                      ← Change
                    </button>
                  )}
                </div>

                {/* Impact warning in sidebar */}
                {initial&&dependents&&dependents.count>0&&(
                  <div className="conn-impact-block">
                    <div className="conn-impact-title">
                      <WarningIcon/>
                      <span>Used by {dependents.count} integration{dependents.count!==1?'s':''}</span>
                    </div>
                    <div className="conn-impact-desc">Changes will propagate to all linked integrations. Active routes are not auto-restarted.</div>
                    <div className="conn-impact-chips">
                      {dependents.integrations.slice(0,8).map(dep=>(
                        <span key={dep.id} className="conn-impact-chip" title={`${dep.usage} · ${dep.status}`}>
                          {dep.usage==='SOURCE'?'↑':dep.usage==='TARGET'?'↓':'⇄'} {dep.name}
                        </span>
                      ))}
                      {dependents.integrations.length>8&&<span style={{fontSize:11,color:'#b45309',marginTop:2}}>+{dependents.integrations.length-8} more</span>}
                    </div>
                  </div>
                )}

                {initial&&(
                  <div style={{marginTop:'auto',paddingTop:12,borderTop:'1px solid var(--border)'}}>
                    <div style={{fontSize:10.5,color:'var(--text3)',fontWeight:600,textTransform:'uppercase',letterSpacing:'.05em',marginBottom:6}}>Status</div>
                    <StatusBadge status={initial.status}/>
                  </div>
                )}
              </div>

              {/* Right form panel — grouped collapsible sections so connectors
                  with 10+ fields stay scannable. Sections with errors auto-open
                  on Save/Test so validation feedback isn't hidden. */}
              <div className="conn-modal-form">
                {/* Top toolbar — field count, dirty indicator, expand-all / collapse-all */}
                <div className="conn-form-toolbar">
                  <span className="conn-form-toolbar-hint">
                    {(meta.fields||[]).length+2} field{(meta.fields||[]).length+2!==1?'s':''} · grouped
                    {dirty && <span style={{marginLeft:8,color:'#d97706',fontWeight:700}}>● Unsaved</span>}
                  </span>
                  <button type="button" className="conn-form-toolbar-btn"
                    onClick={allOpen?collapseAll:expandAll}>
                    {allOpen?'▴ Collapse all':'▾ Expand all'}
                  </button>
                </div>

                {/* Inline test result — replaces the modal-closing behavior so
                    the user sees the outcome AND keeps their place in the form. */}
                {testResult && (
                  <div className={`conn-test-result ${testResult.ok?'is-ok':'is-fail'}`}
                       style={{display:'flex',alignItems:'flex-start',gap:10,padding:'10px 12px',borderRadius:8,marginBottom:12,
                               background: testResult.ok ? 'rgba(22,163,74,.08)' : 'rgba(239,68,68,.08)',
                               border: `1px solid ${testResult.ok?'rgba(22,163,74,.3)':'rgba(239,68,68,.3)'}`,
                               color: testResult.ok?'#15803d':'#b91c1c',fontSize:12.5,lineHeight:1.45}}>
                    <span style={{display:'inline-flex',flexShrink:0}}>{testResult.ok?<CheckCircleIcon size={16}/>:<WarningIcon size={16}/>}</span>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:700,marginBottom:1}}>{testResult.ok?'Test successful':'Test failed'}</div>
                      <div style={{wordBreak:'break-word'}}>{testResult.message}</div>
                    </div>
                    <button onClick={()=>setTestResult(null)} title="Dismiss"
                      style={{background:'none',border:'none',color:'inherit',opacity:.6,cursor:'pointer',fontSize:14,padding:'0 2px',lineHeight:1,display:'inline-flex'}}><CloseIcon size={14}/></button>
                  </div>
                )}

                {/* Identity — Name + Description, always rendered (no collapse) */}
                <CollapsibleSection
                  id="identity" icon="🆔" label="Identity"
                  open={openGroups.identity} onToggle={toggleGroup}
                  collapsible={false}>
                  <div className="form-group">
                    <label className="form-label">Connection Name <span className="req">*</span></label>
                    <input className={`form-input${errors.name?' error':''}`} value={name}
                      onChange={e=>{setNameD(e.target.value);if(errors.name)setErrors(p=>({...p,name:undefined}));}}
                      placeholder={`My ${meta.label} Connection`}/>
                    {errors.name&&<div className="field-error"><WarningIcon/> {errors.name}</div>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Description</label>
                    <input className="form-input" value={description} onChange={e=>setDescriptionD(e.target.value)} placeholder="Optional — what does this connection do?"/>
                  </div>
                </CollapsibleSection>

                {/* Adapter fields, partitioned by classifyConnField() */}
                {(() => {
                  const byGroup = {endpoint:[], auth:[], advanced:[]};
                  (meta.fields||[]).forEach(f => { byGroup[classifyConnField(f)].push(f); });
                  return CONN_FIELD_GROUPS.map(g => {
                    const fields = byGroup[g.id];
                    if (!fields.length) return null;
                    // Auto-open a section if any of its fields has a validation error,
                    // so the user can see what's wrong without hunting.
                    const hasErr = fields.some(f => errors[f.k]);
                    const shown = openGroups[g.id] || hasErr;
                    const reqCount = fields.filter(f => f.req).length;
                    return (
                      <CollapsibleSection key={g.id}
                        id={g.id} icon={g.icon} label={g.label}
                        meta={`${fields.length} field${fields.length!==1?'s':''}${reqCount?` · ${reqCount} required`:''}`}
                        open={shown} onToggle={toggleGroup}
                        hasError={hasErr}>
                        {fields.map(f => (
                          <FieldInput key={f.k} field={f} value={values[f.k]}
                            onChange={setVal} error={errors[f.k]} allValues={values}
                            isEditing={!!initial}
                            secretSaved={((initial||{}).secretsSet||[]).includes(f.k)}/>
                        ))}
                      </CollapsibleSection>
                    );
                  });
                })()}
              </div>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        {step===2&&(
          <div className="modal-footer">
            <button className="btn btn-ghost" onClick={tryClose} style={{marginRight:'auto'}}>Cancel</button>
            <button className="btn btn-success" onClick={testDirect} disabled={testing||saving}>
              {testing?<><Spinner/> Testing…</>:<><BoltActionIcon size={14}/> Test Connection</>}
            </button>
            <button className="btn btn-primary" onClick={save} disabled={saving||testing}>
              {saving?<><Spinner/> Saving…</>:initial?<><SaveActionIcon/> Save Changes</>:'Create Connection'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ===== INTEGRATIONS PAGE ===== */
/* ─── Webhook receiver panel ──────────────────────────────────────────────
   Renders inside the expanded integration row when triggerType=REST.
   Surfaces the public URL (with copy button), summary counts of webhook
   deliveries, and the most recent 10 deliveries with replay buttons.
   Backed by /api/admin/webhook-deliveries (see WebhookDeliveryController). */
function WebhookReceiverPanel({integration,showToast,confirm}){
  const [summary,setSummary]=useState(null);
  const [deliveries,setDeliveries]=useState([]);
  const [loading,setLoading]=useState(false);
  const [openDelivery,setOpenDelivery]=useState(null);
  const [replaying,setReplaying]=useState({});

  // Reconstruct the path the route serves at — matches DynamicRouteEngine.buildRestUri
  // which defaults to /webhooks/{integrationId} when no `path` is set in triggerConfig.
  const cfg=(()=>{
    try{return integration.triggerConfig?JSON.parse(integration.triggerConfig):{};}catch{return{};}
  })();
  const path=cfg.path||`/webhooks/${integration.id}`;
  const fullUrl=`${window.location.origin}/api${path.startsWith('/')?path:'/'+path}`;

  const reload=useCallback(async()=>{
    try{
      setLoading(true);
      const [s,d]=await loadAll([
        ['Delivery summary',API('GET',`/api/admin/webhook-deliveries/summary?integrationId=${encodeURIComponent(integration.id)}`),null],
        ['Deliveries',API('GET',`/api/admin/webhook-deliveries?integrationId=${encodeURIComponent(integration.id)}&limit=10`),[]],
      ],showToast);
      setSummary(s);
      setDeliveries(d);
    }catch(e){showToast&&showToast(e.message,'error');}
    finally{setLoading(false);}
  },[integration.id,showToast]);

  useEffect(()=>{reload();},[reload]);

  const copyUrl=async()=>{
    try{await navigator.clipboard.writeText(fullUrl);showToast&&showToast('Webhook URL copied','success');}
    catch{showToast&&showToast('Copy failed — select manually','error');}
  };
  const replay=async(id)=>{
    const _c=await confirm('Replay this delivery? It will re-run the Fusion write-back with the original payload.');if(!_c)return;
    setReplaying(p=>({...p,[id]:true}));
    try{
      await API('POST',`/api/admin/webhook-deliveries/${encodeURIComponent(id)}/replay`);
      showToast&&showToast('Replay dispatched','success');
      setTimeout(reload,800);
    }catch(e){showToast&&showToast(e.message,'error');}
    finally{setReplaying(p=>({...p,[id]:false}));}
  };

  const statusBadge=(s)=>{
    const m={'SUCCESS':'success','RECEIVED':'info','RETRYING':'warning','FAILED':'error','DUPLICATE':'gray'};
    return <span className={`badge badge-${m[s]||'gray'}`} style={{fontSize:10}}>{s}</span>;
  };

  return(
    <div style={{marginTop:12,padding:14,background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:8}}>
      <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:10,flexWrap:'wrap'}}>
        <span style={{fontSize:13,fontWeight:600,color:'var(--text)',display:'inline-flex',alignItems:'center',gap:6}}><LinkChainIcon size={13}/> Webhook Endpoint</span>
        <code style={{flex:'1 1 auto',padding:'4px 8px',background:'var(--surface)',border:'1px solid var(--border)',borderRadius:5,fontSize:11.5,fontFamily:'ui-monospace,monospace',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{fullUrl}</code>
        <button className="btn btn-ghost btn-xs" onClick={copyUrl} title="Copy URL"><FileDocIcon size={12}/> Copy</button>
        <button className="btn btn-ghost btn-xs" onClick={reload} title="Refresh"><BuilderRefreshIcon size={13}/></button>
      </div>
      {summary && summary.total > 0 && (
        <div style={{display:'flex',gap:14,marginBottom:10,fontSize:11,color:'var(--text2)',flexWrap:'wrap'}}>
          <span><strong>{summary.total}</strong> total</span>
          {Object.entries(summary.countsByStatus||{}).map(([k,v])=>(
            <span key={k}>{statusBadge(k)} <strong>{v}</strong></span>
          ))}
        </div>
      )}
      <div style={{fontSize:12,fontWeight:600,color:'var(--text2)',marginBottom:6}}>Recent Deliveries</div>
      {loading && <div style={{fontSize:11.5,color:'var(--text3)'}}>Loading…</div>}
      {!loading && deliveries.length===0 && (
        <div style={{fontSize:11.5,color:'var(--text3)',padding:'10px 0'}}>No deliveries yet — register this URL with TaxLab to start receiving acknowledgements.</div>
      )}
      {!loading && deliveries.length>0 && (
        <table style={{width:'100%',fontSize:11.5,borderCollapse:'collapse'}}>
          <thead>
            <tr style={{borderBottom:'1px solid var(--border)',color:'var(--text3)',textAlign:'left'}}>
              <th style={{padding:'8px 10px',fontWeight:500}}>Received</th>
              <th style={{padding:'8px 10px',fontWeight:500}}>Event</th>
              <th style={{padding:'8px 10px',fontWeight:500}}>Status</th>
              <th style={{padding:'8px 10px',fontWeight:500}}>Attempts</th>
              <th style={{padding:'8px 10px',fontWeight:500,textAlign:'right'}}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {deliveries.map(d=>(
              <tr key={d.deliveryId} style={{borderBottom:'1px solid var(--border)',cursor:'pointer'}}
                  onClick={()=>setOpenDelivery(d)}>
                <td style={{padding:'8px 10px',fontFamily:'ui-monospace,monospace',color:'var(--text2)'}}>{fmtDate(d.receivedAt)}</td>
                <td style={{padding:'8px 10px'}}>{d.eventType}</td>
                <td style={{padding:'8px 10px'}}>{statusBadge(d.status)}</td>
                <td style={{padding:'8px 10px',color:'var(--text2)'}}>{d.attemptCount||1}</td>
                <td style={{padding:'8px 10px',textAlign:'right'}} onClick={ev=>ev.stopPropagation()}>
                  {d.status==='FAILED' && (
                    <button className="int-row-btn int-row-btn-light" onClick={()=>replay(d.deliveryId)} disabled={replaying[d.deliveryId]}>
                      {replaying[d.deliveryId]?<Spinner/>:<><BuilderRefreshIcon size={17}/> Replay</>}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {openDelivery && (
        <div className="modal-overlay" onClick={e=>{if(e.target===e.currentTarget)setOpenDelivery(null);}}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <div className="modal-title">Delivery {openDelivery.deliveryId}</div>
              <button className="modal-close-btn" onClick={()=>setOpenDelivery(null)} aria-label="Close"><CloseIcon size={16}/></button>
            </div>
            <div className="modal-body">
              <div style={{display:'grid',gridTemplateColumns:'auto 1fr',gap:'6px 14px',fontSize:12,marginBottom:14}}>
                <div style={{color:'var(--text3)'}}>Event</div><div>{openDelivery.eventType}</div>
                <div style={{color:'var(--text3)'}}>Received</div><div>{fmtDate(openDelivery.receivedAt)}</div>
                <div style={{color:'var(--text3)'}}>Processed</div><div>{fmtDate(openDelivery.processedAt)||'—'}</div>
                <div style={{color:'var(--text3)'}}>Status</div><div>{statusBadge(openDelivery.status)}</div>
                <div style={{color:'var(--text3)'}}>Attempts</div><div>{openDelivery.attemptCount||1}</div>
                {openDelivery.fusionInvoiceId && <><div style={{color:'var(--text3)'}}>Fusion Invoice</div><div style={{fontFamily:'ui-monospace,monospace'}}>{openDelivery.fusionInvoiceId}</div></>}
                {openDelivery.errorMessage && <><div style={{color:'var(--text3)'}}>Error</div><div style={{color:'var(--error)'}}>{openDelivery.errorMessage}</div></>}
                {openDelivery.jwtSubject && <><div style={{color:'var(--text3)'}}>JWT subject</div><div style={{fontFamily:'ui-monospace,monospace'}}>{openDelivery.jwtSubject}</div></>}
                {openDelivery.sourceIp && <><div style={{color:'var(--text3)'}}>Source IP</div><div style={{fontFamily:'ui-monospace,monospace'}}>{openDelivery.sourceIp}</div></>}
              </div>
              <div style={{fontWeight:600,marginBottom:4,fontSize:12}}>Payload</div>
              <pre style={{margin:0,padding:10,background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:6,fontSize:11,maxHeight:420,overflow:'auto',fontFamily:'ui-monospace,monospace'}}>{openDelivery.eventPayload||'(empty)'}</pre>
            </div>
            <div className="modal-footer">
              {openDelivery.status==='FAILED' && (
                <button className="btn btn-primary btn-sm" onClick={()=>{replay(openDelivery.deliveryId);setOpenDelivery(null);}}><BuilderRefreshIcon size={14}/> Replay</button>
              )}
              <button className="btn btn-ghost btn-sm" onClick={()=>setOpenDelivery(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function WebhookDeliveryPanel({integration,showToast,confirm}){
  const [summary,setSummary]=useState(null);
  const [deliveries,setDeliveries]=useState([]);
  const [loading,setLoading]=useState(false);
  const [openDelivery,setOpenDelivery]=useState({});
  const [replaying,setReplaying]=useState({});

  // Filter state
  const [filterStatus,setFilterStatus]=useState('');
  const [filterEvent,setFilterEvent]=useState('');
  const [filterHttp,setFilterHttp]=useState('');
  const [searchQ,setSearchQ]=useState('');
  const [showFilters,setShowFilters]=useState(false);

  // Reconstruct the path the route serves at
  const cfg=(()=>{
    try{return integration.triggerConfig?JSON.parse(integration.triggerConfig):{};}catch{return{};}
  })();
  const path=cfg.path||`/webhooks/${integration.id}`;
  const fullUrl=`${window.location.origin}/api${path.startsWith('/')?path:'/'+path}`;

  const reload=useCallback(async()=>{
    try{
      setLoading(true);
      const [s,d]=await loadAll([
        ['Delivery summary',API('GET',`/api/admin/webhook-deliveries/summary?integrationId=${encodeURIComponent(integration.id)}`),null],
        ['Deliveries',API('GET',`/api/admin/webhook-deliveries?integrationId=${encodeURIComponent(integration.id)}&limit=50`),[]],
      ],showToast);
      setSummary(s);
      setDeliveries(d);
    }catch(e){showToast&&showToast(e.message,'error');}
    finally{setLoading(false);}
  },[integration.id,showToast]);

  useEffect(()=>{reload();},[reload]);

  const copyUrl=async()=>{
    try{await navigator.clipboard.writeText(fullUrl);showToast&&showToast('Webhook URL copied','success');}
    catch{showToast&&showToast('Copy failed — select manually','error');}
  };

  // ── Filters ─────────────────────────────────────────────────────────────
  const filtered=deliveries.filter(d=>{
    if(filterStatus && d.status!==filterStatus) return false;
    if(filterEvent  && (d.eventType||'').toLowerCase()!==filterEvent.toLowerCase()) return false;
    if(filterHttp){
      const hs = d.httpStatus!=null ? String(d.httpStatus) : '';
      if(!hs.includes(filterHttp)) return false;
    }
    if(searchQ){
      const q=searchQ.toLowerCase();
      const hay=(d.eventPayload||'')+' '+(d.deliveryId||'')+' '+(d.errorMessage||'')+' '+(d.fusionInvoiceId||'')+' '+(d.sourceIp||'');
      if(!hay.includes(q)) return false;
    }
    return true;
  });

  const eventTypes=useMemo(()=>{
    const s=new Set();
    deliveries.forEach(d=>{ if(d.eventType) s.add(d.eventType); });
    return [...s].sort();
  },[deliveries]);

  const clearFilters=()=>{setFilterStatus('');setFilterEvent('');setFilterHttp('');setSearchQ('');};
  const hasFilters=filterStatus||filterEvent||filterHttp||searchQ;

  // ── Actions ─────────────────────────────────────────────────────────────
  const replay=async(d)=>{
    const _c=await confirm('Replay this delivery? The original payload will be re-processed.');if(!_c)return;
    setReplaying(p=>({...p,[d.deliveryId]:true}));
    try{
      const resp=await API('POST',`/api/admin/webhook-deliveries/${encodeURIComponent(d.deliveryId)}/replay`);
      if(resp && resp.deliveryId){
        showToast&&showToast(`Replay dispatched — ${(resp.deliveryId||'').slice(0,8)}…`, 'success');
        setTimeout(reload,600);
      }else{
        showToast&&showToast('Replay returned unexpected response','error');
      }
    }catch(e){showToast&&showToast('Replay failed: '+e.message,'error');}
    finally{setReplaying(p=>({...p,[d.deliveryId]:false}));}
  };

  const statusBadge=(s)=>{
    const m={SUCCESS:'success',RECEIVED:'info',RETRYING:'warning',FAILED:'error',DUPLICATE:'gray',AUTH_TOKEN:'accent'};
    return <span className={`badge badge-${m[s]||'gray'}`} style={{fontSize:10}}>{s}</span>;
  };

  // ── Detail modal ─────────────────────────────────────────────────────────
  const openDetail=(d)=>setOpenDelivery(p=>({...p,[d.deliveryId]:d}));
  const closeDetail=(id)=>setOpenDelivery(p=>{ const n={...p}; delete n[id]; return n; });

  const renderDetail=(d)=>{
    const payload=d.eventPayload||'';
    return(
      <div className="modal-overlay" onClick={e=>{if(e.target===e.currentTarget)closeDetail(d.deliveryId);}}>
        <div className="modal modal-xl">
          <div className="modal-header">
            <div className="modal-title" style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
              Delivery {(d.deliveryId||'').slice(0,12)}…
              {statusBadge(d.status)}
              {d.httpStatus!=null && <span className={`badge badge-${d.httpStatus<300?'success':d.httpStatus<400?'warning':'error'}`} style={{fontSize:10}}>HTTP {d.httpStatus}</span>}
            </div>
            <button className="modal-close-btn" onClick={()=>closeDetail(d.deliveryId)} aria-label="Close"><CloseIcon size={16}/></button>
          </div>
          <div className="modal-body">
            <div style={{display:'grid',gridTemplateColumns:'auto 1fr',gap:'6px 16px',fontSize:12.5,marginBottom:16}}>
              <div style={{color:'var(--text3)'}}>Received</div>
              <div>{fmtDate(d.receivedAt)}</div>
              <div style={{color:'var(--text3)'}}>Processed</div>
              <div>{d.processedAt ? fmtDate(d.processedAt) : <em style={{color:'var(--text3)'}}>in-flight</em>}</div>
              <div style={{color:'var(--text3)'}}>Status</div>
              <div>{statusBadge(d.status)} {d.status==='FAILED'&&d.errorMessage&&<span style={{color:'var(--error)',marginLeft:6}}>{d.errorMessage}</span>}</div>
              <div style={{color:'var(--text3)'}}>Event type</div>
              <div style={{fontFamily:'ui-monospace,monospace'}}>{d.eventType||'—'}</div>
              <div style={{color:'var(--text3)'}}>HTTP status</div>
              <div>{d.httpStatus!=null ? <span className={`badge badge-${d.httpStatus<300?'success':d.httpStatus<400?'warning':'error'}`}>{d.httpStatus}</span> : <span style={{color:'var(--text3)'}}>—</span>}</div>
              <div style={{color:'var(--text3)'}}>Attempts</div>
              <div>{d.attemptCount||1}</div>
              <div style={{color:'var(--text3)'}}>Source IP</div>
              <div style={{fontFamily:'ui-monospace,monospace'}}>{d.sourceIp||'—'}</div>
              {d.jwtSubject && <><div style={{color:'var(--text3)'}}>JWT subject</div><div style={{fontFamily:'ui-monospace,monospace'}}>{d.jwtSubject}</div></>}
              {d.fusionInvoiceId && <><div style={{color:'var(--text3)'}}>Fusion invoice</div><div style={{fontFamily:'ui-monospace,monospace'}}>{d.fusionInvoiceId}</div></>}
              {d.correlationId && <><div style={{color:'var(--text3)'}}>Correlation ID</div><code style={{fontSize:10}}>{d.correlationId}</code></>}
              {d.traceId && <><div style={{color:'var(--text3)'}}>Trace ID</div><code style={{fontSize:10}}>{d.traceId}</code></>}
              {d.processingDurationMs!=null && <><div style={{color:'var(--text3)'}}>Processing duration</div><div>{d.processingDurationMs} ms</div></>}
              {d.replayedFromId && <><div style={{color:'var(--text3)'}}>Replayed from</div><div style={{fontFamily:'ui-monospace,monospace',fontSize:10}}>{(d.replayedFromId||'').slice(0,16)}…</div></>}
              {d.replayedAt && <><div style={{color:'var(--text3)'}}>Replayed at</div><div>{fmtDate(d.replayedAt)}</div></>}
            </div>

            <div style={{fontWeight:600,fontSize:12,marginBottom:4}}>Event Payload</div>
            <PayloadViewer payload={payload} raw={d.eventPayload||''} integrationName={integration.name}/>

            {d.metadata && (
              <>
                <div style={{fontWeight:600,fontSize:12,marginBottom:4,marginTop:12}}>Metadata</div>
                <pre style={{margin:0,padding:10,background:'var(--surface)',border:'1px solid var(--border)',borderRadius:6,fontSize:10.5,maxHeight:180,overflow:'auto',fontFamily:'ui-monospace,monospace'}}>{typeof d.metadata==='string'?d.metadata:JSON.stringify(d.metadata,null,2)}</pre>
              </>
            )}
          </div>
          <div className="modal-footer">
            {(d.status==='FAILED' || d.status==='RETRYING') && (
              <button className="btn btn-primary btn-sm" onClick={()=>{replay(d);closeDetail(d.deliveryId);}} disabled={replaying[d.deliveryId]}>
                {replaying[d.deliveryId]?<><Spinner size={12}/> Replaying…</>:<><BuilderRefreshIcon size={14}/> Replay Delivery</>}
              </button>
            )}
            <button className="btn btn-ghost btn-sm" onClick={()=>closeDetail(d.deliveryId)}>Close</button>
          </div>
        </div>
      </div>
    );
  };

  return(
    <div style={{marginTop:12,padding:14,background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:8}}>
      {/* Header row */}
      <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:10,flexWrap:'wrap'}}>
        <span style={{fontSize:13,fontWeight:600,color:'var(--text)',display:'inline-flex',alignItems:'center',gap:6}}><LinkChainIcon size={13}/> Webhook Endpoint</span>
        <code style={{flex:'1 1 auto',padding:'4px 8px',background:'var(--surface)',border:'1px solid var(--border)',borderRadius:5,fontSize:11.5,fontFamily:'ui-monospace,monospace',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{fullUrl}</code>
        <button className="btn btn-ghost btn-xs" onClick={copyUrl} title="Copy URL"><FileDocIcon size={12}/> Copy</button>
        <button className="btn btn-ghost btn-xs" onClick={reload} title="Refresh"><BuilderRefreshIcon size={13}/></button>
      </div>

      {/* Summary counts */}
      {summary && summary.total > 0 && (
        <div style={{display:'flex',gap:12,marginBottom:10,fontSize:11,color:'var(--text2)',flexWrap:'wrap'}}>
          <span><strong>{summary.total}</strong> total</span>
          {Object.entries(summary.countsByStatus||{}).map(([k,v])=>(
            <span key={k}>{statusBadge(k)} <strong>{v}</strong></span>
          ))}
        </div>
      )}

      {/* Filter bar */}
      <div style={{display:'flex',gap:8,marginBottom:10,alignItems:'center',flexWrap:'wrap'}}>
        <input type="search" placeholder="Search payload, IDs…" value={searchQ}
          onChange={e=>setSearchQ(e.target.value)}
          style={{flex:'1 1 180px',padding:'4px 8px',fontSize:11.5,borderRadius:4,border:'1px solid var(--border)',background:'var(--surface)',color:'var(--text)'}}/>
        <button className="btn btn-ghost btn-xs" onClick={()=>setShowFilters(p=>!p)}>
          {showFilters?'Hide':'Show'} Filters {hasFilters&&<span className="badge badge-accent" style={{fontSize:9}}>active</span>}
        </button>
        {hasFilters && <button className="btn btn-ghost btn-xs" onClick={clearFilters}>Clear</button>}
      </div>

      {showFilters && (
        <div style={{display:'flex',gap:8,marginBottom:10,flexWrap:'wrap',padding:'8px 10px',background:'var(--surface)',borderRadius:6}}>
          <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}
            style={{fontSize:11.5,padding:'3px 6px',borderRadius:4,border:'1px solid var(--border)',background:'var(--surface)',color:'var(--text)'}}>
            <option value="">All statuses</option>
            <option value="SUCCESS">SUCCESS</option>
            <option value="RECEIVED">RECEIVED</option>
            <option value="RETRYING">RETRYING</option>
            <option value="FAILED">FAILED</option>
            <option value="DUPLICATE">DUPLICATE</option>
          </select>
          <select value={filterEvent} onChange={e=>setFilterEvent(e.target.value)}
            style={{fontSize:11.5,padding:'3px 6px',borderRadius:4,border:'1px solid var(--border)',background:'var(--surface)',color:'var(--text)'}}>
            <option value="">All events</option>
            {eventTypes.map(et=><option key={et} value={et}>{et}</option>)}
          </select>
          <input type="search" placeholder="HTTP code…" value={filterHttp} onChange={e=>setFilterHttp(e.target.value)}
            style={{width:80,padding:'3px 6px',fontSize:11.5,borderRadius:4,border:'1px solid var(--border)',background:'var(--surface)',color:'var(--text)'}}/>
        </div>
      )}

      {/* Deliveries table */}
      <div style={{fontSize:12,fontWeight:600,color:'var(--text2)',marginBottom:6}}>
        Recent Deliveries {filtered.length<deliveries.length && <span style={{fontWeight:400,color:'var(--text3)'}}>({filtered.length} of {deliveries.length})</span>}
      </div>
      {loading && <div style={{fontSize:11.5,color:'var(--text3)'}}>Loading…</div>}
      {!loading && filtered.length===0 && (
        <div style={{fontSize:11.5,color:'var(--text3)',padding:'10px 0'}}>
          {deliveries.length===0 ? 'No deliveries yet — register this URL with TaxLab to start receiving webhooks.' : 'No deliveries match the current filters.'}
        </div>
      )}
      {!loading && filtered.length>0 && (
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',fontSize:11.5,borderCollapse:'collapse'}}>
            <thead>
              <tr style={{borderBottom:'1px solid var(--border)',color:'var(--text3)',textAlign:'left'}}>
                <th style={{padding:'8px 10px',fontWeight:500}}>Received</th>
                <th style={{padding:'8px 10px',fontWeight:500}}>Event</th>
                <th style={{padding:'8px 10px',fontWeight:500}}>Status</th>
                <th style={{padding:'8px 10px',fontWeight:500}}>HTTP</th>
                <th style={{padding:'8px 10px',fontWeight:500}}>Attempts</th>
                <th style={{padding:'8px 10px',fontWeight:500,textAlign:'right'}}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(d=>(
                <tr key={d.deliveryId} style={{borderBottom:'1px solid var(--border)',cursor:'pointer',background: d.status==='FAILED'?'rgba(220,38,38,0.04)':'transparent'}}
                    onClick={()=>openDetail(d)}>
                  <td style={{padding:'8px 10px',fontFamily:'ui-monospace,monospace',color:'var(--text2)',whiteSpace:'nowrap'}}>{fmtDate(d.receivedAt)}</td>
                  <td style={{padding:'8px 10px'}}>{d.eventType}</td>
                  <td style={{padding:'8px 10px'}}>{statusBadge(d.status)}</td>
                  <td style={{padding:'8px 10px'}}>
                    {d.httpStatus!=null
                      ? <span className={`badge badge-${d.httpStatus<300?'success':d.httpStatus<400?'warning':'error'}`} style={{fontSize:10}}>{d.httpStatus}</span>
                      : <span style={{color:'var(--text3)'}}>—</span>}
                  </td>
                  <td style={{padding:'8px 10px',color:'var(--text2)'}}>{d.attemptCount||1}</td>
                  <td style={{padding:'8px 10px',textAlign:'right'}} onClick={ev=>ev.stopPropagation()}>
                    <button className="btn btn-ghost row-icon-action" title="View details" aria-label="View details" onClick={()=>openDetail(d)}>
                      <FileDocIcon size={17}/>
                    </button>
                    {(d.status==='FAILED' || d.status==='RETRYING') && (
                      <button className="btn btn-ghost row-icon-action" onClick={()=>replay(d)} disabled={replaying[d.deliveryId]} title="Replay delivery" aria-label="Replay">
                        {replaying[d.deliveryId]?<Spinner size={14}/>:<BuilderRefreshIcon size={17}/>}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail modals */}
      {Object.entries(openDelivery).filter(([,d])=>d).map(([id,d])=>renderDetail(d))}
    </div>
  );
}

// ── Payload viewer with syntax highlighting ────────────────────────────────

function PayloadViewer({payload, raw, integrationName}){
  const [copied,setCopied]=useState(false);
  const [viewMode,setViewMode]=useState('formatted');

  const copy=async()=>{
    try{ await navigator.clipboard.writeText(raw||payload); setCopied(true); setTimeout(()=>setCopied(false),1500); }
    catch{}
  };

  return(
    <div style={{marginTop:4}}>
      <div style={{display:'flex',gap:6,marginBottom:4,alignItems:'center'}}>
        <div style={{display:'flex',gap:2}}>
          <button className={`btn btn-xs ${viewMode==='formatted'?'btn-primary':'btn-ghost'}`} onClick={()=>setViewMode('formatted')}>Formatted</button>
          <button className={`btn btn-xs ${viewMode==='tree'?'btn-primary':'btn-ghost'}`} onClick={()=>setViewMode('tree')}>Tree</button>
          <button className={`btn btn-xs ${viewMode==='raw'?'btn-primary':'btn-ghost'}`} onClick={()=>setViewMode('raw')}>Raw</button>
        </div>
        <button className="btn btn-ghost btn-xs" onClick={copy}>{copied?'Copied':'Copy'}</button>
        {integrationName && <span style={{fontSize:10,color:'var(--text3)',marginLeft:'auto'}}>Payload — {integrationName}</span>}
      </div>
      <div style={{padding:10,background:'var(--surface)',border:'1px solid var(--border)',borderRadius:6,fontSize:11.5,maxHeight:420,overflow:'auto',fontFamily:'ui-monospace,monospace'}}>
        <pre style={{margin:0}}>{formatPayload(payload, viewMode, raw)}</pre>
      </div>
    </div>
  );
}

function formatPayload(payload, viewMode, raw){
  if(!payload) return '(empty payload)';
  if(viewMode==='raw') return raw||payload;
  try{ return viewMode==='tree' ? renderJSONTree(JSON.parse(payload)) : JSON.stringify(JSON.parse(payload),null,2); }
  catch{ return payload; }
}

function renderJSONTree(obj, depth=0){
  if(obj===null||obj===undefined) return <span style={{color:'var(--text3)'}}>null</span>;
  if(typeof obj==='string'){
    const len=obj.length;
    if(len>80) return <span title={obj}><span style={{color:'#22c55e'}}>"{obj.slice(0,80)}…"</span> <span style={{color:'var(--text3)',fontSize:9}}>({len} chars)</span></span>;
    return <span style={{color:'#22c55e'}}>"{obj}"</span>;
  }
  if(typeof obj==='number') return <span style={{color:'#f59e0b'}}>{obj}</span>;
  if(typeof obj==='boolean') return <span style={{color:'#8b5cf6'}}>{obj?'true':'false'}</span>;
  if(Array.isArray(obj)){
    if(obj.length===0) return <span style={{color:'var(--text3)'}}>[]</span>;
    if(obj.length>20) return <span style={{color:'var(--text3)'}}>[Array({obj.length})]</span>;
    return <span>[{obj.map((item,i)=> <span key={i} style={{marginLeft:16}}>{renderJSONTree(item,depth+1)}{i<obj.length-1?',':''} </span>)}]</span>;
  }
  if(typeof obj==='object'){
    const keys=Object.keys(obj);
    if(keys.length===0) return <span style={{color:'var(--text3)'}}>{'{}'}</span>;
    return <span>{'{'} {keys.slice(0,30).map((k,i)=> <span key={k} style={{marginLeft:16}}><span style={{color:'#60a5fa'}}>{k}</span>: {renderJSONTree(obj[k],depth+1)}{i<Math.min(keys.length,30)-1?',':''} </span>)} {keys.length>30 && <span style={{color:'var(--text3)'}}>… ({keys.length-30} more)</span>}{'}'}</span>;
  }
  return <span>{String(obj)}</span>;
}

function IntegrationsPage({showToast,projects=[],navFilter,clearNavFilter,confirm}){
  const [integrations,setIntegrations]=useState([]);
  const [connections,setConnections]=useState([]);
  const [loading,setLoading]=useState(true);
  const [activating,setActivating]=useState({});
  const [showRouteBuilder,setShowRouteBuilder]=useState(false);
  const [editingRoute,setEditingRoute]=useState(null);
  const [searchQ,setSearchQ]=useState('');
  const [filterStatus,setFilterStatus]=useState('');
  const [filterTrigger,setFilterTrigger]=useState('');
  const [filterProject,setFilterProject]=useState('');
  const [highlightId,setHighlightId]=useState(null);
  // Per-row expand state — collapsed by default so the page is scannable; users
  // can toggle rows open via the chevron to see description, meta + log level.
  const [expandedRows,setExpandedRows]=useState({});
  const toggleExpand=useCallback((id)=>setExpandedRows(prev=>({...prev,[id]:!prev[id]})),[]);
  // Grouping — collapsible sections (persisted). Default: by Project, which
  // mirrors how routes are organised on disk (data/routes/{projectSlug}/).
  const [groupBy,setGroupBy]=useState(()=>{try{return localStorage.getItem('integrations.groupBy.v2')||'system';}catch(e){return 'system';}});
  useEffect(()=>{try{localStorage.setItem('integrations.groupBy.v2',groupBy);}catch(e){}},[groupBy]);
  const [collapsedGroups,setCollapsedGroups]=useState({});
  const toggleGroup=useCallback((k)=>setCollapsedGroups(p=>({...p,[k]:!p[k]})),[]);

  // Apply external nav filter (e.g. from a Project drill-down)
  useEffect(()=>{
    if(!navFilter)return;
    if(navFilter.projectId!=null) setFilterProject(navFilter.projectId);
    if(navFilter.integrationId){
      setHighlightId(navFilter.integrationId);
      setTimeout(()=>setHighlightId(null), 2200);
    }
    // Deep-link from "＋ New endpoint" (API Management → Endpoints): open the
    // route builder straight away so creation is one click from that view.
    if(navFilter.create){ setEditingRoute(null); setShowRouteBuilder(true); }
    clearNavFilter&&clearNavFilter();
  },[navFilter,clearNavFilter]);

  const load=useCallback(async()=>{
    try{
      setLoading(true);
      const [d,c]=await loadAll([
        ['Integrations',API('GET','/api/integrations'),[]],
        ['Connections',API('GET','/api/connections'),[]],
      ],showToast);
      setIntegrations(d);
      setConnections(c);
    }catch(e){showToast(e.message,'error');}
    finally{setLoading(false);}
  },[showToast]);
  useEffect(()=>{load();},[load]);

  const connName=id=>{const c=connections.find(x=>x.id===id);return c?c.name:id||'—';};
  const connAdapter=id=>{const c=connections.find(x=>x.id===id);return (c||{}).adapter||'';};

  const del=async(id,name)=>{
    const _c=await confirm(`Delete integration "${name}"?`);if(!_c)return;
    try{await API('DELETE',`/api/integrations/${id}`);showToast('Deleted','success');load();}
    catch(e){showToast(e.message,'error');}
  };

  const toggleStatus=async(i)=>{
    setActivating(p=>({...p,[i.id]:true}));
    try{
      const r=i.status==='ACTIVE'
        ? await API('POST',`/api/integrations/${i.id}/deactivate`)
        : await API('POST',`/api/integrations/${i.id}/activate`);
      const msg=r.message||`Integration ${i.status==='ACTIVE'?'deactivated':'activated'}`;
      const running=r.routeRunning;
      showToast(msg+(running?' — Route RUNNING ✓':''), i.status==='ACTIVE'?'success':'success');
      load();
    }catch(e){showToast(e.message,'error');}
    finally{setActivating(p=>({...p,[i.id]:false}));}
  };

  const runNow=async(i)=>{
    if(i.status!=='ACTIVE'){showToast('Activate the integration first before running','warning');return;}
    try{
      const r=await API('POST',`/api/integrations/${i.id}/run`,'{}');
      showToast(`"${i.name}" triggered — check Monitoring for results`,'success');
      setTimeout(load,1500);
    }catch(e){showToast(e.message,'error');}
  };

  // Comprehensive debug test — runs the route once with per-step I/O capture
  // and opens the activity drawer with the result. Works regardless of status.
  const [testFor,setTestFor]=useState(null);   // integration object being tested (modal)
  const openTestFor=(i)=>setTestFor(i);

  // normalize triggerType for filter (backend stores as SCHEDULED, UI filter uses 'scheduled')
  const normTrigger=t=>(t||'').toLowerCase().replace(/_/g,'-');

  const filtered=integrations.filter(i=>{
    if(searchQ&&!(i.name||'').toLowerCase().includes(searchQ.toLowerCase()))return false;
    if(filterStatus&&i.status!==filterStatus)return false;
    if(filterTrigger&&normTrigger(i.triggerType)!==filterTrigger)return false;
    if(filterProject&&i.projectId!==filterProject)return false;
    return true;
  });

  // Classify an integration by ERP system from its connections' adapters, with
  // recipe-origin / name fallbacks. SAP = adapter sap-s4hana-cpi (any sap*) or
  // recipe sap-s4hana-einvoice; Fusion = adapter oracle-fusion or recipe
  // oracle-fusion-*. Everything else (TaxLab proxies, demos) → Other.
  const systemOf=(i)=>{
    const adapters=[i.sourceConnectionId,i.targetConnectionId]
      .map(id=>connections.find(c=>c.id===id)).filter(Boolean)
      .map(c=>(c.adapter||'').toLowerCase());
    const origin=(i.recipeOriginId||'').toLowerCase();
    const name=(i.name||'').toLowerCase();
    if(adapters.some(a=>a.startsWith('sap')) || origin.includes('sap') || /\bsap\b/.test(name)) return 'sap';
    if(adapters.some(a=>a.includes('fusion')) || origin.includes('fusion') || name.includes('fusion')) return 'fusion';
    return 'other';
  };

  // Bucket the filtered rows into collapsible groups. groupBy==='' → one flat
  // list (label null = no header rendered).
  const groups=useMemo(()=>{
    if(!groupBy) return [{key:'__all',label:null,items:filtered}];
    const buckets=new Map();
    filtered.forEach(i=>{
      const k = groupBy==='project' ? (i.projectId?('p:'+i.projectId):'p:__none')
              : groupBy==='trigger' ? ('t:'+normTrigger(i.triggerType||'unknown'))
              : groupBy==='system'  ? ('sys:'+systemOf(i))
              : ('s:'+(i.status||'DRAFT'));
      if(!buckets.has(k)) buckets.set(k,[]);
      buckets.get(k).push(i);
    });
    let arr=[...buckets.entries()].map(([key,items])=>{
      let label,icon='',color=null;
      if(groupBy==='project'){
        if(key==='p:__none'){label='Unassigned';icon='📁';color='#94a3b8';}
        else{const p=projects.find(p=>('p:'+p.id)===key);label=(p||{}).name||'Project';icon='📁';color=(p||{}).color||'#6366f1';}
      }else if(groupBy==='trigger'){
        const tm=triggerMeta(key.slice(2)); label=(tm.label||key.slice(2)).toString(); icon=tm.icon||'⚡';
      }else if(groupBy==='system'){
        const sys=key.slice(4);
        label = sys==='sap'?'SAP':sys==='fusion'?'Oracle Fusion':'Other';
        color = sys==='sap'?'#008FD3':sys==='fusion'?'#C74634':'#94a3b8';
      }else{
        label=key.slice(2);
      }
      return {key,label,icon,color,items};
    });
    if(groupBy==='project'){
      const ord=new Map(projects.map((p,idx)=>['p:'+p.id,idx]));
      arr.sort((a,b)=>{ if(a.key==='p:__none')return 1; if(b.key==='p:__none')return -1; return (ord.get(a.key)||999)-(ord.get(b.key)||999); });
    }else if(groupBy==='status'){
      const rank={ACTIVE:0,DEPLOYING:1,DRAFT:2,INACTIVE:3,ERROR:4};
      arr.sort((a,b)=>(rank[a.label]||9)-(rank[b.label]||9));
    }else if(groupBy==='system'){
      const rank={'sys:sap':0,'sys:fusion':1,'sys:other':2};
      arr.sort((a,b)=>(rank[a.key]||9)-(rank[b.key]||9));
    }else{
      arr.sort((a,b)=>b.items.length-a.items.length);
    }
    return arr;
  },[filtered,groupBy,projects,connections]);

  // One integration row — shared by grouped and flat rendering.
  const renderRow=(i)=>{
    const tm=triggerMeta(normTrigger(i.triggerType));
    const isActive=i.status==='ACTIVE';
    const isDeploying=i.status==='DEPLOYING';
    const proj=projects.find(p=>p.id===i.projectId);
    const isRest=(i.triggerType==='REST'||i.triggerType==='WEBHOOK'||i.triggerType==='REST_API');
    return(
      <div key={i.id} className={`integration-row${expandedRows[i.id]?' expanded':''}`} id={`integration-${i.id}`}
        ref={el=>{ if(el && highlightId===i.id) el.scrollIntoView({behavior:'smooth',block:'center'}); }}
        style={highlightId===i.id?{outline:'2px solid var(--accent)',outlineOffset:2,boxShadow:'0 0 0 6px rgba(99,102,241,.18)'}:undefined}>
        {/* COMPACT HEAD — two-line identity (name on its own line so it never
            gets squeezed by the meta chips). Row body opens the Builder; the
            chevron / stats / actions stop propagation. */}
        <div className="integration-row-head"
          onClick={()=>{setEditingRoute(i);setShowRouteBuilder(true);}}
          title="Click to open in Builder · use ▸ to expand details">
          <button className="integration-row-chev" onClick={ev=>{ev.stopPropagation();toggleExpand(i.id);}}
            title={expandedRows[i.id]?'Collapse':'Expand'} aria-label="Toggle details">▸</button>
          <div style={{fontSize:18,flexShrink:0,width:24,textAlign:'center',display:'inline-flex',alignItems:'center',justifyContent:'center'}}><IntegrationFlowIcon size={18} color="#4f46e5"/></div>
          <div style={{display:'flex',flexDirection:'column',gap:5,flex:1,minWidth:0}}>
            <div style={{display:'flex',alignItems:'center',gap:8,minWidth:0}}>
              <span style={{fontWeight:700,fontSize:14.5,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',minWidth:0}} title={i.name}>{i.name}</span>
              {isActive&&<span className="row-chip" style={{color:'#16a34a',background:'#f0fdf4',borderColor:'#bbf7d0',flexShrink:0}}>● LIVE</span>}
            </div>
            <div style={{display:'flex',alignItems:'center',gap:8,minWidth:0,flexWrap:'nowrap',overflow:'hidden'}}>
              {i.builderClassName&&<span className="row-chip" style={{color:'#7c3aed',background:'rgba(124,58,237,.1)',borderColor:'rgba(124,58,237,.3)',flexShrink:0}}><span style={{display:'inline-flex'}}><PaletteIcon size={12}/></span> Java DSL</span>}
              {groupBy!=='project'&&proj&&<span className="row-chip" style={{background:(proj.color||'#6366f1')+'22',color:proj.color||'#6366f1',borderColor:(proj.color||'#6366f1')+'44',flexShrink:0,maxWidth:130,overflow:'hidden',textOverflow:'ellipsis'}}><FolderStackIcon size={12}/> {proj.name}</span>}
              {groupBy!=='trigger'&&<span className="row-chip trigger-chip" style={{flexShrink:0}}>{tm.label||i.triggerType}</span>}
            </div>
          </div>
          <div className="integration-row-stat" onClick={ev=>ev.stopPropagation()}>
            <strong>{fmt(i.executionCount||0)}</strong>runs
          </div>
          <div className="integration-row-stat" onClick={ev=>ev.stopPropagation()}>
            <strong className={i.executionCount?'success':'muted'}>{i.executionCount?Math.round((i.successCount||0)/i.executionCount*100)+'%':'—'}</strong>ok
          </div>
          <span className={`int-row-status${isActive?' is-live':i.status==='ERROR'?' is-error':(i.status==='DEPLOYING'||i.status==='WARNING')?' is-warn':''}`}>{i.status||'DRAFT'}</span>
          <div style={{display:'flex',gap:8,flexShrink:0,alignItems:'center'}} onClick={ev=>ev.stopPropagation()}>
            {/* "Call", not "Test". The builder has its own Test button (NewRouteBuilderModal,
                tooltip "Open live test panel") and that one is the step-by-step debugger. This
                opens TestPane instead: an HTTP client for REST triggers, the backend test runner
                otherwise. Both were labelled "Test", which sent people to the wrong pane.
                The tooltip follows TestPane's own isRest rule (triggerType==='REST') rather than
                the row's broader isRest above — that one also covers WEBHOOK/REST_API because it
                gates the receiver panel, and would describe the wrong pane here. */}
            <button className="int-row-btn int-row-btn-light" onClick={()=>openTestFor(i)}
              title={i.triggerType==='REST'?'Send a request to this endpoint':'Invoke this integration with the backend test runner'}><BeakerActionIcon size={17}/> Call</button>
            {isActive&&<button className="int-row-btn int-row-btn-success" onClick={()=>runNow(i)} title="Trigger Now"><BoltActionIcon size={17}/> Run</button>}
            <button className={`int-row-btn ${isActive?'int-row-btn-grey':'int-row-btn-gradient'}`}
              onClick={()=>toggleStatus(i)} disabled={activating[i.id]||isDeploying}>
              {activating[i.id]||isDeploying?<Spinner/>:(isActive?<><StepIconSvg name="stop" size={17}/> Stop</>:<><BoltActionIcon size={17}/> Activate</>)}
            </button>
            <button className="btn btn-ghost row-icon-action" onClick={()=>{setEditingRoute(i);setShowRouteBuilder(true);}} title="Open Builder" aria-label="Open Builder"><BuilderToolsIcon size={17}/></button>
            <button className="btn btn-danger row-icon-action" onClick={()=>del(i.id,i.name)} title="Delete" aria-label="Delete"><TrashIcon size={17}/></button>
          </div>
        </div>

        {/* EXPANDED DETAIL — only renders when chevron is open */}
        {expandedRows[i.id] && (
          <div className="integration-row-detail" onClick={ev=>ev.stopPropagation()}>
            {i.description && <div style={{color:'var(--text2)'}}>{i.description}</div>}
            {(i.sourceConnectionId||i.targetConnectionId) && (
              <div className="integration-row-meta">
                <span style={{fontWeight:600,color:'var(--text2)'}}>Flow:</span>
                {i.sourceConnectionId&&<span style={{display:'inline-flex',alignItems:'center',gap:4}}>
                  <AdapterIcon id={connAdapter(i.sourceConnectionId)} size={14}/>
                  <span>{connName(i.sourceConnectionId)}</span>
                </span>}
                {i.sourceConnectionId&&i.targetConnectionId&&<span className="flow-arrow">→</span>}
                {i.targetConnectionId&&<span style={{display:'inline-flex',alignItems:'center',gap:4}}>
                  <AdapterIcon id={connAdapter(i.targetConnectionId)} size={14}/>
                  <span>{connName(i.targetConnectionId)}</span>
                </span>}
              </div>
            )}
            <div className="integration-row-meta">
              {i.lastExecuted&&<span title={`Last run: ${fmtDate(i.lastExecuted)}`}><ClockIcon size={12}/> Last run: {fmtDate(i.lastExecuted)} · avg {fmtDur(i.avgDurationMs)}</span>}
              {i.routeFilePath&&<span title={`data/routes/${i.routeFilePath}`} style={{fontFamily:'ui-monospace,monospace',maxWidth:340,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}><FileDocIcon size={12}/> {i.routeFilePath}</span>}
              <span><IntegrationLogLevel integrationId={i.id} showToast={showToast} compact/></span>
            </div>
            {i.status==='ERROR'&&i.lastError&&<div title={i.lastError} style={{padding:'4px 9px',borderRadius:5,background:'rgba(239,68,68,.08)',border:'1px solid rgba(239,68,68,.25)',color:'var(--error)',fontSize:11.5,fontFamily:'ui-monospace,monospace',maxWidth:'100%',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',display:'flex',alignItems:'center',gap:5}}><WarningIcon size={12}/> {i.lastError}</div>}
            {isRest && (
              <WebhookReceiverPanel integration={i} showToast={showToast} confirm={confirm}/>
            )}
            <WebhookDeliveryPanel integration={i} showToast={showToast} confirm={confirm}/>
          </div>
        )}
      </div>
    );
  };

  return(
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Integrations</h1>
          <div className="page-subtitle">Build and manage your data flows, automation pipelines and exposed endpoints</div>
        </div>
      </div>

      <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:20,flexWrap:'wrap'}}>
        <div className="search-field" style={{width:300,flex:'0 0 auto'}}>
          <span className="search-field-icon"><SearchIcon size={20} color="rgb(99,115,129)"/></span>
          <input className="search-field-input" placeholder="Search integrations…" value={searchQ} onChange={e=>setSearchQ(e.target.value)}/>
        </div>
        <select className="form-select" style={{maxWidth:150,flex:'0 0 auto'}} value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
          <option value="">All Status</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
          <option value="ERROR">Error</option>
        </select>
        <IconDropdown
          value={filterTrigger}
          onChange={setFilterTrigger}
          allLabel="All Triggers"
          options={TRIGGER_TYPES.map(t=>({value:t.id,label:t.label}))}
          renderIcon={id=><TriggerTypeIcon id={id} size={16}/>}
          style={{width:200,flex:'0 0 auto'}}
        />
        {projects.length>0&&(
          <select className="form-select" style={{maxWidth:180,flex:'0 0 auto'}} value={filterProject} onChange={e=>setFilterProject(e.target.value)}>
            <option value="">All Projects</option>
            {projects.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
        <select className="form-select" style={{maxWidth:170,flex:'0 0 auto'}} value={groupBy} onChange={e=>{setGroupBy(e.target.value);setCollapsedGroups({});}} title="Group the list into collapsible sections">
          <option value="">No grouping</option>
          <option value="project">Group by Project</option>
          <option value="system">Group by System</option>
          <option value="trigger">Group by Trigger</option>
          <option value="status">Group by Status</option>
        </select>
        <button className="btn btn-refresh" onClick={load} style={{...TAXLAB_LIGHT_STYLE,flexShrink:0}}><BuilderRefreshIcon size={18}/> Refresh</button>
        <div style={{flex:1}}/>
        <button className="btn btn-primary" style={TAXLAB_CTA_STYLE} onClick={()=>{setEditingRoute(null);setShowRouteBuilder(true);}}>
          <AddCircleIcon/> New integration
        </button>
      </div>

      {loading?<div className="text-muted text-sm">Loading…</div>:
       filtered.length===0?
        <div className="empty-state">
          <EmptyStateIcon tone="indigo"><IntegrationFlowIcon/></EmptyStateIcon>
          <p style={{marginBottom:16}}>{integrations.length===0?'No integrations yet. Start building your first flow.':'No integrations match the current filters.'}</p>
          <button className="btn btn-primary" onClick={()=>{setEditingRoute(null);setShowRouteBuilder(true);}}><AddCircleIcon/> New integration</button>
        </div>:
       <div>
         {groups.map(g=>(
           <div key={g.key}>
             {g.label!=null && (
               <div className={`integration-group-head${collapsedGroups[g.key]?' collapsed':''}`}
                 onClick={()=>toggleGroup(g.key)}
                 title={collapsedGroups[g.key]?'Expand section':'Collapse section'}>
                 <span className="chev">▸</span>
                 {g.color ? <span style={{width:9,height:9,borderRadius:'50%',background:g.color,flexShrink:0}}/> : (g.icon&&<span style={{fontSize:13,flexShrink:0}}>{g.icon}</span>)}
                 <span className="grp-name">{g.label}</span>
                 <span className="grp-count">{g.items.length}</span>
                 <span className="grp-line"/>
               </div>
             )}
             {!collapsedGroups[g.key] && g.items.map(renderRow)}
           </div>
         ))}
       </div>
      }

      {showRouteBuilder&&<NewRouteBuilderModal
        initial={editingRoute}
        onClose={()=>{setShowRouteBuilder(false);setEditingRoute(null);}}
        onSaved={()=>{setShowRouteBuilder(false);setEditingRoute(null);load();}}
        showToast={showToast}
        projects={projects}
      />}

      {testFor&&<TestPane
          integration={testFor}
          onClose={()=>setTestFor(null)}
          showToast={showToast}
          connections={connections}
          projects={projects}
        />}
    </div>
  );
}

/* ===== PER-INTEGRATION LOG LEVEL SELECTOR ===== */
function IntegrationLogLevel({integrationId, showToast, compact=false}){
  const LEVELS=['INHERIT','OFF','ERROR','WARN','INFO','DEBUG','TRACE'];
  const [level,setLevel]=useState('INHERIT');
  const [globalLvl,setGlobalLvl]=useState(null);
  const [busy,setBusy]=useState(false);

  useEffect(()=>{
    let alive=true;
    API('GET','/api/admin/log/levels').then(r=>{
      if(!alive)return;
      setGlobalLvl(r.global);
      const k='integration:'+integrationId;
      const cur=(r.overrides||{})[k];
      if(cur) setLevel(cur);
    }).catch(()=>{});
    return()=>{alive=false;};
  },[integrationId]);

  const change=async(v)=>{
    setLevel(v);
    setBusy(true);
    try{
      await API('PUT','/api/admin/log/levels/'+encodeURIComponent('integration:'+integrationId),
                {level: v==='INHERIT' ? 'INHERIT' : v});
      showToast('Log level set to '+v,'success');
    }catch(e){showToast('Could not set level: '+e.message,'error');}
    finally{setBusy(false);}
  };

  const effective = level==='INHERIT' ? (globalLvl||'INFO') : level;
  const tone = effective==='ERROR'||effective==='WARN' ? '#dc2626'
             : effective==='OFF' ? 'var(--text3)'
             : effective==='DEBUG'||effective==='TRACE' ? '#7c3aed'
             : '#16a34a';

  return(
    <div style={{display:'inline-flex',alignItems:'center',gap:4,color:'var(--text3)',fontSize:compact?10.5:12}}>
      <span style={{display:'inline-flex'}}><FileDocIcon size={12}/></span>
      <select value={level} onChange={e=>change(e.target.value)} disabled={busy}
        title="Log level for this integration"
        style={{padding:compact?'0 4px':'1px 6px',fontSize:compact?10.5:11,border:'1px solid var(--border)',borderRadius:4,background:'transparent',color:tone,fontWeight:700,cursor:'pointer',height:compact?18:'auto'}}>
        {LEVELS.map(l=><option key={l} value={l}>{l==='INHERIT'?`inherit (${globalLvl||'INFO'})`:l}</option>)}
      </select>
    </div>
  );
}

function IntegrationModal({initial,onClose,onSaved,showToast}){
  const [step,setStep]=useState(initial?2:1);
  const [triggerType,setTriggerType]=useState((initial||{}).triggerType||'');
  const [connections,setConnections]=useState([]);

  // Parse stored triggerConfig JSON string back to object for form
  const parseCfg=s=>{if(!s)return{};try{return typeof s==='string'?JSON.parse(s):s;}catch{return{};}};

  const [values,setValues]=useState({
    name:(initial||{}).name||'',
    description:(initial||{}).description||'',
    category:(initial||{}).category||'',
    errorStrategy:(initial||{}).errorStrategy||'RETRY',
    retryCount:(initial||{}).retryCount||3,
    sourceConnectionId:(initial||{}).sourceConnectionId||'',
    targetConnectionId:(initial||{}).targetConnectionId||'',
    ...parseCfg((initial||{}).triggerConfig),
  });
  const [errors,setErrors]=useState({});
  const [saving,setSaving]=useState(false);

  const {useEffect}=React;
  useEffect(()=>{API('GET','/api/connections').then(setConnections).catch(()=>{});},[]);

  // Initialize trigger config defaults when trigger type is selected
  useEffect(()=>{
    if(!triggerType)return;
    const fields=TRIGGER_CONFIGS[triggerType]||[];
    setValues(prev=>{
      const defaults={};
      fields.forEach(f=>{if(f.def!==undefined&&prev[f.k]===undefined)defaults[f.k]=String(f.def);});
      return Object.keys(defaults).length?{...defaults,...prev}:prev;
    });
  },[triggerType]);

  const setVal=(k,v)=>{setValues(p=>({...p,[k]:v}));if(errors[k])setErrors(p=>({...p,[k]:undefined}));};

  const save=async()=>{
    const errs=validateIntegrationForm({name:values.name,triggerType});
    if(Object.keys(errs).length){setErrors(errs);return;}
    setSaving(true);
    try{
      // Build triggerConfig as JSON string (backend stores it as String)
      const tcFields=TRIGGER_CONFIGS[triggerType]||[];
      const triggerCfg={};
      tcFields.forEach(f=>{if(values[f.k]!==undefined)triggerCfg[f.k]=values[f.k];});

      const payload={
        name:cleanName(values.name),
        description:values.description,
        triggerType:triggerType.toUpperCase().replace(/-/g,'_'),
        category:values.category||'general',
        errorStrategy:values.errorStrategy||'RETRY',
        retryCount:Number(values.retryCount)||3,
        sourceConnectionId:values.sourceConnectionId||null,
        targetConnectionId:values.targetConnectionId||null,
        triggerConfig:JSON.stringify(triggerCfg),
        status:(initial||{}).status||'DRAFT',
      };
      if(initial)await API('PUT',`/api/integrations/${initial.id}`,payload);
      else await API('POST','/api/integrations',payload);
      onSaved();showToast('Integration saved!','success');
    }catch(e){showToast(e.message,'error');}
    finally{setSaving(false);}
  };

  const tcFields=TRIGGER_CONFIGS[triggerType]||[];

  return(
    <div className="modal-overlay" onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div className="modal modal-xl">
        <div className="modal-header">
          <div className="modal-title">{initial?'Edit Integration':'New Integration'}</div>
          <button className="modal-close-btn" onClick={onClose} aria-label="Close"><CloseIcon size={16}/></button>
        </div>
        <div className="modal-body">
          {!initial&&<div className="wizard-steps mb-4">
            <div className="wizard-step">
              <div className={`step-num ${step>1?'done':step===1?'active':'pending'}`}>{step>1?'✓':'1'}</div>
              <div className={`step-label ${step===1?'active':''}`}>Trigger Type</div>
            </div>
            <div className="wizard-step">
              <div className={`step-num ${step===2?'active':'pending'}`}>2</div>
              <div className={`step-label ${step===2?'active':''}`}>Configuration</div>
            </div>
          </div>}

          {step===1&&(
            <div>
              <p className="text-sm text-muted mb-4">How should this integration be triggered?</p>
              <div className="trigger-grid">
                {TRIGGER_TYPES.map(t=>(
                  <div key={t.id} className={`trigger-card${triggerType===t.id?' selected':''}`}
                    onClick={()=>{setTriggerType(t.id);setStep(2);}}>
                    <div className="trigger-icon"><TriggerTypeIcon id={t.id} size={24}/></div>
                    <div className="trigger-name">{t.label}</div>
                    <div className="trigger-desc">{t.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step===2&&(
            <div>
              {/* Basic Info */}
              <div className="section-title">General</div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Integration Name <span className="req">*</span></label>
                  <input className={`form-input${errors.name?' error':''}`} value={values.name} onChange={e=>setVal('name',e.target.value)} placeholder="My Integration"/>
                  {errors.name&&<div className="field-error"><WarningIcon/> {errors.name}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <input className="form-input" value={values.category} onChange={e=>setVal('category',e.target.value)} placeholder="e.g. Finance, HR, Sales"/>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-input" value={values.description} onChange={e=>setVal('description',e.target.value)} placeholder="Describe what this integration does" rows={2}/>
              </div>

              {/* Trigger Config */}
              {!initial&&<div style={{display:'flex',alignItems:'center',gap:10,marginBottom:14}}>
                <div className="section-title" style={{margin:0}}><TriggerTypeIcon id={triggerType} size={16}/> Trigger: {triggerMeta(triggerType).label}</div>
                <button className="btn btn-ghost btn-xs" onClick={()=>setStep(1)}>← Change</button>
              </div>}
              {tcFields.length>0&&(
                <div className="card-sm mb-4">
                  {tcFields.map(f=><FieldInput key={f.k} field={f} value={values[f.k]} onChange={setVal} error={errors[f.k]} allValues={values}/>)}
                </div>
              )}

              {/* Source Connection */}
              <div className="section-title">Source Connection <span style={{fontWeight:400,fontSize:12,color:'var(--text3)'}}>(where data comes FROM)</span></div>
              <div className="card-sm mb-4">
                {connections.length===0
                  ? <div className="text-sm text-muted">No connections saved yet. <a href="#" onClick={e=>{e.preventDefault();onClose();}} style={{color:'var(--brand-start)'}}>Create a connection first.</a></div>
                  : <div className="form-group" style={{marginBottom:0}}>
                      <select className="form-select" value={values.sourceConnectionId} onChange={e=>setVal('sourceConnectionId',e.target.value)}>
                        <option value="">— None / Trigger provides data —</option>
                        {connections.map(c=>(
                          <option key={c.id} value={c.id}>
                            {(ADAPTERS[c.adapter]||{}).icon?'':''}{c.name} ({c.adapter||c.type})
                          </option>
                        ))}
                      </select>
                      {values.sourceConnectionId&&(()=>{
                        const c=connections.find(x=>x.id===values.sourceConnectionId);
                        return c?<div style={{marginTop:8,display:'flex',alignItems:'center',gap:8,fontSize:12,color:'var(--text2)'}}>
                          <AdapterIcon id={c.adapter} size={18}/>
                          <span><b>{c.name}</b> · {c.host||'—'} · Auth: {c.authType||'None'}</span>
                          <StatusBadge status={c.status}/>
                        </div>:null;
                      })()}
                    </div>
                }
              </div>

              {/* Target Connection */}
              <div className="section-title">Target Connection <span style={{fontWeight:400,fontSize:12,color:'var(--text3)'}}>(where data goes TO)</span></div>
              <div className="card-sm mb-4">
                {connections.length===0
                  ? <div className="text-sm text-muted">No connections saved yet.</div>
                  : <div className="form-group" style={{marginBottom:0}}>
                      <select className="form-select" value={values.targetConnectionId} onChange={e=>setVal('targetConnectionId',e.target.value)}>
                        <option value="">— None / Log only —</option>
                        {connections.map(c=>(
                          <option key={c.id} value={c.id}>
                            {c.name} ({c.adapter||c.type})
                          </option>
                        ))}
                      </select>
                      {values.targetConnectionId&&(()=>{
                        const c=connections.find(x=>x.id===values.targetConnectionId);
                        return c?<div style={{marginTop:8,display:'flex',alignItems:'center',gap:8,fontSize:12,color:'var(--text2)'}}>
                          <AdapterIcon id={c.adapter} size={18}/>
                          <span><b>{c.name}</b> · {c.host||'—'} · Auth: {c.authType||'None'}</span>
                          <StatusBadge status={c.status}/>
                        </div>:null;
                      })()}
                    </div>
                }
              </div>

              {/* Error Handling */}
              <div className="section-title">Error Handling</div>
              <div className="card-sm">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">On Error</label>
                    <select className="form-select" value={values.errorStrategy} onChange={e=>setVal('errorStrategy',e.target.value)}>
                      <option value="RETRY">Retry</option>
                      <option value="STOP">Stop on Error</option>
                      <option value="SKIP">Skip &amp; Continue</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Retry Count</label>
                    <input className="form-input" type="number" value={values.retryCount} onChange={e=>setVal('retryCount',e.target.value)} min={0} max={10}/>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        {step===2&&(
          <div className="modal-footer">
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>
              {saving?<><Spinner/> Saving…</>:<><SaveActionIcon/> Save Integration</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ===== RESUBMIT MODAL ===== */
function ResubmitModal({exec, onClose, onResubmitted, showToast}){
  // Build the list of restart points: "from beginning" + each captured activity step.
  const steps=Array.isArray(exec.activitySteps)?exec.activitySteps:[];
  const failedIdx=steps.findIndex(s=>s.status==='FAILED');
  const [fromStep,setFromStep]=useState(0);
  const [overridePayload,setOverridePayload]=useState('');
  const [useOverride,setUseOverride]=useState(false);
  const [busy,setBusy]=useState(false);
  const [result,setResult]=useState(null);
  const [error,setError]=useState(null);

  // Default starting step = 0 (from beginning). If we have a clearly-failed step,
  // suggest restarting AT that step (so the user can fix and replay just that hop).
  useEffect(()=>{
    if(failedIdx>0) setFromStep(failedIdx);
  },[failedIdx]);

  // Compute the body that will actually be sent so the user can preview/edit it.
  const computedBody=(()=>{
    if(useOverride) return overridePayload;
    if(fromStep>0 && steps[fromStep] && steps[fromStep].input) return steps[fromStep].input;
    return exec.inputPayload||'{}';
  })();

  const submit=async()=>{
    setBusy(true); setError(null);
    try{
      const body={fromStep};
      if(useOverride) body.payloadOverride=overridePayload;
      const r=await API('POST',`/api/executions/${exec.id}/resubmit`,body);
      setResult(r);
      showToast(r.status==='SUCCESS'?'Resubmitted successfully':'Resubmit completed with status '+r.status,
                r.status==='SUCCESS'?'success':'warning');
    }catch(e){
      setError(e.message||'Resubmit failed');
      showToast('Resubmit failed: '+(e.message||'unknown'),'error');
    }finally{ setBusy(false); }
  };

  const handleClose=()=>{
    if(result && onResubmitted) onResubmitted(result);
    else onClose();
  };

  const labelFor=(i,s)=>{
    if(i===0) return 'From the beginning';
    return `From step ${i} · ${s.name||s.type||'step'}${s.status==='FAILED'?' (failed here)':''}`;
  };

  return(
    <div onClick={onClose}
         style={{position:'fixed',inset:0,background:'rgba(15,17,35,.55)',backdropFilter:'blur(3px)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:20}}>
      <div onClick={ev=>ev.stopPropagation()}
           style={{background:'var(--surface)',borderRadius:14,width:'100%',maxWidth:760,maxHeight:'90vh',display:'flex',flexDirection:'column',boxShadow:'var(--shadow-lg)',border:'1px solid var(--border)',overflow:'hidden'}}>
        {/* Header */}
        <div style={{padding:'16px 22px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:12,background:'linear-gradient(135deg,rgba(99,102,241,.08),rgba(139,92,246,.04))'}}>
          <span style={{display:'inline-flex',color:'var(--accent)'}}><BuilderRefreshIcon size={22}/></span>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontWeight:800,fontSize:16}}>Resubmit Failed Message</div>
            <div style={{fontSize:11.5,color:'var(--text3)',marginTop:2}}>
              {exec.integrationName||exec.integrationId} · {fmtDate(exec.startedAt)} · <StatusBadge status={exec.status}/>
            </div>
          </div>
          <button onClick={handleClose} className="modal-close-btn" aria-label="Close"><CloseIcon size={16}/></button>
        </div>

        {/* Body — split into form (top) + result (bottom, only when present) */}
        <div style={{flex:1,overflow:'auto',padding:'16px 22px',display:'flex',flexDirection:'column',gap:16}}>
          {exec.errorMessage&&(
            <div style={{padding:'8px 12px',borderRadius:6,background:'rgba(239,68,68,.06)',border:'1px solid rgba(239,68,68,.25)',color:'var(--error)',fontSize:12,fontFamily:'ui-monospace,monospace',whiteSpace:'pre-wrap',display:'flex',alignItems:'flex-start',gap:6}}><WarningIcon size={13}/> {exec.errorMessage}</div>
          )}

          <div>
            <div style={{fontSize:11,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'.7px',marginBottom:6}}>Restart point</div>
            <div className="text-xs text-muted" style={{marginBottom:8,lineHeight:1.5}}>
              After resubmitting, the integration can restart from the beginning, or — when activity steps were captured — from any successful checkpoint, picking up the message body that step would have received.
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:6}}>
              <label style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',borderRadius:8,background:fromStep===0?'rgba(99,102,241,.1)':'var(--surface3)',border:'1px solid '+(fromStep===0?'rgba(99,102,241,.35)':'var(--border)'),cursor:'pointer'}}>
                <input type="radio" name="fromStep" checked={fromStep===0} onChange={()=>setFromStep(0)}/>
                <div>
                  <div style={{fontWeight:600,fontSize:13}}>From the beginning</div>
                  <div style={{fontSize:11,color:'var(--text3)'}}>Replays the integration with the original input payload.</div>
                </div>
              </label>
              {steps.length>0&&steps.map((s,i)=>{
                if(i===0)return null;
                const isFail=s.status==='FAILED';
                return(
                  <label key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',borderRadius:8,
                       background:fromStep===i?'rgba(99,102,241,.1)':'var(--surface3)',
                       border:'1px solid '+(fromStep===i?'rgba(99,102,241,.35)':isFail?'rgba(239,68,68,.3)':'var(--border)'),
                       cursor:'pointer'}}>
                    <input type="radio" name="fromStep" checked={fromStep===i} onChange={()=>setFromStep(i)}/>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:600,fontSize:13,display:'flex',alignItems:'center',gap:6}}>
                        <span style={{display:'inline-flex'}}>{stepIcon(s.type,14)}</span>
                        <span>{labelFor(i,s)}</span>
                      </div>
                      {s.input&&<div style={{fontSize:10.5,color:'var(--text3)',fontFamily:'ui-monospace,monospace',marginTop:2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>input: {s.input.substring(0,100)}{s.input.length>100?'…':''}</div>}
                    </div>
                  </label>
                );
              })}
              {steps.length===0&&(
                <div className="text-xs text-muted" style={{padding:8}}>This execution has no captured activity steps — only "from the beginning" is available. (Enable DEBUG/TRACE log level on the integration to capture per-step replay points.)</div>
              )}
            </div>
          </div>

          <div>
            <label style={{display:'flex',alignItems:'center',gap:8,fontSize:13,marginBottom:6,cursor:'pointer'}}>
              <input type="checkbox" checked={useOverride} onChange={e=>setUseOverride(e.target.checked)}/>
              <span style={{fontWeight:600}}>Override payload</span>
              <span className="text-xs text-muted">— send a custom body instead of the captured one</span>
            </label>
            {useOverride?(
              <textarea value={overridePayload} onChange={e=>setOverridePayload(e.target.value)} rows={6}
                placeholder="Paste / edit the JSON or text payload to send"
                style={{width:'100%',padding:'8px 10px',border:'1px solid var(--border)',borderRadius:6,background:'var(--surface3)',color:'var(--text)',fontSize:12,fontFamily:'ui-monospace,monospace',boxSizing:'border-box',outline:'none',resize:'vertical'}}/>
            ):(
              <pre style={{margin:0,padding:'10px 12px',background:'var(--surface3)',border:'1px solid var(--border)',borderRadius:6,fontSize:11.5,fontFamily:'ui-monospace,monospace',maxHeight:160,overflow:'auto',whiteSpace:'pre-wrap',wordBreak:'break-all',color:'var(--text2)'}}>{computedBody||'(empty)'}</pre>
            )}
            <div className="text-xs text-muted" style={{marginTop:4}}>This is the body the resubmit will use as input.</div>
          </div>

          {result&&(
            <div style={{padding:'10px 12px',borderRadius:8,background:result.status==='SUCCESS'?'rgba(34,197,94,.08)':'rgba(239,68,68,.07)',border:'1px solid '+(result.status==='SUCCESS'?'rgba(34,197,94,.3)':'rgba(239,68,68,.3)')}}>
              <div style={{fontSize:13,fontWeight:700,color:result.status==='SUCCESS'?'var(--success)':'var(--error)'}}>
                {result.status==='SUCCESS'?<><CheckCircleIcon size={14}/> Resubmit succeeded</>:<><WarningIcon size={14}/> Resubmit failed</>}
              </div>
              <div className="text-xs text-muted" style={{marginTop:4,display:'flex',gap:14,flexWrap:'wrap'}}>
                <span>new exec: <b style={{fontFamily:'ui-monospace,monospace'}}>{(result.id||'').slice(0,8)}</b></span>
                <span>duration: <b>{fmtDur(result.durationMs)}</b></span>
                <span>steps captured: <b>{(result.activitySteps||[]).length}</b></span>
              </div>
              {result.errorMessage&&<div style={{marginTop:6,padding:'6px 10px',borderRadius:5,background:'rgba(239,68,68,.05)',color:'var(--error)',fontSize:11.5,fontFamily:'ui-monospace,monospace',whiteSpace:'pre-wrap'}}>{result.errorMessage}</div>}
            </div>
          )}
          {error&&<div style={{padding:'8px 12px',borderRadius:6,background:'rgba(239,68,68,.06)',border:'1px solid rgba(239,68,68,.25)',color:'var(--error)',fontSize:12}}>{error}</div>}
        </div>

        {/* Footer */}
        <div style={{padding:'12px 22px',borderTop:'1px solid var(--border)',display:'flex',gap:10,justifyContent:'flex-end',background:'var(--surface2)'}}>
          <button className="btn btn-ghost" onClick={handleClose}>{result?'Close':'Cancel'}</button>
          {!result&&(
            <button className="btn btn-primary" onClick={submit} disabled={busy}>
              {busy?<><Spinner/> Resubmitting…</>:<><BuilderRefreshIcon size={14}/> {'Resubmit'+(fromStep>0?` from step ${fromStep}`:' from beginning')}</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ===== OBSERVABILITY (executions table) ===== */
function ExecRow({e,selected,onSelect,onResubmit,checked,onCheck}){
  return(
    <tr style={{cursor:'pointer',background:selected?'var(--surface3)':checked?'rgba(99,102,241,.05)':'',transition:'background .15s'}} onClick={()=>onSelect(e)}>
      <td onClick={ev=>ev.stopPropagation()} style={{width:30,textAlign:'center'}}>
        {e.status==='FAILED'&&<input type="checkbox" checked={!!checked} onChange={ev=>onCheck&&onCheck(e,ev.target.checked)}/>}
      </td>
      <td style={{fontVariantNumeric:'tabular-nums'}}>
        {e.instanceSeq!=null
          ? <span style={{fontWeight:700,color:'var(--accent)',fontFamily:'ui-monospace,monospace',fontSize:13}}>#{e.instanceSeq}</span>
          : <span style={{color:'var(--text3)'}}>—</span>}
      </td>
      <td>
        <div style={{display:'flex',alignItems:'center',gap:6}}>
          <span style={{fontSize:10,color:'var(--text3)'}}>▸</span>
          <span className="font-semibold" style={{fontSize:13}}>{e.integrationName||e.integrationId||'—'}</span>
        </div>
        {e.correlationId&&<div style={{fontSize:10,color:'var(--text3)',marginTop:1,fontFamily:'monospace'}}>{e.correlationId.slice(0,8)}</div>}
      </td>
      <td>
        <div style={{display:'flex',alignItems:'center',gap:6}}>
          <StatusBadge status={e.status}/>
          {e.httpStatus!=null&&<span title="Upstream HTTP status" style={{
            fontFamily:'ui-monospace,monospace',fontSize:11,fontWeight:700,padding:'1px 6px',borderRadius:4,
            color:e.httpStatus>=400?'#dc2626':(e.httpStatus>=300?'#b45309':'#059669'),
            background:e.httpStatus>=400?'rgba(239,68,68,.12)':(e.httpStatus>=300?'rgba(180,83,9,.12)':'rgba(16,185,129,.12)')
          }}>{e.httpStatus}</span>}
        </div>
      </td>
      <td style={{fontVariantNumeric:'tabular-nums',fontSize:13}}>
        {e.recordCount!=null
          ? <span><b>{e.recordCount}</b> <span style={{color:'var(--text3)'}}>{e.recordLabel||'records'}</span></span>
          : <span style={{color:'var(--text3)'}}>—</span>}
      </td>
      <td style={{fontVariantNumeric:'tabular-nums'}}>{fmtDur(e.durationMs)}</td>
      <td className="text-sm text-muted">{fmtDate(e.startedAt||e.startTime)}</td>
      <td style={{maxWidth:220,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',fontSize:12,color:e.errorMessage?'var(--error)':'var(--text3)'}}>{e.errorMessage||e.stepTrace||'—'}</td>
      <td onClick={ev=>ev.stopPropagation()}>
        {e.status==='FAILED'&&<button className="int-row-btn int-row-btn-light" onClick={()=>onResubmit&&onResubmit(e)}><BuilderRefreshIcon size={17}/> Resubmit</button>}
      </td>
    </tr>
  );
}

/* ===== ACTIVITY I/O DRAWER (Logic-Apps style) ===== */
/* Recursively parse JSON that has been embedded as a STRING.
 *
 * Camel snapshots put the HTTP payload in "body" as a string, so an error response renders
 * as  "body": "{\"error_description\":\"No active API product subscription...\"}"  —
 * the one field an operator actually needs, delivered as escape soup. Parsing it in turn
 * promotes it to a real nested object.
 *
 * Only strings that look like a JSON object/array are attempted, and a parse failure keeps
 * the original: header values such as "[Accept-Encoding, Origin, ...]" start with '[' but
 * are not JSON, and must survive untouched. Depth-capped so a pathological payload cannot
 * spin. */
function deepParseJson(v,depth){
  depth=depth||0;
  if(depth>6) return v;
  if(typeof v==='string'){
    const t=v.trim();
    if(t.length>1&&((t[0]==='{'&&t[t.length-1]==='}')||(t[0]==='['&&t[t.length-1]===']'))){
      try{return deepParseJson(JSON.parse(t),depth+1);}catch(e){return v;}
    }
    return v;
  }
  if(Array.isArray(v)) return v.map(x=>deepParseJson(x,depth+1));
  if(v&&typeof v==='object'){
    const o={};
    Object.keys(v).forEach(k=>{o[k]=deepParseJson(v[k],depth+1);});
    return o;
  }
  return v;
}
function fmtJson(s){
  if(s==null) return '';
  try{return JSON.stringify(deepParseJson(JSON.parse(s)),null,2);}catch{return s;}
}

/* Syntax-highlighted JSON. Tokenises the pretty-printed text and colours keys, strings,
 * numbers and literals. Everything is a <span> inside one <pre>, so selection and copy
 * still yield plain text. Colours come from theme variables so the five themes and both
 * light/dark modes stay legible — hard-coded hex would be unreadable in midnight. */
/* (?:\s*:) is grouped-optional rather than \s*:? — the looser form let a string VALUE
 * swallow the newline and indent that follow it, colouring whitespace and making token
 * boundaries depend on layout. Only a real key consumes its colon. */
const JSON_TOKEN=/("(?:\\u[a-fA-F0-9]{4}|\\[^u]|[^\\"])*"(?:\s*:)?|\b(?:true|false|null)\b|-?\d+(?:\.\d+)?(?:[eE][+\-]?\d+)?)/g;
function JsonView({text}){
  if(!text) return <span style={{color:'var(--text3)'}}>(empty)</span>;
  if(text.length>200000) return <span>{text}</span>;   // don't tokenise a huge blob
  const out=[];
  let last=0,m,i=0;
  while((m=JSON_TOKEN.exec(text))!==null){
    if(m.index>last) out.push(text.slice(last,m.index));
    const tok=m[0];
    let color='var(--text)';
    if(tok[0]==='"'){
      color=/:\s*$/.test(tok)?'var(--accent)':'var(--success)';   // key vs string value
    }else if(/^(true|false|null)$/.test(tok)){
      color='var(--brand)';
    }else{
      color='var(--warning)';
    }
    out.push(<span key={i++} style={{color:color}}>{tok}</span>);
    last=m.index+tok.length;
  }
  if(last<text.length) out.push(text.slice(last));
  return <>{out}</>;
}
function stepIcon(type, size=14){
  switch(type){
    case 'TRIGGER':      return <StepIconSvg name="from" size={size}/>;
    case 'SOURCE_FETCH': return <BuilderInboundIcon size={size}/>;
    case 'TRANSFORM':    return <StepIconSvg name="transform" size={size}/>;
    case 'TARGET_SEND':  return <StepIconSvg name="to" size={size}/>;
    case 'ERROR':        return <WarningIcon size={size}/>;
    default:             return <StepIconSvg name="process" size={size}/>;
  }
}
function stepStatusColor(s){
  if(s==='SUCCESS') return 'var(--success)';
  if(s==='FAILED')  return 'var(--error)';
  if(s==='SKIPPED') return 'var(--text3)';
  return 'var(--accent)';
}

/* ── OPERATOR-VIEW STEP FILTER ──────────────────────────────────────────
   Camel captures every processor as an activity step. For an operator
   debugging an integration, the property/header plumbing is noise — the
   useful surface is HTTP calls, custom processors, splits/choices, and
   anything that took non-trivial time or failed. Operator view (default)
   hides the boilerplate; Developer view shows everything we have today.
   Always kept visible regardless of mode:
     • FAILED steps (errors are never hidden)
     • TRIGGER (entry point)
     • HTTP endpoints (anything with httpMethod= in the name)
     • process: refs (custom Java processors)
     • split: / choice: (control-flow markers)
     • steps >= 50ms (likely doing real work)
*/
const TRIVIAL_STEP_PATTERNS = [
  /^setProperty\[/,
  /^setHeader\[/,
  /^setHeaders\[/,
  /^removeHeaders?\[/,
  /^removeProperty\[/,
  /^setBody\[/,
  /^marshal\[/,
  /^unmarshal\[/,
  /^log$/,
  /^log\[/,
  /^delay\[/,
  /^to\[/, // bare to[direct:...] internal hops (real HTTP calls match httpMethod= and are not bare 'to[')
];
function isTrivialStep(s){
  if(!s) return false;
  if(s.status==='FAILED') return false;                 // never hide errors
  if((s.type||'').toUpperCase()==='TRIGGER') return false;
  if((s.durationMs||0)>=50) return false;               // anything slow is "interesting"
  const name=s.name||'';
  if(/httpMethod=/.test(name)) return false;            // HTTP calls
  if(/^ref:/.test(name)) return false;                  // process: refs
  if(/^split\b/.test(name)) return false;
  if(/^choice\b/.test(name)) return false;
  if(/^aggregate\b/.test(name)) return false;
  if(/^circuitBreaker\b/.test(name)) return false;
  if(/^doTry\b|^doCatch\b|^doFinally\b/.test(name)) return false;
  return TRIVIAL_STEP_PATTERNS.some(p=>p.test(name));
}
function filterOperatorSteps(steps){
  return (steps||[]).filter(s=>!isTrivialStep(s));
}

function buildLegacySteps(e){
  // Pre-activitySteps execution log: synthesise steps from inputPayload / outputPayload / stepTrace.
  const out=[];
  out.push({
    name:'Trigger · '+(e.triggerType||'execution'),
    type:'TRIGGER',
    status:'SUCCESS',
    durationMs:0,
    startedAt:e.startedAt,
    input:'(none)',
    output:e.inputPayload||'{}',
    metadata:{note:'reconstructed from execution log (no per-activity capture)'}
  });
  if(e.targetSystem){
    out.push({
      name:'Send to '+e.targetSystem,
      type:'TARGET_SEND',
      status:e.status==='SUCCESS'?'SUCCESS':e.status==='FAILED'?'FAILED':'SKIPPED',
      durationMs:e.durationMs,
      startedAt:e.startedAt,
      input:e.inputPayload,
      output:e.outputPayload,
      error:e.errorMessage,
      metadata:{connection:e.targetSystem}
    });
  }
  return out;
}

function ActivityDrawer({exec,onClose}){
  const allSteps=(exec.activitySteps&&exec.activitySteps.length>0)
              ? exec.activitySteps : buildLegacySteps(exec);
  // viewMode persists across drawer opens so operators don't have to toggle
  // every time. Default is 'operator' — the OIC-style filtered view.
  const [viewMode,setViewMode]=useState(()=>{
    try{return localStorage.getItem('execView.mode')||'operator';}catch{return 'operator';}
  });
  React.useEffect(()=>{ try{localStorage.setItem('execView.mode',viewMode);}catch{} },[viewMode]);
  const steps = viewMode==='operator' ? filterOperatorSteps(allSteps) : allSteps;
  const hiddenCount = allSteps.length - steps.length;
  const [selectedIdx,setSelectedIdx]=useState(0);
  // Reset selection when filter changes (current selectedIdx may now be out of range)
  React.useEffect(()=>{ setSelectedIdx(0); },[viewMode,(exec||{}).id]);
  const sel=steps[selectedIdx]||steps[0];
  const [trace,setTrace]=useState(null); // { traceId, spans }

  // Pull trace siblings — every span (across multiple routes) sharing the same trace id
  useEffect(()=>{
    if(!(exec||{}).id) return;
    let alive=true;
    API('GET',`/api/executions/${exec.id}/trace`).then(r=>{
      if(alive && r && Array.isArray(r.spans) && r.spans.length>1) setTrace(r);
    }).catch(()=>{});
    return()=>{alive=false;};
  },[(exec||{}).id]);

  return(
    <div style={{position:'fixed',inset:0,background:'rgba(15,17,35,.45)',backdropFilter:'blur(2px)',zIndex:990,display:'flex',justifyContent:'flex-end'}} onClick={onClose}>
      <div onClick={ev=>ev.stopPropagation()} style={{width:'min(960px,85vw)',height:'100%',background:'var(--surface)',borderLeft:'1px solid var(--border)',boxShadow:'var(--shadow-lg)',display:'flex',flexDirection:'column',overflow:'hidden'}}>
        {/* Header */}
        <div style={{padding:'14px 20px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:12}}>
          <div style={{flex:1,minWidth:0}}>
            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:3}}>
              <span style={{fontSize:18,color:'#6366f1'}}><FileDocIcon size={18}/></span>
              <div style={{fontWeight:800,fontSize:15,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{exec.integrationName||exec.integrationId||'Execution'}</div>
              {exec.instanceSeq!=null && <span style={{padding:'2px 8px',background:'var(--accent)',color:'#fff',borderRadius:4,fontSize:11,fontWeight:700,letterSpacing:'.3px'}}>Run #{exec.instanceSeq}</span>}
              <StatusBadge status={exec.status}/>
            </div>
            <div style={{fontSize:11,color:'var(--text3)',display:'flex',gap:14,flexWrap:'wrap'}}>
              <span>Started: <b>{fmtDate(exec.startedAt)}</b></span>
              <span>Duration: <b>{fmtDur(exec.durationMs)}</b></span>
              {exec.httpStatus!=null&&<span>Upstream HTTP: <b style={{color:exec.httpStatus>=400?'var(--error)':'var(--text)'}}>{exec.httpStatus}</b></span>}
              {exec.recordCount!=null&&<span>Records: <b>{exec.recordCount} {exec.recordLabel||'records'}</b></span>}
              {exec.correlationId&&<span style={{fontFamily:'ui-monospace,monospace'}}>corr: {exec.correlationId.slice(0,12)}</span>}
              {exec.retryCount>0&&<span style={{color:'var(--warning)'}}>retries: {exec.retryCount}</span>}
            </div>
          </div>
          {/* Operator | Developer view toggle — hides Camel boilerplate by default */}
          <div className="exec-view-toggle">
            <button onClick={()=>setViewMode('operator')}
                    className={viewMode==='operator'?'active':''}
                    title={'Show business-meaningful steps only'+(hiddenCount>0&&viewMode!=='operator'?` (hides ${hiddenCount})`:'')}
                    >
              Operator
            </button>
            <button onClick={()=>setViewMode('developer')}
                    className={viewMode==='developer'?'active':''}
                    title="Show every Camel processor (setProperty, setHeader, marshal, …)"
                    >
              Developer
            </button>
          </div>
          <button onClick={onClose} className="modal-close-btn" aria-label="Close"><CloseIcon size={16}/></button>
        </div>

        {/* Trace strip — shows when this execution shares a trace_id with other route spans */}
        {trace && (
          <div style={{padding:'10px 20px',borderBottom:'1px solid var(--border)',background:'rgba(99,102,241,.06)'}}>
            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:6}}>
              <span style={{fontSize:11,fontWeight:700,color:'var(--accent)',textTransform:'uppercase',letterSpacing:'.7px',display:'inline-flex',alignItems:'center',gap:5}}><LinkChainIcon size={12}/> Trace</span>
              <span style={{fontFamily:'ui-monospace,monospace',fontSize:11,color:'var(--text3)'}}>#{(trace.traceId||'').slice(0,12)}</span>
              <span style={{fontSize:11,color:'var(--text3)'}}>· {trace.spans.length} spans across this flow</span>
            </div>
            <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
              {trace.spans.map(span=>{
                const isThis=span.id===exec.id;
                const ok=span.status==='SUCCESS';
                return(
                  <div key={span.id}
                       onClick={()=>{ if(!isThis) window.dispatchEvent(new CustomEvent('open-exec',{detail:span})); }}
                       title={`${span.integrationName||span.integrationId||'?'} · ${span.status} · ${fmtDur(span.durationMs)}`}
                       style={{padding:'4px 9px',borderRadius:14,fontSize:11,fontWeight:600,
                          cursor:isThis?'default':'pointer',
                          background: isThis ? 'var(--accent)' : ok ? '#f0fdf4' : 'rgba(239,68,68,.08)',
                          color: isThis ? '#fff' : ok ? '#16a34a' : '#dc2626',
                          border:'1px solid '+(isThis?'var(--accent)':ok?'#bbf7d0':'rgba(239,68,68,.3)'),
                          display:'flex',alignItems:'center',gap:5}}>
                    <span style={{display:'inline-flex'}}>{ok?<CheckCircleIcon size={13}/>:<WarningIcon size={13}/>}</span>
                    <span style={{maxWidth:140,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{span.integrationName||span.integrationId||'?'}</span>
                    <span style={{opacity:.7}}>{fmtDur(span.durationMs)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Body: activity timeline + I/O panel */}
        <div style={{flex:1,display:'grid',gridTemplateColumns:'320px 1fr',minHeight:0}}>
          {/* Timeline */}
          <div style={{borderRight:'1px solid var(--border)',overflowY:'auto',background:'var(--surface2)'}}>
            <div style={{padding:'12px 16px 6px',fontSize:10,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'1px',display:'flex',alignItems:'center',gap:8}}>
              <span>Activities ({steps.length}{hiddenCount>0 && viewMode==='operator' ? <span style={{textTransform:'none',color:'var(--text3)',fontWeight:500,letterSpacing:'normal'}}> · {hiddenCount} hidden</span> : null})</span>
              {hiddenCount>0 && viewMode==='operator' && (
                <button onClick={()=>setViewMode('developer')}
                        style={{padding:'2px 7px',borderRadius:4,border:'1px solid var(--border)',background:'transparent',cursor:'pointer',fontSize:9.5,color:'var(--text3)',textTransform:'none',letterSpacing:'normal',fontWeight:600}}
                        title="Switch to Developer view to see all hidden steps">show all</button>
              )}
            </div>
            {/* Sibling-step recovery banner — explain when steps were borrowed
                 from another row in the same cron-tick cohort (Camel split-
                 exchange race; only one sibling drains the step buffer). */}
            {exec.stepsFromSiblingSeq && (
              <div style={{margin:'0 14px 6px',padding:'7px 10px',background:'rgba(99,102,241,.07)',border:'1px solid rgba(99,102,241,.25)',borderRadius:6,fontSize:11,color:'var(--text2)',lineHeight:1.4}}>
                <span style={{fontWeight:700,color:'var(--accent)'}}>↻ Steps from sibling Run #{exec.stepsFromSiblingSeq}</span>
                <div style={{fontSize:10,color:'var(--text3)',marginTop:2}}>
                  This row had no captured steps. The detail panel is showing the activity timeline from a sibling exchange in the same cron tick — they ran the same flow, so the trace is representative.
                </div>
              </div>
            )}
            <div style={{padding:'4px 10px 14px'}}>
              {steps.map((s,i)=>{
                const c=stepStatusColor(s.status);
                const active=i===selectedIdx;
                return(
                  <div key={i} onClick={()=>setSelectedIdx(i)} style={{position:'relative',padding:'10px 12px',borderRadius:8,background:active?'var(--surface3)':'transparent',border:active?'1px solid var(--border2)':'1px solid transparent',cursor:'pointer',marginBottom:4,transition:'background .12s'}}>
                    <div style={{display:'flex',alignItems:'center',gap:10}}>
                      <span style={{display:'inline-flex'}}>{stepIcon(s.type,18)}</span>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:600,color:'var(--text)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{s.name||s.type}</div>
                        <div style={{fontSize:10.5,color:'var(--text3)',marginTop:2,display:'flex',gap:8}}>
                          <span style={{color:c,fontWeight:600}}>● {s.status||'—'}</span>
                          <span>{fmtDur(s.durationMs)}</span>
                        </div>
                      </div>
                    </div>
                    {i<steps.length-1&&<div style={{position:'absolute',left:21,top:42,width:2,height:14,background:'var(--border2)'}}/>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Detail panel */}
          {sel?(
            <div style={{overflowY:'auto',padding:'18px 22px'}}>
              <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:14,paddingBottom:12,borderBottom:'1px solid var(--border)'}}>
                <span style={{display:'inline-flex'}}>{stepIcon(sel.type,22)}</span>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:700,fontSize:15}}>{sel.name||sel.type}</div>
                  <div style={{fontSize:11,color:'var(--text3)',marginTop:2,display:'flex',gap:14,flexWrap:'wrap'}}>
                    <span style={{color:stepStatusColor(sel.status),fontWeight:600}}>● {sel.status||'—'}</span>
                    <span>type: <b>{sel.type||'—'}</b></span>
                    <span>duration: <b>{fmtDur(sel.durationMs)}</b></span>
                    {sel.startedAt&&<span>started: <b>{fmtDate(sel.startedAt)}</b></span>}
                  </div>
                </div>
              </div>

              {sel.metadata&&Object.keys(sel.metadata).length>0&&(
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:10,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'1px',marginBottom:6}}>Parameters</div>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:6}}>
                    {Object.entries(sel.metadata).map(([k,v])=>(
                      <div key={k} style={{fontSize:11.5,padding:'6px 10px',background:'var(--surface3)',borderRadius:6,border:'1px solid var(--border)'}}>
                        <span style={{color:'var(--text3)'}}>{k}: </span>
                        <span style={{fontFamily:'ui-monospace,monospace',color:'var(--text)'}}>{String(v)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{marginBottom:14}}>
                <div style={{fontSize:10,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'1px',marginBottom:6,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <span>Input</span>
                  {sel.input&&<button className="btn btn-ghost btn-xs" style={{fontSize:10,display:'inline-flex',alignItems:'center',gap:4}} onClick={()=>{(navigator.clipboard||'').writeText(fmtJson(sel.input));}}><BuilderCopyIcon size={10}/> Copy</button>}
                </div>
                <pre style={{fontSize:11.5,fontFamily:'ui-monospace,monospace',background:'var(--surface3)',padding:'10px 12px',borderRadius:6,border:'1px solid var(--border)',maxHeight:240,overflow:'auto',margin:0,whiteSpace:'pre-wrap',wordBreak:'break-word',lineHeight:1.5,color:'var(--text)'}}><JsonView text={sel.input?fmtJson(sel.input):''}/></pre>
              </div>

              <div style={{marginBottom:14}}>
                <div style={{fontSize:10,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'1px',marginBottom:6,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <span>Output</span>
                  {sel.output&&<button className="btn btn-ghost btn-xs" style={{fontSize:10,display:'inline-flex',alignItems:'center',gap:4}} onClick={()=>{(navigator.clipboard||'').writeText(fmtJson(sel.output));}}><BuilderCopyIcon size={10}/> Copy</button>}
                </div>
                <pre style={{fontSize:11.5,fontFamily:'ui-monospace,monospace',background:'var(--surface3)',padding:'10px 12px',borderRadius:6,border:'1px solid var(--border)',maxHeight:280,overflow:'auto',margin:0,whiteSpace:'pre-wrap',wordBreak:'break-word',lineHeight:1.5,color:'var(--text)'}}><JsonView text={sel.output?fmtJson(sel.output):''}/></pre>
              </div>

              {sel.error&&(
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:10,fontWeight:700,color:'var(--error)',textTransform:'uppercase',letterSpacing:'1px',marginBottom:6}}>Error</div>
                  <pre style={{fontSize:11.5,fontFamily:'ui-monospace,monospace',background:'rgba(239,68,68,.07)',padding:'10px 12px',borderRadius:6,border:'1px solid rgba(239,68,68,.25)',margin:0,whiteSpace:'pre-wrap',wordBreak:'break-all',color:'var(--error)'}}>{sel.error}</pre>
                </div>
              )}

              {exec.stepTrace&&selectedIdx===0&&(
                <div style={{marginTop:18,paddingTop:14,borderTop:'1px dashed var(--border)'}}>
                  <div style={{fontSize:10,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'1px',marginBottom:6}}>Step Trace</div>
                  <div style={{fontSize:11.5,fontFamily:'ui-monospace,monospace',background:'var(--surface3)',padding:'8px 12px',borderRadius:6,border:'1px solid var(--border)',color:'var(--accent)',wordBreak:'break-all'}}>{exec.stepTrace}</div>
                </div>
              )}
            </div>
          ):(
            <div style={{display:'flex',alignItems:'center',justifyContent:'center',color:'var(--text3)',fontSize:13}}>Select an activity to inspect its input and output.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function MonitoringPage({showToast,confirm}){
  const [executions,setExecutions]=useState([]);
  const [metrics,setMetrics]=useState({});
  const [loading,setLoading]=useState(true);
  const [refreshing,setRefreshing]=useState(false);
  const [filterIntId,setFilterIntId]=useState('');
  const [filterStatus,setFilterStatus]=useState('');
  const [searchQ,setSearchQ]=useState('');
  const [colFilters,setColFilters]=useState({integration:'',duration:'',from:'',to:'',trace:''});
  const [sortKey,setSortKey]=useState('startedAt');
  const [sortDir,setSortDir]=useState('desc');
  const [tab,setTab]=useState('executions');
  const [lastRefresh,setLastRefresh]=useState(null);
  const [drawerExec,setDrawerExec]=useState(null);
  const [showAdv,setShowAdv]=useState(false);

  const openExec=useCallback(async(e)=>{
    try{
      const full=await API('GET',`/api/executions/${e.id}`);
      setDrawerExec(full||e);
    }catch{
      setDrawerExec(e);
    }
  },[]);

  // Allow ActivityDrawer's trace strip to swap to a sibling span on click
  useEffect(()=>{
    const handler=(ev)=>{ if(ev.detail) openExec(ev.detail); };
    window.addEventListener('open-exec',handler);
    return()=>window.removeEventListener('open-exec',handler);
  },[openExec]);

  // Resubmit modal state
  const [resubmitFor,setResubmitFor]=useState(null);
  const openResubmit=useCallback(async(e)=>{
    // Pull the full record so we have activity steps for the picker
    try{
      const full=await API('GET',`/api/executions/${e.id}`);
      setResubmitFor(full||e);
    }catch{ setResubmitFor(e); }
  },[]);

  // Bulk-select state for FAILED rows
  const [selectedIds,setSelectedIds]=useState(new Set());
  const [bulkBusy,setBulkBusy]=useState(false);
  const onCheck=useCallback((e,on)=>{
    setSelectedIds(prev=>{
      const next=new Set(prev);
      if(on) next.add(e.id); else next.delete(e.id);
      return next;
    });
  },[]);
  const clearSel=useCallback(()=>setSelectedIds(new Set()),[]);
  const bulkResubmit=async()=>{
    const ids=[...selectedIds];
    if(ids.length===0) return;
    const _c=await confirm(`Resubmit ${ids.length} failed execution(s) from beginning?`);if(!_c)return;
    setBulkBusy(true);
    try{
      const r=await API('POST','/api/executions/bulk-resubmit',{ids,fromStep:0});
      showToast(`Resubmitted ${r.requested}: ${r.succeeded} ✓ · ${r.failed} ✗`, r.failed===0?'success':'warning');
      clearSel();
      load(true);
    }catch(e){ showToast('Bulk resubmit failed: '+e.message,'error'); }
    finally{ setBulkBusy(false); }
  };

  // Pre-computed select-all state for the table header (avoids inline IIFE inside JSX).
  // The actual values depend on `sorted`, which is computed below — we reference them
  // safely because everything in this function runs every render and `sorted` is defined
  // before the JSX returns.
  const selectAllRef=useRef(null);

  const load=useCallback(async(silent=false)=>{
    if(!silent){setLoading(true);setRefreshing(true);}
    else setRefreshing(true);
    try{
      const [execs,mets]=await loadAll([
        ['Executions',API('GET','/api/executions?limit=500'),[]],
        ['Metrics',API('GET','/api/metrics'),{}],
      ],showToast);
      setExecutions(Array.isArray(execs)?execs:[]);
      setMetrics(mets||{});
      setLastRefresh(new Date());
      if(!silent) showToast('Refreshed','success');
    }catch(e){
      showToast('Refresh failed: '+e.message,'error');
    }
    finally{
      // Always clear `loading` once a fetch resolves — even silent calls — otherwise
      // the initial mount (which uses silent=true to avoid the "Refreshed" toast)
      // leaves the page stuck on the "Loading…" placeholder until a manual refresh.
      setLoading(false);
      setRefreshing(false);
    }
  },[showToast]);

  useEffect(()=>{load(true);},[load]);

  // Auto-refresh control — interval in seconds, 0 = off. Persisted across reloads.
  const [autoRefreshSec,setAutoRefreshSec]=useState(()=>{
    try{ const v=localStorage.getItem('observability.autoRefreshSec'); return v==null?30:Number(v); }catch{return 30;}
  });
  useEffect(()=>{
    try{ localStorage.setItem('observability.autoRefreshSec', String(autoRefreshSec)); }catch{}
  },[autoRefreshSec]);
  useEffect(()=>{
    if(!autoRefreshSec || autoRefreshSec<=0) return; // off
    const ms = Math.max(2,autoRefreshSec)*1000;
    const t=setInterval(()=>load(true), ms);
    return()=>clearInterval(t);
  },[load,autoRefreshSec]);

  // Tick a "next refresh in Xs" countdown so the user sees auto-refresh actually working
  const [secsToNext,setSecsToNext]=useState(autoRefreshSec||0);
  useEffect(()=>{ setSecsToNext(autoRefreshSec||0); },[autoRefreshSec, lastRefresh]);
  useEffect(()=>{
    if(!autoRefreshSec || autoRefreshSec<=0) return;
    const t=setInterval(()=>setSecsToNext(s=>s>0?s-1:autoRefreshSec),1000);
    return()=>clearInterval(t);
  },[autoRefreshSec]);

  // Build name map from execution data
  const intNameMap={};
  executions.forEach(e=>{if(e.integrationId&&e.integrationName)intNameMap[e.integrationId]=e.integrationName;});
  const uniqueInts=Object.entries(intNameMap);

  const matchesSearch=(e,q)=>{
    if(!q)return true;
    const ql=q.toLowerCase();
    // Allow searching by Run #N — accept "1496", "#1496", or "run 1496"
    const qNum=ql.replace(/^run\s*#?\s*/,'').replace(/^#/,'');
    return [e.integrationName,e.integrationId,e.status,e.correlationId,
            e.stepTrace,e.errorMessage,e.sourceSystem,e.targetSystem,
            e.instanceSeq!=null?String(e.instanceSeq):null,
            e.recordLabel]
      .filter(Boolean).some(v=>String(v).toLowerCase().includes(ql) ||
                                 (qNum && String(v)===qNum));
  };

  const filtered=executions.filter(e=>{
    if(filterIntId&&e.integrationId!==filterIntId)return false;
    if(filterStatus&&e.status!==filterStatus)return false;
    if(!matchesSearch(e,searchQ))return false;
    if(colFilters.integration&&!String(e.integrationName||e.integrationId||'').toLowerCase().includes(colFilters.integration.toLowerCase()))return false;
    if(colFilters.trace&&!String(e.errorMessage||e.stepTrace||'').toLowerCase().includes(colFilters.trace.toLowerCase()))return false;
    if(colFilters.duration){
      const min=parseInt(colFilters.duration,10);
      if(!isNaN(min)&&(e.durationMs||0)<min)return false;
    }
    if(colFilters.from){
      try{ if(new Date(e.startedAt) < new Date(colFilters.from)) return false; }catch{}
    }
    if(colFilters.to){
      try{ if(new Date(e.startedAt) > new Date(colFilters.to)) return false; }catch{}
    }
    return true;
  });

  const sorted=React.useMemo(()=>{
    const cmp=(a,b)=>{
      const va=({instanceSeq:a.instanceSeq||0,integration:a.integrationName||a.integrationId,status:a.status,records:a.recordCount||0,duration:a.durationMs||0,startedAt:a.startedAt||'',trace:a.errorMessage||a.stepTrace||''})[sortKey];
      const vb=({instanceSeq:b.instanceSeq||0,integration:b.integrationName||b.integrationId,status:b.status,records:b.recordCount||0,duration:b.durationMs||0,startedAt:b.startedAt||'',trace:b.errorMessage||b.stepTrace||''})[sortKey];
      if(va==null&&vb==null)return 0;
      if(va==null)return 1;
      if(vb==null)return -1;
      if(typeof va==='number'&&typeof vb==='number') return sortDir==='asc'?va-vb:vb-va;
      const cm=String(va).localeCompare(String(vb));
      return sortDir==='asc'?cm:-cm;
    };
    return [...filtered].sort(cmp);
  },[filtered,sortKey,sortDir]);

  const toggleSort=(k)=>{
    if(sortKey===k) setSortDir(d=>d==='asc'?'desc':'asc');
    else { setSortKey(k); setSortDir('asc'); }
  };
  const sortIndicator=(k)=> sortKey===k ? (sortDir==='asc'?' ▲':' ▼') : '';

  // Derived select-all state for the failed-row checkboxes.
  const failedIds = sorted.filter(e=>e.status==='FAILED').map(e=>e.id);
  const allFailedSelected = failedIds.length>0 && failedIds.every(id=>selectedIds.has(id));
  const someFailedSelected = selectedIds.size>0 && !allFailedSelected;
  useEffect(()=>{ if(selectAllRef.current) selectAllRef.current.indeterminate = someFailedSelected; },[someFailedSelected]);
  const toggleAllFailed = (on) => setSelectedIds(()=> on ? new Set(failedIds) : new Set());

  // Stat cards: prefer all-time aggregates from /api/metrics; fall back to loaded slice.
  const totalAll = metrics.totalAll || executions.length;
  const successAll = metrics.successAll || executions.filter(e=>e.status==='SUCCESS').length;
  const failedAll = metrics.failedAll || executions.filter(e=>e.status==='FAILED').length;
  const successRateAll = metrics.successRateAll || (totalAll? Math.round(successAll/totalAll*100):0);
  const sessionAvg=executions.length?Math.round(executions.reduce((s,e)=>s+(e.durationMs||0),0)/executions.length):null;

  return(
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Executions</h1>
          <div className="page-subtitle">Every integration run — outcome, timing and the steps inside it
            {lastRefresh&&<span style={{marginLeft:10,fontSize:11,color:'var(--text3)'}}>· Updated {lastRefresh.toLocaleTimeString()}{refreshing?' · refreshing…':autoRefreshSec>0?` · next in ${secsToNext}s`:''}</span>}
          </div>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          {/* Auto-refresh control */}
          <div title="Auto-refresh interval"
               style={{display:'flex',alignItems:'center',gap:6,padding:'4px 10px',borderRadius:7,
                       background:autoRefreshSec>0?'rgba(34,197,94,.08)':'var(--surface)',
                       border:'1px solid '+(autoRefreshSec>0?'rgba(34,197,94,.35)':'var(--border)')}}>
            <span style={{width:7,height:7,borderRadius:'50%',
                          background:autoRefreshSec>0?'var(--success)':'var(--text3)',
                          boxShadow:autoRefreshSec>0?'0 0 0 3px rgba(34,197,94,.2)':'none'}}/>
            <span style={{fontSize:11,fontWeight:600,color:autoRefreshSec>0?'var(--success)':'var(--text3)',textTransform:'uppercase',letterSpacing:'.5px'}}>
              Auto
            </span>
            <select value={autoRefreshSec} onChange={e=>setAutoRefreshSec(Number(e.target.value))}
              style={{padding:'2px 5px',fontSize:12,border:'none',background:'transparent',color:'var(--text)',outline:'none',cursor:'pointer',fontWeight:600}}>
              <option value="0">Off</option>
              <option value="5">5s</option>
              <option value="10">10s</option>
              <option value="15">15s</option>
              <option value="30">30s</option>
              <option value="60">1m</option>
              <option value="300">5m</option>
              <option value="900">15m</option>
            </select>
          </div>
          <button className="btn btn-refresh" style={TAXLAB_LIGHT_STYLE} onClick={()=>load(false)} disabled={refreshing}>{refreshing?<><Spinner/> Refreshing</>:<><BuilderRefreshIcon size={18}/> Refresh</>}</button>
        </div>
      </div>

      <div className="metric-grid mb-4">
        {[
          {l:'Total Executions',v:totalAll,c:'#3b82f6',ic:(<svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2S2 6.477 2 12s4.477 10 10 10" opacity=".35"></path><path fill="currentColor" d="M10.2 8.5c0-.66.72-1.07 1.29-.74l4.81 2.78c.57.33.57 1.15 0 1.48l-4.81 2.78c-.57.33-1.29-.08-1.29-.74z"></path></svg>)},
          {l:'Successful',v:successAll,c:'#22c55e',ic:(<svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" fillRule="evenodd" d="M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12S6.477 2 12 2s10 4.477 10 10m-5.97-3.03a.75.75 0 0 1 0 1.06l-5 5a.75.75 0 0 1-1.06 0l-2-2a.75.75 0 1 1 1.06-1.06l1.47 1.47l2.235-2.235L14.97 8.97a.75.75 0 0 1 1.06 0" clipRule="evenodd"></path></svg>)},
          {l:'Failed',v:failedAll,c:'#ef4444',ic:(<svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" fillRule="evenodd" d="M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12S6.477 2 12 2s10 4.477 10 10M8.97 8.97a.75.75 0 0 1 1.06 0L12 10.94l1.97-1.97a.75.75 0 0 1 1.06 1.06L13.06 12l1.97 1.97a.75.75 0 0 1-1.06 1.06L12 13.06l-1.97 1.97a.75.75 0 0 1-1.06-1.06L10.94 12l-1.97-1.97a.75.75 0 0 1 0-1.06" clipRule="evenodd"></path></svg>)},
          {l:'Success Rate',v:successRateAll+'%',c:successRateAll===100?'#22c55e':successRateAll>=90?'#f59e0b':'#ef4444',ic:(<svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M4 19h16v2H2V3h2zm2-8 3.2 3.2 4.3-4.3 2.5 2.5L20 8.5V16H6z" opacity=".75"></path></svg>)},
        ].map(m=>(
          <div key={m.l} className="stat-card" style={{cursor:'default'}}>
            <div className="stat-icon" style={{background:m.c+'22',border:`1px solid ${m.c}44`}}>
              <span style={{fontSize:18,fontWeight:700,color:m.c,display:'inline-flex',alignItems:'center',justifyContent:'center'}}>{m.ic}</span>
            </div>
            <div>
              <div style={{fontSize:24,fontWeight:700,color:m.c}}>{m.v}</div>
              <div style={{fontSize:12,fontWeight:600,color:'var(--text2)'}}>{m.l}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="tab-pills mb-4">
        {[['executions','Executions'],['metrics','Metrics']].map(([t,l])=>(
          <div key={t} className={`tab-pill${tab===t?' active':''}`} onClick={()=>setTab(t)} style={{display:'inline-flex',alignItems:'center',gap:8}}>
            {t==='executions'?(
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0}}>
                <path fill="currentColor" d="M21 15.998v-6c0-2.828 0-4.242-.879-5.121C19.353 4.109 18.175 4.012 16 4H8c-2.175.012-3.353.109-4.121.877C3 5.756 3 7.17 3 9.998v6c0 2.829 0 4.243.879 5.122c.878.878 2.293.878 5.121.878h6c2.828 0 4.243 0 5.121-.878c.879-.88.879-2.293.879-5.122" opacity=".5"></path>
                <path fill="currentColor" d="M8 3.5A1.5 1.5 0 0 1 9.5 2h5A1.5 1.5 0 0 1 16 3.5v1A1.5 1.5 0 0 1 14.5 6h-5A1.5 1.5 0 0 1 8 4.5z"></path>
                <path fill="currentColor" fillRule="evenodd" d="M6.25 10.5A.75.75 0 0 1 7 9.75h.5a.75.75 0 0 1 0 1.5H7a.75.75 0 0 1-.75-.75m3.5 0a.75.75 0 0 1 .75-.75H17a.75.75 0 0 1 0 1.5h-6.5a.75.75 0 0 1-.75-.75M6.25 14a.75.75 0 0 1 .75-.75h.5a.75.75 0 0 1 0 1.5H7a.75.75 0 0 1-.75-.75m3.5 0a.75.75 0 0 1 .75-.75H17a.75.75 0 0 1 0 1.5h-6.5a.75.75 0 0 1-.75-.75" clipRule="evenodd"></path>
              </svg>
            ):(
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0}}>
                <path fill="currentColor" d="M12 22c-4.714 0-7.071 0-8.536-1.465C2 19.072 2 16.714 2 12s0-7.071 1.464-8.536C4.93 2 7.286 2 12 2s7.071 0 8.535 1.464C22 4.93 22 7.286 22 12s0 7.071-1.465 8.535C19.072 22 16.714 22 12 22" opacity=".5"></path>
                <path fill="currentColor" d="M12 5.25a.75.75 0 0 1 .75.75v12a.75.75 0 0 1-1.5 0V6a.75.75 0 0 1 .75-.75m-5 3a.75.75 0 0 1 .75.75v9a.75.75 0 0 1-1.5 0V9A.75.75 0 0 1 7 8.25m10 4a.75.75 0 0 1 .75.75v5a.75.75 0 0 1-1.5 0v-5a.75.75 0 0 1 .75-.75"></path>
              </svg>
            )}
            <span>{l}</span>
          </div>
        ))}
      </div>

      {tab==='executions'&&(()=>{
        const advActive = !!(colFilters.integration||colFilters.duration||colFilters.from||colFilters.to||colFilters.trace);
        const anyFilter = !!(searchQ||filterIntId||filterStatus||advActive);
        const clearAll = ()=>{setSearchQ('');setFilterIntId('');setFilterStatus('');setColFilters({integration:'',duration:'',from:'',to:'',trace:''});};
        return(
        <div className="card exec-card">
          {/* Toolbar */}
          <div className="exec-toolbar">
            <div className="search-field" style={{width:320}}>
              <span className="search-field-icon"><SearchIcon size={20} color="rgb(99,115,129)"/></span>
              <input className="search-field-input" placeholder="Search across all columns…" value={searchQ} onChange={e=>setSearchQ(e.target.value)}/>
            </div>
            <select className="form-select" style={{maxWidth:200,fontSize:13}} value={filterIntId} onChange={e=>setFilterIntId(e.target.value)}>
              <option value="">All Integrations</option>
              {uniqueInts.map(([id,name])=><option key={id} value={id}>{name}</option>)}
            </select>
            <select className="form-select" style={{maxWidth:150,fontSize:13}} value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
              <option value="">All Status</option>
              <option value="SUCCESS">Success</option>
              <option value="FAILED">Failed</option>
              <option value="RETRYING">Retrying</option>
            </select>
            <button className={`btn-toggle${showAdv||advActive?' active':''}`} onClick={()=>setShowAdv(s=>!s)}>
              <span style={{display:'inline-flex',transition:'transform .18s',transform:showAdv?'rotate(180deg)':'rotate(0deg)'}}><ChevronDownIcon size={14}/></span>
              Advanced{advActive?` · ${Object.values(colFilters).filter(Boolean).length}`:''}
            </button>
            {anyFilter&&<button className="btn btn-ghost btn-xs" onClick={clearAll}><TrashIcon size={12}/> Clear all</button>}
            <span style={{marginLeft:'auto',fontSize:12,color:'var(--text3)',whiteSpace:'nowrap'}}>
              <b>{sorted.length}</b> of {executions.length}
            </span>
          </div>

          {/* Advanced filters (collapsible) */}
          {showAdv&&(
            <div className="exec-adv">
              <div className="exec-adv-field">
                <label>Integration name contains</label>
                <input value={colFilters.integration} onChange={e=>setColFilters(p=>({...p,integration:e.target.value}))} placeholder="e.g. Hello"/>
              </div>
              <div className="exec-adv-field">
                <label>Trace / error contains</label>
                <input value={colFilters.trace} onChange={e=>setColFilters(p=>({...p,trace:e.target.value}))} placeholder="e.g. timeout"/>
              </div>
              <div className="exec-adv-field">
                <label>Min duration (ms)</label>
                <input type="number" value={colFilters.duration} onChange={e=>setColFilters(p=>({...p,duration:e.target.value}))} placeholder="0"/>
              </div>
              <div className="exec-adv-field">
                <label>Started between</label>
                <div className="exec-adv-row">
                  <input type="datetime-local" value={colFilters.from} onChange={e=>setColFilters(p=>({...p,from:e.target.value}))}/>
                  <span style={{color:'var(--text3)',fontSize:11}}>→</span>
                  <input type="datetime-local" value={colFilters.to} onChange={e=>setColFilters(p=>({...p,to:e.target.value}))}/>
                </div>
              </div>
            </div>
          )}

          {/* Active filter chips */}
          {anyFilter&&(
            <div style={{padding:'8px 16px',borderBottom:'1px solid var(--border)',background:'var(--surface)'}}>
              <div className="exec-filter-chips">
                {searchQ&&<span className="exec-chip">search: {searchQ}<button onClick={()=>setSearchQ('')}>×</button></span>}
                {filterIntId&&<span className="exec-chip">integration: {intNameMap[filterIntId]||filterIntId}<button onClick={()=>setFilterIntId('')}>×</button></span>}
                {filterStatus&&<span className="exec-chip">status: {filterStatus}<button onClick={()=>setFilterStatus('')}>×</button></span>}
                {colFilters.integration&&<span className="exec-chip">name: {colFilters.integration}<button onClick={()=>setColFilters(p=>({...p,integration:''}))}>×</button></span>}
                {colFilters.trace&&<span className="exec-chip">trace: {colFilters.trace}<button onClick={()=>setColFilters(p=>({...p,trace:''}))}>×</button></span>}
                {colFilters.duration&&<span className="exec-chip">≥ {colFilters.duration} ms<button onClick={()=>setColFilters(p=>({...p,duration:''}))}>×</button></span>}
                {colFilters.from&&<span className="exec-chip">from: {colFilters.from}<button onClick={()=>setColFilters(p=>({...p,from:''}))}>×</button></span>}
                {colFilters.to&&<span className="exec-chip">to: {colFilters.to}<button onClick={()=>setColFilters(p=>({...p,to:''}))}>×</button></span>}
              </div>
            </div>
          )}

          {/* Bulk actions bar — only when at least one row is selected */}
          {selectedIds.size>0&&(
            <div style={{display:'flex',alignItems:'center',gap:10,padding:'8px 14px',marginBottom:10,background:'rgba(99,102,241,.08)',border:'1px solid rgba(99,102,241,.3)',borderRadius:8,fontSize:13}}>
              <span style={{fontWeight:700,color:'var(--accent)'}}>{selectedIds.size} selected</span>
              <button className="btn btn-secondary btn-xs" onClick={bulkResubmit} disabled={bulkBusy}>
                {bulkBusy?<><Spinner/> Resubmitting…</>:<><BuilderRefreshIcon size={14}/> Resubmit {selectedIds.size} from beginning</>}
              </button>
              <button className="btn btn-ghost btn-xs" onClick={clearSel} disabled={bulkBusy}>Clear selection</button>
            </div>
          )}

          {/* Table */}
          {loading ? (
            <div className="text-muted text-sm" style={{padding:32,textAlign:'center'}}>Loading…</div>
          ) : sorted.length===0 ? (
            <div className="empty-state" style={{margin:0,padding:'40px 20px'}}>
              <EmptyStateIcon tone="green"><ClipboardListIcon/></EmptyStateIcon>
              <p>No executions match the current filters</p>
              {anyFilter&&<button className="btn btn-ghost btn-sm" onClick={clearAll} style={{marginTop:10}}><TrashIcon size={13}/> Clear filters</button>}
            </div>
          ) : (
            <div className="exec-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{width:30,textAlign:'center'}}>
                      {failedIds.length>0&&<input type="checkbox" checked={allFailedSelected} ref={selectAllRef}
                             onChange={ev=>toggleAllFailed(ev.target.checked)} title="Select all FAILED rows"/>}
                    </th>
                    {[
                      {k:'instanceSeq',l:'Run #'},
                      {k:'integration',l:'Integration'},
                      {k:'status',l:'Status'},
                      {k:'records',l:'Records'},
                      {k:'duration',l:'Duration'},
                      {k:'startedAt',l:'Start Time'},
                      {k:'trace',l:'Trace / Error'},
                    ].map(c=>(
                      <th key={c.k} className="sortable" onClick={()=>toggleSort(c.k)}>
                        {c.l}<span style={{color:sortKey===c.k?'var(--accent)':'var(--text3)',fontSize:10,marginLeft:6}}>{sortIndicator(c.k)||'⇅'}</span>
                      </th>
                    ))}
                    <th style={{width:64}}></th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map(e=><ExecRow key={e.id} e={e} selected={(drawerExec||{}).id===e.id} onSelect={openExec} onResubmit={openResubmit}
                                          checked={selectedIds.has(e.id)} onCheck={onCheck}/>)}
                </tbody>
              </table>
            </div>
          )}
        </div>
        );
      })()}

      {tab==='metrics'&&(
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20}}>
          <div className="card">
            <div className="section-title">Today's Overview</div>
            <div style={{display:'grid',gap:8}}>
              {[
                ['Executions Today', metrics.totalToday||'—'],
                ['Successful Today', metrics.successToday||'—'],
                ['Failed Today', metrics.failedToday||'—'],
                ['Success Rate', metrics.successRate!=null?(metrics.successRate+'%'):'—'],
                ['Active Integrations', metrics.activeIntegrations||'—'],
                ['Total Integrations', metrics.totalIntegrations||'—'],
                ['Total Connections', metrics.totalConnections||'—'],
                ['Avg Duration (all-time)', fmtDur(metrics.avgDurationAll)],
                ['Avg Duration (session)', fmtDur(sessionAvg)],
              ].map(([k,v])=>(
                <div key={k} className="flex justify-between" style={{padding:'7px 12px',background:'var(--surface3)',borderRadius:'var(--radius)',fontSize:13}}>
                  <span style={{color:'var(--text2)'}}>{k}</span>
                  <span className="font-semibold">{String(v)}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="card">
            <div className="section-title">Hourly Activity (Today)</div>
            {(metrics.hourlyStats||[]).filter(h=>h.count>0).length===0
              ?<div className="text-sm text-dim" style={{padding:20,textAlign:'center'}}>No activity today yet</div>
              :<div style={{display:'flex',flexDirection:'column',gap:4,maxHeight:340,overflowY:'auto'}}>
                {(metrics.hourlyStats||[]).map(h=>(
                  <div key={h.hour} style={{display:'flex',alignItems:'center',gap:8,fontSize:12}}>
                    <span style={{width:28,textAlign:'right',color:'var(--text3)',fontFamily:'monospace'}}>{h.hour}h</span>
                    <div style={{flex:1,height:18,background:'var(--surface3)',borderRadius:4,overflow:'hidden',position:'relative'}}>
                      {h.count>0&&<div style={{position:'absolute',left:0,top:0,height:'100%',width:`${Math.min(100,(h.success||0)/(Math.max(...(metrics.hourlyStats||[]).map(x=>x.count),1))*100)}%`,background:'var(--success)',opacity:.7}}/>}
                      {(h.failed||0)>0&&<div style={{position:'absolute',right:0,top:0,height:'100%',width:`${(h.failed/Math.max(...(metrics.hourlyStats||[]).map(x=>x.count),1))*100}%`,background:'var(--error)',opacity:.7}}/>}
                    </div>
                    <span style={{width:24,textAlign:'right',color:'var(--text2)',fontWeight:600}}>{h.count||''}</span>
                  </div>
                ))}
              </div>
            }
          </div>
          {(metrics.recentErrors||[]).length>0&&(
            <div className="card" style={{gridColumn:'1/-1'}}>
              <div className="section-title" style={{color:'var(--error)',display:'flex',alignItems:'center',gap:8}}><WarningIcon size={15}/> Recent Errors</div>
              <div style={{display:'grid',gap:8}}>
                {(metrics.recentErrors||[]).map(err=>(
                  <div key={err.id} style={{padding:'10px 14px',background:'rgba(239,68,68,.06)',borderRadius:'var(--radius)',border:'1px solid rgba(239,68,68,.2)',fontSize:12}}>
                    <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                      <span className="font-semibold">{err.integrationName||'—'}</span>
                      <span style={{color:'var(--text3)'}}>{fmtDate(err.startedAt)}</span>
                    </div>
                    <div style={{color:'var(--error)',fontFamily:'monospace'}}>{err.errorMessage||'Unknown error'}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {drawerExec&&<ActivityDrawer exec={drawerExec} onClose={()=>setDrawerExec(null)}/>}
      {resubmitFor&&<ResubmitModal
        exec={resubmitFor}
        onClose={()=>setResubmitFor(null)}
        onResubmitted={(newLog)=>{ setResubmitFor(null); load(true); openExec(newLog); }}
        showToast={showToast}/>}
    </div>
  );
}

/* ===== LOOKUP TABLES PAGE ===== */
function LookupTablesPage({showToast,confirm}){
  const [tables,setTables]=useState([]);
  const [loading,setLoading]=useState(true);
  const [selected,setSelected]=useState(null);
  const [showNewTable,setShowNewTable]=useState(false);
  const [newTableName,setNewTableName]=useState('');
  const [newTableDesc,setNewTableDesc]=useState('');
  const [showAddEntry,setShowAddEntry]=useState(false);
  const [newEntry,setNewEntry]=useState({sourceValue:'',targetValue:'',description:''});
  const [saving,setSaving]=useState(false);

  const load=useCallback(async()=>{
    try{setLoading(true);const d=await API('GET','/api/lookups');setTables(d);}
    catch(e){showToast(e.message,'error');}
    finally{setLoading(false);}
  },[showToast]);
  useEffect(()=>{load();},[load]);

  const createTable=async()=>{
    if(!newTableName.trim()){showToast('Table name required','error');return;}
    setSaving(true);
    try{
      const t=await API('POST','/api/lookups',{name:newTableName,description:newTableDesc,entries:[]});
      showToast('Table created','success');setShowNewTable(false);setNewTableName('');setNewTableDesc('');
      await load();setSelected(t);
    }catch(e){showToast(e.message,'error');}
    finally{setSaving(false);}
  };

  const delTable=async(id)=>{
    const _c=await confirm('Delete this lookup table?');if(!_c)return;
    try{await API('DELETE',`/api/lookups/${id}`);showToast('Deleted','success');setSelected(null);load();}
    catch(e){showToast(e.message,'error');}
  };

  const addEntry=async()=>{
    if(!newEntry.sourceValue||!newEntry.targetValue){showToast('Source and target values required','error');return;}
    setSaving(true);
    try{
      const t=await API('POST',`/api/lookups/${selected.id}/entries`,newEntry);
      setSelected(t);setShowAddEntry(false);setNewEntry({sourceValue:'',targetValue:'',description:''});
      load();showToast('Entry added','success');
    }catch(e){showToast(e.message,'error');}
    finally{setSaving(false);}
  };

  const delEntry=async(entryId)=>{
    try{const t=await API('DELETE',`/api/lookups/${selected.id}/entries/${entryId}`);setSelected(t);load();showToast('Entry deleted','success');}
    catch(e){showToast(e.message,'error');}
  };

  return(
    <div>
      <div className="page-header">
        <div><h1 className="page-title">Lookup Tables</h1><div className="page-subtitle">Manage value mappings and code translation tables</div></div>
        <button className="btn btn-primary" style={TAXLAB_CTA_STYLE} onClick={()=>setShowNewTable(true)}><AddCircleIcon/> New Table</button>
      </div>
      <div className="grid-2" style={{gap:20,alignItems:'start'}}>
        <div className="card">
          <div className="section-title">Tables</div>
          {loading?<div className="text-muted text-sm">Loading…</div>:
           tables.length===0?<div className="empty-state"><EmptyStateIcon tone="amber"><LookupBarsIcon/></EmptyStateIcon><p>No lookup tables</p></div>:
          <div style={{display:'grid',gap:8}}>
            {tables.map(t=>(
              <div key={t.id} className="flex items-center gap-3" style={{padding:'12px 14px',background:(selected||{}).id===t.id?'var(--surface3)':'var(--surface2)',borderRadius:'var(--radius)',border:`1px solid ${(selected||{}).id===t.id?'var(--brand-start)':'var(--border)'}`,cursor:'pointer',transition:'all .15s'}} onClick={()=>setSelected(t)}>
                <div style={{width:20,height:20,display:'inline-flex',alignItems:'center',justifyContent:'center',color:'rgb(79,70,229)'}}><svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0}}><path fill="currentColor" d="M12 22c-4.714 0-7.071 0-8.536-1.465C2 19.072 2 16.714 2 12s0-7.071 1.464-8.536C4.93 2 7.286 2 12 2s7.071 0 8.535 1.464C22 4.93 22 7.286 22 12s0 7.071-1.465 8.535C19.072 22 16.714 22 12 22" opacity=".5"></path><path fill="currentColor" d="M12 5.25a.75.75 0 0 1 .75.75v12a.75.75 0 0 1-1.5 0V6a.75.75 0 0 1 .75-.75m-5 3a.75.75 0 0 1 .75.75v9a.75.75 0 0 1-1.5 0V9A.75.75 0 0 1 7 8.25m10 4a.75.75 0 0 1 .75.75v5a.75.75 0 0 1-1.5 0v-5a.75.75 0 0 1 .75-.75"></path></svg></div>
                <div style={{flex:1}}>
                  <div style={{fontWeight:600,fontSize:13}}>{t.name}</div>
                  <div className="text-xs text-muted">{(t.entries||[]).length} entries</div>
                </div>
                <button className="btn btn-danger row-icon-action" onClick={e=>{e.stopPropagation();delTable(t.id);}} title="Delete" aria-label="Delete"><TrashIcon size={17}/></button>
              </div>
            ))}
          </div>}
          {showNewTable&&(
            <div className="mt-3" style={{padding:14,background:'var(--surface3)',borderRadius:'var(--radius)',border:'1px solid var(--border2)'}}>
              <div className="form-group">
                <label className="form-label">Table Name <span className="req">*</span></label>
                <input className="form-input" value={newTableName} onChange={e=>setNewTableName(e.target.value)} placeholder="e.g. CountryCodeMapping"/>
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <input className="form-input" value={newTableDesc} onChange={e=>setNewTableDesc(e.target.value)} placeholder="Optional"/>
              </div>
              <div className="flex gap-2">
                <button className="btn btn-primary btn-sm" onClick={createTable} disabled={saving}>Create</button>
                <button className="btn btn-ghost btn-sm" onClick={()=>setShowNewTable(false)}>Cancel</button>
              </div>
            </div>
          )}
        </div>
        <div className="card">
          {!selected?<div className="empty-state"><EmptyStateIcon tone="orange"><FileDocIcon/></EmptyStateIcon><p>Select a table to view entries</p></div>:(
            <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="section-title" style={{margin:0}}>{selected.name}</div>
                  {selected.description&&<div className="text-xs text-muted mt-1">{selected.description}</div>}
                </div>
                <button className="btn btn-secondary btn-sm" onClick={()=>setShowAddEntry(p=>!p)}><AddCircleIcon/> Add Entry</button>
              </div>
              {showAddEntry&&(
                <div style={{padding:14,background:'var(--surface3)',borderRadius:'var(--radius)',border:'1px solid var(--border2)',marginBottom:14}}>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Source Value <span className="req">*</span></label>
                      <input className="form-input" value={newEntry.sourceValue} onChange={e=>setNewEntry(p=>({...p,sourceValue:e.target.value}))} placeholder="e.g. MY"/>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Target Value <span className="req">*</span></label>
                      <input className="form-input" value={newEntry.targetValue} onChange={e=>setNewEntry(p=>({...p,targetValue:e.target.value}))} placeholder="e.g. Malaysia"/>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Description</label>
                    <input className="form-input" value={newEntry.description} onChange={e=>setNewEntry(p=>({...p,description:e.target.value}))} placeholder="Optional note"/>
                  </div>
                  <div className="flex gap-2">
                    <button className="btn btn-primary btn-sm" onClick={addEntry} disabled={saving}>Add</button>
                    <button className="btn btn-ghost btn-sm" onClick={()=>setShowAddEntry(false)}>Cancel</button>
                  </div>
                </div>
              )}
              {(selected.entries||[]).length===0?<div className="empty-state"><EmptyStateIcon tone="orange"><FileDocIcon/></EmptyStateIcon><p>No entries yet</p></div>:
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Source Value</th><th>Target Value</th><th>Description</th><th></th></tr></thead>
                  <tbody>
                    {(selected.entries||[]).map(e=>(
                      <tr key={e.id}>
                        <td><code style={{background:'var(--surface3)',padding:'2px 6px',borderRadius:4,fontSize:12}}>{e.sourceValue}</code></td>
                        <td><code style={{background:'var(--surface3)',padding:'2px 6px',borderRadius:4,fontSize:12}}>{e.targetValue}</code></td>
                        <td className="text-sm text-muted">{e.description||'—'}</td>
                        <td><button className="btn btn-danger row-icon-action" onClick={()=>delEntry(e.id)} title="Delete" aria-label="Delete"><TrashIcon size={17}/></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ===== SETTINGS PAGE ===== */
/* Each Settings sub-section is its own routed page now — the sidebar's
 * Settings group provides the navigation, replacing the old horizontal
 * tab-pill row. Audit lives under Observability and is no longer a Settings
 * concern. */

function SettingsPageHeader({title, subtitle}){
  return (
    <div className="page-header">
      <div><h1 className="page-title">{title}</h1><div className="page-subtitle">{subtitle}</div></div>
    </div>
  );
}

function SettingsGeneral({showToast}){
  return (
    <div>
      <SettingsPageHeader title="General Settings" subtitle="Core platform configuration"/>
      <div className="card" style={{maxWidth:600}}>
        <div className="form-group"><label className="form-label">Platform Name</label><input className="form-input" defaultValue="Axon"/></div>
        <div className="form-group"><label className="form-label">Default Timezone</label>
          <select className="form-select"><option>UTC</option><option>Asia/Kuala_Lumpur</option><option>Asia/Singapore</option><option>America/New_York</option><option>Europe/London</option></select>
        </div>
        <div className="form-group"><label className="form-label">Execution Log Retention (days)</label><input className="form-input" type="number" defaultValue="90"/></div>
        <button className="btn btn-primary" onClick={()=>showToast('Settings saved','success')}><SaveActionIcon/> Save Changes</button>
      </div>
    </div>
  );
}

function SettingsSecurity({showToast}){
  const [me, setMe] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  // The identity card reads the central realm's discovery document, like the Keycloak
  // page does -- until 2026-10-29 it was a hard-coded description of the local Keycloak
  // that was decommissioned on 2026-10-17.
  const [realm, setRealm] = React.useState(null);
  const [realmState, setRealmState] = React.useState('loading');
  React.useEffect(() => {
    fetch('/auth/realms/axon/.well-known/openid-configuration')
      .then(r => r.ok ? r.json() : Promise.reject(new Error(r.status)))
      .then(d => { setRealm(d); setRealmState('up'); })
      .catch(() => { setRealm(null); setRealmState('down'); });
  }, []);
  const realmMatch = realm && /^(https?:\/\/.+)\/realms\/([^/]+)$/.exec(realm.issuer || '');
  const realmName = realmMatch ? realmMatch[2] : null;
  const realmBadge = {loading:['#64748b','Checking…'], up:['#16a34a','Reachable'], down:['#dc2626','Unreachable']}[realmState];
  React.useEffect(() => {
    (async () => {
      try {
        const resp = await fetch('/api/auth/me', {credentials: 'include'});
        if (resp.ok) setMe(await resp.json());
      } catch (_) {}
      finally { setLoading(false); }
    })();
  }, []);
  return (
    <div>
      <SettingsPageHeader title="Security" subtitle="Identity provider (Keycloak) and access policy"/>
      <div className="card" style={{maxWidth:640}}>
        <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:18,padding:'12px 14px',background:'var(--surface2)',borderRadius:8,border:'1px solid var(--border)'}}>
          <div style={{width:36,height:36,borderRadius:8,background:'var(--brand-grad)',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:800,fontSize:13}}>KC</div>
          <div style={{flex:1}}>
            <div style={{fontWeight:600,fontSize:14}}>TaxLab Keycloak</div>
            <div className="text-sm text-muted">Central identity provider{realmName ? <> · realm <code>{realmName}</code></> : null}</div>
          </div>
          <span style={{padding:'2px 8px',borderRadius:12,background:realmBadge[0]+'22',color:realmBadge[0],fontSize:11,fontWeight:600}}>{realmBadge[1]}</span>
        </div>
        {[
          ['Issuer', realm ? realm.issuer : '—'],
          ['Token endpoint', realm ? realm.token_endpoint : '—'],
          ['JWKS', realm ? realm.jwks_uri : '—'],
          ['Console sign-in', 'axon-spa (public): the Axon form, or "Sign in with Keycloak" with code + PKCE'],
          ['API audience', 'axon-api: roles platform-admin / platform-developer / platform-viewer'],
          ['Gateway routes', 'openid-connect, bearer-only, against the same realm'],
        ].map(([k,v])=>(
          <div key={k} className="flex justify-between" style={{padding:'10px 0',borderBottom:'1px solid var(--border)',fontSize:13,gap:16}}>
            <span className="text-muted" style={{flexShrink:0}}>{k}</span>
            <span className="font-mono" style={{fontSize:12,textAlign:'right',wordBreak:'break-all'}}>{v}</span>
          </div>
        ))}
        <div style={{marginTop:'14px',padding:'10px 12px',background:'var(--surface2)',borderRadius:6,fontSize:12,color:'var(--text2)'}}>
          <strong>Access policy:</strong> /api/admin/** requires <code>platform-admin</code>. All other /api/** paths require an authenticated user (minimum <code>platform-viewer</code>). Webhooks (/api/webhooks/**) and the SPA shell are public.
        </div>
      </div>
      <div className="card" style={{maxWidth:640,marginTop:16}}>
        <div style={{fontWeight:600,marginBottom:10,fontSize:14}}>Current session</div>
        {loading ? <div className="text-sm text-muted">Checking session…</div>
         : me && me.authenticated ? (
          <div>
            <div style={{display:'flex',justifyContent:'space-between',padding:'6px 0',fontSize:13}}><span className="text-muted">User</span><span className="font-semibold">{me.username}</span></div>
            <div style={{display:'flex',justifyContent:'space-between',padding:'6px 0',fontSize:13,flexWrap:'wrap',gap:6}}>
              <span className="text-muted">Authorities</span>
              <span style={{fontSize:11}}>{(me.authorities||[]).map(a=>a.authority).join(', ')}</span>
            </div>
          </div>
         ) : (
          <div className="text-sm text-muted">No active session. Sign in via the user menu (top-right).</div>
         )}
      </div>
    </div>
  );
}

function SettingsKeycloak({showToast}){
  // Axon has no Keycloak of its own any more: people and every APISIX route are
  // authenticated by the central TaxLab realm. This page reads the realm through
  // Axon's /auth proxy and links out to its consoles -- it cannot embed them, because
  // central Keycloak sends X-Frame-Options: SAMEORIGIN / frame-ancestors 'self'.
  const [realm, setRealm] = React.useState(null);   // discovery document
  const [state, setState] = React.useState('loading'); // loading | up | down
  const load = React.useCallback(() => {
    setState('loading');
    fetch('/auth/realms/axon/.well-known/openid-configuration')
      .then(r => r.ok ? r.json() : Promise.reject(new Error(r.status)))
      .then(d => { setRealm(d); setState('up'); })
      .catch(() => { setRealm(null); setState('down'); });
  }, []);
  React.useEffect(load, [load]);

  const m = realm && /^(https?:\/\/.+)\/realms\/([^/]+)$/.exec(realm.issuer || '');
  const base = m ? m[1] : null, name = m ? m[2] : null;
  const links = base ? [
    {label:'Admin Console',   href:`${base}/admin/${name}/console/`, hint:'Users, groups, clients, roles and realm settings. Needs a realm admin account.'},
    {label:'Account Console', href:`${realm.issuer}/account/`,       hint:'Your own profile, password, MFA and sessions.'},
  ] : [];
  const badge = {loading:['#64748b','Checking…'], up:['#16a34a','Reachable'], down:['#dc2626','Unreachable']}[state];

  return (
    <div>
      <SettingsPageHeader title="Keycloak" subtitle="Central identity provider for people and API routes"/>

      <div className="card" style={{maxWidth:860,marginBottom:16}}>
        <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap',marginBottom:12}}>
          <div style={{width:30,height:30,borderRadius:8,background:'var(--brand-grad)',display:'flex',alignItems:'center',
                       justifyContent:'center',color:'#fff',fontWeight:800,fontSize:12,flexShrink:0}}>KC</div>
          <span style={{fontWeight:600,fontSize:14}}>TaxLab Keycloak</span>
          {name && <span className="text-sm text-muted">realm <code>{name}</code></span>}
          <span style={{padding:'2px 8px',borderRadius:12,background:badge[0]+'22',color:badge[0],fontSize:11,fontWeight:600}}>{badge[1]}</span>
          <div style={{flex:1}}/>
          <button className="btn btn-sm btn-ghost" onClick={load}>Re-check</button>
        </div>

        {state==='down' && (
          <div className="text-sm" style={{color:'#dc2626',marginBottom:12}}>
            Axon could not read the realm. Federated sign-in and the gateway's token checks depend on it;
            the local Axon account still works.
          </div>
        )}

        {realm && (
          <div style={{display:'grid',gridTemplateColumns:'auto 1fr',gap:'6px 16px',fontSize:13}}>
            <span className="text-muted">Issuer</span><span className="font-mono" style={{fontSize:12,wordBreak:'break-all'}}>{realm.issuer}</span>
            <span className="text-muted">Token</span><span className="font-mono" style={{fontSize:12,wordBreak:'break-all'}}>{realm.token_endpoint}</span>
            <span className="text-muted">JWKS</span><span className="font-mono" style={{fontSize:12,wordBreak:'break-all'}}>{realm.jwks_uri}</span>
            <span className="text-muted">Authorize</span><span className="font-mono" style={{fontSize:12,wordBreak:'break-all'}}>{realm.authorization_endpoint}</span>
          </div>
        )}
      </div>

      {links.length>0 && (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))',gap:12,maxWidth:860}}>
          {links.map(l => (
            <div key={l.label} className="card">
              <div style={{fontWeight:600,marginBottom:4}}>{l.label}</div>
              <div className="text-sm text-muted" style={{marginBottom:12}}>{l.hint}</div>
              <a href={l.href} target="_blank" rel="noreferrer" className="btn btn-sm btn-primary">Open in new tab ↗</a>
            </div>
          ))}
        </div>
      )}

      <div className="text-sm text-muted" style={{marginTop:16,maxWidth:860}}>
        Realm configuration is managed as code in <code>taxlab-identity</code>; changes made in the console
        are not written back there.
      </div>
    </div>
  );
}

function SettingsNotifications({showToast}){
  return (
    <div>
      <SettingsPageHeader title="Notifications" subtitle="Where and when the platform pings on-call"/>
      <div className="card" style={{maxWidth:600}}>
        <div className="form-group"><label className="form-label">Alert Email</label><input className="form-input" type="email" placeholder="ops@company.com"/></div>
        <div className="form-group"><label className="form-label">Slack Webhook URL</label><input className="form-input" placeholder="https://hooks.slack.com/services/..."/></div>
        <div className="form-group"><label className="form-label">Notify on</label>
          <div style={{display:'grid',gap:8,marginTop:6}}>
            {['Integration Failure','Integration Success','Connection Error','Execution Timeout'].map(n=>(
              <label key={n} className="flex items-center gap-3 text-sm" style={{cursor:'pointer'}}>
                <input type="checkbox" defaultChecked={n.includes('Failure')||n.includes('Error')}/>
                {n}
              </label>
            ))}
          </div>
        </div>
        <button className="btn btn-primary" onClick={()=>showToast('Notification settings saved','success')}><SaveActionIcon/> Save Changes</button>
      </div>
    </div>
  );
}

function SettingsAbout(){
  return (
    <div>
      <SettingsPageHeader title="About" subtitle="Build and runtime information"/>
      <div className="card" style={{maxWidth:600}}>
        <div style={{display:'flex',alignItems:'center',gap:14,marginBottom:20}}>
          <div style={{width:52,height:52,borderRadius:12,background:'var(--brand-grad)',display:'flex',alignItems:'center',justifyContent:'center',boxShadow:'0 3px 14px rgba(255,86,48,.35)'}}>
            <span style={{fontSize:20,fontWeight:900,color:'#fff',letterSpacing:'-1px'}}>7L</span>
          </div>
          <div>
            <div style={{fontWeight:700,fontSize:16}}>Axon</div>
            <div className="text-sm text-muted">Integration Platform</div>
          </div>
        </div>
        {[
          ['Version','2.0.0'],['Runtime','Axon 1.0.0'],
          ['Java','26'],['License','Commercial'],
          ['Support','support@taxlab.ai'],['Documentation','docs.taxlab.ai'],
        ].map(([k,v])=>(
          <div key={k} className="flex justify-between" style={{padding:'9px 0',borderBottom:'1px solid var(--border)',fontSize:13}}>
            <span className="text-muted">{k}</span><span className="font-semibold">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ===== FUTURE RUNS PAGE ===== */
function FutureRunsPage({showToast, goToIntegrations}){
  const [data,setData]=useState(null);
  const [loading,setLoading]=useState(true);
  const [windowH,setWindowH]=useState(24);
  const [maxPer,setMaxPer]=useState(60);
  const [lastRefresh,setLastRefresh]=useState(null);
  const [filterIntegration,setFilterIntegration]=useState('');
  const [filterProject,setFilterProject]=useState('');
  const [collisionThreshold,setCollisionThreshold]=useState(2);

  const load=useCallback(async(silent=false)=>{
    if(!silent) setLoading(true);
    try{
      const d=await API('GET',`/api/scheduled/upcoming?withinHours=${windowH}&maxPerIntegration=${maxPer}`);
      setData(d); setLastRefresh(new Date());
    }catch(e){ if(!silent) showToast('Failed to load schedule: '+e.message,'error'); }
    finally{ setLoading(false); }
  },[windowH,maxPer,showToast]);

  useEffect(()=>{load(false);},[load]);
  useEffect(()=>{
    const t=setInterval(()=>load(true), 30000);
    return()=>clearInterval(t);
  },[load]);

  if(loading||!data) return (
    <div>
      <div className="page-header"><div><h1 className="page-title">Future Runs</h1><div className="page-subtitle">Loading scheduled integration timeline…</div></div></div>
      <div className="card text-muted text-sm">Loading…</div>
    </div>
  );

  const runs=data.runs||[];
  const schedules=data.schedules||[];
  const density=data.density||[];

  // Build distinct lists for filters
  const projectIds=[...new Set(schedules.map(s=>s.projectId).filter(Boolean))];
  const filteredRuns=runs.filter(r=>{
    if(filterIntegration && r.integrationId!==filterIntegration) return false;
    if(filterProject && r.projectId!==filterProject) return false;
    return true;
  });
  const filteredSchedules=schedules.filter(s=>{
    if(filterIntegration && s.integrationId!==filterIntegration) return false;
    if(filterProject && s.projectId!==filterProject) return false;
    return true;
  });

  const collisions=density.filter(d=>d.count>=collisionThreshold);
  const peak=density.reduce((m,d)=>Math.max(m,d.count),0);
  const totalFires=filteredRuns.length;
  const range={from:data.fromEpochMs, to:data.toEpochMs};

  return(
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Future Runs</h1>
          <div className="page-subtitle">
            Timeline of all ACTIVE scheduled integrations · spot collisions before they hurt
            {lastRefresh&&<span style={{marginLeft:10,fontSize:11,color:'var(--text3)'}}>· Updated {lastRefresh.toLocaleTimeString()}</span>}
          </div>
        </div>
        <button className="btn btn-refresh" style={TAXLAB_LIGHT_STYLE} onClick={()=>load(false)}><BuilderRefreshIcon size={18}/> Refresh</button>
      </div>

      {/* Toolbar */}
      <div className="card" style={{marginBottom:16,padding:'12px 16px',display:'flex',gap:12,alignItems:'center',flexWrap:'wrap'}}>
        <label className="text-xs" style={{color:'var(--text3)',fontWeight:600,textTransform:'uppercase',letterSpacing:'.6px'}}>Window</label>
        <div style={{display:'flex',background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:8,padding:3,gap:2}}>
          {[[1,'1h'],[6,'6h'],[24,'24h'],[72,'3d'],[168,'7d']].map(([h,l])=>(
            <button key={h} onClick={()=>setWindowH(h)}
              style={{padding:'4px 12px',borderRadius:6,border:'none',cursor:'pointer',fontSize:12,fontWeight:600,
                background:windowH===h?'var(--brand-start)':'none',
                color:windowH===h?'#fff':'var(--text3)'}}>{l}</button>
          ))}
        </div>
        <select className="form-select" style={{maxWidth:220,fontSize:13}} value={filterIntegration} onChange={e=>setFilterIntegration(e.target.value)}>
          <option value="">All Integrations ({schedules.length})</option>
          {schedules.map(s=><option key={s.integrationId} value={s.integrationId}>{s.integrationName}</option>)}
        </select>
        {projectIds.length>0&&(
          <select className="form-select" style={{maxWidth:200,fontSize:13}} value={filterProject} onChange={e=>setFilterProject(e.target.value)}>
            <option value="">All Projects</option>
            {projectIds.map(p=><option key={p} value={p}>{p.slice(0,8)}…</option>)}
          </select>
        )}
        <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12.5,color:'var(--text2)'}}>
          Collision threshold ≥
          <input type="number" min="2" max="20" value={collisionThreshold}
            onChange={e=>setCollisionThreshold(Math.max(2,parseInt(e.target.value)||2))}
            style={{width:55,padding:'4px 8px',fontSize:12.5,border:'1px solid var(--border)',borderRadius:6,background:'var(--surface2)'}}/>
        </label>
        <span style={{marginLeft:'auto',fontSize:12,color:'var(--text3)'}}>
          {filteredSchedules.length} scheduled · {totalFires} firings · peak/min {peak} · <b style={{color:collisions.length>0?'var(--error)':'var(--text2)'}}>{collisions.length} collision{collisions.length===1?'':'s'}</b>
        </span>
      </div>

      {/* Density chart */}
      <div className="card" style={{marginBottom:16}}>
        <div className="section-title">Density · firings per minute</div>
        {density.length===0
          ? <div className="text-sm text-muted" style={{padding:'14px 0',textAlign:'center'}}>No scheduled firings in the next {windowH}h.</div>
          : <DensityChart density={density} range={range} threshold={collisionThreshold} filterIntegration={filterIntegration} filterProject={filterProject} schedules={schedules} runs={filteredRuns}/>
        }
      </div>

      {/* Per-integration timeline */}
      <div className="card" style={{marginBottom:16}}>
        <div className="section-title">Per-integration timeline</div>
        {filteredSchedules.length===0
          ? <div className="text-sm text-muted" style={{padding:'14px 0',textAlign:'center'}}>No active scheduled integrations match the filter.</div>
          : <PerIntegrationTimeline schedules={filteredSchedules} runs={filteredRuns} range={range} goToIntegrations={goToIntegrations}/>
        }
      </div>

      {/* Collisions */}
      {collisions.length>0&&(
        <div className="card" style={{marginBottom:16,borderLeft:'3px solid var(--error)'}}>
          <div className="section-title" style={{color:'var(--error)',display:'flex',alignItems:'center',gap:8}}><WarningIcon size={15}/> Collision windows</div>
          <div className="text-sm text-muted" style={{marginBottom:8}}>Minutes where {collisionThreshold}+ integrations fire simultaneously.</div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Time (UTC)</th><th>Concurrent</th><th>Integrations</th></tr></thead>
              <tbody>
                {collisions.map(c=>(
                  <tr key={c.bucketStartMs}>
                    <td style={{fontFamily:'ui-monospace,monospace',fontSize:12}}>{new Date(c.bucketStartMs).toISOString().replace('T',' ').slice(0,16)}</td>
                    <td><span style={{padding:'2px 8px',borderRadius:12,background:'rgba(239,68,68,.12)',color:'#dc2626',fontWeight:700,fontSize:12}}>{c.count}×</span></td>
                    <td style={{fontSize:12.5}}>{(c.integrations||[]).join(' · ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Upcoming list */}
      <div className="card">
        <div className="section-title">Next firings (sorted)</div>
        {filteredRuns.length===0
          ? <div className="text-sm text-muted" style={{padding:'14px 0',textAlign:'center'}}>No upcoming firings in the selected window.</div>
          : <UpcomingList runs={filteredRuns.slice(0,200)} goToIntegrations={goToIntegrations}/>
        }
      </div>
    </div>
  );
}

function DensityChart({density,range,threshold,filterIntegration,filterProject,schedules,runs}){
  // Build per-minute bars across the entire range (60min × hours).
  const totalMinutes=Math.max(1,Math.round((range.to-range.from)/60000));
  const max=density.reduce((m,d)=>Math.max(m,d.count),1);
  // Build sparse map from bucket → count for filtered runs
  const filteredDensity={};
  runs.forEach(r=>{
    const b=Math.floor(r.fireAtEpochMs/60000)*60000;
    filteredDensity[b]=(filteredDensity[b]||0)+1;
  });
  const buckets=Object.entries(filteredDensity).map(([k,v])=>({ms:+k,count:v})).sort((a,b)=>a.ms-b.ms);

  // For wide windows, downsample to a fixed visual width
  const targetBars=Math.min(180,Math.max(60,totalMinutes));
  const stepMs=Math.ceil((range.to-range.from)/targetBars/60000)*60000;
  const visBuckets=[];
  for(let t=range.from;t<range.to;t+=stepMs){
    let count=0; const names=new Set();
    runs.forEach(r=>{ if(r.fireAtEpochMs>=t && r.fireAtEpochMs<t+stepMs){ count++; names.add(r.integrationName); }});
    visBuckets.push({ms:t, count, integrations:[...names]});
  }
  const visMax=visBuckets.reduce((m,b)=>Math.max(m,b.count),1);

  return(
    <div>
      <div style={{display:'flex',alignItems:'flex-end',gap:1,height:120,padding:'8px 0',background:'linear-gradient(to top,var(--surface3) 0,transparent 1px) 0 100%/100% 30px no-repeat'}}>
        {visBuckets.map((b,i)=>{
          const h=Math.max(2,Math.round(b.count/visMax*108));
          const colliding=b.count>=threshold;
          const tip=`${new Date(b.ms).toISOString().slice(11,16)} UTC — ${b.count} fire(s)${b.integrations.length?'\n'+b.integrations.slice(0,8).join('\n')+(b.integrations.length>8?'\n…':''):''}`;
          return(
            <div key={i} title={tip} style={{flex:1,height:h,minWidth:2,
              background:colliding?'var(--error)':'var(--accent)',
              opacity:b.count===0?0.08:colliding?0.85:0.55,
              borderRadius:'2px 2px 0 0',cursor:b.count?'help':'default',transition:'opacity .15s'}}/>
          );
        })}
      </div>
      <div style={{display:'flex',justifyContent:'space-between',fontSize:10.5,color:'var(--text3)',fontFamily:'ui-monospace,monospace',marginTop:6}}>
        <span>{new Date(range.from).toISOString().slice(0,16).replace('T',' ')}</span>
        <span>{new Date((range.from+range.to)/2).toISOString().slice(0,16).replace('T',' ')}</span>
        <span>{new Date(range.to).toISOString().slice(0,16).replace('T',' ')}</span>
      </div>
      <div style={{display:'flex',gap:14,marginTop:8,fontSize:11,color:'var(--text3)'}}>
        <span><span style={{display:'inline-block',width:10,height:10,borderRadius:2,background:'var(--accent)',opacity:0.55,marginRight:4,verticalAlign:'middle'}}/>normal</span>
        <span><span style={{display:'inline-block',width:10,height:10,borderRadius:2,background:'var(--error)',opacity:0.85,marginRight:4,verticalAlign:'middle'}}/>collision (≥{threshold} concurrent)</span>
      </div>
    </div>
  );
}

function PerIntegrationTimeline({schedules,runs,range,goToIntegrations}){
  // For each integration, render a row with markers at firing times.
  const span=range.to-range.from;
  return(
    <div style={{display:'flex',flexDirection:'column',gap:8}}>
      {schedules.map(s=>{
        const myRuns=runs.filter(r=>r.integrationId===s.integrationId);
        const dotsTip=myRuns.slice(0,12).map(r=>new Date(r.fireAtEpochMs).toISOString().slice(11,16)).join(', ');
        return(
          <div key={s.integrationId} style={{display:'grid',gridTemplateColumns:'200px 1fr 80px',alignItems:'center',gap:10}}>
            <div style={{minWidth:0}}>
              <div onClick={()=>goToIntegrations&&goToIntegrations({integrationId:s.integrationId})}
                style={{fontWeight:600,fontSize:12.5,color:'var(--accent)',cursor:'pointer',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                {s.integrationName}
              </div>
              <div style={{fontSize:10.5,color:'var(--text3)',fontFamily:'ui-monospace,monospace',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{s.description}</div>
            </div>
            <div style={{position:'relative',height:18,background:'var(--surface3)',borderRadius:9,overflow:'hidden',border:'1px solid var(--border)'}}>
              {myRuns.map((r,i)=>{
                const pct=((r.fireAtEpochMs-range.from)/span)*100;
                if(pct<0||pct>100) return null;
                return(
                  <div key={i} title={new Date(r.fireAtEpochMs).toISOString()}
                    style={{position:'absolute',left:`${pct}%`,top:0,bottom:0,width:3,marginLeft:-1.5,background:s.kind==='cron'?'#7c3aed':'var(--accent)',borderRadius:2}}/>
                );
              })}
            </div>
            <div style={{textAlign:'right',fontSize:11,color:'var(--text3)',fontFamily:'ui-monospace,monospace'}}>{myRuns.length} fire{myRuns.length===1?'':'s'}</div>
          </div>
        );
      })}
    </div>
  );
}

function UpcomingList({runs,goToIntegrations}){
  return(
    <div className="table-wrap">
      <table>
        <thead><tr><th>Fire at (UTC)</th><th>In</th><th>Integration</th><th>Schedule</th></tr></thead>
        <tbody>
          {runs.map((r,i)=>{
            const inMs=r.fireAtEpochMs-Date.now();
            const inLabel=inMs<60000?'<1m':inMs<3600000?Math.round(inMs/60000)+'m':Math.round(inMs/3600000*10)/10+'h';
            return(
              <tr key={i}>
                <td style={{fontFamily:'ui-monospace,monospace',fontSize:12}}>{new Date(r.fireAtEpochMs).toISOString().replace('T',' ').slice(0,19)}</td>
                <td style={{fontSize:12.5,color:'var(--text2)'}}>{inMs<0?'now':'in '+inLabel}</td>
                <td>
                  <span onClick={()=>goToIntegrations&&goToIntegrations({integrationId:r.integrationId})}
                    style={{cursor:'pointer',color:'var(--accent)',fontWeight:600,fontSize:13}}>{r.integrationName}</span>
                </td>
                <td style={{fontSize:11.5,color:'var(--text3)',fontFamily:'ui-monospace,monospace'}}>
                  <span style={{padding:'1px 6px',borderRadius:8,background:r.scheduleKind==='cron'?'rgba(124,58,237,.1)':'rgba(99,102,241,.1)',color:r.scheduleKind==='cron'?'#7c3aed':'var(--accent)',marginRight:6,fontWeight:600,textTransform:'uppercase',fontSize:9}}>{r.scheduleKind}</span>
                  {r.scheduleDescription}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ===== AUDIT LOG VIEWER ===== */
function AuditSettings({showToast}){
  const [rows,setRows]=useState([]);
  const [filterType,setFilterType]=useState('');
  const [filterAction,setFilterAction]=useState('');
  const [filterEntityId,setFilterEntityId]=useState('');
  const [loading,setLoading]=useState(true);
  const [detail,setDetail]=useState(null); // expanded row's full payload

  const load=useCallback(async()=>{
    setLoading(true);
    try{
      const params=new URLSearchParams({limit:'200'});
      if(filterType) params.set('entityType',filterType);
      if(filterAction) params.set('action',filterAction);
      if(filterEntityId) params.set('entityId',filterEntityId);
      const d=await API('GET','/api/admin/audit?'+params.toString());
      setRows(Array.isArray(d)?d:[]);
    }catch(e){ showToast('Could not load audit log: '+e.message,'error'); }
    finally{ setLoading(false); }
  },[filterType,filterAction,filterEntityId,showToast]);
  useEffect(()=>{load();},[load]);

  const openDetail=async(id)=>{
    try{ const d=await API('GET','/api/admin/audit/'+id); setDetail(d); }
    catch(e){ showToast('Detail load failed: '+e.message,'error'); }
  };

  const colorFor=(action)=>{
    if(!action) return 'var(--text3)';
    if(action.startsWith('CREATE')) return '#16a34a';
    if(action.startsWith('UPDATE')) return '#0891b2';
    if(action.startsWith('DELETE')) return '#dc2626';
    if(action.startsWith('ACTIVATE')) return '#7c3aed';
    if(action.startsWith('DEACTIVATE')) return '#d97706';
    if(action.startsWith('HEALTH_ERROR')) return '#dc2626';
    if(action.startsWith('HEALTH_ACTIVE')) return '#16a34a';
    return 'var(--text2)';
  };

  return(
    <div style={{display:'flex',flexDirection:'column',gap:14}}>
      <div className="page-header" style={{marginBottom:0}}><div><h1 className="page-title">Audit Log</h1><div className="page-subtitle">Who changed what, and when</div></div></div>
      <div className="card">
        <div className="section-title">Entries</div>
        <div className="text-xs text-muted" style={{marginBottom:10}}>
          Every CRUD on connections / integrations + activate/deactivate/health-check transitions are recorded. Useful for change tracking, post-incident forensics, and compliance.
        </div>
        <div style={{display:'flex',gap:10,marginBottom:12,flexWrap:'wrap'}}>
          <select className="form-select" style={{maxWidth:160,fontSize:13}} value={filterType} onChange={e=>setFilterType(e.target.value)}>
            <option value="">All entities</option>
            <option value="integration">Integration</option>
            <option value="connection">Connection</option>
            <option value="project">Project</option>
          </select>
          <select className="form-select" style={{maxWidth:180,fontSize:13}} value={filterAction} onChange={e=>setFilterAction(e.target.value)}>
            <option value="">All actions</option>
            <option value="CREATE">Create</option>
            <option value="UPDATE">Update</option>
            <option value="DELETE">Delete</option>
            <option value="ACTIVATE">Activate</option>
            <option value="ACTIVATE_FAIL">Activate (failed)</option>
            <option value="DEACTIVATE">Deactivate</option>
            <option value="HEALTH_ACTIVE">Health → ACTIVE</option>
            <option value="HEALTH_ERROR">Health → ERROR</option>
          </select>
          <input className="form-input" style={{maxWidth:240,fontSize:13}} placeholder="Filter by entity id…"
            value={filterEntityId} onChange={e=>setFilterEntityId(e.target.value)}/>
          <button className="btn btn-refresh" style={TAXLAB_LIGHT_STYLE} onClick={load}><BuilderRefreshIcon size={18}/> Refresh</button>
          <span style={{marginLeft:'auto',fontSize:12,color:'var(--text3)'}}>{rows.length} rows</span>
        </div>
        {loading?<div className="text-muted text-sm" style={{padding:14,textAlign:'center'}}>Loading…</div>:
         rows.length===0?<div className="empty-state"><EmptyStateIcon tone="slate"><AuditTrailIcon/></EmptyStateIcon><p>No audit entries match the filter</p></div>:
          <div className="table-wrap">
            <table>
              <thead><tr><th>Time</th><th>Actor</th><th>Action</th><th>Entity</th><th>Name</th><th>Detail</th><th></th></tr></thead>
              <tbody>
                {rows.map(r=>(
                  <tr key={r.id}>
                    <td className="text-xs text-muted" style={{fontFamily:'ui-monospace,monospace',whiteSpace:'nowrap'}}>{fmtDate(r.ts)}</td>
                    <td className="text-xs" style={{fontWeight:600}}>{r.actor||'system'}</td>
                    <td><span style={{padding:'2px 8px',borderRadius:10,fontSize:10.5,fontWeight:700,background:colorFor(r.action)+'18',color:colorFor(r.action),border:'1px solid '+colorFor(r.action)+'44'}}>{r.action}</span></td>
                    <td className="text-xs" style={{color:'var(--text2)'}}>{r.entityType||'—'}</td>
                    <td style={{fontSize:12.5,fontWeight:600,maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.entityName||(r.entityId||'').slice(0,8)||'—'}</td>
                    <td style={{fontSize:11.5,color:'var(--text3)',maxWidth:280,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.detail||'—'}</td>
                    <td><button className="btn btn-ghost btn-xs" onClick={()=>openDetail(r.id)}>View</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        }
      </div>

      {detail&&(
        <div onClick={()=>setDetail(null)}
             style={{position:'fixed',inset:0,background:'rgba(15,17,35,.55)',backdropFilter:'blur(3px)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:20}}>
          <div onClick={ev=>ev.stopPropagation()}
               style={{background:'var(--surface)',borderRadius:14,width:'100%',maxWidth:820,maxHeight:'90vh',display:'flex',flexDirection:'column',boxShadow:'var(--shadow-lg)',border:'1px solid var(--border)',overflow:'hidden'}}>
            <div style={{padding:'14px 22px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:12}}>
              <span style={{fontSize:20,display:'inline-flex'}}><AuditTrailIcon size={20} color="var(--accent)"/></span>
              <div style={{flex:1}}>
                <div style={{fontWeight:800,fontSize:15}}>Audit entry</div>
                <div style={{fontSize:11,color:'var(--text3)',fontFamily:'ui-monospace,monospace'}}>{detail.id}</div>
              </div>
              <button onClick={()=>setDetail(null)} className="modal-close-btn" aria-label="Close"><CloseIcon size={16}/></button>
            </div>
            <div style={{flex:1,overflow:'auto',padding:'14px 22px',display:'flex',flexDirection:'column',gap:12}}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,fontSize:12.5}}>
                <div><div className="text-xs text-muted">Time</div><div style={{fontFamily:'ui-monospace,monospace'}}>{detail.ts}</div></div>
                <div><div className="text-xs text-muted">Actor</div><div>{detail.actor}</div></div>
                <div><div className="text-xs text-muted">Action</div><div style={{color:colorFor(detail.action),fontWeight:700}}>{detail.action}</div></div>
                <div><div className="text-xs text-muted">Entity</div><div>{detail.entityType} · {detail.entityName||(detail.entityId||'').slice(0,8)}</div></div>
              </div>
              {detail.detail&&<div><div className="text-xs text-muted" style={{marginBottom:3}}>Detail</div><pre style={{margin:0,padding:'8px 10px',background:'var(--surface3)',borderRadius:6,fontSize:11.5,fontFamily:'ui-monospace,monospace',whiteSpace:'pre-wrap',wordBreak:'break-all'}}>{detail.detail}</pre></div>}
              {detail.beforeJson&&<div><div className="text-xs text-muted" style={{marginBottom:3}}>Before</div><pre style={{margin:0,padding:'8px 10px',background:'var(--surface3)',borderRadius:6,fontSize:11,fontFamily:'ui-monospace,monospace',whiteSpace:'pre-wrap',maxHeight:220,overflow:'auto'}}>{(()=>{try{return JSON.stringify(JSON.parse(detail.beforeJson),null,2);}catch{return detail.beforeJson;}})()}</pre></div>}
              {detail.afterJson&&<div><div className="text-xs text-muted" style={{marginBottom:3}}>After</div><pre style={{margin:0,padding:'8px 10px',background:'var(--surface3)',borderRadius:6,fontSize:11,fontFamily:'ui-monospace,monospace',whiteSpace:'pre-wrap',maxHeight:220,overflow:'auto'}}>{(()=>{try{return JSON.stringify(JSON.parse(detail.afterJson),null,2);}catch{return detail.afterJson;}})()}</pre></div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ===== LOGGING SETTINGS ===== */
function LoggingSettings({showToast}){
  const [s,setS]=useState(null);
  const [levels,setLevels]=useState(null);
  const [busy,setBusy]=useState(false);
  const [running,setRunning]=useState(false);
  const LEVELS=['OFF','ERROR','WARN','INFO','DEBUG','TRACE'];

  const load=useCallback(async()=>{
    try{
      const [a,b]=await loadAll([
        ['Log settings',API('GET','/api/admin/log/settings'),null],
        ['Log levels',API('GET','/api/admin/log/levels'),[]],
      ],showToast);
      if(a) setS(a);
      setLevels(b);
    }catch(e){showToast('Could not load logging settings: '+e.message,'error');}
  },[showToast]);
  useEffect(()=>{load();},[load]);
  useEffect(()=>{
    const t=setInterval(load,5000); return()=>clearInterval(t);
  },[load]);

  const save=async()=>{
    setBusy(true);
    try{
      const r=await API('POST','/api/admin/log/settings',{
        retentionDays:s.retentionDays,
        archivalEnabled:s.archivalEnabled,
        globalLevel:s.globalLevel,
        bodyCaptureEnabled:s.bodyCaptureEnabled,
        bodyMaxChars:s.bodyMaxChars,
      });
      setS(r);
      showToast('Logging settings saved','success');
    }catch(e){showToast('Save failed: '+e.message,'error');}
    finally{setBusy(false);}
  };

  const runRetention=async()=>{
    setRunning(true);
    try{
      const r=await API('POST','/api/admin/log/retention/run');
      if(r.ok) showToast(`Retention run · archived ${r.archived} · purged ${r.purged}`,'success');
      else showToast('Retention failed: '+(r.error||'unknown'),'error');
      load();
    }catch(e){showToast('Retention call failed: '+e.message,'error');}
    finally{setRunning(false);}
  };

  if(!s||!levels) return <div className="card text-muted text-sm" style={{maxWidth:760}}>Loading…</div>;
  const h=s.health||{};

  return(
    <div style={{maxWidth:760,display:'flex',flexDirection:'column',gap:18}}>
      <SettingsPageHeader title="Logging" subtitle="Execution-log retention, body capture and log levels"/>
      <div className="card">
        <div className="section-title">Storage & Retention</div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
          <div className="form-group" style={{margin:0}}>
            <label className="form-label">Retention (days)</label>
            <input className="form-input" type="number" min="1" max="3650" value={s.retentionDays}
              onChange={e=>setS({...s,retentionDays:parseInt(e.target.value)||1})}/>
            <div className="text-xs text-muted" style={{marginTop:4}}>Logs older than this are archived/purged by the nightly job.</div>
          </div>
          <div className="form-group" style={{margin:0}}>
            <label className="form-label">Body capture max chars</label>
            <input className="form-input" type="number" min="0" max="65536" value={s.bodyMaxChars}
              onChange={e=>setS({...s,bodyMaxChars:parseInt(e.target.value)||0})}/>
            <div className="text-xs text-muted" style={{marginTop:4}}>Truncate input/output snapshots above this length.</div>
          </div>
          <label className="flex items-center gap-2" style={{cursor:'pointer',fontSize:13}}>
            <input type="checkbox" checked={!!s.archivalEnabled} onChange={e=>setS({...s,archivalEnabled:e.target.checked})}/>
            Archive purged rows to gzipped JSONL
          </label>
          <label className="flex items-center gap-2" style={{cursor:'pointer',fontSize:13}}>
            <input type="checkbox" checked={!!s.bodyCaptureEnabled} onChange={e=>setS({...s,bodyCaptureEnabled:e.target.checked})}/>
            Capture body snapshots (DEBUG / TRACE only)
          </label>
        </div>
        <div className="text-xs text-muted" style={{marginTop:10,fontFamily:'ui-monospace,monospace'}}>
          DB: {s.dbPath} · Archive dir: {s.archivalDir}
        </div>
      </div>

      <div className="card">
        <div className="section-title">Default Log Level</div>
        <div className="form-group" style={{margin:0}}>
          <label className="form-label">Global level</label>
          <select className="form-select" value={s.globalLevel}
            onChange={e=>setS({...s,globalLevel:e.target.value})}>
            {LEVELS.map(l=><option key={l} value={l}>{l}</option>)}
          </select>
          <div className="text-xs text-muted" style={{marginTop:6,lineHeight:1.6}}>
            <b>OFF/ERROR/WARN</b> persist only failed exchanges · <b>INFO</b> persists every exchange (no body) · <b>DEBUG</b> includes a body snapshot · <b>TRACE</b> captures every step processor's input/output.
            Per-integration overrides are set on each integration row.
          </div>
        </div>
      </div>

      {Object.keys(levels.overrides||{}).length>0&&(
        <div className="card">
          <div className="section-title">Active Overrides</div>
          <div style={{display:'grid',gap:6}}>
            {Object.entries(levels.overrides).map(([k,v])=>(
              <div key={k} className="flex justify-between" style={{padding:'6px 10px',background:'var(--surface3)',borderRadius:6,fontSize:12.5}}>
                <span style={{fontFamily:'ui-monospace,monospace'}}>{k}</span>
                <span className="font-semibold" style={{color:'var(--accent)'}}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        <div className="section-title">Writer Health</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(120px,1fr))',gap:10,fontSize:12.5}}>
          {[
            ['Rows in DB',h.rowsInDb],
            ['Queue size',h.queueSize],
            ['Enqueued',h.enqueued],
            ['Written',h.rowsWritten],
            ['Dropped',h.dropped||0],
          ].map(([k,v])=>(
            <div key={k} style={{padding:'8px 12px',background:'var(--surface3)',borderRadius:8,border:'1px solid var(--border)'}}>
              <div className="text-xs text-muted">{k}</div>
              <div style={{fontWeight:700,fontSize:18,color:k==='Dropped'&&v>0?'var(--error)':'var(--text)'}}>{v||'—'}</div>
            </div>
          ))}
        </div>
        <div className="text-xs text-muted" style={{marginTop:8}}>Auto-refreshes every 5s.</div>
      </div>

      <div style={{display:'flex',gap:10}}>
        <button className="btn btn-primary" onClick={save} disabled={busy}>{busy?<><Spinner/> Saving…</>:<><SaveActionIcon/> Save Changes</>}</button>
        <button className="btn btn-secondary" onClick={runRetention} disabled={running} title="Manually trigger archive + purge">
          {running?<><Spinner/> Running…</>:<><TrashIcon size={15}/> Run Retention Now</>}
        </button>
      </div>
    </div>
  );
}

/* Builder + YAML editor extracted to builder.js � included via <script> tag in <body> below this main script. */
/* ===== PROJECTS PAGE ===== */
function ProjectsPage({showToast,goToIntegrations,confirm}){
  const [projects,setProjects]=useState([]);
  const [allIntegrations,setAllIntegrations]=useState([]);
  const [loading,setLoading]=useState(true);
  const [showModal,setShowModal]=useState(false);
  const [editing,setEditing]=useState(null);
  const [expanded,setExpanded]=useState({});           // {projectId: true}

  const load=useCallback(async()=>{
    try{
      setLoading(true);
      const [ps,is]=await loadAll([
        ['Projects',API('GET','/api/projects'),[]],
        ['Integrations',API('GET','/api/integrations'),[]],
      ],showToast);
      setProjects(ps);
      setAllIntegrations(Array.isArray(is)?is:[]);
    }
    catch(e){showToast('Failed to load projects','error');}
    finally{setLoading(false);}
  },[showToast]);
  useEffect(()=>{load();},[load]);

  const integrationsFor=useCallback((projectId)=>(
    allIntegrations.filter(i=>i.projectId===projectId)
  ),[allIntegrations]);

  const toggleExpand=(p)=>setExpanded(prev=>({...prev,[p.id]:!prev[p.id]}));

  const del=async(p)=>{
    const _c=await confirm(`Delete project "${p.name}"?
All integrations must be reassigned first.`);if(!_c)return;
    try{await API('DELETE',`/api/projects/${p.id}`);showToast('Deleted','success');load();}
    catch(e){showToast(e.message,'error');}
  };

  return(
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Projects</h1>
          <div className="page-subtitle">Group integrations into logical workspaces · each project maps to a folder on disk</div>
        </div>
        <button className="btn btn-primary" style={TAXLAB_CTA_STYLE} onClick={()=>{setEditing(null);setShowModal(true);}}><AddCircleIcon/> New Project</button>
      </div>
      {loading?<div className="text-muted text-sm">Loading…</div>:
       projects.length===0?(
        <div className="empty-state">
          <EmptyStateIcon tone="indigo"><FolderStackIcon/></EmptyStateIcon>
          <p style={{marginBottom:16}}>No projects yet. Create one to group your integrations.</p>
          <button className="btn btn-primary" onClick={()=>{setEditing(null);setShowModal(true);}}><AddCircleIcon/> New Project</button>
        </div>
       ):(
        <div style={{display:'flex',flexDirection:'column',gap:14,marginTop:4}}>
          {projects.map(p=>{
            const isOpen=!!expanded[p.id];
            const projectIntegrations=integrationsFor(p.id);
            const activeCount=projectIntegrations.filter(i=>i.status==='ACTIVE').length;
            return(
              <div key={p.id} className="card" style={{borderLeft:`4px solid ${p.color||'#6366f1'}`,padding:0,overflow:'hidden'}}>
                {/* Header */}
                <div onClick={()=>toggleExpand(p)} style={{display:'flex',alignItems:'center',gap:14,padding:'16px 20px',cursor:'pointer',userSelect:'none'}}>
                  <span style={{fontSize:11,color:'var(--text3)',transition:'transform .2s',display:'inline-block',transform:isOpen?'rotate(90deg)':'rotate(0deg)',width:10,flexShrink:0}}>▶</span>
                  <div style={{width:38,height:38,borderRadius:10,background:(p.color||'#6366f1')+'22',border:`1.5px solid ${p.color||'#6366f1'}44`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,fontSize:18}}><FolderStackIcon size={18} color={p.color||'#6366f1'}/></div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                      <span style={{fontWeight:700,fontSize:15,color:'var(--text)'}}>{p.name}</span>
                      <span style={{fontSize:11,fontFamily:'ui-monospace,monospace',color:'var(--text3)'}}>/{p.slug}</span>
                      <span style={{fontSize:11,fontWeight:600,color:'var(--text2)',background:'var(--surface3)',border:'1px solid var(--border)',borderRadius:12,padding:'1px 8px'}}>
                        {projectIntegrations.length} integration{projectIntegrations.length!==1?'s':''}
                      </span>
                      {activeCount>0&&<span style={{fontSize:10,fontWeight:700,color:'#16a34a',background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:12,padding:'1px 7px'}}>● {activeCount} LIVE</span>}
                    </div>
                    {p.description&&<div style={{fontSize:12.5,color:'var(--text2)',marginTop:4,lineHeight:1.45}}>{p.description}</div>}
                  </div>
                  <div style={{display:'flex',gap:8,flexShrink:0,alignItems:'center'}} onClick={ev=>ev.stopPropagation()}>
                    <button className="int-row-btn int-row-btn-light" onClick={()=>goToIntegrations&&goToIntegrations({projectId:p.id})} title="Open in Integrations"><IntegrationFlowIcon size={17}/> Open</button>
                    <button className="btn btn-ghost row-icon-action" onClick={()=>{setEditing(p);setShowModal(true);}} title="Edit" aria-label="Edit"><PencilIcon size={17}/></button>
                    <button className="btn btn-danger row-icon-action" onClick={()=>del(p)} title="Delete" aria-label="Delete"><TrashIcon size={17}/></button>
                  </div>
                </div>

                {/* Expanded integrations list */}
                {isOpen&&(
                  <div style={{borderTop:'1px solid var(--border)',background:'var(--surface)'}}>
                    {projectIntegrations.length===0?(
                      <div style={{padding:'14px 20px',fontSize:12.5,color:'var(--text3)',display:'flex',alignItems:'center',gap:10}}>
                        <span>No integrations assigned to this project yet.</span>
                        <button className="btn btn-ghost btn-xs" onClick={()=>goToIntegrations&&goToIntegrations({projectId:p.id})}>Add in Integrations →</button>
                      </div>
                    ):(
                      <div>
                        {projectIntegrations.map(i=>(
                          <div key={i.id} className="project-route-row"
                               onClick={()=>goToIntegrations&&goToIntegrations({projectId:p.id,integrationId:i.id})}
                               style={{display:'flex',alignItems:'center',gap:12,padding:'10px 20px',cursor:'pointer',borderBottom:'1px solid var(--border)',transition:'background .12s'}}>
                            <span style={{fontSize:15,flexShrink:0,display:'inline-flex'}}><IntegrationFlowIcon size={15} color={p.color||'#6366f1'}/></span>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                                <span style={{fontWeight:600,fontSize:13,color:'var(--text)'}}>{i.name}</span>
                                {i.status==='ACTIVE'&&<span style={{fontSize:9,fontWeight:700,color:'#16a34a',background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:12,padding:'1px 6px'}}>● LIVE</span>}
                                {i.status==='ERROR'&&<span style={{fontSize:9,fontWeight:700,color:'#dc2626',background:'#fef2f2',border:'1px solid #fecaca',borderRadius:12,padding:'1px 6px',display:'inline-flex',alignItems:'center',gap:3}}><WarningIcon size={9}/> ERROR</span>}
                                {i.status==='DRAFT'&&<span style={{fontSize:9,fontWeight:700,color:'var(--text3)',background:'var(--surface3)',border:'1px solid var(--border)',borderRadius:12,padding:'1px 6px'}}>DRAFT</span>}
                                {i.status==='INACTIVE'&&<span style={{fontSize:9,fontWeight:700,color:'var(--text3)',background:'var(--surface3)',border:'1px solid var(--border)',borderRadius:12,padding:'1px 6px'}}>INACTIVE</span>}
                                {i.triggerType&&<span style={{fontSize:10.5,color:'var(--text3)'}}><IntegrationFlowIcon size={11} color="var(--text3)"/> {i.triggerType}</span>}
                              </div>
                              {i.description&&<div style={{fontSize:11.5,color:'var(--text3)',marginTop:2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{i.description}</div>}
                            </div>
                            <span style={{fontSize:11,color:'var(--accent)',flexShrink:0}}>Open →</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
       )}
      {showModal&&<ProjectModal initial={editing} onClose={()=>setShowModal(false)} onSaved={()=>{setShowModal(false);load();}} showToast={showToast}/>}
    </div>
  );
}

function ProjectModal({initial,onClose,onSaved,showToast}){
  const [name,setName]=useState((initial||{}).name||'');
  const [description,setDescription]=useState((initial||{}).description||'');
  const [color,setColor]=useState((initial||{}).color||'#6366f1');
  const [saving,setSaving]=useState(false);
  const slug=(initial||{}).slug||name.toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'');

  const save=async()=>{
    if(!name.trim()){showToast('Name is required','error');return;}
    setSaving(true);
    try{
      const payload={name:name.trim(),description:description.trim(),color};
      if(initial) await API('PUT',`/api/projects/${initial.id}`,payload);
      else await API('POST','/api/projects',payload);
      onSaved();showToast('Project saved!','success');
    }catch(e){showToast(e.message,'error');}
    finally{setSaving(false);}
  };

  return(
    <div style={{position:'fixed',inset:0,background:'rgba(15,17,35,.55)',backdropFilter:'blur(3px)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:20}}>
      <div style={{background:'var(--surface)',borderRadius:16,width:'100%',maxWidth:480,boxShadow:'var(--shadow-lg)',border:'1px solid var(--border)',overflow:'hidden'}}>
        <div style={{padding:'16px 24px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:10}}>
          <span style={{fontSize:20,display:'inline-flex'}}><FolderStackIcon size={20} color="var(--accent)"/></span>
          <div style={{fontWeight:800,fontSize:16}}>{initial?'Edit Project':'New Project'}</div>
          <div style={{flex:1}}/>
          <button onClick={onClose} className="modal-close-btn" aria-label="Close"><CloseIcon size={16}/></button>
        </div>
        <div style={{padding:24,display:'flex',flexDirection:'column',gap:16}}>
          <div className="form-group" style={{margin:0}}>
            <label className="form-label">Name *</label>
            <input className="form-input" value={name} onChange={e=>setName(e.target.value)} placeholder="UAE E-Invoice"/>
            {!initial&&name&&<div style={{fontSize:11,color:'var(--text3)',marginTop:4,fontFamily:'ui-monospace,monospace'}}>slug: {slug}</div>}
            {initial&&<div style={{fontSize:11,color:'var(--text3)',marginTop:4,fontFamily:'ui-monospace,monospace'}}>slug: {initial.slug} (immutable)</div>}
          </div>
          <div className="form-group" style={{margin:0}}>
            <label className="form-label">Description</label>
            <input className="form-input" value={description} onChange={e=>setDescription(e.target.value)} placeholder="What does this project do?"/>
          </div>
          <div className="form-group" style={{margin:0}}>
            <label className="form-label">Color</label>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <input type="color" value={color} onChange={e=>setColor(e.target.value)} style={{width:44,height:36,borderRadius:8,border:'1.5px solid var(--border2)',cursor:'pointer',padding:2}}/>
              <span style={{fontSize:12,fontFamily:'monospace',color:'var(--text2)'}}>{color}</span>
            </div>
          </div>
        </div>
        <div style={{padding:'12px 24px',borderTop:'1px solid var(--border)',display:'flex',gap:10,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving||!name.trim()}>{saving?<><Spinner/> Saving…</>:<><SaveActionIcon/> Save</>}</button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================================
 * RECIPE MARKETPLACE
 * Browse pre-built integration accelerators (OIC-style "Recipes"), inspect
 * their docs / spec / source-target mapping, and install them as one or more
 * Integration records linked back to the recipe via recipeOriginId.
 * ============================================================================ */

/* ============================================================================
 * USER MANAGEMENT — Users, Roles, Permissions
 *
 * The platform doesn't enforce auth yet, but the data model is real and fully
 * editable so operators can plan their access matrix today and have it take
 * effect the moment auth is enabled. Four bootstrapped system roles ship out
 * of the box (Administrator, Developer, Service Deployer, Monitoring User);
 * custom roles can be created freely. System roles are locked from edit /
 * delete to prevent accidental lockouts.
 * ============================================================================ */

/* ============================================================================
 * THEMING — Light / Dark / Midnight / Solar / High Contrast × 5 brand colors
 *
 * Single source of truth: data-theme + data-brand on <html>. The CSS file
 * defines the variables; React just persists choices and toggles attributes.
 * ============================================================================ */
const THEMES = [
  {id:'light',         name:'Light',          hint:'Charcoal-neutral · crisp daylight', tileBg:'#fcfdfd', tileSurface:'#ffffff', tileBorder:'#dfe3e8', tileText:'#1c252e', tileSidebar:'#212b36'},
  {id:'dark',          name:'Dark',           hint:'TaxLab charcoal · low-glare',      tileBg:'#1c252e', tileSurface:'#212b36', tileBorder:'#454f5b', tileText:'#f9fafb', tileSidebar:'#141a20'},
  {id:'midnight',      name:'Midnight',       hint:'Deep graphite · focused contrast',  tileBg:'#141a20', tileSurface:'#1a2129', tileBorder:'#4a5158', tileText:'#f4f6f8', tileSidebar:'#0f141a'},
  {id:'solar',         name:'Solar',          hint:'Warm neutral · daylight comfort',   tileBg:'#fff5cc', tileSurface:'#fcfdfd', tileBorder:'#ffd666', tileText:'#1c252e', tileSidebar:'#454f5b'},
  {id:'high-contrast', name:'High Contrast',  hint:'WCAG AAA · accessibility',      tileBg:'#ffffff', tileSurface:'#ffffff', tileBorder:'#000000', tileText:'#000000', tileSidebar:'#000000'},
];

const BRANDS = [
  {id:'crimson', name:'Crimson',  grad:'linear-gradient(135deg,#ff5630,#ffab00)'},
  {id:'indigo',  name:'Indigo',   grad:'linear-gradient(135deg,#5119b7,#8e33ff)'},
  {id:'emerald', name:'Emerald',  grad:'linear-gradient(135deg,#118d57,#22c55e)'},
  {id:'sunset',  name:'Sunset',   grad:'linear-gradient(135deg,#ff5630,#ffc107)'},
  {id:'ocean',   name:'Ocean',    grad:'linear-gradient(135deg,#006c9c,#00b8d9)'},
];

function useTheme(){
  const [theme,setThemeState] = useState(()=>{
    try { return localStorage.getItem('theme') || 'light'; } catch{ return 'light'; }
  });
  const [brand,setBrandState] = useState(()=>{
    try { return localStorage.getItem('themeBrand') || 'crimson'; } catch{ return 'crimson'; }
  });

  const setTheme = useCallback((t)=>{
    setThemeState(t);
    try { localStorage.setItem('theme', t); } catch{}
    if (t === 'light') document.documentElement.removeAttribute('data-theme');
    else document.documentElement.setAttribute('data-theme', t);
  },[]);
  const setBrand = useCallback((b)=>{
    setBrandState(b);
    try { localStorage.setItem('themeBrand', b); } catch{}
    if (b === 'crimson') document.documentElement.removeAttribute('data-brand');
    else document.documentElement.setAttribute('data-brand', b);
  },[]);

  return {theme, brand, setTheme, setBrand};
}

/* Re-usable theme-tile preview */
function ThemeTile({theme, active, onClick}){
  const styleVars = {
    '--tile-bg': theme.tileBg,
    '--tile-surface': theme.tileSurface,
    '--tile-border': theme.tileBorder,
    '--tile-text': theme.tileText,
    '--tile-sidebar': theme.tileSidebar,
  };
  return (
    <button type="button" className={`theme-tile${active?' active':''}`} onClick={onClick} style={styleVars}>
      <div className="theme-tile-preview">
        <div className="swatch-sidebar"/>
        <div className="swatch-main">
          <div className="swatch-card"/>
        </div>
      </div>
      <div className="theme-tile-meta">
        <div style={{flex:1}}>
          <div className="theme-tile-name">{theme.name}</div>
          <div style={{fontSize:10.5,color:'var(--text3)',marginTop:1,lineHeight:1.4}}>{theme.hint}</div>
        </div>
        <div className="theme-tile-check">✓</div>
      </div>
    </button>
  );
}

/* Topbar quick-toggle popover */
function ThemePickerPopover({theme, brand, setTheme, setBrand, onClose}){
  const ref = React.useRef(null);
  useEffect(()=>{
    const onDoc=(e)=>{ if(ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey=(e)=>{ if(e.key==='Escape') onClose(); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return ()=>{ document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  },[onClose]);
  return (
    <div ref={ref} className="theme-picker-pop">
      <div style={{fontSize:11,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'.6px',marginBottom:8}}>Theme</div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:14}}>
        {THEMES.map(t=>(
          <ThemeTile key={t.id} theme={t} active={theme===t.id} onClick={()=>setTheme(t.id)}/>
        ))}
      </div>
      <div style={{fontSize:11,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'.6px',marginBottom:8}}>Brand color</div>
      <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
        {BRANDS.map(b=>(
          <button key={b.id} className={`brand-swatch${brand===b.id?' active':''}`} onClick={()=>setBrand(b.id)}
                  title={b.name} style={{'--swatch-grad':b.grad}}/>
        ))}
      </div>
    </div>
  );
}

/* ===== Settings → Performance =====
 * Live JVM + CPU + Camel + Kubernetes (when on K8s) telemetry. Polls
 * /api/admin/perf-summary every 5 s. Spring Boot Actuator + Micrometer
 * supplies the data; PerfController flattens it into a single payload so
 * each refresh is one round-trip rather than six.
 *
 * The Kubernetes card only renders when the backend reports k8s metadata
 * (i.e. the KUBERNETES_SERVICE_HOST env var is present in the running pod).
 * Locally it's hidden — only the JVM/CPU/Camel cards show.
 */
function SettingsPerformance(){
  const [perf, setPerf] = useState(null);
  const [error, setError] = useState(null);
  const [lastTick, setLastTick] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let id;
    async function tick(){
      try {
        const r = await fetch('/api/admin/perf-summary', { cache:'no-store', headers: await authHeaders() });
        if (!r.ok) throw new Error('HTTP ' + r.status);
        const data = await r.json();
        if (!cancelled) { setPerf(data); setError(null); setLastTick(new Date()); }
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    }
    tick();
    id = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const fmtBytes = (b) => {
    if (b == null) return '—';
    if (b < 1024) return b + ' B';
    if (b < 1024*1024) return (b/1024).toFixed(1) + ' KB';
    if (b < 1024*1024*1024) return (b/1024/1024).toFixed(1) + ' MB';
    return (b/1024/1024/1024).toFixed(2) + ' GB';
  };
  const fmtPct = (p) => p == null || p < 0 ? '—' : (p * 100).toFixed(1) + '%';
  const fmtMs  = (ms) => {
    if (ms == null) return '—';
    if (ms < 1000) return ms + ' ms';
    if (ms < 60000) return (ms/1000).toFixed(1) + ' s';
    if (ms < 3600000) return Math.floor(ms/60000) + 'm ' + Math.floor((ms/1000)%60) + 's';
    return Math.floor(ms/3600000) + 'h ' + Math.floor((ms/60000)%60) + 'm';
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">System Performance</h1>
          <div className="page-subtitle">
            JVM &amp; Axon runtime telemetry · auto-refreshes every 5 s
            {lastTick && <span style={{marginLeft:8, fontSize:11, color:'var(--text3)'}}>· last tick {lastTick.toLocaleTimeString()}</span>}
          </div>
        </div>
      </div>

      {error && (
        <div className="card" style={{marginBottom:14, borderLeft:'3px solid #dc2626', color:'#b91c1c', fontSize:13}}>
          <span style={{display:'inline-flex',alignItems:'center',gap:8}}><WarningIcon size={15}/> Could not fetch /api/admin/perf-summary — {error}. Confirm the server is running and Spring Boot Actuator is enabled.</span>
        </div>
      )}
      {!perf && !error && (
        <div className="card" style={{padding:30, textAlign:'center', color:'var(--text3)'}}>Loading…</div>
      )}

      {perf && (
        <>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(260px, 1fr))', gap:14, marginBottom:18}}>
            {/* JVM */}
            <div className="card">
              <div className="section-title" style={{display:'flex',alignItems:'center',gap:10}}>
                <span style={{width:32,height:32,borderRadius:9,background:'rgba(79,70,229,.14)',color:'rgb(79,70,229)',display:'inline-flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><JvmChipIcon size={18}/></span>
                JVM
              </div>
              <div className="perf-rows">
                <div><span>Heap used</span><strong>{fmtBytes((perf.jvm||'').heapUsed)} / {fmtBytes((perf.jvm||'').heapMax)}</strong></div>
                <div><span>Heap committed</span><strong>{fmtBytes((perf.jvm||'').heapCommitted)}</strong></div>
                <div><span>Non-heap</span><strong>{fmtBytes((perf.jvm||'').nonHeapUsed)}</strong></div>
                <div><span>Threads</span><strong>{(perf.jvm||'').threadsLive} live · {(perf.jvm||'').threadsDaemon} daemon</strong></div>
                <div><span>Peak threads</span><strong>{(perf.jvm||'').threadsPeak}</strong></div>
                <div><span>Uptime</span><strong>{fmtMs((perf.jvm||'').uptime)}</strong></div>
              </div>
            </div>
            {/* CPU */}
            <div className="card">
              <div className="section-title" style={{display:'flex',alignItems:'center',gap:10}}>
                <span style={{width:32,height:32,borderRadius:9,background:'rgba(217,119,6,.14)',color:'rgb(183,110,0)',display:'inline-flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><CpuChipIcon size={18}/></span>
                CPU
              </div>
              <div className="perf-rows">
                <div><span>Process</span><strong>{fmtPct((perf.cpu||'').processUsage)}</strong></div>
                <div><span>System</span><strong>{fmtPct((perf.cpu||'').systemUsage)}</strong></div>
                <div><span>Load avg (1m)</span><strong>{(perf.cpu||'').loadAverage1m == null || perf.cpu.loadAverage1m < 0 ? '—' : perf.cpu.loadAverage1m.toFixed(2)}</strong></div>
                <div><span>Processors</span><strong>{(perf.cpu||'').processors}</strong></div>
              </div>
            </div>
            {/* Camel */}
            <div className="card">
              <div className="section-title" style={{display:'flex',alignItems:'center',gap:10}}>
                <span style={{width:32,height:32,borderRadius:9,background:'rgba(8,145,178,.14)',color:'rgb(8,145,178)',display:'inline-flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><RoutesMapIcon size={18}/></span>
                Routes
              </div>
              <div className="perf-rows">
                <div><span>Status</span><strong style={{color: (perf.camel||'').status === 'Started' ? '#16a34a' : 'var(--text2)'}}>{(perf.camel||'').status || '—'}</strong></div>
                <div><span>Routes</span><strong>{(perf.camel||'').routeCount || '—'}</strong></div>
                <div><span>Total exchanges</span><strong>{Number((perf.camel||{}).totalExchanges||0).toLocaleString()}</strong></div>
                <div><span>Completed</span><strong style={{color:'#16a34a'}}>{Number((perf.camel||{}).completed||0).toLocaleString()}</strong></div>
                <div><span>Failed</span><strong style={{color: (perf.camel||'').failed > 0 ? '#dc2626' : 'var(--text2)'}}>{Number((perf.camel||{}).failed||0).toLocaleString()}</strong></div>
                <div><span>In flight</span><strong>{(perf.camel||'').inFlight || '—'}</strong></div>
              </div>
            </div>
            {/* Kubernetes — only when running on K8s */}
            {perf.k8s && (
              <div className="card">
                <div className="section-title">⎈ Kubernetes</div>
                <div className="perf-rows">
                  <div><span>Pod</span><strong style={{fontFamily:'monospace', fontSize:11}}>{perf.k8s.pod || '—'}</strong></div>
                  {perf.k8s.namespace && <div><span>Namespace</span><strong>{perf.k8s.namespace}</strong></div>}
                  {perf.k8s.node && <div><span>Node</span><strong style={{fontFamily:'monospace', fontSize:11}}>{perf.k8s.node}</strong></div>}
                  {perf.k8s.podIp && <div><span>Pod IP</span><strong style={{fontFamily:'monospace', fontSize:11}}>{perf.k8s.podIp}</strong></div>}
                  <div><span>Container uptime</span><strong>{fmtMs(perf.k8s.processUptimeMs)}</strong></div>
                </div>
              </div>
            )}
          </div>

          {/* Per-route table — only if there's something to show */}
          {Array.isArray((perf.camel||'').byRoute) && perf.camel.byRoute.length > 0 && (
            <div className="card">
              <div className="section-title">Routes</div>
              <table style={{width:'100%', borderCollapse:'collapse', fontSize:12.5}}>
                <thead>
                  <tr style={{textAlign:'left', borderBottom:'1px solid var(--border)'}}>
                    <th style={{padding:'8px 6px'}}>Route</th>
                    <th style={{padding:'8px 6px', textAlign:'right'}}>Total</th>
                    <th style={{padding:'8px 6px', textAlign:'right'}}>Done</th>
                    <th style={{padding:'8px 6px', textAlign:'right'}}>Failed</th>
                    <th style={{padding:'8px 6px', textAlign:'right'}}>In flight</th>
                    <th style={{padding:'8px 6px', textAlign:'right'}}>Avg time</th>
                  </tr>
                </thead>
                <tbody>
                  {perf.camel.byRoute.map(r => (
                    <tr key={r.id} style={{borderBottom:'1px solid var(--border)'}}>
                      <td style={{padding:'7px 6px', fontFamily:'monospace', fontSize:11}}>{r.id}</td>
                      <td style={{padding:'7px 6px', textAlign:'right'}}>{(r.total||'').toLocaleString() || '—'}</td>
                      <td style={{padding:'7px 6px', textAlign:'right', color:'#16a34a'}}>{(r.completed||'').toLocaleString() || '—'}</td>
                      <td style={{padding:'7px 6px', textAlign:'right', color: r.failed > 0 ? '#dc2626' : 'var(--text3)'}}>{(r.failed||'').toLocaleString() || '—'}</td>
                      <td style={{padding:'7px 6px', textAlign:'right'}}>{r.inflight || '—'}</td>
                      <td style={{padding:'7px 6px', textAlign:'right'}}>{r.meanProcessingTime != null ? fmtMs(r.meanProcessingTime) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* Settings → Appearance — full page version */
function SettingsAppearance(){
  const {theme, brand, setTheme, setBrand} = useTheme();
  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">Appearance</h1><div className="page-subtitle">Theme and accent color · applies to your browser only</div></div>
      </div>

      <div className="card" style={{marginBottom:18}}>
        <div className="section-title">Theme</div>
        <div style={{fontSize:12.5,color:'var(--text2)',marginBottom:14,lineHeight:1.5}}>
          Pick a base theme. Your choice is saved in this browser; other users (and other devices) keep their own preference.
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))',gap:10}}>
          {THEMES.map(t=>(
            <ThemeTile key={t.id} theme={t} active={theme===t.id} onClick={()=>setTheme(t.id)}/>
          ))}
        </div>
      </div>

      <div className="card">
        <div className="section-title">Brand accent</div>
        <div style={{fontSize:12.5,color:'var(--text2)',marginBottom:14,lineHeight:1.5}}>
          Tints primary buttons, the brand logo, and the active-nav indicator. The selected swatch shows a dark ring.
        </div>
        <div style={{display:'flex',gap:14,flexWrap:'wrap',alignItems:'center'}}>
          {BRANDS.map(b=>(
            <div key={b.id} style={{display:'flex',flexDirection:'column',alignItems:'center',gap:6}}>
              <button className={`brand-swatch${brand===b.id?' active':''}`} onClick={()=>setBrand(b.id)}
                      title={b.name} style={{'--swatch-grad':b.grad,width:48,height:48,borderRadius:12}}/>
              <span style={{fontSize:11,fontWeight:600,color:brand===b.id?'var(--text)':'var(--text3)'}}>{b.name}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function UserManagementPage({showToast,confirm}){
  const [tab,setTab]=useState('users');
  const [users,setUsers]=useState([]);
  const [roles,setRoles]=useState([]);
  const [permissions,setPermissions]=useState([]);
  const [loading,setLoading]=useState(true);
  const [editUser,setEditUser]=useState(null);
  const [editRole,setEditRole]=useState(null);

  const reload=useCallback(async()=>{
    try{
      setLoading(true);
      const [u,r,p]=await loadAll([
        ['Users',API('GET','/api/admin/users'),[]],
        ['Roles',API('GET','/api/admin/roles'),[]],
        ['Permissions',API('GET','/api/admin/permissions?grouped=true'),{}],
      ],showToast);
      setUsers(Array.isArray(u)?u:[]);
      setRoles(Array.isArray(r)?r:[]);
      setPermissions(p||{});
    }catch(e){showToast('Failed to load: '+e.message,'error');}
    finally{setLoading(false);}
  },[showToast]);
  useEffect(()=>{reload();},[reload]);

  const permTotal = Object.values(permissions).reduce((s,arr)=>s+((arr||{}).length||0),0);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">User Management</h1>
          <div className="page-subtitle">Manage who can do what — users, roles, and the permission catalogue</div>
        </div>
        {tab==='users' && <button className="btn btn-primary" style={TAXLAB_CTA_STYLE} onClick={()=>setEditUser({})}><AddCircleIcon/> New User</button>}
        {tab==='roles' && <button className="btn btn-primary" style={TAXLAB_CTA_STYLE} onClick={()=>setEditRole({permissions:[]})}><AddCircleIcon/> New Role</button>}
      </div>

      <div className="um-tabs">
        <button className={`um-tab${tab==='users'?' active':''}`} onClick={()=>setTab('users')}>
          <span>
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0}}>
              <path fill="currentColor" d="M15.5 7.5a3.5 3.5 0 1 1-7 0a3.5 3.5 0 0 1 7 0"></path>
              <path fill="currentColor" d="M19.5 7.5a2.5 2.5 0 1 1-5 0a2.5 2.5 0 0 1 5 0m-15 0a2.5 2.5 0 1 0 5 0a2.5 2.5 0 0 0-5 0" opacity=".4"></path>
              <path fill="currentColor" d="M18 16.5c0 1.933-2.686 3.5-6 3.5s-6-1.567-6-3.5S8.686 13 12 13s6 1.567 6 3.5"></path>
              <path fill="currentColor" d="M22 16.5c0 1.38-1.79 2.5-4 2.5s-4-1.12-4-2.5s1.79-2.5 4-2.5s4 1.12 4 2.5m-20 0C2 17.88 3.79 19 6 19s4-1.12 4-2.5S8.21 14 6 14s-4 1.12-4 2.5" opacity=".4"></path>
            </svg>
          </span><span>Users</span>{users.length>0 && <span className="count">{users.length}</span>}
        </button>
        <button className={`um-tab${tab==='roles'?' active':''}`} onClick={()=>setTab('roles')}>
          <span>
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0}}>
              <path fill="currentColor" d="M2 16c0-2.828 0-4.243.879-5.121C3.757 10 5.172 10 8 10h8c2.828 0 4.243 0 5.121.879C22 11.757 22 13.172 22 16s0 4.243-.879 5.121C20.243 22 18.828 22 16 22H8c-2.828 0-4.243 0-5.121-.879C2 20.243 2 18.828 2 16" opacity=".5"></path>
              <path fill="currentColor" d="M12 18a2 2 0 1 0 0-4a2 2 0 0 0 0 4M6.75 8a5.25 5.25 0 0 1 10.5 0v2.004c.567.005 1.064.018 1.5.05V8a6.75 6.75 0 0 0-13.5 0v2.055a24 24 0 0 1 1.5-.051z"></path>
            </svg>
          </span><span>Roles</span>{roles.length>0 && <span className="count">{roles.length}</span>}
        </button>
        <button className={`um-tab${tab==='permissions'?' active':''}`} onClick={()=>setTab('permissions')}>
          <span>
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0}}>
              <path fill="currentColor" d="M14 3a7 7 0 0 0-6.7 9l-5 5V21h4l1-1h2l1-1v-2l1.3-1.3A7 7 0 1 0 14 3" opacity=".5"></path>
              <path fill="currentColor" d="M14 5a5 5 0 1 1 0 10a5 5 0 0 1 0-10m2 3a1 1 0 1 0 0 2a1 1 0 0 0 0-2"></path>
            </svg>
          </span><span>Permissions</span>{permTotal>0 && <span className="count">{permTotal}</span>}
        </button>
      </div>

      {loading && <div className="text-muted text-sm">Loading…</div>}
      {!loading && tab==='users' && <UsersTab users={users} roles={roles} onEdit={setEditUser} onChanged={reload} showToast={showToast} confirm={confirm}/>}
      {!loading && tab==='roles' && <RolesTab roles={roles} permissions={permissions} onEdit={setEditRole} onChanged={reload} showToast={showToast} confirm={confirm}/>}
      {!loading && tab==='permissions' && <PermissionsTab permissions={permissions}/>}

      {editUser && <UserEditModal user={editUser} roles={roles} onClose={()=>setEditUser(null)} onSaved={()=>{setEditUser(null);reload();}} showToast={showToast}/>}
      {editRole && <RoleEditModal role={editRole} permissions={permissions} onClose={()=>setEditRole(null)} onSaved={()=>{setEditRole(null);reload();}} showToast={showToast}/>}
    </div>
  );
}

/* ── Users tab ── */
function UsersTab({users, roles, onEdit, onChanged, showToast, confirm}){
  const roleById = React.useMemo(()=>{
    const m={}; for(const r of roles) m[r.id]=r; return m;
  },[roles]);

  const del=async(u)=>{
    const _c=await confirm(`Delete user "${u.name}"?`);if(!_c)return;
    try{await API('DELETE','/api/admin/users/'+u.id);showToast('User deleted','success');onChanged();}
    catch(e){showToast(e.message,'error');}
  };

  if(users.length===0) return (
    <div className="empty-state">
      <EmptyStateIcon tone="rose"><UsersGroupIcon/></EmptyStateIcon>
      <p style={{marginBottom:14}}>No users yet. Add your first user to plan your access matrix.</p>
      <button className="btn btn-primary" onClick={()=>onEdit({})}><AddCircleIcon/> New User</button>
    </div>
  );

  return (
    <div>
      {users.map(u=>{
        const initials=(u.name||'?').split(/\s+/).map(s=>s[0]).slice(0,2).join('').toUpperCase();
        return (
          <div key={u.id} className="user-row">
            <div className="user-avatar-circ" style={{background:u.avatarColor||'#7c3aed'}}>{initials}</div>
            <div className="user-meta">
              <div className="user-name">
                <span>{u.name}</span>
                <span className={`user-status-pill ${u.status||'ACTIVE'}`}>{u.status||'ACTIVE'}</span>
              </div>
              <div className="user-email">{u.email}</div>
              <div className="user-roles">
                {(u.roleIds||[]).length===0 && <span style={{fontSize:11,color:'var(--text3)',fontStyle:'italic'}}>No roles assigned</span>}
                {(u.roleIds||[]).map(rid=>{
                  const r=roleById[rid];
                  if(!r) return <span key={rid} className="role-chip" style={{background:'#94a3b8'}}>{rid}</span>;
                  return <span key={rid} className={`role-chip${r.system?' system':''}`} style={{background:r.color||'#7c3aed'}}>{r.system&&<LockIcon size={9}/>} {r.name}</span>;
                })}
              </div>
            </div>
            <div style={{textAlign:'right',flexShrink:0,fontSize:11,color:'var(--text3)'}}>
              <div>{(u.permissionCount||0)} effective permission{u.permissionCount===1?'':'s'}</div>
              {u.lastLoginAt ? <div>Last login: {fmtDate(u.lastLoginAt)}</div> : <div>Never logged in</div>}
            </div>
            <div style={{display:'flex',gap:8,flexShrink:0,alignItems:'center'}}>
              <button className="int-row-btn int-row-btn-grey" onClick={()=>onEdit(u)}><PencilIcon size={17}/> Edit</button>
              <button className="btn btn-danger row-icon-action" onClick={()=>del(u)} title="Delete" aria-label="Delete"><TrashIcon size={17}/></button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Roles tab ── */
function RolesTab({roles, permissions, onEdit, onChanged, showToast, confirm}){
  const del=async(r)=>{
    if(r.system) return showToast('System roles cannot be deleted','warning');
    const _c=await confirm(`Delete role "${r.name}"?`);if(!_c)return;
    try{await API('DELETE','/api/admin/roles/'+r.id);showToast('Role deleted','success');onChanged();}
    catch(e){showToast(e.message,'error');}
  };
  const cloneRole=async(r)=>{
    try{await API('POST','/api/admin/roles/'+r.id+'/clone');showToast('Role cloned · edit the copy','success');onChanged();}
    catch(e){showToast(e.message,'error');}
  };

  if(roles.length===0) return <div className="text-muted text-sm">No roles</div>;

  return (
    <div>
      {roles.map(r=>(
        <div key={r.id} className="role-card">
          <div className="role-accent" style={{background:r.color||'#7c3aed'}}/>
          <div className="role-card-header">
            <div className="role-card-icon" style={{background:r.color||'#7c3aed'}}>{r.system?<ShieldRoleIcon size={16}/>:<MaskRoleIcon size={16}/>}</div>
            <div className="role-card-meta">
              <div className="role-card-title">
                <span>{r.name}</span>
                {r.system && <span className="system-lock"><LockIcon size={12}/> System</span>}
              </div>
              <div className="role-card-desc">{r.description}</div>
              <div className="role-card-stats">
                <span><strong>{r.permissionCount||0}</strong> permissions</span>
                <span><strong>{r.userCount||0}</strong> user{r.userCount===1?'':'s'} assigned</span>
              </div>
            </div>
            <div style={{display:'flex',gap:8,flexShrink:0,alignItems:'center'}}>
              <button className="int-row-btn int-row-btn-grey" onClick={()=>onEdit(r)}>{r.system?<><EyeIcon open size={17}/> View</>:<><PencilIcon size={17}/> Edit</>}</button>
              {r.system && <button className="int-row-btn int-row-btn-light" onClick={()=>cloneRole(r)} title="Create an editable copy"><FileDocIcon size={17}/> Clone</button>}
              {!r.system && <button className="btn btn-danger row-icon-action" onClick={()=>del(r)} title="Delete" aria-label="Delete"><TrashIcon size={17}/></button>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Permissions tab — read-only catalogue ── */
function PermissionsTab({permissions}){
  return (
    <div>
      <div className="r-card" style={{background:'rgba(124,58,237,.06)',borderColor:'rgba(124,58,237,.2)',marginBottom:16}}>
        <div className="r-card-pad">
          <div style={{fontSize:14,fontWeight:700,color:'#7c3aed',display:'inline-flex',alignItems:'center',gap:8}}><KeyBadgeIcon size={14}/> Permission catalogue</div>
          <div style={{fontSize:12.5,color:'var(--text2)',marginTop:5,lineHeight:1.5}}>
            Every entry maps to a guarded code path in the platform. You can't add or remove permissions — only assign them via roles.
          </div>
        </div>
      </div>
      {Object.entries(permissions).map(([cat, perms])=>(
        <div key={cat} style={{marginBottom:18}}>
          <div className="perm-cat-header">
            <span>{cat}</span>
            <span className="perm-cat-count">{perms.length}</span>
          </div>
          {perms.map(p=>(
            <div key={p.id} className="perm-row">
              <div style={{flex:1}}>
                <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                  <span className="perm-label">{p.label}</span>
                  <span className="perm-id">{p.id}</span>
                </div>
                <div className="perm-desc">{p.description}</div>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/* ── User edit modal ── */
function UserEditModal({user, roles, onClose, onSaved, showToast}){
  const isNew=!user.id;
  const [name,setName]=useState(user.name||'');
  const [email,setEmail]=useState(user.email||'');
  const [status,setStatus]=useState(user.status||'ACTIVE');
  const [roleIds,setRoleIds]=useState(user.roleIds||[]);
  const [saving,setSaving]=useState(false);

  const toggleRole=(id)=>setRoleIds(prev=>prev.includes(id)?prev.filter(x=>x!==id):[...prev,id]);

  const submit=async()=>{
    setSaving(true);
    try{
      const payload={name,email,status,roleIds};
      if(isNew) await API('POST','/api/admin/users',payload);
      else await API('PUT','/api/admin/users/'+user.id,payload);
      showToast(isNew?'User created':'User updated','success');
      onSaved();
    }catch(e){showToast(e.message,'error');}
    finally{setSaving(false);}
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal user-management-modal" onClick={e=>e.stopPropagation()} style={{maxWidth:600}}>
        <div className="modal-header">
          <div className="modal-heading">
            <span className="modal-heading-icon user"><UsersGroupIcon size={20}/></span>
            <div>
              <div className="modal-title">{isNew?'New User':'Edit User'}</div>
              <div className="modal-subtitle">Profile and role assignments</div>
            </div>
          </div>
          <button className="modal-close-btn" onClick={onClose} aria-label="Close"><CloseIcon size={16}/></button>
        </div>
        <div className="modal-body">
          <div className="modal-body-scroll">
            <section className="modal-form-section">
              <div className="modal-form-section-title"><UsersGroupIcon size={15}/> User details</div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Full Name <span className="req">*</span></label>
                  <input className="form-input" value={name} onChange={e=>setName(e.target.value)} placeholder="Jane Doe"/>
                </div>
                <div className="form-group">
                  <label className="form-label">Email <span className="req">*</span></label>
                  <input className="form-input" type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="jane@taxlab.ai"/>
                </div>
              </div>
              <div className="form-group" style={{marginBottom:0}}>
                <label className="form-label">Status</label>
                <select className="form-select" value={status} onChange={e=>setStatus(e.target.value)}>
                  <option value="ACTIVE">Active</option>
                  <option value="INVITED">Invited (not yet logged in)</option>
                  <option value="DISABLED">Disabled</option>
                </select>
              </div>
            </section>
            <section className="modal-form-section">
              <div className="modal-form-section-title"><ShieldRoleIcon size={15}/> Assigned roles</div>
              <div className="role-picker-list">
                {roles.map(r=>{
                  const checked=roleIds.includes(r.id);
                  return (
                    <label key={r.id} className={`role-picker-item${checked?' selected':''}`}>
                      <input type="checkbox" checked={checked} onChange={()=>toggleRole(r.id)} style={{width:16,height:16,accentColor:'#7c3aed'}}/>
                      <span className={`role-chip${r.system?' system':''}`} style={{background:r.color||'#7c3aed'}}>{r.system&&<LockIcon size={9}/>} {r.name}</span>
                      <span className="role-picker-description">{r.description}</span>
                      <span className="role-picker-count">{r.permissionCount||0} perms</span>
                    </label>
                  );
                })}
              </div>
            </section>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving||!name||!email}>{saving?<Spinner/>:(isNew?'Create User':<><SaveActionIcon/> Save Changes</>)}</button>
        </div>
      </div>
    </div>
  );
}

/* ── Role edit modal — permissions grouped by category ── */
function RoleEditModal({role, permissions, onClose, onSaved, showToast}){
  const isNew=!role.id;
  const isSystem=!!role.system;
  const [name,setName]=useState(role.name||'');
  const [description,setDescription]=useState(role.description||'');
  const [color,setColor]=useState(role.color||'#7c3aed');
  const [perms,setPerms]=useState(role.permissions||[]);
  const [saving,setSaving]=useState(false);

  const allCatPermIds=(cat)=>(permissions[cat]||[]).map(p=>p.id);
  const allChecked=(cat)=>{
    const ids=allCatPermIds(cat);
    return ids.length>0 && ids.every(id=>perms.includes(id));
  };
  const someChecked=(cat)=>{
    const ids=allCatPermIds(cat);
    return ids.some(id=>perms.includes(id)) && !allChecked(cat);
  };
  const toggleCat=(cat)=>{
    if(isSystem) return;
    const ids=allCatPermIds(cat);
    setPerms(prev=>{
      if(allChecked(cat)) return prev.filter(p=>!ids.includes(p));
      return Array.from(new Set([...prev, ...ids]));
    });
  };
  const togglePerm=(id)=>{
    if(isSystem) return;
    setPerms(prev=>prev.includes(id)?prev.filter(x=>x!==id):[...prev,id]);
  };
  const totalAvailable=Object.values(permissions).reduce((s,arr)=>s+arr.length,0);

  const submit=async()=>{
    setSaving(true);
    try{
      const payload={name,description,color,permissions:perms};
      if(isNew) await API('POST','/api/admin/roles',payload);
      else await API('PUT','/api/admin/roles/'+role.id,payload);
      showToast(isNew?'Role created':'Role updated','success');
      onSaved();
    }catch(e){showToast(e.message,'error');}
    finally{setSaving(false);}
  };

  const COLORS=['#7c3aed','#dc2626','#0891b2','#16a34a','#d97706','#0ea5e9','#db2777','#2563eb','#475569'];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal user-management-modal" onClick={e=>e.stopPropagation()} style={{maxWidth:820}}>
        <div className="modal-header">
          <div className="modal-heading">
            <span className="modal-heading-icon role"><ShieldRoleIcon size={20}/></span>
            <div>
              <div className="modal-title">
                {isSystem?'View System Role':(isNew?'New Role':'Edit Role')}
                {isSystem && <span className="system-lock" style={{marginLeft:10}}><LockIcon size={12}/> System</span>}
              </div>
              <div className="modal-subtitle">{isSystem?'System roles are read-only — clone to customise':'Define which permissions this role grants'}</div>
            </div>
          </div>
          <button className="modal-close-btn" onClick={onClose} aria-label="Close"><CloseIcon size={16}/></button>
        </div>
        <div className="modal-body">
          <div className="modal-body-scroll">
            <section className="modal-form-section">
              <div className="modal-form-section-title"><ShieldRoleIcon size={15}/> Role details</div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Role Name <span className="req">*</span></label>
                  <input className="form-input" value={name} onChange={e=>setName(e.target.value)} disabled={isSystem} placeholder="e.g. Tax Auditor"/>
                </div>
                <div className="form-group">
                  <label className="form-label">Color</label>
                  <div className="role-color-picker">
                    {COLORS.map(c=>(
                      <button key={c} type="button" disabled={isSystem} onClick={()=>setColor(c)}
                        className={color===c?'selected':''}
                        style={{background:c,cursor:isSystem?'not-allowed':'pointer'}}
                        aria-label={`Use ${c} role color`}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <div className="form-group" style={{marginBottom:0}}>
                <label className="form-label">Description</label>
                <textarea className="form-input" value={description} onChange={e=>setDescription(e.target.value)} disabled={isSystem} rows={2} placeholder="What does this role grant?"/>
              </div>
            </section>
            <section className="modal-form-section">
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,marginBottom:10}}>
                <div className="modal-form-section-title" style={{marginBottom:0}}><KeyBadgeIcon size={15}/> Permissions <span className="modal-form-section-count">{perms.length}/{totalAvailable}</span></div>
                {!isSystem && <div style={{display:'flex',gap:6}}>
                  <button type="button" className="btn btn-ghost btn-xs" onClick={()=>setPerms(Object.values(permissions).flat().map(p=>p.id))}>Select all</button>
                  <button type="button" className="btn btn-ghost btn-xs" onClick={()=>setPerms([])}>Clear</button>
                </div>}
              </div>
          {Object.entries(permissions).map(([cat, list])=>(
            <div key={cat}>
              <div className="perm-cat-header" style={{cursor:isSystem?'default':'pointer'}} onClick={()=>toggleCat(cat)}>
                <input type="checkbox" checked={allChecked(cat)} ref={el=>{if(el) el.indeterminate=someChecked(cat);}} onChange={()=>toggleCat(cat)} disabled={isSystem} style={{accentColor:'#7c3aed'}}/>
                <span>{cat}</span>
                <span className="perm-cat-count">{list.filter(p=>perms.includes(p.id)).length} / {list.length}</span>
              </div>
              {list.map(p=>{
                const checked=perms.includes(p.id);
                return (
                  <div key={p.id} className={`perm-row${checked?' checked':''}`}>
                    <label>
                      <input type="checkbox" checked={checked} onChange={()=>togglePerm(p.id)} disabled={isSystem}/>
                      <div style={{flex:1}}>
                        <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                          <span className="perm-label">{p.label}</span>
                          <span className="perm-id">{p.id}</span>
                        </div>
                        <div className="perm-desc">{p.description}</div>
                      </div>
                    </label>
                  </div>
                );
              })}
            </div>
          ))}
            </section>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>{isSystem?'Close':'Cancel'}</button>
          {!isSystem && <button className="btn btn-primary" onClick={submit} disabled={saving||!name}>{saving?<Spinner/>:(isNew?'Create Role':<><SaveActionIcon/> Save Changes</>)}</button>}
        </div>
      </div>
    </div>
  );
}

/* Recipe Marketplace components extracted to recipes.js � included via <script> tag in <body>. */

/* ===== APP SHELL =====
 * Sidebar is a 2-level tree. Top-level entries with `children` render as a
 * collapsible group header; leaves render as nav buttons. The flat list of
 * tab ids the App routes on is computed via flattenNav() so the rest of the
 * app (topbar title finder, deep links from Projects, etc.) doesn't need to
 * know the tree shape.
 */
const NAV_ITEMS=[
  {id:'dashboard',label:'Dashboard',icon:'dashboard'},
  {id:'impact',label:'Impact Analysis',icon:'performance'},
  {id:'api-management',label:'API Management',icon:'apiManagement',group:true,children:[
    // Apigee-aligned (2026-10-17): APIs are the building blocks; API products bundle their
    // operations and are what organisations are granted. See access.js.
    {id:'access-products',label:'API Products',icon:'products'},
    {id:'api-products',label:'APIs',icon:'endpoints'},
    {id:'access-orgs',label:'Organisations',icon:'subscribers'},
    {id:'api-portal',label:'Developer Portal',icon:'developers'},
    {id:'api-gateway',label:'API Gateway',icon:'apiGateway'},
    {id:'api-environments',label:'Environments',icon:'environments'},
    {id:'api-webhooks',label:'Webhook Deliveries',icon:'webhooks'},
  ]},
  {id:'ai-management',label:'AI Gateway',icon:'ai',group:true,children:[
    {id:'ai-providers',label:'Providers',icon:'extension'},
    {id:'ai-usage',label:'Usage & Cost',icon:'apiKeys'},
  ]},
  {id:'develop',label:'Develop',icon:'develop',group:true,children:[
    {id:'projects',label:'Projects',icon:'projects'},
    {id:'recipes',label:'Accelerators',icon:'recipes'},
    {id:'integrations',label:'Integrations',icon:'integrations'},
    {id:'designer',label:'YAML Designer',icon:'develop'},
    {id:'java-designer',label:'Java Designer',icon:'java'},
    {id:'connections',label:'Connections',icon:'connections'},
    {id:'lookups',label:'Lookup Tables',icon:'lookups'},
  ]},
  {id:'observability',label:'Observability',icon:'observability',group:true,children:[
    {id:'monitoring',label:'Executions',icon:'executions'},
    {id:'logs',label:'Logs',icon:'monitoring'},
    {id:'prometheus-metrics',label:'Prometheus',icon:'executions'},
    {id:'metrics',label:'Metrics',icon:'executions'},
    {id:'camelbee',label:'Route Topology',icon:'observability'},
    {id:'gateway-tracing',label:'Distributed Tracing',icon:'observability'},
    {id:'schedule',label:'Future Runs',icon:'schedule'},
    {id:'audit',label:'Audit Log',icon:'audit'},
  ]},
  {id:'settings',label:'Settings',icon:'settings',group:true,children:[
    {id:'settings-general',label:'General',icon:'general'},
    {id:'settings-appearance',label:'Appearance',icon:'appearance'},
    {id:'settings-performance',label:'System Performance',icon:'performance'},
    {id:'settings-logging',label:'Logging',icon:'logging'},
    {id:'settings-notifications',label:'Notifications',icon:'notifications'},
    {id:'settings-security',label:'Security',icon:'security'},
    {id:'settings-keycloak',label:'Keycloak',icon:'security'},
    {id:'settings-users',label:'User Management',icon:'users'},
    {id:'settings-about',label:'About',icon:'about'},
  ]},
];
function renderNavIcon(name){
  const common={width:22,height:22,viewBox:'0 0 24 24','aria-hidden':'true'};
  switch(name){
    case 'ai': return <svg {...common}><path fill="currentColor" d="M11.083 3.402c.32-.878 1.514-.878 1.834 0l1.55 4.26a3 3 0 0 0 1.79 1.79l4.261 1.55c.878.32.878 1.514 0 1.834l-4.26 1.55a3 3 0 0 0-1.79 1.79l-1.55 4.261c-.32.878-1.515.878-1.835 0l-1.55-4.26a3 3 0 0 0-1.79-1.79l-4.26-1.55c-.879-.32-.879-1.515 0-1.835l4.26-1.55a3 3 0 0 0 1.79-1.79z" opacity=".7"></path><path fill="currentColor" d="M18.402 2.2c.184-.505.87-.505 1.054 0l.53 1.455a1.7 1.7 0 0 0 1.014 1.014l1.456.53c.504.184.504.87 0 1.054l-1.456.53a1.7 1.7 0 0 0-1.014 1.014l-.53 1.456c-.184.504-.87.504-1.054 0l-.53-1.456a1.7 1.7 0 0 0-1.014-1.014l-1.456-.53c-.504-.184-.504-.87 0-1.054l1.456-.53a1.7 1.7 0 0 0 1.014-1.014zM5.03 16.06c.14-.386.665-.386.805 0l.405 1.112c.15.412.474.736.886.886l1.112.405c.386.14.386.665 0 .805l-1.112.405a1.3 1.3 0 0 0-.886.886l-.405 1.112c-.14.386-.665.386-.805 0l-.405-1.112a1.3 1.3 0 0 0-.886-.886l-1.112-.405c-.386-.14-.386-.665 0-.805l1.112-.405a1.3 1.3 0 0 0 .886-.886z"></path></svg>;
    case 'dashboard': return <svg {...common}><path fill="currentColor" d="M3.293 9.293C3 9.586 3 10.057 3 11v6c0 .943 0 1.414.293 1.707S4.057 19 5 19s1.414 0 1.707-.293S7 17.943 7 17v-6c0-.943 0-1.414-.293-1.707S5.943 9 5 9s-1.414 0-1.707.293"></path><path fill="currentColor" d="M17.293 2.293C17 2.586 17 3.057 17 4v13c0 .943 0 1.414.293 1.707S18.057 19 19 19s1.414 0 1.707-.293S21 17.943 21 17V4c0-.943 0-1.414-.293-1.707S19.943 2 19 2s-1.414 0-1.707.293" opacity=".4"></path><path fill="currentColor" d="M10 7c0-.943 0-1.414.293-1.707S11.057 5 12 5s1.414 0 1.707.293S14 6.057 14 7v10c0 .943 0 1.414-.293 1.707S12.943 19 12 19s-1.414 0-1.707-.293S10 17.943 10 17z" opacity=".7"></path><path fill="currentColor" d="M3 21.25a.75.75 0 0 0 0 1.5h18a.75.75 0 0 0 0-1.5z"></path></svg>;
    case 'develop': return <svg {...common}><path fill="currentColor" d="M1 12c0-5.185 0-7.778 1.61-9.39C4.223 1 6.816 1 12 1s7.778 0 9.39 1.61C23 4.223 23 6.816 23 12s0 7.778-1.61 9.39C19.777 23 17.184 23 12 23s-7.778 0-9.39-1.61C1 19.777 1 17.184 1 12" opacity=".5"></path><path fill="currentColor" d="M13.926 14.302c.245-.191.467-.413.912-.858l5.54-5.54c.134-.134.073-.365-.106-.427a6.1 6.1 0 0 1-2.3-1.449a6.1 6.1 0 0 1-1.45-2.3c-.061-.18-.292-.24-.426-.106l-5.54 5.54c-.445.444-.667.667-.858.912a5 5 0 0 0-.577.932c-.133.28-.233.579-.431 1.175l-.257.77l-.409 1.226l-.382 1.148a.817.817 0 0 0 1.032 1.033l1.15-.383l1.224-.408l.77-.257c.597-.199.895-.298 1.175-.432q.498-.237.933-.576m8.187-8.132a3.028 3.028 0 0 0-4.282-4.283l-.179.178a.73.73 0 0 0-.206.651c.027.15.077.37.168.633a4.9 4.9 0 0 0 1.174 1.863a4.9 4.9 0 0 0 1.862 1.174c.263.09.483.141.633.168c.24.043.48-.035.652-.207z"></path></svg>;
    // Java Designer — braces, to read as source code rather than a canvas.
    case 'java': return <svg {...common}><path fill="currentColor" d="M9.4 2.6a1 1 0 0 1 0 1.4A6 6 0 0 0 7.6 8.3v1.2a3 3 0 0 1-2 2.8a3 3 0 0 1 2 2.8v1.2a6 6 0 0 0 1.8 4.3a1 1 0 0 1-1.4 1.4a8 8 0 0 1-2.4-5.7v-1.2a1 1 0 0 0-1-1a1 1 0 0 1 0-2a1 1 0 0 0 1-1V8.3A8 8 0 0 1 8 2.6a1 1 0 0 1 1.4 0" opacity=".55"></path><path fill="currentColor" d="M14.6 2.6a1 1 0 0 1 1.4 0a8 8 0 0 1 2.4 5.7v1.2a1 1 0 0 0 1 1a1 1 0 0 1 0 2a1 1 0 0 0-1 1v1.2a8 8 0 0 1-2.4 5.7a1 1 0 0 1-1.4-1.4a6 6 0 0 0 1.8-4.3v-1.2a3 3 0 0 1 2-2.8a3 3 0 0 1-2-2.8V8.3a6 6 0 0 0-1.8-4.3a1 1 0 0 1 0-1.4"></path></svg>;
    case 'projects': return <svg {...common}><path fill="currentColor" d="M10 20h4c3.771 0 5.657 0 6.828-1.172S22 15.771 22 12c0-.442-.002-1.608-.004-2H2c-.002.392 0 1.558 0 2c0 3.771 0 5.657 1.171 6.828S6.23 20 10 20" opacity=".5"></path><path fill="currentColor" d="M9.995 4h4.01c3.781 0 5.672 0 6.846 1.116c.846.803 1.083 1.96 1.149 3.884v1H2V9c.066-1.925.303-3.08 1.149-3.884C4.323 4 6.214 4 9.995 4M12.5 15.25a.75.75 0 0 0 0 1.5H14a.75.75 0 0 0 0-1.5zm-6.5 0a.75.75 0 0 0 0 1.5h4a.75.75 0 0 0 0-1.5z"></path></svg>;
    case 'recipes': return <svg {...common}><path fill="currentColor" d="M6.6 11.397c0-2.726 0-4.089.843-4.936c.844-.847 2.201-.847 4.917-.847h2.88c2.715 0 4.073 0 4.916.847c.844.847.844 2.21.844 4.936v4.82c0 2.726 0 4.089-.844 4.936c-.843.847-2.201.847-4.916.847h-2.88c-2.716 0-4.073 0-4.917-.847s-.843-2.21-.843-4.936z"></path><path fill="currentColor" d="M4.172 3.172C3 4.343 3 6.229 3 10v2c0 3.771 0 5.657 1.172 6.828c.617.618 1.433.91 2.62 1.048c-.192-.84-.192-1.996-.192-3.66v-4.819c0-2.726 0-4.089.843-4.936c.844-.847 2.201-.847 4.917-.847h2.88c1.652 0 2.8 0 3.638.19c-.138-1.193-.43-2.012-1.05-2.632C16.657 2 14.771 2 11 2S5.343 2 4.172 3.172" opacity=".5"></path></svg>;
    case 'integrations': return <svg {...common}><path fill="currentColor" d="M8 5.5h8a3 3 0 0 0 3-3a.5.5 0 0 0-.5-.5h-13a.5.5 0 0 0-.5.5a3 3 0 0 0 3 3m8 13H8a3 3 0 0 0-3 3a.5.5 0 0 0 .5.5h13a.5.5 0 0 0 .5-.5a3 3 0 0 0-3-3" opacity=".5"></path><path fill="currentColor" d="M5 11.5c0-1.886 0-2.828.586-3.414S7.114 7.5 9 7.5h6c1.886 0 2.828 0 3.414.586S19 9.614 19 11.5v1c0 1.886 0 2.828-.586 3.414S16.886 16.5 15 16.5H9c-1.886 0-2.828 0-3.414-.586S5 14.386 5 12.5z"></path></svg>;
    case 'connections': return <svg {...common}><path fill="currentColor" d="M6 13h12c1.886 0 2.828 0 3.414.586S22 15.114 22 17s0 2.828-.586 3.414S19.886 21 18 21H6c-1.886 0-2.828 0-3.414-.586S2 18.886 2 17s0-2.828.586-3.414S4.114 13 6 13M6 3h12c1.886 0 2.828 0 3.414.586S22 5.114 22 7s0 2.828-.586 3.414S19.886 11 18 11H6c-1.886 0-2.828 0-3.414-.586S2 8.886 2 7s0-2.828.586-3.414S4.114 3 6 3" opacity=".5"></path><path fill="currentColor" d="M10.25 7a.75.75 0 0 1 .75-.75h7a.75.75 0 0 1 0 1.5h-7a.75.75 0 0 1-.75-.75m-5 0A.75.75 0 0 1 6 6.25h2a.75.75 0 0 1 0 1.5H6A.75.75 0 0 1 5.25 7m5 10a.75.75 0 0 1 .75-.75h7a.75.75 0 0 1 0 1.5h-7a.75.75 0 0 1-.75-.75m-5 0a.75.75 0 0 1 .75-.75h2a.75.75 0 0 1 0 1.5H6a.75.75 0 0 1-.75-.75"></path></svg>;
    case 'lookups': return <svg {...common}><path fill="currentColor" d="M5.172 3.172C4 4.343 4 6.229 4 10v4c0 3.771 0 5.657 1.172 6.828S8.229 22 12 22s5.657 0 6.828-1.172S20 17.771 20 14v-4c0-3.771 0-5.657-1.172-6.828S15.771 2 12 2S6.343 2 5.172 3.172" opacity=".5"></path><path fill="currentColor" d="M9 4.25a.75.75 0 0 0 0 1.5h6a.75.75 0 0 0 0-1.5zM12 19a2 2 0 1 0 0-4a2 2 0 0 0 0 4"></path></svg>;
    case 'observability': return <svg {...common}><path fill="currentColor" d="M12 22c-4.714 0-7.071 0-8.536-1.465C2 19.072 2 16.714 2 12s0-7.071 1.464-8.536C4.93 2 7.286 2 12 2s7.071 0 8.535 1.464C22 4.93 22 7.286 22 12s0 7.071-1.465 8.535C19.072 22 16.714 22 12 22" opacity=".5"></path><path fill="currentColor" d="M12 5.25a.75.75 0 0 1 .75.75v12a.75.75 0 0 1-1.5 0V6a.75.75 0 0 1 .75-.75m-5 3a.75.75 0 0 1 .75.75v9a.75.75 0 0 1-1.5 0V9A.75.75 0 0 1 7 8.25m10 4a.75.75 0 0 1 .75.75v5a.75.75 0 0 1-1.5 0v-5a.75.75 0 0 1 .75-.75"></path></svg>;
    case 'executions': return <svg {...common}><path fill="currentColor" d="M2 12c0-4.714 0-7.071 1.464-8.536C4.93 2 7.286 2 12 2s7.071 0 8.535 1.464C22 4.93 22 7.286 22 12s0 7.071-1.465 8.535C19.072 22 16.714 22 12 22s-7.071 0-8.536-1.465C2 19.072 2 16.714 2 12" opacity=".5"></path><path fill="currentColor" d="M12.75 7a.75.75 0 0 0-1.5 0v5.19l-1.72-1.72a.75.75 0 0 0-1.06 1.06l3 3a.75.75 0 0 0 1.06 0l3-3a.75.75 0 1 0-1.06-1.06l-1.72 1.72zM8 16.25a.75.75 0 0 0 0 1.5h8a.75.75 0 0 0 0-1.5z"></path></svg>;
    case 'schedule': return <svg {...common}><path fill="currentColor" d="M2 12c0-4.714 0-7.071 1.464-8.536C4.93 2 7.286 2 12 2s7.071 0 8.535 1.464C22 4.93 22 7.286 22 12s0 7.071-1.465 8.535C19.072 22 16.714 22 12 22s-7.071 0-8.536-1.465C2 19.072 2 16.714 2 12" opacity=".5"></path><path fill="currentColor" d="M12.75 17a.75.75 0 0 1-1.5 0v-5.19l-1.72 1.72a.75.75 0 0 1-1.06-1.06l3-3a.75.75 0 0 1 1.06 0l3 3a.75.75 0 1 1-1.06 1.06l-1.72-1.72zM8 7.75a.75.75 0 0 1 0-1.5h8a.75.75 0 0 1 0 1.5z"></path></svg>;
    case 'audit': return <svg {...common}><path fill="currentColor" d="M21 15.998v-6c0-2.828 0-4.242-.879-5.121C19.353 4.109 18.175 4.012 16 4H8c-2.175.012-3.353.109-4.121.877C3 5.756 3 7.17 3 9.998v6c0 2.829 0 4.243.879 5.122c.878.878 2.293.878 5.121.878h6c2.828 0 4.243 0 5.121-.878c.879-.88.879-2.293.879-5.122" opacity=".5"></path><path fill="currentColor" d="M8 3.5A1.5 1.5 0 0 1 9.5 2h5A1.5 1.5 0 0 1 16 3.5v1A1.5 1.5 0 0 1 14.5 6h-5A1.5 1.5 0 0 1 8 4.5z"></path><path fill="currentColor" fill-rule="evenodd" d="M6.25 10.5A.75.75 0 0 1 7 9.75h.5a.75.75 0 0 1 0 1.5H7a.75.75 0 0 1-.75-.75m3.5 0a.75.75 0 0 1 .75-.75H17a.75.75 0 0 1 0 1.5h-6.5a.75.75 0 0 1-.75-.75M6.25 14a.75.75 0 0 1 .75-.75h.5a.75.75 0 0 1 0 1.5H7a.75.75 0 0 1-.75-.75m3.5 0a.75.75 0 0 1 .75-.75H17a.75.75 0 0 1 0 1.5h-6.5a.75.75 0 0 1-.75-.75m-3.5 3.5a.75.75 0 0 1 .75-.75h.5a.75.75 0 0 1 0 1.5H7a.75.75 0 0 1-.75-.75m3.5 0a.75.75 0 0 1 .75-.75H17a.75.75 0 0 1 0 1.5h-6.5a.75.75 0 0 1-.75-.75" clip-rule="evenodd"></path></svg>;
    case 'apiManagement': return <svg {...common}><path fill="currentColor" fill-rule="evenodd" d="M2.028 11.25A10 10 0 0 1 12 2c-.83 0-1.57.364-2.18.921c-.605.554-1.116 1.328-1.53 2.242c-.416.92-.74 1.996-.959 3.163a20 20 0 0 0-.318 2.924zm0 1.5h4.985c.036 1.002.143 1.988.318 2.924c.22 1.167.543 2.243.959 3.163c.414.914.925 1.688 1.53 2.242c.61.557 1.35.921 2.18.921c-5.27 0-9.589-4.077-9.972-9.25" clip-rule="evenodd"></path><path fill="currentColor" d="M12 2c.831 0 1.57.364 2.18.921c.605.554 1.117 1.328 1.53 2.242c.417.92.74 1.996.959 3.163c.175.936.282 1.922.318 2.924h4.985A10 10 0 0 0 12 2m4.669 13.674c-.219 1.167-.542 2.243-.959 3.163c-.413.914-.925 1.688-1.53 2.242c-.61.557-1.349.921-2.18.921c5.27 0 9.589-4.077 9.972-9.25h-4.985a20 20 0 0 1-.318 2.924"></path><path fill="currentColor" d="M12 3.396c-.275 0-.63.117-1.043.495c-.416.38-.833.977-1.201 1.79c-.366.808-.663 1.784-.867 2.873c-.16.859-.26 1.768-.296 2.696h6.814a18.5 18.5 0 0 0-.296-2.696c-.204-1.09-.5-2.065-.867-2.872c-.368-.814-.784-1.41-1.2-1.791c-.414-.378-.769-.495-1.044-.495m-3.111 12.05c.204 1.09.501 2.065.867 2.873c.368.813.785 1.41 1.2 1.79c.414.379.77.496 1.044.496c.275 0 .63-.117 1.044-.495c.416-.381.832-.978 1.2-1.791c.366-.808.663-1.783.867-2.873c.161-.858.261-1.768.296-2.696H8.593c.035.928.135 1.838.296 2.696" opacity=".5"></path></svg>;
    case 'products': return <svg {...common}><path fill="currentColor" d="M10 20h4c3.771 0 5.657 0 6.828-1.172S22 15.771 22 12c0-.442-.002-1.608-.004-2H2c-.002.392 0 1.558 0 2c0 3.771 0 5.657 1.171 6.828S6.23 20 10 20" opacity=".5"></path><path fill="currentColor" d="M9.995 4h4.01c3.781 0 5.672 0 6.846 1.116c.846.803 1.083 1.96 1.149 3.884v1H2V9c.066-1.925.303-3.08 1.149-3.884C4.323 4 6.214 4 9.995 4M12.5 15.25a.75.75 0 0 0 0 1.5H14a.75.75 0 0 0 0-1.5zm-6.5 0a.75.75 0 0 0 0 1.5h4a.75.75 0 0 0 0-1.5z"></path></svg>;
    case 'environments': return <svg {...common}><path fill="currentColor" d="M2 6.5C2 4.567 4.239 3 7 3h10c2.761 0 5 1.567 5 3.5S19.761 10 17 10H7c-2.761 0-5-1.567-5-3.5" opacity=".5"></path><path fill="currentColor" d="M2 12c0 1.933 2.239 3.5 5 3.5h10c2.761 0 5-1.567 5-3.5c0-.66-.26-1.277-.713-1.805C20.35 11.07 18.774 11.75 17 11.75H7c-1.774 0-3.35-.68-4.287-1.555C2.26 10.723 2 11.34 2 12" opacity=".7"></path><path fill="currentColor" d="M2 17.5C2 19.433 4.239 21 7 21h10c2.761 0 5-1.567 5-3.5c0-.66-.26-1.277-.713-1.805C20.35 16.57 18.774 17.25 17 17.25H7c-1.774 0-3.35-.68-4.287-1.555C2.26 16.223 2 16.84 2 17.5"></path></svg>;
    case 'apiGateway': return <svg {...common}><path fill="currentColor" fill-rule="evenodd" d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10s10-4.477 10-10S17.523 2 12 2m0 2c4.418 0 8 3.582 8 8s-3.582 8-8 8s-8-3.582-8-8s3.582-8 8-8" opacity=".5"></path><path fill="currentColor" d="M12 6.75a2.25 2.25 0 1 0 0 4.5a2.25 2.25 0 0 0 0-4.5M12 15.5a3.25 3.25 0 0 1 3.25 3.25h-6.5A3.25 3.25 0 0 1 12 15.5M8.5 10a1.5 1.5 0 1 1 3 0a1.5 1.5 0 0 1-3 0m7 0a1.5 1.5 0 1 1 3 0a1.5 1.5 0 0 1-3 0m-3.5 5.5a1 1 0 1 0 0-2a1 1 0 0 0 0 2" opacity=".7"></path></svg>;
    case 'endpoints': return <svg {...common}><path fill="currentColor" fill-rule="evenodd" d="M2.028 11.25A10 10 0 0 1 12 2c-.83 0-1.57.364-2.18.921c-.605.554-1.116 1.328-1.53 2.242c-.416.92-.74 1.996-.959 3.163a20 20 0 0 0-.318 2.924zm0 1.5h4.985c.036 1.002.143 1.988.318 2.924c.22 1.167.543 2.243.959 3.163c.414.914.925 1.688 1.53 2.242c.61.557 1.35.921 2.18.921c-5.27 0-9.589-4.077-9.972-9.25" clip-rule="evenodd"></path><path fill="currentColor" d="M12 2c.831 0 1.57.364 2.18.921c.605.554 1.117 1.328 1.53 2.242c.417.92.74 1.996.959 3.163c.175.936.282 1.922.318 2.924h4.985A10 10 0 0 0 12 2m4.669 13.674c-.219 1.167-.542 2.243-.959 3.163c-.413.914-.925 1.688-1.53 2.242c-.61.557-1.349.921-2.18.921c5.27 0 9.589-4.077 9.972-9.25h-4.985a20 20 0 0 1-.318 2.924"></path><path fill="currentColor" d="M12 3.396c-.275 0-.63.117-1.043.495c-.416.38-.833.977-1.201 1.79c-.366.808-.663 1.784-.867 2.873c-.16.859-.26 1.768-.296 2.696h6.814a18.5 18.5 0 0 0-.296-2.696c-.204-1.09-.5-2.065-.867-2.872c-.368-.814-.784-1.41-1.2-1.791c-.414-.378-.769-.495-1.044-.495m-3.111 12.05c.204 1.09.501 2.065.867 2.873c.368.813.785 1.41 1.2 1.79c.414.379.77.496 1.044.496c.275 0 .63-.117 1.044-.495c.416-.381.832-.978 1.2-1.791c.366-.808.663-1.783.867-2.873c.161-.858.261-1.768.296-2.696H8.593c.035.928.135 1.838.296 2.696" opacity=".5"></path></svg>;
    case 'webhooks': return <svg {...common}><g fill="currentColor" opacity=".5"><path fill-rule="evenodd" d="M12.75 22c5.172-.384 9.25-4.708 9.25-9.986V12c0-5.523-4.477-10-10-10S2 6.477 2 12v.014c0 5.278 4.078 9.602 9.25 9.986v-.028A10 10 0 0 1 2 12.007C2.004 6.48 6.48 2 12 2s9.996 4.48 10 10.007c-.004 5.267-4.08 9.582-9.25 9.965z" clip-rule="evenodd"></path><path d="M11.25 21.972a10 10 0 0 0 1.5 0c5.17-.383 9.246-4.698 9.25-9.965C21.996 6.48 17.52 2 12 2S2.004 6.48 2 12.007c.004 5.267 4.08 9.582 9.25 9.965"></path></g><path fill="currentColor" d="M8.5 12.515a3.505 3.505 0 0 0 2.75 3.424v6.033a10 10 0 0 0 1.5 0V15.94a3.505 3.505 0 0 0 2.75-3.424v-.501a1 1 0 0 0-1-1.001h-.25V9.01a.75.75 0 1 0-1.5 0v2.003h-1.5V9.01a.75.75 0 1 0-1.5 0v2.003H9.5a1 1 0 0 0-1 1.001z"></path></svg>;
    case 'docs': return <svg {...common}><path fill="currentColor" fill-rule="evenodd" d="M2.25 6a3 3 0 0 1 3-3h13.5a3 3 0 0 1 3 3v12a3 3 0 0 1-3 3H5.25a3 3 0 0 1-3-3z" clip-rule="evenodd"></path><path fill="currentColor" d="M9.75 3.75h4.5a.75.75 0 0 1 0 1.5h-4.5a.75.75 0 0 1 0-1.5m0 4.5h6a.75.75 0 0 1 0 1.5h-6a.75.75 0 0 1 0-1.5m0 4.5h6a.75.75 0 0 1 0 1.5h-6a.75.75 0 0 1 0-1.5m0 4.5h4.5a.75.75 0 0 1 0 1.5h-4.5a.75.75 0 0 1 0-1.5" opacity=".6"></path></svg>;
    case 'extension': return <svg {...common}><path fill="currentColor" d="M12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12S6.477 2 12 2m0 2c-.075 0-.15.002-.224.005C9.37 5.79 8.853 6.64 9.08 7.63c.175.762.878 1.277 1.64 1.12c.443-.092.837-.357 1.091-.75H15c.38 0 .693.28.75.658L15.65 14.6c.036.284.03.57-.015.853c-.123.762-.69 1.382-1.447 1.58A9.96 9.96 0 0 1 12 20c-1.44 0-2.81-.303-4.04-.853c-.758-.197-1.324-.818-1.447-1.58a2.6 2.6 0 0 1-.015-.853L5.25 8.388c.057-.378.37-.658.75-.658h3.189c.254-.393.648-.658 1.091-.75c.762-.157 1.465.358 1.64 1.12c.227.99-.29 1.84-1.07 2.145a.75.75 0 0 1-.54-1.434c.262-.099.44-.34.44-.62c0-.38-.28-.693-.658-.75A7.97 7.97 0 0 0 12 4a7.97 7.97 0 0 0-3.111.61c-.378.057-.658.37-.658.75c0 .28.178.52.44.62a.75.75 0 0 1-.54 1.434c-.78-.304-1.297-1.155-1.07-2.145" opacity=".5"></path><path fill="currentColor" d="M15.89 4.158a.75.75 0 0 0-1.015 1.25l.468.381c.38.308.657.712.8 1.165h-1.945a.75.75 0 0 0 0 1.5h1.945c-.143.453-.42.857-.8 1.165l-.468.381a.75.75 0 0 0 1.015 1.25l2.118-1.727a.75.75 0 0 0 .245-1.183l-.468-.381a2.25 2.25 0 0 1-.8-1.165h1.945a.75.75 0 0 0 0-1.5h-1.945c.143-.453.42-.857.8-1.165l.468-.381a.75.75 0 0 0-1.015-1.25z" opacity=".6"></path></svg>;
    case 'apiKeys': return <svg {...common}><path fill="currentColor" d="M3.464 20.536C4.93 22 7.286 22 12 22s7.071 0 8.535-1.465C22 19.072 22 16.714 22 12s0-7.071-1.465-8.536C19.072 2 16.714 2 12 2S4.929 2 3.464 3.464C2 4.93 2 7.286 2 12s0 7.071 1.464 8.535" opacity=".5"></path><path fill="currentColor" d="M18.581 9.474a.75.75 0 1 0-1.162-.948l-5.168 6.33a.749.749 0 0 0-.879 1.116l.286.438a.75.75 0 0 0 1.209.064zm-4 0a.75.75 0 1 0-1.162-.948l-5.133 6.288l-1.705-2.088a.75.75 0 0 0-1.162.948l2.286 2.8a.75.75 0 0 0 1.162 0z"></path></svg>;
    case 'subscribers':
    case 'developers':
    case 'users': return <svg {...common}><path fill="currentColor" d="M15.5 7.5a3.5 3.5 0 1 1-7 0a3.5 3.5 0 0 1 7 0"></path><path fill="currentColor" d="M19.5 7.5a2.5 2.5 0 1 1-5 0a2.5 2.5 0 0 1 5 0m-15 0a2.5 2.5 0 1 0 5 0a2.5 2.5 0 0 0-5 0" opacity=".4"></path><path fill="currentColor" d="M18 16.5c0 1.933-2.686 3.5-6 3.5s-6-1.567-6-3.5S8.686 13 12 13s6 1.567 6 3.5"></path><path fill="currentColor" d="M22 16.5c0 1.38-1.79 2.5-4 2.5s-4-1.12-4-2.5s1.79-2.5 4-2.5s4 1.12 4 2.5m-20 0C2 17.88 3.79 19 6 19s4-1.12 4-2.5S8.21 14 6 14s-4 1.12-4 2.5" opacity=".4"></path></svg>;
    case 'settings': return <svg {...common}><path fill="currentColor" fill-rule="evenodd" d="M14.279 2.152C13.909 2 13.439 2 12.5 2s-1.408 0-1.779.152a2 2 0 0 0-1.09 1.083c-.094.223-.13.484-.145.863a1.62 1.62 0 0 1-.796 1.353a1.64 1.64 0 0 1-1.579.008c-.338-.178-.583-.276-.825-.308a2.03 2.03 0 0 0-1.49.396c-.318.242-.553.646-1.022 1.453c-.47.807-.704 1.21-.757 1.605c-.07.526.074 1.058.4 1.479c.148.192.357.353.68.555c.477.297.783.803.783 1.361s-.306 1.064-.782 1.36c-.324.203-.533.364-.682.556a2 2 0 0 0-.399 1.479c.053.394.287.798.757 1.605s.704 1.21 1.022 1.453c.424.323.96.465 1.49.396c.242-.032.487-.13.825-.308a1.64 1.64 0 0 1 1.58.008c.486.28.774.795.795 1.353c.015.38.051.64.145.863c.204.49.596.88 1.09 1.083c.37.152.84.152 1.779.152s1.409 0 1.779-.152a2 2 0 0 0 1.09-1.083c.094-.223.13-.483.145-.863c.02-.558.309-1.074.796-1.353a1.64 1.64 0 0 1 1.579-.008c.338.178.583.276.825.308c.53.07 1.066-.073 1.49-.396c.318-.242.553-.646 1.022-1.453c.47-.807.704-1.21.757-1.605a2 2 0 0 0-.4-1.479c-.148-.192-.357-.353-.68-.555c-.477-.297-.783-.803-.783-1.361s.306-1.064.782-1.36c.324-.203.533-.364.682-.556a2 2 0 0 0 .399-1.479c-.053-.394-.287-.798-.757-1.605s-.704-1.21-1.022-1.453a2.03 2.03 0 0 0-1.49-.396c-.242.032-.487.13-.825.308a1.64 1.64 0 0 1-1.58-.008a1.62 1.62 0 0 1-.795-1.353c-.015-.38-.051-.64-.145-.863a2 2 0 0 0-1.09-1.083" clip-rule="evenodd" opacity=".5"></path><path fill="currentColor" d="M15.523 12c0 1.657-1.354 3-3.023 3s-3.023-1.343-3.023-3S10.83 9 12.5 9s3.023 1.343 3.023 3"></path></svg>;
    case 'general': return <svg {...common}><path fill="currentColor" d="M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12S6.477 2 12 2s10 4.477 10 10" opacity=".5"></path><path fill="currentColor" d="M12 7.75c-.621 0-1.125.504-1.125 1.125a.75.75 0 0 1-1.5 0a2.625 2.625 0 1 1 4.508 1.829q-.138.142-.264.267a7 7 0 0 0-.571.617c-.22.282-.298.489-.298.662V13a.75.75 0 0 1-1.5 0v-.75c0-.655.305-1.186.614-1.583c.229-.294.516-.58.75-.814q.106-.105.193-.194A1.125 1.125 0 0 0 12 7.75M12 17a1 1 0 1 0 0-2a1 1 0 0 0 0 2"></path></svg>;
    case 'appearance': return <svg {...common}><path fill="currentColor" d="M14.2 3H9.8C5.652 3 3.577 3 2.289 4.318S1 7.758 1 12s0 6.364 1.289 7.682S5.652 21 9.8 21h4.4c4.148 0 6.223 0 7.511-1.318S23 16.242 23 12s0-6.364-1.289-7.682S18.348 3 14.2 3" opacity=".5"></path><path fill="currentColor" d="M19.128 8.033a.825.825 0 0 0-1.056-1.268l-2.375 1.98c-1.026.855-1.738 1.447-2.34 1.833c-.582.375-.977.5-1.357.5s-.774-.125-1.357-.5c-.601-.386-1.314-.978-2.34-1.834L5.928 6.765a.825.825 0 0 0-1.056 1.268l2.416 2.014c.975.812 1.765 1.47 2.463 1.92c.726.466 1.434.762 2.25.762c.814 0 1.522-.296 2.249-.763c.697-.448 1.487-1.107 2.462-1.92z"></path></svg>;
    case 'performance': return <svg {...common}><path fill="currentColor" d="M12 22c-4.714 0-7.071 0-8.536-1.465C2 19.072 2 16.714 2 12s0-7.071 1.464-8.536C4.93 2 7.286 2 12 2s7.071 0 8.535 1.464C22 4.93 22 7.286 22 12s0 7.071-1.465 8.535C19.072 22 16.714 22 12 22" opacity=".5"></path><path fill="currentColor" fill-rule="evenodd" d="M16.656 2.75a.75.75 0 0 1 0-1.5H22a.75.75 0 0 1 .75.75v5.344a.75.75 0 0 1-1.5 0V3.81l-7.44 7.439H16a.75.75 0 0 1 0 1.5h-4a.75.75 0 0 1-.75-.75V8a.75.75 0 0 1 1.5 0v2.19l7.44-7.44z" clip-rule="evenodd"></path></svg>;
    case 'logging': return <svg {...common}><path fill="currentColor" d="M21 15.998v-6c0-2.828 0-4.242-.879-5.121C19.353 4.109 18.175 4.012 16 4H8c-2.175.012-3.353.109-4.121.877C3 5.756 3 7.17 3 9.998v6c0 2.829 0 4.243.879 5.122c.878.878 2.293.878 5.121.878h6c2.828 0 4.243 0 5.121-.878c.879-.88.879-2.293.879-5.122" opacity=".5"></path><path fill="currentColor" d="M8 3.5A1.5 1.5 0 0 1 9.5 2h5A1.5 1.5 0 0 1 16 3.5v1A1.5 1.5 0 0 1 14.5 6h-5A1.5 1.5 0 0 1 8 4.5z"></path><path fill="currentColor" fill-rule="evenodd" d="M6.25 10.5A.75.75 0 0 1 7 9.75h.5a.75.75 0 0 1 0 1.5H7a.75.75 0 0 1-.75-.75m3.5 0a.75.75 0 0 1 .75-.75H17a.75.75 0 0 1 0 1.5h-6.5a.75.75 0 0 1-.75-.75M6.25 14a.75.75 0 0 1 .75-.75h.5a.75.75 0 0 1 0 1.5H7a.75.75 0 0 1-.75-.75m3.5 0a.75.75 0 0 1 .75-.75H17a.75.75 0 0 1 0 1.5h-6.5a.75.75 0 0 1-.75-.75" clip-rule="evenodd"></path></svg>;
    case 'notifications': return <svg {...common}><path fill="currentColor" d="M18.75 9v.704c0 .845.24 1.671.692 2.374l1.108 1.723c1.011 1.574.239 3.713-1.52 4.21a25.8 25.8 0 0 1-14.06 0c-1.759-.497-2.531-2.636-1.52-4.21l1.108-1.723a4.4 4.4 0 0 0 .693-2.374V9c0-3.866 3.022-7 6.749-7s6.75 3.134 6.75 7" opacity=".5"></path><path fill="currentColor" d="M7.243 18.545a5.002 5.002 0 0 0 9.513 0c-3.145.59-6.367.59-9.513 0"></path></svg>;
    case 'security': return <svg {...common}><path fill="currentColor" d="M2 16c0-2.828 0-4.243.879-5.121C3.757 10 5.172 10 8 10h8c2.828 0 4.243 0 5.121.879C22 11.757 22 13.172 22 16s0 4.243-.879 5.121C20.243 22 18.828 22 16 22H8c-2.828 0-4.243 0-5.121-.879C2 20.243 2 18.828 2 16" opacity=".5"></path><path fill="currentColor" d="M12 18a2 2 0 1 0 0-4a2 2 0 0 0 0 4M6.75 8a5.25 5.25 0 0 1 10.5 0v2.004c.567.005 1.064.018 1.5.05V8a6.75 6.75 0 0 0-13.5 0v2.055a24 24 0 0 1 1.5-.051z"></path></svg>;
    case 'about': return <svg {...common}><path fill="currentColor" d="M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12S6.477 2 12 2s10 4.477 10 10" opacity=".5"></path><path fill="currentColor" d="M12 7.75c-.621 0-1.125.504-1.125 1.125a.75.75 0 0 1-1.5 0a2.625 2.625 0 1 1 4.508 1.829q-.138.142-.264.267a7 7 0 0 0-.571.617c-.22.282-.298.489-.298.662V13a.75.75 0 0 1-1.5 0v-.75c0-.655.305-1.186.614-1.583c.229-.294.516-.58.75-.814q.106-.105.193-.194A1.125 1.125 0 0 0 12 7.75M12 17a1 1 0 1 0 0-2a1 1 0 0 0 0 2"></path></svg>;
    default: return <svg {...common}><circle cx="12" cy="12" r="9" fill="currentColor" opacity=".25"/><circle cx="12" cy="12" r="3" fill="currentColor"/></svg>;
  }
}
function AddCircleIcon({size=18}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0}}>
      <path fill="currentColor" fillRule="evenodd" d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2S2 6.477 2 12s4.477 10 10 10m.75-13a.75.75 0 0 0-1.5 0v2.25H9a.75.75 0 0 0 0 1.5h2.25V15a.75.75 0 0 0 1.5 0v-2.25H15a.75.75 0 0 0 0-1.5h-2.25z" clipRule="evenodd"></path>
    </svg>
  );
}
function SaveActionIcon({size=18}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0}}>
      <path fill="currentColor" fillRule="evenodd" d="M20.536 20.536C22 19.07 22 16.714 22 12c0-.341 0-.512-.015-.686a4.04 4.04 0 0 0-.921-2.224a8 8 0 0 0-.483-.504l-5.167-5.167a9 9 0 0 0-.504-.483a4.04 4.04 0 0 0-2.224-.92C12.512 2 12.342 2 12 2C7.286 2 4.929 2 3.464 3.464C2 4.93 2 7.286 2 12s0 7.071 1.464 8.535c.685.685 1.563 1.05 2.786 1.243v-.83c0-.899 0-1.648.08-2.242c.084-.628.27-1.195.725-1.65c.456-.456 1.023-.642 1.65-.726c.595-.08 1.345-.08 2.243-.08h2.104c.899 0 1.648 0 2.242.08c.628.084 1.195.27 1.65.726c.456.455.642 1.022.726 1.65c.08.594.08 1.343.08 2.242v.83c1.223-.194 2.102-.558 2.785-1.242M6.25 8A.75.75 0 0 1 7 7.25h6a.75.75 0 0 1 0 1.5H7A.75.75 0 0 1 6.25 8" clipRule="evenodd"></path>
      <path fill="currentColor" d="M16.183 18.905c.065.483.067 1.131.067 2.095v.931C15.094 22 13.7 22 12 22s-3.094 0-4.25-.069V21c0-.964.002-1.612.067-2.095c.062-.461.169-.659.3-.789s.327-.237.788-.3c.483-.064 1.131-.066 2.095-.066h2c.964 0 1.612.002 2.095.067c.461.062.659.169.789.3s.237.327.3.788"></path>
    </svg>
  );
}

/** Flatten the nav tree to a list of leaves (id+label+icon) for lookups. */
function flattenNav(items){
  const out=[];
  for(const n of items){
    if(n.children){ out.push(...flattenNav(n.children)); }
    else { out.push(n); }
  }
  return out;
}
const NAV_LEAVES = flattenNav(NAV_ITEMS);
const findNavLeaf = (id) => NAV_LEAVES.find(n=>n.id===id);

/* ════════════════ Impact Analysis — e-invoicing readiness ════════════════ */
const RBAND={
  READY:{label:'Ready',c:'var(--success)',bg:'var(--success-bg)',bd:'var(--success-border)'},
  NEARLY_READY:{label:'Nearly Ready',c:'#0891b2',bg:'#ecfeff',bd:'#a5f3fc'},
  SIGNIFICANT_GAPS:{label:'Significant Gaps',c:'var(--warning)',bg:'var(--warning-bg)',bd:'var(--warning-border)'},
  NOT_READY:{label:'Not Ready',c:'var(--error)',bg:'var(--error-bg)',bd:'var(--error-border)'},
  CRITICAL_GAPS:{label:'Critical Gaps',c:'var(--error)',bg:'var(--error-bg)',bd:'var(--error-border)'},
  BLOCKED:{label:'Critical Gaps',c:'var(--error)',bg:'var(--error-bg)',bd:'var(--error-border)'}, // pre-rename records
};
const RSTAT={
  PASS:{i:'✓',c:'var(--success)',bg:'var(--success-bg)',bd:'var(--success-border)'},
  WARN:{i:'!',c:'var(--warning)',bg:'var(--warning-bg)',bd:'var(--warning-border)'},
  FAIL:{i:'✕',c:'var(--error)',bg:'var(--error-bg)',bd:'var(--error-border)'},
  BLOCKED:{i:'∅',c:'var(--text3)',bg:'var(--surface3)',bd:'var(--border)'},
  SKIPPED:{i:'–',c:'var(--text3)',bg:'var(--surface3)',bd:'var(--border)'},
  INFO:{i:'i',c:'var(--info)',bg:'var(--info-bg)',bd:'var(--info-border)'},
};
const SEVC={CRITICAL:'var(--error)',HIGH:'var(--warning)',MEDIUM:'#0891b2',LOW:'var(--text3)',INFO:'var(--text3)'};
const rband=(b)=>RBAND[b]||RBAND.NOT_READY;
// Declared in more than one script and intentionally `var`: these are classic scripts
// sharing one global scope, so `const` here is a redeclaration SyntaxError the moment
// Babel stops downlevelling to ES5 (data-presets="react"). `var` is last-one-wins,
// which is exactly the behaviour this had while the es2015 transform was running.
var TAXLAB_CTA_STYLE={
  fontFamily:'"Public Sans", sans-serif',
  fontWeight:600,
  fontSize:14,
  border:'none',
  cursor:'pointer',
  opacity:1,
  borderRadius:10,
  padding:'9px 18px',
  display:'inline-flex',
  alignItems:'center',
  justifyContent:'center',
  gap:8,
  transition:'0.18s',
  whiteSpace:'nowrap',
  width:'auto',
  height:40,
  lineHeight:1.4,
  boxSizing:'border-box',
  background:'linear-gradient(90deg, rgb(237, 76, 139) 0%, rgb(241, 91, 94) 25%, rgb(247, 108, 43) 50%, rgb(249, 114, 23) 100%)',
  color:'rgb(255, 255, 255)',
};
// Intentionally `var`, not `const` — shared global scope; see the note in index.html.
var TAXLAB_LIGHT_STYLE={
  fontFamily:'"Public Sans", sans-serif',
  fontWeight:600,
  fontSize:14,
  border:'1px solid rgb(196, 205, 213)',
  cursor:'pointer',
  opacity:1,
  borderRadius:10,
  padding:'9px 18px',
  display:'inline-flex',
  alignItems:'center',
  justifyContent:'center',
  gap:8,
  transition:'0.18s',
  whiteSpace:'nowrap',
  width:'auto',
  height:40,
  lineHeight:1.4,
  boxSizing:'border-box',
  background:'rgb(255, 255, 255)',
  color:'rgb(29, 37, 46)',
};

/* ───────────────────────── Impact charts ─────────────────────────────────
   Hand-rolled SVG on purpose. The SPA ships as a single file with no build
   step and a strict CSP, so a charting library would mean either a blocked
   CDN request or a bundle larger than the whole application. Every colour
   resolves to a theme variable, so the charts follow light/dark with the rest
   of the UI instead of hard-coding a palette that breaks in one of them. */

const RATING_ORDER=['A','P','N','-'];
const RATING_LABEL={A:'Available',P:'Partial',N:'Not available','-':'Not triggered'};
const APPROACH_C={
  'N/A – Available':'var(--success)',
  'Transformation logic (mapping layer)':'#0891b2',
  'Change within ERP':'var(--warning)',
  'ASP (fixed platform constant)':'var(--info)',
  'N/A – condition not triggered':'var(--text3)',
};
const approachC=(k)=>APPROACH_C[k]||'var(--text3)';

/* Count field rows by any key, preserving a stable display order. */
function tally(rows,key){
  const m={};
  (rows||[]).forEach(r=>{ const k=r[key]||'—'; m[k]=(m[k]||0)+1; });
  return m;
}

/* A ring rather than a pie: the centre is where the number that matters goes,
   and a reader compares arc lengths far more reliably than wedge areas. */
function ChartDonut({data,order,colors,labels,size=148,thickness=19,centre,centreSub}){
  const keys=(order||Object.keys(data)).filter(k=>(data[k]||0)>0);
  const total=keys.reduce((s,k)=>s+(data[k]||0),0);
  if(!total) return <div style={{fontSize:12,color:'var(--text3)'}}>No data</div>;
  const r=(size-thickness)/2, circ=2*Math.PI*r;
  let off=0;
  return (
    <div style={{display:'flex',gap:18,alignItems:'center',flexWrap:'wrap'}}>
      <div style={{position:'relative',width:size,height:size,flexShrink:0}}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img">
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--surface3)" strokeWidth={thickness}/>
          <g transform={`rotate(-90 ${size/2} ${size/2})`}>
            {keys.map(k=>{
              const len=circ*((data[k]||0)/total), el=(
                <circle key={k} cx={size/2} cy={size/2} r={r} fill="none"
                  stroke={(colors&&colors[k])||'var(--text3)'} strokeWidth={thickness}
                  strokeDasharray={`${len} ${circ-len}`} strokeDashoffset={-off}
                  strokeLinecap="butt"><title>{`${(labels&&labels[k])||k}: ${data[k]}`}</title></circle>
              );
              off+=len; return el;
            })}
          </g>
        </svg>
        <div style={{position:'absolute',inset:0,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',pointerEvents:'none'}}>
          <div style={{fontSize:26,fontWeight:800,color:'var(--text)',lineHeight:1}}>{centre!=null?centre:total}</div>
          {centreSub&&<div style={{fontSize:10.5,color:'var(--text3)',marginTop:3,textAlign:'center',maxWidth:size-44,lineHeight:1.25}}>{centreSub}</div>}
        </div>
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:6,minWidth:150}}>
        {keys.map(k=>(
          <div key={k} style={{display:'flex',alignItems:'center',gap:8,fontSize:12.5}}>
            <span style={{width:10,height:10,borderRadius:3,background:(colors&&colors[k])||'var(--text3)',flexShrink:0}}/>
            <span style={{color:'var(--text2)',flex:1}}>{(labels&&labels[k])||k}</span>
            <b style={{color:'var(--text)'}}>{data[k]}</b>
            <span style={{color:'var(--text3)',width:36,textAlign:'right'}}>{Math.round((data[k]/total)*100)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Ranked horizontal bars. Sorted by value because the question a reader has is
   "what is biggest", and an alphabetical axis makes them do that work by eye. */
function ChartBars({data,colorFn,labelWidth=210,sort=true,max}){
  let entries=Object.entries(data||{}).filter(([,v])=>v>0);
  if(sort) entries=entries.sort((a,b)=>b[1]-a[1]);
  if(!entries.length) return <div style={{fontSize:12,color:'var(--text3)'}}>No data</div>;
  const top=max||Math.max(...entries.map(([,v])=>v));
  const total=entries.reduce((s,[,v])=>s+v,0);
  return (
    <div style={{display:'flex',flexDirection:'column',gap:9}}>
      {entries.map(([k,v])=>(
        <div key={k} style={{display:'flex',alignItems:'center',gap:10,fontSize:12.5}}>
          <span style={{width:labelWidth,flexShrink:0,color:'var(--text2)',lineHeight:1.3}}>{k}</span>
          <div style={{flex:1,height:14,background:'var(--surface3)',borderRadius:4,overflow:'hidden',minWidth:60}}>
            <div style={{height:'100%',width:Math.max(2,(v/top)*100)+'%',background:colorFn?colorFn(k):'var(--info)',borderRadius:4,transition:'width .45s'}}/>
          </div>
          <b style={{width:34,textAlign:'right',color:'var(--text)'}}>{v}</b>
          <span style={{width:36,textAlign:'right',color:'var(--text3)'}}>{Math.round((v/total)*100)}%</span>
        </div>
      ))}
    </div>
  );
}

/* One entity, one 100%-stacked bar. Comparing business units is the whole point
   of scoping per VAT registration, and side-by-side bars make an outlier obvious. */
function ChartStack({rows,height=20}){
  if(!rows||!rows.length) return null;
  return (
    <div style={{display:'flex',flexDirection:'column',gap:12}}>
      {rows.map((r,i)=>{
        const total=RATING_ORDER.reduce((s,k)=>s+(r.counts[k]||0),0)||1;
        return (
          <div key={i}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',gap:10,marginBottom:5,fontSize:12.5,flexWrap:'wrap'}}>
              <span style={{fontWeight:700,color:'var(--text)'}}>{r.label}</span>
              <span style={{color:'var(--text3)',fontSize:11.5}}>{r.sub}</span>
            </div>
            <div style={{display:'flex',height,borderRadius:5,overflow:'hidden',background:'var(--surface3)'}}>
              {RATING_ORDER.map(k=>{
                const v=r.counts[k]||0; if(!v) return null;
                return <div key={k} title={`${RATING_LABEL[k]}: ${v}`} style={{width:(v/total)*100+'%',background:RATINGC[k],display:'flex',alignItems:'center',justifyContent:'center'}}>
                  {(v/total)>0.07&&<span style={{fontSize:10.5,fontWeight:800,color:'#fff'}}>{v}</span>}
                </div>;
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* Where the gaps cluster. A list of 188 rated rows tells you a count; a section
   grid tells you it is all in Buyer identification, which is one conversation
   with one team rather than a project-wide scramble. */
function ChartHeatmap({fields}){
  const sections=[];
  const grid={};
  (fields||[]).forEach(f=>{
    const s=f.section||'—';
    if(!grid[s]){ grid[s]={A:0,P:0,N:0,'-':0}; sections.push(s); }
    const r=RATING_ORDER.includes(f.rating)?f.rating:'-';
    grid[s][r]++;
  });
  if(!sections.length) return null;
  const worst=(s)=>{ const g=grid[s]; const app=g.A+g.P+g.N; return app?(g.N*2+g.P)/(app*2):0; };
  sections.sort((a,b)=>worst(b)-worst(a));
  const cell=(v,c,tot)=>(
    <td style={{padding:'0 3px'}}>
      <div title={v+' field(s)'} style={{height:26,borderRadius:5,display:'flex',alignItems:'center',justifyContent:'center',
        fontSize:11.5,fontWeight:700,color:v?'#fff':'var(--text3)',
        background:v?c:'var(--surface3)',opacity:v?Math.min(1,0.42+0.58*(v/Math.max(1,tot))):1}}>{v||'·'}</div>
    </td>
  );
  return (
    <div style={{overflowX:'auto'}}>
      <table style={{width:'100%',borderCollapse:'separate',borderSpacing:'0 4px',fontSize:12}}>
        <thead><tr style={{color:'var(--text3)',fontSize:10.5,textAlign:'center'}}>
          <th style={{textAlign:'left',padding:'0 6px'}}>PINT section</th>
          {RATING_ORDER.map(k=><th key={k} style={{padding:'0 3px',width:'12%'}}>{RATING_LABEL[k]}</th>)}
          <th style={{padding:'0 6px',width:90,textAlign:'right'}}>Gap load</th>
        </tr></thead>
        <tbody>
          {sections.map(s=>{
            const g=grid[s], tot=g.A+g.P+g.N+g['-'], load=Math.round(worst(s)*100);
            return (
              <tr key={s}>
                <td style={{padding:'0 6px',color:'var(--text2)',whiteSpace:'nowrap'}}>{s}</td>
                {RATING_ORDER.map(k=><React.Fragment key={k}>{cell(g[k],RATINGC[k],tot)}</React.Fragment>)}
                <td style={{padding:'0 6px'}}>
                  <div style={{display:'flex',alignItems:'center',gap:6,justifyContent:'flex-end'}}>
                    <div style={{width:44,height:6,background:'var(--surface3)',borderRadius:4,overflow:'hidden'}}>
                      <div style={{height:'100%',width:load+'%',background:load>60?'var(--error)':load>30?'var(--warning)':'var(--success)'}}/>
                    </div>
                    <span style={{color:'var(--text3)',fontSize:11,width:28,textAlign:'right'}}>{load}%</span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* Translation coverage per mapping table. This is the chart that stops the report
   flattering itself: a field can be populated on every invoice and still be
   unusable because nothing turns its code into the PINT one. */
function ChartTranslations({fields}){
  const byId={};
  (fields||[]).forEach(f=>{
    if(!f.lookupId||f.lookupCoverage==null||f.lookupCoverage<0) return;
    const e=byId[f.lookupId]||(byId[f.lookupId]={pct:f.lookupCoverage,unmapped:[],exists:f.lookupExists!==false,fields:[]});
    e.pct=Math.min(e.pct,f.lookupCoverage);
    e.fields.push(f.id);
    (f.lookupUnmapped||[]).forEach(u=>{ if(!e.unmapped.includes(u)) e.unmapped.push(u); });
  });
  const ids=Object.keys(byId);
  if(!ids.length) return null;
  ids.sort((a,b)=>byId[a].pct-byId[b].pct);
  return (
    <div className="card" style={{marginBottom:16}}>
      <div style={{fontWeight:800,fontSize:14}}>Code translation coverage</div>
      <div style={{fontSize:12,color:'var(--text3)',marginBottom:12,lineHeight:1.5}}>
        Each mapping table measured against the codes this tenant actually uses. A field whose
        table cannot translate every observed code is rated <b>Partial</b> however well populated
        the source column is — the value would reach the ASP untranslated.
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:13}}>
        {ids.map(id=>{
          const e=byId[id], c=e.pct===100?'var(--success)':e.pct>0?'var(--warning)':'var(--error)';
          return (
            <div key={id}>
              <div style={{display:'flex',alignItems:'baseline',gap:8,flexWrap:'wrap',marginBottom:4}}>
                <code style={{fontSize:12,color:'var(--text)',fontWeight:700}}>{id}</code>
                <span style={{fontSize:11,color:'var(--text3)'}}>{e.fields.join(', ')}</span>
                <div style={{flex:1}}/>
                <span style={{fontSize:12.5,fontWeight:800,color:c}}>{e.pct}%</span>
              </div>
              <div style={{height:9,background:'var(--surface3)',borderRadius:5,overflow:'hidden'}}>
                <div style={{height:'100%',width:Math.max(1.5,e.pct)+'%',background:c,borderRadius:5,transition:'width .45s'}}/>
              </div>
              {!e.exists&&<div style={{fontSize:11.5,color:'var(--error)',marginTop:4}}>No translation table exists yet — build it.</div>}
              {e.exists&&e.unmapped.length>0&&(
                <div style={{fontSize:11.5,color:'var(--text2)',marginTop:4}}>
                  Codes with no entry: {e.unmapped.map((u,i)=>(
                    <code key={i} style={{background:'var(--surface3)',border:'1px solid var(--border)',borderRadius:4,padding:'1px 5px',marginRight:4,fontSize:11}}>{u}</code>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* What to do first. Weight × severity, so the ranking reflects the scoring model
   the band came from rather than a reader's impression of which finding sounds worse. */
function ChartPriorities({checks,limit=8}){
  const SEVW={CRITICAL:4,HIGH:3,MEDIUM:2,LOW:1,INFO:0};
  const open=(checks||[]).filter(c=>(c.status==='FAIL'||c.status==='WARN')&&c.category!==TAXQ_CAT)
    .map(c=>({...c,_w:(c.weight||1)*(SEVW[c.severity]!=null?SEVW[c.severity]:1)+(c.status==='FAIL'?1:0)}))
    .sort((a,b)=>b._w-a._w).slice(0,limit);
  if(!open.length) return null;
  const top=open[0]._w||1;
  return (
    <div className="card" style={{marginBottom:16}}>
      <div style={{fontWeight:800,fontSize:14}}>Remediation priority</div>
      <div style={{fontSize:12,color:'var(--text3)',marginBottom:12}}>Open findings ranked by severity and scoring weight — the order to work them in.</div>
      <div style={{display:'flex',flexDirection:'column',gap:10}}>
        {open.map((c,i)=>(
          <div key={i} style={{display:'flex',alignItems:'center',gap:10,fontSize:12.5}}>
            <span style={{width:20,height:20,borderRadius:5,background:'var(--surface3)',color:'var(--text3)',fontSize:11,fontWeight:800,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>{i+1}</span>
            <span style={{flex:'0 0 42%',minWidth:0,color:'var(--text)',fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={c.title}>{c.title}</span>
            <div style={{flex:1,height:13,background:'var(--surface3)',borderRadius:4,overflow:'hidden',minWidth:50}}>
              <div style={{height:'100%',width:Math.max(3,(c._w/top)*100)+'%',background:c.status==='FAIL'?'var(--error)':'var(--warning)',borderRadius:4}}/>
            </div>
            <span style={{fontSize:9.5,fontWeight:800,letterSpacing:.5,color:SEVC[c.severity]||'var(--text3)',border:'1px solid var(--border2)',borderRadius:5,padding:'0 5px',flexShrink:0}}>{c.severity}</span>
            {c.effort&&c.effort!=='—'&&<span style={{color:'var(--text3)',fontSize:11,flexShrink:0,width:96,textAlign:'right',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={c.effort}>{c.effort}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

/* The one card a CFO reads. Everything below it is the evidence for these numbers. */
function ImpactDashboard({a}){
  const fields=a.fieldMappings||[];
  if(!fields.length) return null;
  const applicable=fields.filter(f=>f.rating!=='-');
  const mand=applicable.filter(f=>f.requirement==='M'||f.requirement==='CM');
  const mandOk=mand.filter(f=>f.rating==='A').length;
  const ratings=tally(fields,'rating');
  const approaches=tally(fields,'approach');
  const pctMand=mand.length?Math.round((mandOk/mand.length)*100):0;
  const pill=(label,val,color,sub)=>(
    <div style={{flex:'1 1 150px',minWidth:140,background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:11,padding:'12px 14px'}}>
      <div style={{fontSize:22,fontWeight:800,color:color||'var(--text)',lineHeight:1.1}}>{val}</div>
      <div style={{fontSize:11.5,color:'var(--text2)',marginTop:3,fontWeight:600}}>{label}</div>
      {sub&&<div style={{fontSize:10.5,color:'var(--text3)',marginTop:2,lineHeight:1.3}}>{sub}</div>}
    </div>
  );
  return (
    <div className="card" style={{marginBottom:16}}>
      <div style={{fontWeight:800,fontSize:14}}>Where this tenant stands</div>
      <div style={{fontSize:12,color:'var(--text3)',marginBottom:14}}>
        Measured against PINT AE {a.catalogVersion||''} across {fields.length} field rows{a.entities&&a.entities.length?' and '+a.entities.length+' in-scope entit'+(a.entities.length>1?'ies':'y'):''}.
      </div>
      <div style={{display:'flex',gap:10,flexWrap:'wrap',marginBottom:20}}>
        {pill('Mandatory fields ready',mandOk+' / '+mand.length,pctMand>=80?'var(--success)':pctMand>=50?'var(--warning)':'var(--error)',pctMand+'% of what the mandate requires')}
        {pill('Gaps to close',(ratings.N||0)+(ratings.P||0),'var(--error)',(ratings.N||0)+' not available · '+(ratings.P||0)+' partial')}
        {pill('ERP changes needed',approaches['Change within ERP']||0,'var(--warning)','configuration or data work in Fusion')}
        {pill('Mapping-layer work',approaches['Transformation logic (mapping layer)']||0,'#0891b2','handled by the integration')}
        {pill('Out of scope for now',ratings['-']||0,'var(--text3)','conditions this tenant never triggers')}
      </div>
      <div style={{display:'flex',gap:28,flexWrap:'wrap',alignItems:'flex-start'}}>
        <div style={{flex:'1 1 330px',minWidth:300}}>
          <div style={{fontSize:12,fontWeight:700,color:'var(--text2)',marginBottom:10}}>Field readiness</div>
          <ChartDonut data={ratings} order={RATING_ORDER} colors={RATINGC} labels={RATING_LABEL}
            centre={pctMand+'%'} centreSub="mandatory fields available"/>
        </div>
        <div style={{flex:'1 1 360px',minWidth:300}}>
          <div style={{fontSize:12,fontWeight:700,color:'var(--text2)',marginBottom:10}}>How each gap gets closed</div>
          <ChartBars data={approaches} colorFn={approachC} labelWidth={192}/>
        </div>
      </div>
    </div>
  );
}

/* Gap distribution by PINT section, plus the per-entity comparison. */
function ImpactGapMap({a}){
  const fields=a.fieldMappings||[];
  const es=a.entities||[];
  if(!fields.length) return null;
  const rows=es.filter(e=>e.ratingCounts).map(e=>({
    label:e.businessUnit||'—',
    counts:e.ratingCounts||{},
    sub:(e.mandatoryAvailable!=null&&e.mandatoryTotal?e.mandatoryAvailable+'/'+e.mandatoryTotal+' mandatory · ':'')
        +(e.invoices!=null?e.invoices+' invoices sampled':''),
  }));
  return (
    <div className="card" style={{marginBottom:16}}>
      <div style={{fontWeight:800,fontSize:14}}>Where the gaps are</div>
      <div style={{fontSize:12,color:'var(--text3)',marginBottom:14}}>
        Gap load weights a missing field twice a partial one, so the sections at the top are where
        the implementation effort concentrates.
      </div>
      <ChartHeatmap fields={fields}/>
      {rows.length>1&&(<>
        <div style={{fontWeight:700,fontSize:13,margin:'22px 0 4px'}}>By business unit</div>
        <div style={{fontSize:12,color:'var(--text3)',marginBottom:12}}>
          The mandate applies per VAT registration, so a unit that diverges here needs its own remediation plan.
        </div>
        <ChartStack rows={rows}/>
        <div style={{display:'flex',gap:14,flexWrap:'wrap',marginTop:12,fontSize:11.5}}>
          {RATING_ORDER.map(k=>(
            <span key={k} style={{display:'inline-flex',alignItems:'center',gap:6,color:'var(--text3)'}}>
              <span style={{width:10,height:10,borderRadius:3,background:RATINGC[k]}}/>{RATING_LABEL[k]}
            </span>
          ))}
        </div>
      </>)}
    </div>
  );
}

function ImpactDist({data,limit=14}){
  let entries=[];
  if(Array.isArray(data)) entries=data.map(v=>[v,null]);
  else if(data&&typeof data==='object') entries=Object.entries(data).sort((a,b)=>(b[1]||0)-(a[1]||0));
  if(!entries.length) return <span style={{color:'var(--text3)',fontSize:12}}>—</span>;
  return <div style={{display:'flex',flexWrap:'wrap',gap:6,marginTop:5}}>
    {entries.slice(0,limit).map(([k,v],i)=>(
      <span key={i} style={{padding:'2px 9px',background:'var(--surface3)',border:'1px solid var(--border)',borderRadius:20,fontSize:11.5,color:'var(--text2)'}}>{String(k)}{v!=null?<b style={{marginLeft:5,color:'var(--text)'}}>{v}</b>:null}</span>
    ))}
    {entries.length>limit?<span style={{fontSize:11.5,color:'var(--text3)',alignSelf:'center'}}>+{entries.length-limit} more</span>:null}
  </div>;
}

const TRACK_STATE={
  IN_PROGRESS:               {label:'In hand',        c:'var(--info)',    bg:'var(--surface3)',   bd:'var(--border2)'},
  ACCEPTED:                  {label:'Accepted',       c:'var(--text3)',   bg:'var(--surface3)',   bd:'var(--border)'},
  FIXED_PENDING_VERIFICATION:{label:'Reported fixed', c:'var(--warning)', bg:'var(--warning-bg)', bd:'var(--warning-border)'},
};
const TRACK_DRIFT={
  OVERDUE:    'Due date passed and still open',
  REGRESSED:  'This has got worse since it was marked',
  LOOKS_FIXED:'This run reads it as resolved — worth closing',
  UNCONFIRMED:'Reported fixed, but the data still says otherwise',
  ORPHANED:   'Nothing in this run matches it any more',
};

/* Work already underway, shown against the finding it belongs to.

   A report that proposes work a client is already doing gets ignored, and it takes the rest of
   the report with it. Both manual assessments carry this state for exactly that reason — one
   says outright, against two of its findings, to track them against the existing go-live
   timeline rather than raise independent work items.

   Three things this deliberately does not do. It does not change the score: a gap being worked
   on is still a gap, and if saying "we are on it" moved the number the number would measure
   optimism. It does not hide the finding. And it does not take the claim on trust — every note
   is re-checked against the run it is shown beside, so one that has gone stale says so instead
   of quietly reassuring. */
function TrackingBadge({note}){
  if(!note) return null;
  const st=TRACK_STATE[note.state]||TRACK_STATE.IN_PROGRESS;
  const drift=note.drift&&note.drift!=='ON_TRACK'?TRACK_DRIFT[note.drift]:null;
  return (
    <span title={note.driftNote||note.note||''} style={{display:'inline-flex',alignItems:'center',gap:5,
      fontSize:9.5,fontWeight:800,letterSpacing:.4,padding:'1px 7px',borderRadius:5,
      color:drift?'var(--warning)':st.c,background:drift?'var(--warning-bg)':st.bg,
      border:'1px solid '+(drift?'var(--warning-border)':st.bd),whiteSpace:'nowrap'}}>
      {st.label}{note.reference?' · '+note.reference:''}{drift?' · !':''}
    </span>
  );
}

function TrackingDetail({note}){
  if(!note) return null;
  const drift=note.drift&&note.drift!=='ON_TRACK';
  return (
    <div style={{marginTop:7,padding:'8px 11px',borderRadius:8,fontSize:12,lineHeight:1.45,
      background:drift?'var(--warning-bg)':'var(--surface2)',
      border:'1px solid '+(drift?'var(--warning-border)':'var(--border)')}}>
      <span style={{fontWeight:700,color:'var(--text)'}}>Already in hand:</span>{' '}
      <span style={{color:'var(--text2)'}}>
        {note.reference?note.reference:'no reference recorded'}
        {note.owner?' · '+note.owner:''}
        {note.dueDate?' · due '+note.dueDate:''}
      </span>
      <div style={{color:'var(--text3)',marginTop:4}}>
        Track it there — this report should not raise an independent work item for it.
      </div>
      {note.note&&<div style={{color:'var(--text3)',marginTop:3}}>↳ {note.note}</div>}
      {drift&&(
        <div style={{color:'var(--warning)',marginTop:5,fontWeight:600}}>
          {note.driftNote||TRACK_DRIFT[note.drift]}
        </div>
      )}
    </div>
  );
}

/* The editor. A read-only tracking feature is one nobody can populate, so it would record
   nothing and the report would keep re-proposing the same work.

   The one rule enforced here as well as on the server: "in hand" needs a reference. Without
   one it is indistinguishable from "we discussed it once", and it is the state most likely to
   be set optimistically and then forgotten — which would turn this from a way to focus a
   report into a way to make it look calmer. */
function TrackingEditor({connectionId,target,observedNow,note,onSaved,showToast}){
  const [open,setOpen]=React.useState(false);
  const [state,setState]=React.useState(note?note.state:'IN_PROGRESS');
  const [reference,setReference]=React.useState(note?note.reference||'':'');
  const [owner,setOwner]=React.useState(note?note.owner||'':'');
  const [dueDate,setDueDate]=React.useState(note?note.dueDate||'':'');
  const [text,setText]=React.useState(note?note.note||'':'');
  const [busy,setBusy]=React.useState(false);

  const needsRef=state==='IN_PROGRESS'&&!reference.trim();

  const save=async()=>{
    if(needsRef) return;
    setBusy(true);
    try{
      await API('POST','/api/readiness/remediation',{connectionId,target,state,reference,owner,
        dueDate:dueDate||null,note:text,observedWhenMarked:note?note.observedWhenMarked:observedNow});
      setOpen(false);
      if(showToast) showToast('Recorded','success');
      if(onSaved) onSaved();
    }catch(e){ if(showToast) showToast('Could not save: '+e.message,'error'); }
    finally{ setBusy(false); }
  };
  const clear=async()=>{
    setBusy(true);
    try{
      await API('DELETE','/api/readiness/remediation/'+note.id);
      setOpen(false);
      if(showToast) showToast('Closed','success');
      if(onSaved) onSaved();
    }catch(e){ if(showToast) showToast('Could not close: '+e.message,'error'); }
    finally{ setBusy(false); }
  };

  if(!open) return (
    <button onClick={()=>setOpen(true)} style={{marginTop:6,fontSize:11,padding:'2px 9px',borderRadius:6,
      border:'1px solid var(--border2)',background:'transparent',color:'var(--text3)',cursor:'pointer'}}>
      {note?'Edit tracking':'Mark as already in hand'}
    </button>
  );

  const inp={fontSize:12,padding:'4px 7px',borderRadius:6,border:'1px solid var(--border2)',
    background:'var(--surface)',color:'var(--text)',width:'100%'};
  return (
    <div style={{marginTop:7,padding:'10px 11px',borderRadius:8,background:'var(--surface2)',
      border:'1px solid var(--border)'}}>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:8}}>
        <label style={{fontSize:11,color:'var(--text3)'}}>State
          <select value={state} onChange={e=>setState(e.target.value)} style={inp}>
            <option value="IN_PROGRESS">Remediation in progress</option>
            <option value="FIXED_PENDING_VERIFICATION">Reported fixed, not yet visible</option>
            <option value="ACCEPTED">Accepted — no work planned</option>
          </select>
        </label>
        <label style={{fontSize:11,color:'var(--text3)'}}>
          Reference{state==='IN_PROGRESS'?' (required)':''}
          <input value={reference} onChange={e=>setReference(e.target.value)} placeholder="BRD / ticket / workstream"
            style={{...inp,borderColor:needsRef?'var(--error)':'var(--border2)'}}/>
        </label>
        <label style={{fontSize:11,color:'var(--text3)'}}>Owner
          <input value={owner} onChange={e=>setOwner(e.target.value)} placeholder="who owns it" style={inp}/>
        </label>
        <label style={{fontSize:11,color:'var(--text3)'}}>Expected
          <input value={dueDate} onChange={e=>setDueDate(e.target.value)} placeholder="YYYY-MM-DD" style={inp}/>
        </label>
      </div>
      <input value={text} onChange={e=>setText(e.target.value)} placeholder="anything a reader would need to know"
        style={{...inp,marginTop:8}}/>
      {needsRef&&(
        <div style={{fontSize:11,color:'var(--error)',marginTop:6,lineHeight:1.4}}>
          A reference is required for work in progress — without one the report cannot tell a
          reader where it already lives, and nobody can check the claim later.
        </div>
      )}
      <div style={{display:'flex',gap:7,marginTop:9,alignItems:'center'}}>
        <button onClick={save} disabled={busy||needsRef} style={{fontSize:12,padding:'4px 12px',borderRadius:6,
          border:'none',background:needsRef?'var(--surface3)':'var(--brand)',
          color:needsRef?'var(--text3)':'#fff',cursor:needsRef?'not-allowed':'pointer'}}>Save</button>
        <button onClick={()=>setOpen(false)} disabled={busy} style={{fontSize:12,padding:'4px 10px',borderRadius:6,
          border:'1px solid var(--border2)',background:'transparent',color:'var(--text2)',cursor:'pointer'}}>Cancel</button>
        <div style={{flex:1}}/>
        {note&&<button onClick={clear} disabled={busy} style={{fontSize:11,padding:'4px 10px',borderRadius:6,
          border:'1px solid var(--border2)',background:'transparent',color:'var(--error)',cursor:'pointer'}}>
          Close note
        </button>}
      </div>
      <div style={{fontSize:11,color:'var(--text3)',marginTop:7,lineHeight:1.45}}>
        This does not change the score and does not retire the finding. A gap being worked on is
        still a gap until the data shows otherwise — it changes who should act, not whether
        there is a problem.
      </div>
    </div>
  );
}

function ImpactCheckRow({c,note,connectionId,onTracked,showToast}){
  const s=RSTAT[c.status]||RSTAT.SKIPPED;
  const open=c.status!=='PASS'&&c.status!=='INFO'&&!c.notAssessed;
  // A tracked finding keeps its row and its status; only the instruction changes.
  const showFix=c.remediation&&open&&!note;
  return (
    <div style={{display:'flex',gap:11,padding:'11px 0',borderBottom:'1px solid var(--border)'}}>
      <div style={{width:22,height:22,borderRadius:6,background:s.bg,color:s.c,border:'1px solid '+s.bd,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:800,fontSize:12,flexShrink:0,marginTop:1}}>{s.i}</div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{display:'flex',gap:8,alignItems:'baseline',flexWrap:'wrap'}}>
          <span style={{fontWeight:700,fontSize:13.5,color:'var(--text)'}}>{c.title}</span>
          {c.severity&&<span style={{fontSize:9.5,fontWeight:700,letterSpacing:.5,color:SEVC[c.severity]||'var(--text3)',border:'1px solid var(--border2)',borderRadius:5,padding:'0 5px',textTransform:'uppercase'}}>{c.severity}</span>}
          {c.metric&&<span style={{fontSize:12,color:'var(--text2)'}}>{c.metric}</span>}
          <TrackingBadge note={note}/>
        </div>
        {c.finding&&<div style={{fontSize:12.5,color:'var(--text2)',marginTop:3,lineHeight:1.45}}>{c.finding}</div>}
        {showFix&&(
          <div style={{marginTop:7,padding:'8px 11px',background:'var(--surface2)',borderRadius:8,border:'1px solid var(--border)',fontSize:12,lineHeight:1.45}}>
            <span style={{fontWeight:700,color:'var(--text)'}}>Fix:</span> <span style={{color:'var(--text2)'}}>{c.remediation}</span>
            {c.fusionPath&&<div style={{color:'var(--text3)',marginTop:4}}>↳ {c.fusionPath}</div>}
            {c.effort&&<div style={{color:'var(--text3)',marginTop:2}}>Effort: {c.effort}</div>}
          </div>
        )}
        <TrackingDetail note={note}/>
        {open&&connectionId&&(
          <TrackingEditor connectionId={connectionId} target={'check:'+c.id} observedNow={c.status}
            note={note} onSaved={onTracked} showToast={showToast}/>
        )}
      </div>
    </div>
  );
}

const AVAILC={
  AVAILABLE:{l:'Available',c:'var(--success)'},
  CONSTANT:{l:'Constant',c:'var(--success)'},
  COMPUTED:{l:'Computed',c:'var(--info)'},
  PARTIAL:{l:'Partial',c:'var(--warning)'},
  ENRICHMENT:{l:'Needs enrichment',c:'var(--warning)'},
  LOOKUP:{l:'Needs lookup',c:'var(--warning)'},
  DFF_MISSING:{l:'Needs DFF',c:'var(--error)'},
  EMPTY:{l:'Empty',c:'var(--error)'},
  MISSING:{l:'Not in REST',c:'var(--error)'},
  NOT_APPLICABLE:{l:'Not applicable',c:'var(--text3)'},
};
const availInfo=(a)=>AVAILC[a]||{l:a||'—',c:'var(--text3)'};
const RATINGC={A:'var(--success)',P:'var(--warning)',N:'var(--error)','-':'var(--text3)'};

/* Per-entity scope, VAT groups, indicative phase and the applicability matrix.
   The mandate applies per VAT registration, so this is what a consultant reads first. */
function ImpactEntities({a}){
  const es=a.entities||[];
  if(!es.length) return null;
  const th={padding:'6px 8px',fontWeight:700,whiteSpace:'nowrap'};
  const td={padding:'6px 8px',verticalAlign:'top'};
  const conds=Object.keys(a.applicability||{});
  const yn=(v)=>v===true?<span style={{color:'var(--success)',fontWeight:700}}>Yes</span>
    :v===false?<span style={{color:'var(--text3)'}}>No</span>:<span style={{color:'var(--text3)'}}>—</span>;
  const num=(n)=>(n==null?'—':Number(n).toLocaleString());
  return (
    <div className="card" style={{marginBottom:16}}>
      <div style={{fontWeight:800,fontSize:14}}>Entity scope, applicability &amp; phase</div>
      <div style={{fontSize:12,color:'var(--text3)',marginBottom:10}}>
        {es.length} business unit{es.length>1?'s':''} in the sample
        {a.businessUnitFilter?<> · scoped to <b>{a.businessUnitFilter}</b></>:' · whole-tenant run'}
        {a.windowApplied===false&&<span style={{color:'var(--warning)'}}> · the pod rejected the scope filter, so this is the newest {a.sampleSize} invoices across the whole population</span>}
        {a.catalogVersion&&<> · rated against <b>PINT AE v{a.catalogVersion}</b></>}
      </div>
      <div style={{overflowX:'auto'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12.5}}>
          <thead><tr style={{textAlign:'left',color:'var(--text3)',fontSize:11,borderBottom:'2px solid var(--border)'}}>
            <th style={th}>Business unit</th><th style={th}>TRN</th><th style={th}>Inv.</th><th style={th}>Lines</th>
            <th style={th}>Est. annual AED</th><th style={th}>Phase</th><th style={th}>BTAE-02</th>
            <th style={th}>Doc types</th><th style={th}>Mandatory</th><th style={th}>A / P / N / –</th>
          </tr></thead>
          <tbody>
            {es.map((e,i)=>{ const rc=e.ratingCounts||{}; return (
              <tr key={i} style={{borderTop:'1px solid var(--border)'}}>
                <td style={td}><b>{e.businessUnit}</b>
                  {e.taxGroup&&<div style={{fontSize:11,color:'var(--warning)'}}>VAT group with {(e.taxGroupMembers||[]).join(', ')}</div>}</td>
                <td style={{...td,whiteSpace:'nowrap'}}>{e.sellerTrn||'—'}
                  {e.sellerTrn&&!e.trnValidFormat&&<div style={{fontSize:11,color:'var(--error)'}}>not 15 digits</div>}</td>
                <td style={td}>{e.invoices}</td>
                <td style={td}>{e.lines}</td>
                <td style={td}>{num(e.estimatedAnnualTurnoverAed)}</td>
                <td style={td}>{e.phase||'—'}</td>
                <td style={{...td,fontFamily:'var(--mono, monospace)'}}>{e.transactionTypeCode||'—'}</td>
                <td style={td}>{(e.docTypes||[]).join(', ')||'—'}</td>
                <td style={td}>{e.mandatoryAvailable}/{e.mandatoryTotal}</td>
                <td style={{...td,whiteSpace:'nowrap'}}>
                  <span style={{color:RATINGC.A,fontWeight:700}}>{rc.A||0}</span> / <span style={{color:RATINGC.P,fontWeight:700}}>{rc.P||0}</span>
                  {' / '}<span style={{color:RATINGC.N,fontWeight:700}}>{rc.N||0}</span> / <span style={{color:RATINGC['-']}}>{rc['-']||0}</span>
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>
      {conds.length>0&&(
        <>
          <div style={{fontWeight:700,fontSize:13,margin:'16px 0 4px'}}>Applicability — inferred from the data</div>
          <div style={{fontSize:12,color:'var(--text3)',marginBottom:6}}>A conditionally-mandatory field is only a gap once its condition is triggered. Hover a row for the evidence.</div>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12.5}}>
              <thead><tr style={{textAlign:'left',color:'var(--text3)',fontSize:11,borderBottom:'2px solid var(--border)'}}>
                <th style={th}>Condition</th><th style={th}>Tenant</th>
                {es.map((e,i)=><th key={i} style={th}>{e.businessUnit}</th>)}
              </tr></thead>
              <tbody>
                {conds.map((k,i)=>(
                  <tr key={i} style={{borderTop:'1px solid var(--border)'}} title={(a.applicabilityEvidence||{})[k]||''}>
                    <td style={td}>{(a.conditionLabels||{})[k]||k}</td>
                    <td style={td}>{yn((a.applicability||{})[k])}</td>
                    {es.map((e,j)=><td key={j} style={td}>{yn((e.applicability||{})[k])}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {(es[0].unInferredFlags||[]).length>0&&(
        <div style={{marginTop:12,padding:'10px 12px',background:'var(--warning-bg)',border:'1px solid var(--warning-border)',borderRadius:8,fontSize:12,color:'var(--text2)',lineHeight:1.5}}>
          <b>Not derivable from transaction data.</b> These BTAE-02 flags are emitted as 0 until the business confirms them: {(es[0].unInferredFlags||[]).join(', ')}.
        </div>
      )}
    </div>
  );
}

function ImpactFieldMatrix({fields}){
  if(!fields||!fields.length) return (
    <div className="card" style={{marginBottom:16}}>
      <div style={{fontWeight:800,fontSize:14,marginBottom:6}}>PINT AE Field Mapping &amp; Availability</div>
      <div style={{fontSize:13,color:'var(--text2)',lineHeight:1.5}}>Not available for this assessment — it predates this feature, or connectivity was blocked so no data could be sampled. <b>Re-run the assessment</b> to generate the field mapping.</div>
    </div>
  );
  const th={padding:'6px 8px',fontWeight:700,whiteSpace:'nowrap'};
  const td={padding:'6px 8px',verticalAlign:'top'};
  const flows=[['AR','AR — Customer Invoices (receivablesInvoices)'],['AP','AP — Supplier / Self-Billed (Payables invoices)']];
  const rated=fields.some(f=>f.rating);
  return (
    <div className="card" style={{marginBottom:16}}>
      <div style={{fontWeight:800,fontSize:14}}>PINT AE Field Mapping &amp; Availability</div>
      <div style={{fontSize:12,color:'var(--text3)',marginBottom:6}}>
        Both flows — AR (customer invoices) and AP (supplier / self-billed invoices).
        {rated&&<> <b>Req.</b> M = mandatory, CM = conditionally mandatory (only a gap once its condition is triggered), O = optional.
        <b> Rating</b> A = available, P = partial, N = not available, – = condition not triggered.</>}
      </div>
      {flows.map(([fl,label])=>{
        const ff=fields.filter(f=>(f.flow||'AR')===fl);
        if(!ff.length) return null;
        // A field is "available" when rated A; older assessments carry no rating,
        // so fall back to the availability bands they were rendered with.
        const okSet=['AVAILABLE','CONSTANT','COMPUTED'];
        const isOk=(f)=>f.rating?f.rating==='A':okSet.includes(f.availability);
        const na=ff.filter(f=>f.rating==='-');
        const applicable=ff.filter(f=>f.rating!=='-');
        const mand=applicable.filter(f=>f.requirement==='M'||f.requirement==='CM');
        const ok=applicable.filter(isOk);
        const mandOk=mand.filter(isOk);
        return (
          <div key={fl} style={{marginBottom:14}}>
            <div style={{display:'flex',alignItems:'center',gap:10,margin:'12px 0 6px',flexWrap:'wrap'}}>
              <div style={{fontWeight:700,fontSize:13,flex:1}}>{label}</div>
              <span style={{fontSize:12,color:'var(--text2)'}}>{mandOk.length}/{mand.length} mandatory · {ok.length}/{applicable.length} applicable available{na.length?' · '+na.length+' not applicable':''}</span>
            </div>
            {rated&&(
              <div style={{display:'flex',height:9,borderRadius:5,overflow:'hidden',background:'var(--surface3)',marginBottom:10}}
                   title="Rating mix for this flow">
                {RATING_ORDER.map(k=>{
                  const v=ff.filter(f=>f.rating===k).length;
                  if(!v) return null;
                  return <div key={k} title={RATING_LABEL[k]+': '+v} style={{width:(v/ff.length)*100+'%',background:RATINGC[k]}}/>;
                })}
              </div>
            )}
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12.5}}>
                <thead><tr style={{textAlign:'left',color:'var(--text3)',fontSize:11,borderBottom:'2px solid var(--border)'}}>
                  <th style={th}>Section</th><th style={th}>PINT AE field</th><th style={th}>Req.</th><th style={th}>Applies to</th><th style={th}>Fusion source</th><th style={th}>Availability</th>
                  {rated&&<th style={th}>Rating</th>}{rated&&<th style={th}>Approach</th>}<th style={th}>Cov.</th>
                </tr></thead>
                <tbody>
                  {ff.map((f,i)=>{ const ai=availInfo(f.availability); return (
                    <tr key={i} style={{borderTop:'1px solid var(--border)'}} title={f.note||''}>
                      <td style={{...td,color:'var(--text3)',whiteSpace:'nowrap'}}>
                        {f.section}
                        {f.ibg&&<div style={{fontSize:10,color:'var(--text3)',opacity:.75}}>{f.ibg}</div>}
                      </td>
                      <td style={td}><b>{f.name}</b> <span style={{color:'var(--text3)',fontSize:11}}>{f.id}</span></td>
                      <td style={td}>{f.requirement}{f.mof&&<span title="Counted in the MoF mandatory dictionary" style={{fontWeight:800}}>*</span>}</td>
                      {/* A gap with no reason attached is a line item. This is the reason. */}
                      <td style={{...td,color:'var(--text3)',fontSize:11.5,maxWidth:180}}>
                        {(f.applicableScenarios||[]).length
                          ? (f.applicableScenarios[0]==='All scenarios'
                              ? <span style={{opacity:.7}}>All scenarios</span>
                              : f.applicableScenarios.join(' · '))
                          : <span style={{opacity:.5}}>—</span>}
                      </td>
                      <td style={{...td,color:'var(--text2)'}}>{f.fusionSource}</td>
                      <td style={{...td,whiteSpace:'nowrap'}}>
                        <span style={{color:ai.c,fontWeight:700}}>{ai.l}</span>
                        {f.lookupId&&f.lookupCoverage!=null&&f.lookupCoverage>=0&&(
                          <span title={(f.lookupExists===false?'No translation table exists yet':f.lookupCoverage+'% of the observed codes translate via '+f.lookupId)+(f.lookupUnmapped&&f.lookupUnmapped.length?' — unmapped: '+f.lookupUnmapped.join(', '):'')}
                            style={{marginLeft:6,fontSize:10,fontWeight:800,padding:'0 5px',borderRadius:5,
                              color:f.lookupCoverage===100?'var(--success)':f.lookupCoverage>0?'var(--warning)':'var(--error)',
                              border:'1px solid var(--border2)'}}>map {f.lookupCoverage}%</span>
                        )}
                      </td>
                      {rated&&<td style={{...td,color:RATINGC[f.rating]||'var(--text3)',fontWeight:700}}>{f.rating||'—'}</td>}
                      {rated&&<td style={{...td,color:'var(--text2)'}}>{f.approach||'—'}</td>}
                      <td style={td}>{f.coverage<0?'—':f.coverage+'%'}</td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const TAXQ_CAT='Tax Coding Quality (VAT exposure)';

/* These findings are a different species from everything else in the report: they are about
   whether the VAT already charged is right, not whether an e-invoice can be produced. Mixed
   into the readiness list a reader treats them as formatting gaps and skips them, or treats
   them as verdicts and repeats them to a client as fact. Neither is what they are, so they
   get their own card with the caveat attached to it rather than buried per row. */
function ImpactTaxQuality({checks}){
  const items=(checks||[]).filter(c=>c.category===TAXQ_CAT);
  if(!items.length) return null;
  const open=items.filter(c=>c.status==='WARN'||c.status==='FAIL');
  const unmeasured=items.filter(c=>c.notAssessed||c.status==='SKIPPED'||c.status==='BLOCKED');
  const clean=items.filter(c=>c.status==='PASS');
  return (
    <div className="card" style={{marginBottom:16,borderLeft:'3px solid var(--error)'}}>
      <div style={{display:'flex',alignItems:'baseline',gap:10,flexWrap:'wrap'}}>
        <div style={{fontWeight:800,fontSize:14}}>Tax coding quality</div>
        <span style={{fontSize:10,fontWeight:800,letterSpacing:.5,color:'var(--error)',border:'1px solid var(--error-border)',background:'var(--error-bg)',borderRadius:5,padding:'1px 6px'}}>VAT EXPOSURE · NOT READINESS</span>
        <div style={{flex:1}}/>
        <span style={{fontSize:12,color:'var(--text2)'}}>
          {open.length} pattern{open.length===1?'':'s'} to review · {clean.length} clean · {unmeasured.length} unmeasurable
        </span>
      </div>
      <div style={{fontSize:12.5,color:'var(--text2)',margin:'8px 0 4px',lineHeight:1.5}}>
        Found while reading the invoices for e-invoicing readiness: coding patterns that may mean VAT was
        under- or over-charged. These carry <b>no weight in the readiness score</b> — they are the client's
        exposure today, not a blocker for the mandate.
        <b> Each one is a pattern, not a verdict:</b> the determinants that would make a coding correct
        (group membership, place of supply, the nature of the supply) are not on an invoice, so every finding
        names the invoices to check and what would make it legitimate.
      </div>
      {items.map((c,i)=><ImpactCheckRow key={i} c={c}/>)}
    </div>
  );
}

/* Upload a consultant's own workbook and see where the automated ratings disagree with a
   human assessor — and crucially in which direction. An agreement percentage on its own is a
   vanity number; the rows where this engine claims more readiness than the baseline are the
   ones that under-scope a project, so they are listed first and coloured as the problem. */
function ImpactBaselineCompare({assessmentId}){
  const [busy,setBusy]=useState(false);
  const [err,setErr]=useState(null);
  const [res,setRes]=useState(null);
  const [file,setFile]=useState(null);
  const inputRef=React.useRef(null);

  const run=async(column)=>{
    if(!file) return;
    setBusy(true); setErr(null);
    try{
      const fd=new FormData();
      fd.append('file',file);
      if(column) fd.append('column',column);
      // Verified against the deployed host: this endpoint answers 401 without a bearer
      // token, so the upload has never worked in deployed mode — it failed with the
      // generic error text rather than saying the session was the problem.
      const r=await fetch('/api/readiness/assessments/'+assessmentId+'/compare',
        {method:'POST',body:fd,headers:await authHeaders()});
      const j=await r.json();
      if(!r.ok){ setErr(j.error||('HTTP '+r.status)); setRes(null); }
      else if(!j||typeof j.matched!=='number'){
        setErr('The server answered but not with a comparison — no matched-term count came back. '
              +'Nothing is shown rather than showing a result that is not one.');
        setRes(null);
      }
      else { setRes(j); setErr(null); }
    }catch(e){ setErr(String(e&&e.message||e)); }
    finally{ setBusy(false); }
  };

  const pill=(label,val,color,hint)=>(
    <div title={hint||''} style={{flex:'1 1 130px',minWidth:120,background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:11,padding:'10px 13px'}}>
      <div style={{fontSize:20,fontWeight:800,color:color||'var(--text)',lineHeight:1.1}}>{val}</div>
      <div style={{fontSize:11,color:'var(--text2)',marginTop:3,fontWeight:600}}>{label}</div>
    </div>
  );
  const th={padding:'6px 8px',fontWeight:700,whiteSpace:'nowrap'};
  const td={padding:'6px 8px',verticalAlign:'top'};
  const rows=(list,tone)=>(
    <div style={{overflowX:'auto'}}>
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:12.5}}>
        <thead><tr style={{textAlign:'left',color:'var(--text3)',fontSize:11,borderBottom:'2px solid var(--border)'}}>
          <th style={th}>Term</th><th style={th}>Business term</th><th style={th}>Req.</th>
          <th style={th}>Ours</th><th style={th}>Baseline</th>
        </tr></thead>
        <tbody>
          {list.map((r,i)=>(
            <tr key={i} style={{borderTop:'1px solid var(--border)'}}>
              <td style={{...td,whiteSpace:'nowrap',color:'var(--text3)'}}>{r.id}</td>
              <td style={td}>{r.name||'—'}</td>
              <td style={td}>{r.requirement||'—'}</td>
              <td style={{...td,fontWeight:800,color:tone}}>{r.ours}</td>
              <td style={{...td,fontWeight:800,color:'var(--text2)'}}>{r.theirs}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="card" style={{marginBottom:16}}>
      <div style={{fontWeight:800,fontSize:14}}>Compare with a consultant baseline</div>
      <div style={{fontSize:12,color:'var(--text3)',marginBottom:12,lineHeight:1.5}}>
        Upload a manual gap analysis — an .xlsx workbook, or a CSV of PINT term ids and A/P/N ratings —
        and this lines it up against the automated matrix term by term. The layout does not have to match:
        any column of term ids and any column of ratings will do.
      </div>
      <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap',marginBottom:10}}>
        <input ref={inputRef} type="file" accept=".xlsx,.csv,.tsv,.txt" style={{display:'none'}}
               onChange={e=>{ setFile(e.target.files&&e.target.files[0]); setRes(null); setErr(null); }}/>
        <button className="btn btn-refresh" style={TAXLAB_LIGHT_STYLE} onClick={()=>inputRef.current&&inputRef.current.click()}>
          {file?'Choose a different file':'Choose a baseline file'}
        </button>
        {file&&<span style={{fontSize:12.5,color:'var(--text2)'}}>{file.name}</span>}
        {file&&<button className="btn" style={TAXLAB_CTA_STYLE} disabled={busy} onClick={()=>run(null)}>
          {busy?'Comparing…':'Compare'}
        </button>}
      </div>
      {err&&(
        <div style={{padding:'9px 12px',background:'var(--error-bg)',border:'1px solid var(--error-border)',borderRadius:9,color:'var(--error)',fontSize:12.5,lineHeight:1.45}}>{err}</div>
      )}
      {res&&(<>
        {res.baselineColumns&&res.baselineColumns.length>1&&(
          <div style={{display:'flex',gap:7,flexWrap:'wrap',alignItems:'center',marginBottom:12}}>
            <span style={{fontSize:12,color:'var(--text3)'}}>Baseline column:</span>
            {res.baselineColumns.map((col,i)=>(
              <button key={i} onClick={()=>run(col)} disabled={busy}
                style={{padding:'3px 10px',borderRadius:20,fontSize:11.5,cursor:busy?'default':'pointer',
                  border:'1px solid '+(col===res.baselineColumn?'var(--accent,#0891b2)':'var(--border)'),
                  background:col===res.baselineColumn?'var(--surface3)':'transparent',
                  color:col===res.baselineColumn?'var(--text)':'var(--text2)',fontWeight:col===res.baselineColumn?700:500}}>
                {col}
              </button>
            ))}
          </div>
        )}
        <div style={{display:'flex',gap:10,flexWrap:'wrap',marginBottom:14}}>
          {pill('Agreement',res.agreementPct+'%','var(--text)',res.agreed+' of '+res.matched+' matched terms')}
          {pill('We are optimistic',res.weAreOptimisticOn,'var(--error)','Rows where this engine claims more readiness than the baseline — these under-scope the work')}
          {pill('We are harsher',res.weAreHarsherOn,'var(--warning)','Rows where this engine is stricter than the baseline — costs review time, not scope')}
          {pill('Only in baseline',(res.onlyInBaseline||[]).length,'var(--text3)','Terms the baseline rates that this assessment does not carry')}
          {pill('Only here',(res.onlyInAssessment||[]).length,'var(--text3)','Terms this assessment rates that the baseline does not carry')}
        </div>
        {res.note&&<div style={{fontSize:12.5,color:'var(--text2)',lineHeight:1.5,marginBottom:14}}>{res.note}</div>}
        {(res.optimistic||[]).length>0&&(<>
          <div style={{fontWeight:700,fontSize:13,color:'var(--error)',margin:'4px 0 6px'}}>
            We claim more readiness than the baseline ({res.optimistic.length})
          </div>
          <div style={{fontSize:12,color:'var(--text3)',marginBottom:8}}>
            Work through these first: if the baseline is right, the gap count here is too low and so is the estimate.
          </div>
          {rows(res.optimistic,'var(--error)')}
        </>)}
        {(res.harsher||[]).length>0&&(<>
          <div style={{fontWeight:700,fontSize:13,color:'var(--warning)',margin:'18px 0 6px'}}>
            We are stricter than the baseline ({res.harsher.length})
          </div>
          {rows(res.harsher,'var(--warning)')}
        </>)}
        {((res.onlyInBaseline||[]).length>0||(res.onlyInAssessment||[]).length>0)&&(
          <div style={{marginTop:16,fontSize:12,color:'var(--text3)',lineHeight:1.6}}>
            {(res.onlyInBaseline||[]).length>0&&<div>Only in the baseline: {res.onlyInBaseline.join(', ')}</div>}
            {(res.onlyInAssessment||[]).length>0&&<div>Only in this assessment: {res.onlyInAssessment.join(', ')}</div>}
          </div>
        )}
      </>)}
    </div>
  );
}

/* What the sample actually covers, said before any number derived from it.

   The engine has always known when a pod refused the scope — it records the predicates it
   had to drop and falls back to filtering after the fetch — but none of that reached the
   page. A report headed "taxation country AE" that is really the newest N rows of a
   multi-country population is the most expensive kind of wrong here, because every figure
   below it looks precise. So this sits above them and says so. */
function ImpactScopeBanner({a}){
  const dp=a.dataProfile||{};
  const requested=[];
  if(a.taxationCountryFilter) requested.push('country '+a.taxationCountryFilter);
  if(a.businessUnitFilter) requested.push('business unit '+a.businessUnitFilter);
  if(a.legalEntityFilter) requested.push('legal entity '+a.legalEntityFilter);
  if(a.windowDays) requested.push('last '+a.windowDays+' days');
  const dropped=a.droppedScopePredicates||[];
  const honoured=dp.scopeFilterApplied===true;
  const clientScoped=dp.clientScoped===true;
  const cap=dp.arQueryCapability||null;
  if(!requested.length&&!dropped.length) return null;

  const tone = honoured ? {c:'var(--success)',bg:'var(--success-bg)',bd:'var(--success-border)',label:'Scope applied by the pod'}
             : clientScoped ? {c:'var(--warning)',bg:'var(--warning-bg)',bd:'var(--warning-border)',label:'Scope applied after fetching'}
             : {c:'var(--error)',bg:'var(--error-bg)',bd:'var(--error-border)',label:'Scope NOT applied'};
  return (
    <div className="card" style={{marginBottom:16,borderLeft:'3px solid '+tone.c}}>
      <div style={{display:'flex',alignItems:'baseline',gap:10,flexWrap:'wrap'}}>
        <div style={{fontWeight:800,fontSize:14}}>What this sample covers</div>
        <span style={{fontSize:10,fontWeight:800,letterSpacing:.5,color:tone.c,background:tone.bg,border:'1px solid '+tone.bd,borderRadius:5,padding:'1px 6px'}}>{tone.label}</span>
        <div style={{flex:1}}/>
        <span style={{fontSize:12,color:'var(--text2)'}}>
          {dp.sampled} of {dp.totalInvoices} invoices{dp.rowsScanned?' · '+dp.rowsScanned+' rows scanned':''}
        </span>
      </div>
      <div style={{fontSize:12.5,color:'var(--text2)',marginTop:8,lineHeight:1.5}}>
        <b>Requested:</b> {requested.length?requested.join(' · '):'no scope — the whole tenant'}
      </div>
      {dropped.length>0&&(
        <div style={{fontSize:12.5,color:'var(--text2)',marginTop:4,lineHeight:1.5}}>
          <b>The pod would not filter on:</b> {dropped.map((d,i)=>(
            <code key={i} style={{background:'var(--surface3)',border:'1px solid var(--border)',borderRadius:4,padding:'1px 5px',marginRight:5,fontSize:11.5}}>{d}</code>
          ))}
        </div>
      )}
      {cap&&(
        <div style={{fontSize:12.5,color:'var(--text2)',marginTop:4,lineHeight:1.5}}>
          <b>Filterable attributes on receivablesInvoices:</b>{' '}
          {(cap.accepted&&cap.accepted.length)?cap.accepted.join(', '):'none that this engine knows to ask for'}
          {cap.rejected&&Object.keys(cap.rejected).length>0&&(
            <> · <span style={{color:'var(--text3)'}}>rejected: {Object.keys(cap.rejected).join(', ')}</span></>
          )}
        </div>
      )}
      {dp.scopeFilterNote&&(
        <div style={{fontSize:12.5,color:tone.c,marginTop:8,lineHeight:1.5,fontWeight:500}}>{dp.scopeFilterNote}</div>
      )}
      {dp.countryScopeNote&&(
        <div style={{fontSize:12.5,color:'var(--text2)',marginTop:6,lineHeight:1.5}}>{dp.countryScopeNote}</div>
      )}
      {dp.scanCapped&&(
        <div style={{fontSize:12.5,color:'var(--error)',marginTop:8,lineHeight:1.5,fontWeight:600}}>
          The page budget ran out before the sample filled, so this is a partial view of the in-scope
          population — widen the sample or scope the run more tightly before quoting coverage figures.
        </div>
      )}
      {!honoured&&!clientScoped&&dropped.length>0&&(
        <div style={{fontSize:12.5,color:'var(--error)',marginTop:8,lineHeight:1.5,fontWeight:600}}>
          Every figure below is drawn from this sample, not from the scope that was asked for. Read them
          accordingly, and ask the pod administrator to expose the attributes above as finder attributes.
        </div>
      )}
    </div>
  );
}

const SCENARIO_VERDICT={
  APPLIES:          {label:'Applies',            c:'var(--success)', bg:'var(--success-bg)', bd:'var(--success-border)'},
  NEEDS_CONFIRMATION:{label:'Needs confirmation',c:'var(--warning)', bg:'var(--warning-bg)', bd:'var(--warning-border)'},
  NOT_TRIGGERED:    {label:'Not triggered',      c:'var(--text3)',   bg:'var(--surface3)',   bd:'var(--border)'},
  OUT_OF_SCOPE:     {label:'Not an e-invoice',   c:'var(--text3)',   bg:'var(--surface3)',   bd:'var(--border)'},
};

/* The layer a finance team reads first.

   "Do you raise self-billed invoices?" is a question a controller answers in a sentence;
   "is the selfBilled condition triggered?" is not a question at all outside the codebase.
   Each scenario fixes a document type and a BTAE-02 flag string, so confirming one settles
   several fields at once — which is why this sits above the field matrix rather than inside it.

   Three verdicts, not two. A scenario that turns on where the parties are established or on a
   commercial arrangement cannot be read from an invoice, and reporting it "not triggered"
   because the data is silent is how a scope gets understated. Those say so instead. */
const ASK_KIND={
  CONFIRM:        {label:'Confirm',        c:'var(--warning)', bg:'var(--warning-bg)', bd:'var(--warning-border)'},
  VERIFY_ABSENCE: {label:'Verify absence', c:'var(--text2)',   bg:'var(--surface3)',   bd:'var(--border)'},
  UNBLOCK:        {label:'Grant access',   c:'var(--brand)',   bg:'var(--surface3)',   bd:'var(--border2)'},
};

/* The only part of this report a human still has to do.

   Automating a gap analysis does not remove the manual step — it narrows it to the part that
   is genuinely a conversation. Free zone, deemed supply, the margin scheme and disclosed
   agency all turn on where the parties are established or on a commercial arrangement, and no
   sample of any size will settle them.

   Saying "needs business confirmation" and stopping there is the most expensive place to stop:
   it is the point where the automation has done all of the work and handed over none of it.
   So each item carries the question in words a controller can answer, why we are asking rather
   than measuring, what a yes would change, and who has to answer. */
const OPTIONAL_VERDICT={
  ALREADY_CAPTURED:   {label:'Already captured', c:'var(--success)', bg:'var(--success-bg)', bd:'var(--success-border)'},
  PARTIALLY_CAPTURED: {label:'Partly captured',  c:'var(--warning)', bg:'var(--warning-bg)', bd:'var(--warning-border)'},
  WORTH_ADDING:       {label:'Your call',        c:'var(--text2)',   bg:'var(--surface3)',   bd:'var(--border2)'},
  NOT_APPLICABLE:     {label:'Not applicable',   c:'var(--text3)',   bg:'var(--surface3)',   bd:'var(--border)'},
  UNKNOWN:            {label:'Not assessed',     c:'var(--text3)',   bg:'var(--surface3)',   bd:'var(--border)'},
};
const OPTIONAL_ORDER=['ALREADY_CAPTURED','PARTIALLY_CAPTURED','WORTH_ADDING','UNKNOWN','NOT_APPLICABLE'];

/* The terms the mandate leaves to you.

   These were sitting in the field matrix rated like compliance items, which is the one reading
   that is certainly wrong: an optional field cannot be a gap. Nothing on this card is required
   and nothing on it is a finding.

   The verdict is derived from the tenant's own data — what is already populated, what is only
   partly populated, and what belongs to something they do not appear to do — because sixty
   terms each carrying advice that could have been written without looking at their data would
   be filler, and filler teaches a reader to skim the parts that matter. Collapsed by default
   for the same reason: it is the least urgent thing in the report. */
function ImpactOptionalFields({a}){
  const items=a.optionalFields||[];
  const [open,setOpen]=React.useState(false);
  if(!items.length) return null;
  const by=(v)=>items.filter(x=>x.verdict===v);
  const captured=by('ALREADY_CAPTURED').length, yours=by('WORTH_ADDING').length;

  const row=(it)=>{
    const v=OPTIONAL_VERDICT[it.verdict]||OPTIONAL_VERDICT.UNKNOWN;
    return (
      <div key={(it.flow||'')+it.id} style={{display:'flex',gap:11,padding:'9px 0',borderBottom:'1px solid var(--border)'}}>
        <span style={{flexShrink:0,marginTop:1,fontSize:9.5,fontWeight:800,letterSpacing:.4,padding:'2px 7px',
          borderRadius:5,color:v.c,background:v.bg,border:'1px solid '+v.bd,whiteSpace:'nowrap',
          minWidth:106,textAlign:'center'}}>{v.label}</span>
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:'flex',gap:8,alignItems:'baseline',flexWrap:'wrap'}}>
            <span style={{fontWeight:700,fontSize:13}}>{it.name}</span>
            <span style={{color:'var(--text3)',fontSize:11}}>{it.id}</span>
            {it.ibg&&<span style={{color:'var(--text3)',fontSize:10,opacity:.75}}>{it.ibg}</span>}
          </div>
          <div style={{fontSize:12,color:'var(--text2)',marginTop:3,lineHeight:1.45}}>{it.advice}</div>
          {it.format&&(
            <div style={{fontSize:11.5,color:'var(--text3)',marginTop:3}}>
              Accepted: <code style={{fontSize:11}}>{it.format}</code>
            </div>
          )}
          {it.excludes&&it.excludes.length>0&&(
            <div style={{fontSize:11.5,color:'var(--warning)',marginTop:3}}>
              Cannot be used alongside {it.excludes.join(', ')}.
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="card" style={{marginBottom:16}}>
      <div onClick={()=>setOpen(!open)} style={{display:'flex',alignItems:'baseline',gap:10,flexWrap:'wrap',cursor:'pointer'}}>
        <div style={{fontWeight:800,fontSize:14}}>Optional fields</div>
        <div style={{flex:1}}/>
        <span style={{fontSize:12,color:'var(--text2)'}}>
          {items.length} terms · {captured} already captured · {yours} your call
        </span>
        <span style={{fontSize:11,color:'var(--text3)'}}>{open?'▲ hide':'▼ show'}</span>
      </div>
      <div style={{fontSize:12,color:'var(--text3)',marginTop:6,lineHeight:1.5}}>
        Nothing here is a gap and nothing here is required — these are the terms the mandate
        leaves to you. The point of the list is that the cheapest moment to add a field nobody
        is forcing you to add is while the mapping is being built anyway.
      </div>
      {open&&OPTIONAL_ORDER.map(v=>{
        const group=by(v);
        if(!group.length) return null;
        return (
          <div key={v} style={{marginTop:14}}>
            <div style={{fontWeight:700,fontSize:13,marginBottom:4}}>
              {OPTIONAL_VERDICT[v].label} <span style={{fontWeight:500,color:'var(--text3)'}}>({group.length})</span>
            </div>
            {group.map(row)}
          </div>
        );
      })}
    </div>
  );
}

const ANSWER_OPTS=[
  {v:'YES',     label:'Yes'},
  {v:'NO',      label:'No'},
  {v:'UNKNOWN', label:"Don't know"},
];

/* Answering, where the question is asked.

   The worklist has been asking these since the scenario layer landed and there was nowhere to
   put the reply, so every re-run asked again — which is how a client learns to stop reading a
   report. An answer here is not a comment: it settles the condition, the scenario resolves, the
   fields it governs come into or out of scope, and the gap counts move with them.

   It reaches the score through pint-mandatory-coverage, which is computed from the matrix at
   weight 10 of ~122 scored weight. Worth stating precisely rather than promising "the score
   moves": on a tenant whose coverage check is already failing at zero, bringing two more
   fields into scope changes the gap list and the coverage figure and leaves the headline
   number where it was.

   Which is also why the caption says so. An answer that changes the score has to be given
   knowingly, and the provenance — who answered, when — travels into every report that uses it. */
function AnswerControl({connectionId,assessmentId,target,current,onAnswered,showToast}){
  const [busy,setBusy]=React.useState(false);
  const [who,setWho]=React.useState(current?current.answeredBy||'':'');
  const [open,setOpen]=React.useState(false);

  if(!connectionId||!target) return null;

  const send=async(value)=>{
    setBusy(true);
    try{
      await API('POST','/api/readiness/answers',{connectionId,target,answer:value,
        answeredBy:who||null,source:'UI',askedByAssessmentId:assessmentId});
      if(showToast) showToast('Recorded — takes effect on the next run','success');
      setOpen(false);
      if(onAnswered) onAnswered();
    }catch(e){ if(showToast) showToast('Could not record: '+e.message,'error'); }
    finally{ setBusy(false); }
  };
  const withdraw=async()=>{
    setBusy(true);
    try{
      await API('DELETE','/api/readiness/answers/'+current.id);
      if(showToast) showToast('Withdrawn','success');
      if(onAnswered) onAnswered();
    }catch(e){ if(showToast) showToast('Could not withdraw: '+e.message,'error'); }
    finally{ setBusy(false); }
  };

  if(current) return (
    <div style={{marginTop:6,fontSize:11.5,color:'var(--text2)',display:'flex',gap:8,
      alignItems:'center',flexWrap:'wrap'}}>
      <span style={{fontWeight:800,fontSize:9.5,letterSpacing:.4,padding:'1px 7px',borderRadius:5,
        color:current.answer==='UNKNOWN'?'var(--text3)':'var(--success)',
        background:current.answer==='UNKNOWN'?'var(--surface3)':'var(--success-bg)',
        border:'1px solid '+(current.answer==='UNKNOWN'?'var(--border)':'var(--success-border)')}}>
        Answered: {current.answer==='UNKNOWN'?"don't know":current.answer.toLowerCase()}
      </span>
      <span style={{color:'var(--text3)'}}>
        {current.answeredBy?current.answeredBy:'unattributed'}
        {current.answeredAt?' · '+String(current.answeredAt).slice(0,10):''}
        {current.source?' · '+String(current.source).toLowerCase():''}
      </span>
      <button onClick={withdraw} disabled={busy} style={{fontSize:11,padding:'1px 8px',borderRadius:5,
        border:'1px solid var(--border2)',background:'transparent',color:'var(--text3)',cursor:'pointer'}}>
        Withdraw
      </button>
    </div>
  );

  if(!open) return (
    <button onClick={()=>setOpen(true)} style={{marginTop:6,fontSize:11,padding:'2px 9px',
      borderRadius:6,border:'1px solid var(--border2)',background:'transparent',
      color:'var(--text2)',cursor:'pointer'}}>
      Answer this
    </button>
  );

  return (
    <div style={{marginTop:7,padding:'9px 11px',borderRadius:8,background:'var(--surface2)',
      border:'1px solid var(--border)'}}>
      <div style={{display:'flex',gap:7,alignItems:'center',flexWrap:'wrap'}}>
        {ANSWER_OPTS.map(o=>(
          <button key={o.v} onClick={()=>send(o.v)} disabled={busy}
            style={{fontSize:12,padding:'4px 13px',borderRadius:6,border:'1px solid var(--border2)',
              background:'var(--surface)',color:'var(--text)',cursor:'pointer'}}>
            {o.label}
          </button>
        ))}
        <input value={who} onChange={e=>setWho(e.target.value)} placeholder="your name"
          style={{fontSize:12,padding:'4px 7px',borderRadius:6,border:'1px solid var(--border2)',
            background:'var(--surface)',color:'var(--text)',width:140}}/>
        <button onClick={()=>setOpen(false)} disabled={busy} style={{fontSize:11,padding:'3px 9px',
          borderRadius:6,border:'none',background:'transparent',color:'var(--text3)',cursor:'pointer'}}>
          Cancel
        </button>
      </div>
      <div style={{fontSize:11,color:'var(--text3)',marginTop:7,lineHeight:1.45}}>
        This changes the next assessment, not this one. A yes or no decides whether the fields
        above are in scope, so the gap list and the mandatory-coverage figure move with it — and
        the score too, where that check is not already at its floor. Your name travels with the
        answer into every report that uses it. "Don't know" is a real answer and is recorded
        as one.
      </div>
    </div>
  );
}

function AnswerImport({assessmentId,onImported,showToast}){
  const [result,setResult]=React.useState(null);
  const [busy,setBusy]=React.useState(false);
  const ref=React.useRef(null);

  const upload=async(file)=>{
    if(!file) return;
    setBusy(true); setResult(null);
    try{
      const fd=new FormData();
      fd.append('file',file);
      const res=await fetch('/api/readiness/assessments/'+assessmentId+'/answers/import',
        {method:'POST',body:fd,headers:await authHeaders()});
      const body=await res.json();
      if(!res.ok) throw new Error(body.error||('HTTP '+res.status));
      setResult(body);
      if(showToast) showToast(body.accepted+' answer(s) read','success');
      if(onImported) onImported();
    }catch(e){ if(showToast) showToast('Import failed: '+e.message,'error'); }
    finally{ setBusy(false); if(ref.current) ref.current.value=''; }
  };

  return (
    <div style={{marginTop:10,paddingTop:10,borderTop:'1px solid var(--border)'}}>
      <div style={{display:'flex',gap:9,alignItems:'center',flexWrap:'wrap'}}>
        <span style={{fontSize:12,color:'var(--text2)'}}>
          Had the workbook filled in offline?
        </span>
        <input ref={ref} type="file" accept=".xlsx" style={{display:'none'}}
          onChange={e=>upload(e.target.files&&e.target.files[0])}/>
        <button onClick={()=>ref.current&&ref.current.click()} disabled={busy}
          style={{fontSize:11.5,padding:'3px 11px',borderRadius:6,border:'1px solid var(--border2)',
            background:'transparent',color:'var(--text)',cursor:'pointer'}}>
          {busy?'Reading…':'Upload the returned workbook'}
        </button>
        <span style={{fontSize:11,color:'var(--text3)'}}>
          reads the Open Questions sheet
        </span>
      </div>
      {result&&(
        <div style={{marginTop:8,fontSize:12,color:'var(--text2)',lineHeight:1.45}}>
          <b>{result.accepted}</b> answer{result.accepted===1?'':'s'} read
          {result.rowsSeen!=null&&<span style={{color:'var(--text3)'}}> of {result.rowsSeen} row(s) with content</span>}.
          {result.takesEffect&&<span style={{color:'var(--text3)'}}> {result.takesEffect}</span>}
          {(result.rejected||[]).length>0&&(
            <div style={{marginTop:7,padding:'8px 11px',borderRadius:8,background:'var(--warning-bg)',
              border:'1px solid var(--warning-border)'}}>
              <div style={{fontWeight:700,color:'var(--warning)',fontSize:12}}>
                {result.rejected.length} row{result.rejected.length===1?'':'s'} could not be read
              </div>
              {result.rejected.map((x,i)=>(
                <div key={i} style={{fontSize:11.5,color:'var(--text2)',marginTop:4}}>
                  {x.ref?<code style={{fontSize:11}}>{x.ref}</code>:null} {x.reason}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ImpactWorklist({a,answers,connectionId,onAnswered,showToast}){
  const items=a.confirmations||[];
  if(!items.length) return null;
  const owners=[...new Set(items.map(i=>i.owner))];

  const row=(it)=>{
    const k=ASK_KIND[it.kind]||ASK_KIND.CONFIRM;
    return (
      <div key={it.id} style={{display:'flex',gap:11,padding:'11px 0',borderBottom:'1px solid var(--border)'}}>
        <span style={{flexShrink:0,marginTop:1,fontSize:9.5,fontWeight:800,letterSpacing:.4,padding:'2px 7px',
          borderRadius:5,color:k.c,background:k.bg,border:'1px solid '+k.bd,whiteSpace:'nowrap',
          minWidth:104,textAlign:'center'}}>{k.label}</span>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontWeight:700,fontSize:13.5,color:'var(--text)',lineHeight:1.4}}>{it.question}</div>
          {it.why&&<div style={{fontSize:12,color:'var(--text3)',marginTop:3,lineHeight:1.45}}>{it.why}</div>}
          {it.ifYes&&(
            <div style={{fontSize:12,color:'var(--text2)',marginTop:4,lineHeight:1.45}}>
              <b style={{fontWeight:700}}>If yes:</b> {it.ifYes}
            </div>
          )}
          {it.needed&&(
            <div style={{fontSize:11.5,color:'var(--text3)',marginTop:3,lineHeight:1.45}}>
              ↳ {it.needed}
            </div>
          )}
          {it.kind!=='UNBLOCK'&&(
            <AnswerControl connectionId={connectionId} assessmentId={a.id}
              target={String(it.id).startsWith('condition:')?it.id:'question:'+it.id}
              current={(answers||[]).find(x=>x.target===(String(it.id).startsWith('condition:')?it.id:'question:'+it.id))}
              onAnswered={onAnswered} showToast={showToast}/>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="card" style={{marginBottom:16,borderLeft:'3px solid var(--warning)'}}>
      <div style={{display:'flex',alignItems:'baseline',gap:10,flexWrap:'wrap'}}>
        <div style={{fontWeight:800,fontSize:14}}>Open questions</div>
        <div style={{flex:1}}/>
        <span style={{fontSize:12,color:'var(--text2)'}}>
          {items.length} item{items.length===1?'':'s'} · {owners.length} owner{owners.length===1?'':'s'}
        </span>
      </div>
      <div style={{fontSize:12,color:'var(--text3)',marginTop:6,lineHeight:1.5}}>
        Everything this run could settle from data, it settled. What is left needs a person:
        either because an invoice does not record it at all, or because the sample we were
        allowed to read cannot prove the absence of something. Each item says what a yes would
        change, so you can decide which are worth chasing.
      </div>
      <AnswerImport assessmentId={a.id} onImported={onAnswered} showToast={showToast}/>
      {owners.map(o=>(
        <div key={o} style={{marginTop:14}}>
          <div style={{fontWeight:700,fontSize:13,marginBottom:4}}>
            {o} <span style={{fontWeight:500,color:'var(--text3)'}}>
              ({items.filter(i=>i.owner===o).length})
            </span>
          </div>
          {items.filter(i=>i.owner===o).map(row)}
        </div>
      ))}
    </div>
  );
}

function ImpactScenarios({a}){
  const all=a.scenarios||[];
  if(!all.length) return null;
  const by=(v)=>all.filter(s=>s.verdict===v);
  const applies=by('APPLIES'), confirm=by('NEEDS_CONFIRMATION'), excluded=by('OUT_OF_SCOPE');
  // Two different claims share the NOT_TRIGGERED verdict, and conflating them would tell a
  // reader we looked and found nothing when in fact the business said it does not happen.
  const absent=by('NOT_TRIGGERED').filter(x=>!x.ruledOutByBusiness);
  const ruledOut=by('NOT_TRIGGERED').filter(x=>x.ruledOutByBusiness);

  const row=(s)=>{
    const v=SCENARIO_VERDICT[s.verdict]||SCENARIO_VERDICT.NOT_TRIGGERED;
    return (
      <div key={s.id} style={{display:'flex',gap:11,padding:'10px 0',borderBottom:'1px solid var(--border)'}}>
        <span style={{flexShrink:0,marginTop:1,fontSize:9.5,fontWeight:800,letterSpacing:.4,padding:'2px 7px',
          borderRadius:5,color:v.c,background:v.bg,border:'1px solid '+v.bd,whiteSpace:'nowrap',minWidth:116,textAlign:'center'}}>
          {v.label}
        </span>
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:'flex',gap:8,alignItems:'baseline',flexWrap:'wrap'}}>
            <span style={{fontWeight:700,fontSize:13.5,color:'var(--text)'}}>{s.name}</span>
            {s.docType&&<span style={{fontSize:11.5,color:'var(--text2)',fontWeight:700}}>type {s.docType}</span>}
            {s.btae02&&s.btae02!=='00000000'&&(
              <code style={{fontSize:11,background:'var(--surface3)',border:'1px solid var(--border)',borderRadius:4,padding:'0 5px'}}>
                BTAE-02 {s.btae02}
              </code>
            )}
          </div>
          {s.note&&<div style={{fontSize:12.5,color:'var(--text2)',marginTop:3,lineHeight:1.45}}>{s.note}</div>}
          {s.evidence&&<div style={{fontSize:11.5,color:'var(--text3)',marginTop:3}}>↳ {s.evidence}</div>}
          {/* The field cost, which is what turns "please confirm this" into a decision.
              A scenario that gates nothing says so: that is our catalogue's gap, not the
              tenant's, and letting it look like an answerable question wastes their time. */}
          {s.fieldCount!=null&&s.inScope!==false&&(
            <div style={{fontSize:11.5,marginTop:4,color:'var(--text3)'}}>
              {s.fieldCount===0 ? (
                <span>Switches on no field we track yet — confirming it would not change this report.</span>
              ) : (
                <span title={(s.fieldIds||[]).join(', ')}>
                  Switches on <b style={{color:'var(--text2)'}}>{s.fieldCount}</b> field{s.fieldCount===1?'':'s'}
                  {s.gapFieldCount>0&&<span style={{color:'var(--error)'}}> · {s.gapFieldCount} already a gap</span>}
                  {s.partialFieldCount>0&&<span style={{color:'var(--warning)'}}> · {s.partialFieldCount} partial</span>}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const group=(title,items,blurb)=>items.length===0?null:(
    <div style={{marginTop:14}}>
      <div style={{fontWeight:700,fontSize:13,marginBottom:2}}>{title} ({items.length})</div>
      {blurb&&<div style={{fontSize:12,color:'var(--text3)',marginBottom:6,lineHeight:1.45}}>{blurb}</div>}
      {items.map(row)}
    </div>
  );

  return (
    <div className="card" style={{marginBottom:16}}>
      <div style={{display:'flex',alignItems:'baseline',gap:10,flexWrap:'wrap'}}>
        <div style={{fontWeight:800,fontSize:14}}>Business scenarios</div>
        <div style={{flex:1}}/>
        <span style={{fontSize:12,color:'var(--text2)'}}>
          {applies.length} apply · {confirm.length} need confirming · {absent.length} not triggered
          {ruledOut.length>0?' · '+ruledOut.length+' ruled out':''}
        </span>
      </div>
      <div style={{fontSize:12,color:'var(--text3)',marginTop:6,lineHeight:1.5}}>
        Every scenario is listed, not only the ones that fire — otherwise a reader cannot tell
        "we checked and you do not do this" from "we did not look". Each one fixes a document type
        and a BTAE-02 flag string, so confirming one settles several fields at once.
      </div>
      {group('Applies to this tenant',applies)}
      {group('Needs business confirmation',confirm,
        'These turn on where the parties are established or on a commercial arrangement, neither of '
        +'which appears on an invoice. They cannot be answered from data at all — only by asking.')}
      {group('Looked for, not found',absent)}
      {group('Ruled out by the business',ruledOut,
        'Answered no. Not something the data showed either way — these are out of scope on '
        +'your own confirmation, and who said so travels with the report.')}
      {group('Not e-invoices at all',excluded,
        'Carried because the question gets asked in every engagement. These are deliberately outside '
        +'the mandate.')}
    </div>
  );
}

function ImpactReport({a,onBack,showToast}){
  const [tracking,setTracking]=React.useState(a.remediation||null);
  const reloadTracking=React.useCallback(()=>{
    if(!a.id) return;
    API('GET','/api/readiness/assessments/'+a.id+'/remediation')
      .then(setTracking)
      .catch(()=>{});
  },[a.id]);
  React.useEffect(()=>{reloadTracking();},[reloadTracking]);
  const [answers,setAnswers]=React.useState([]);
  const reloadAnswers=React.useCallback(()=>{
    if(!a.connectionId) return;
    API('GET','/api/readiness/answers?connectionId='+encodeURIComponent(a.connectionId))
      .then(r=>setAnswers(r.answers||[]))
      .catch(()=>{});
  },[a.connectionId]);
  React.useEffect(()=>{reloadAnswers();},[reloadAnswers]);
  const noteFor=React.useCallback((target)=>
    ((tracking&&tracking.notes)||[]).find(n=>n.target===target)||null,[tracking]);
  const band=rband(a.band);
  const dp=a.dataProfile||{};
  const groups=[];
  (a.checks||[]).forEach(c=>{ if(c.category===TAXQ_CAT) return; let g=groups.find(x=>x.cat===c.category); if(!g){g={cat:c.category,items:[]};groups.push(g);} g.items.push(c); });
  const [expOpen,setExpOpen]=useState(false);
  const slug=(a.connectionName||'fusion').replace(/\W+/g,'-').toLowerCase();
  const base='/api/readiness/assessments/'+a.id;
  const _download=(text,mime,name)=>{ const blob=new Blob([text],{type:mime}); const url=URL.createObjectURL(blob); const el=document.createElement('a'); el.href=url; el.download=name; el.click(); URL.revokeObjectURL(url); };
  const dlFile=async(fmt,mime,ext)=>{ try{ const r=await fetch(base+'/report.'+fmt); const t=await r.text(); _download(t,mime,'impact-analysis-'+slug+'.'+ext); }catch(e){} };
  const exportPdf=()=>{ window.open(base+'/report.html?print=1','_blank'); };
  /* Same tile language as the Platform Overview cards, so a reader moving between the
     two pages is not re-learning the furniture. */
  const stat=(label,val)=>(
    <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,
                 padding:'12px 14px',minWidth:132,flex:'1 1 132px'}}>
      <div style={{fontSize:21,fontWeight:800,color:'var(--text)',letterSpacing:'-.3px'}}>{val==null?'\u2014':val}</div>
      <div style={{fontSize:11,color:'var(--text3)',marginTop:3}}>{label}</div>
    </div>
  );
  /* Verdict tile — count plus its meaning, coloured by outcome. The old hero printed
     these as four coloured bullets on one line, which read as decoration; at a glance
     nobody could tell 7 failed from 7 blocked. */
  const verdict=(n,label,col,bg,bd)=>(
    <div style={{flex:'1 1 92px',background:bg,border:'1px solid '+bd,borderRadius:12,padding:'10px 12px'}}>
      <div style={{fontSize:20,fontWeight:800,color:col,lineHeight:1.1}}>{n==null?'\u2014':n}</div>
      <div style={{fontSize:10.5,color:'var(--text3)',marginTop:2}}>{label}</div>
    </div>
  );
  return (
    <div>
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:18}}>
        <button className="btn btn-ghost btn-sm" onClick={onBack}>← Back</button>
        <div style={{flex:1}}/>
        <div style={{position:'relative'}}>
          <button className="btn btn-refresh" style={TAXLAB_LIGHT_STYLE} onClick={()=>setExpOpen(o=>!o)}>
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0}}>
              <path fill="currentColor" fillRule="evenodd" d="M8.845 7.905a.75.75 0 0 0 1.06 0l1.72-1.72v8.19a.75.75 0 0 0 1.5 0v-8.19l1.72 1.72a.75.75 0 1 0 1.06-1.06l-3-3a.75.75 0 0 0-1.06 0l-3 3a.75.75 0 0 0 0 1.06" clipRule="evenodd"></path>
              <path fill="currentColor" d="M12.375 20.375a8 8 0 0 0 8-8h-3.75c-.943 0-1.414 0-1.707.293s-.293.764-.293 1.707a2.25 2.25 0 0 1-4.5 0c0-.943 0-1.414-.293-1.707s-.764-.293-1.707-.293h-3.75a8 8 0 0 0 8 8"></path>
            </svg>
            Export
          </button>
          {expOpen&&(<>
            <div onClick={()=>setExpOpen(false)} style={{position:'fixed',inset:0,zIndex:60}}/>
            <div style={{position:'absolute',right:0,top:'112%',zIndex:61,background:'var(--surface)',border:'1px solid var(--border)',borderRadius:10,boxShadow:'var(--shadow-lg)',minWidth:190,overflow:'hidden',padding:4}}>
              {[['🖨  PDF (print / save)',exportPdf],['📄  Word (.doc)',()=>window.open(base+'/report.doc','_blank')],['📊  Excel workbook (.xlsx)',()=>window.open(base+'/report.xlsx','_blank')],...((a.fieldMappings&&a.fieldMappings.length)?[['🗂  PINT field map (CSV)',()=>window.open(base+'/report-fields.csv','_blank')]]:[]),['🌐  HTML file',()=>dlFile('html','text/html','html')],['📝  Markdown',()=>dlFile('md','text/markdown','md')]].map(([lbl,fn],i)=>(
                <button key={i} onClick={()=>{setExpOpen(false);fn();}} style={{display:'block',width:'100%',textAlign:'left',padding:'9px 12px',background:'none',border:'none',borderRadius:7,color:'var(--text)',fontSize:13,cursor:'pointer'}} onMouseOver={e=>e.currentTarget.style.background='var(--surface2)'} onMouseOut={e=>e.currentTarget.style.background='none'}>{lbl}</button>
              ))}
            </div>
          </>)}
        </div>
      </div>

      {/* Hero — score dial, verdict tiles, and the run's provenance. */}
      <div style={{display:'grid',gridTemplateColumns:'minmax(0,260px) minmax(0,1fr)',gap:16,marginBottom:16}}>
        <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',
                     padding:'16px 18px',display:'flex',flexDirection:'column',justifyContent:'center'}}>
          <AxGauge pct={a.overallScore} tone={band.c}
                   caption={'Readiness score \u00b7 ' + (a.overallScore!=null?a.overallScore:'\u2014') + ' of 100'}/>
          <div style={{textAlign:'center',marginTop:8}}>
            <span style={{padding:'4px 12px',borderRadius:20,background:band.bg,color:band.c,
                          border:'1px solid '+band.bd,fontWeight:800,fontSize:12.5}}>{band.label}</span>
          </div>
        </div>

        <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',
                     padding:'16px 18px',display:'flex',flexDirection:'column',gap:12}}>
          <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
            <span style={{fontWeight:800,fontSize:16,color:'var(--text)'}}>{a.connectionName}</span>
            <span style={{fontSize:11.5,color:'var(--text3)',background:'var(--surface3)',
                          border:'1px solid var(--border)',borderRadius:20,padding:'2px 9px'}}>{a.erp}</span>
          </div>
          <div style={{fontSize:14,color:'var(--text)',fontWeight:600,lineHeight:1.45}}>{a.headline}</div>

          <div style={{display:'flex',gap:9,flexWrap:'wrap'}}>
            {verdict(a.checksPassed,'passed','var(--success)','var(--success-bg)','var(--success-border)')}
            {verdict(a.checksWarned,'warnings','var(--warning)','var(--warning-bg)','var(--warning-border)')}
            {verdict(a.checksFailed,'failed','var(--error)','var(--error-bg)','var(--error-border)')}
            {verdict(a.checksBlocked,'blocked','var(--text3)','var(--surface2)','var(--border)')}
          </div>

          {(a.criticalGaps>0||(tracking&&(tracking.trackedGapCount>0||tracking.needsAttentionCount>0)))&&(
            <div style={{display:'flex',gap:8,flexWrap:'wrap',fontSize:11.5}}>
              {a.criticalGaps>0&&(
                <span style={{display:'inline-flex',alignItems:'center',gap:5,fontWeight:700,color:'var(--error)',
                              background:'var(--error-bg)',border:'1px solid var(--error-border)',borderRadius:20,padding:'3px 10px'}}>
                  ⚠ {a.criticalGaps} critical gap{a.criticalGaps>1?'s':''}
                </span>
              )}
              {tracking&&tracking.trackedGapCount>0&&(
                <span title="Findings someone is already working on — still counted as gaps"
                      style={{display:'inline-flex',alignItems:'center',gap:5,color:'var(--info)',
                              background:'var(--info-bg)',border:'1px solid var(--info-border)',borderRadius:20,padding:'3px 10px'}}>
                  {tracking.trackedGapCount} already in hand
                </span>
              )}
              {tracking&&tracking.needsAttentionCount>0&&(
                <span title="Notes that have gone stale: overdue, regressed, or now reading as resolved"
                      style={{display:'inline-flex',alignItems:'center',gap:5,fontWeight:700,color:'var(--warning)',
                              background:'var(--warning-bg)',border:'1px solid var(--warning-border)',borderRadius:20,padding:'3px 10px'}}>
                  ⚠ {tracking.needsAttentionCount} note{tracking.needsAttentionCount>1?'s':''} to revisit
                </span>
              )}
            </div>
          )}

          <div style={{fontSize:11,color:'var(--text3)',borderTop:'1px solid var(--border)',paddingTop:9,marginTop:'auto'}}>
            Run {a.startedAt?new Date(a.startedAt).toLocaleString():'—'} · {a.durationMs}ms · host {a.connectionHost}
          </div>
        </div>
      </div>

      <ImpactScopeBanner a={a}/>

      <ImpactDashboard a={a}/>

      {/* Category breakdown — sorted worst-first, because the point of this panel is
          where to start, not alphabetical order. */}
      {a.categories&&a.categories.length>0&&(
        <AxPanel title="Readiness by category"
                 action={<span style={{fontSize:11.5,color:'var(--text3)'}}>weakest first</span>}>
          <div style={{display:'flex',flexDirection:'column',gap:14}}>
            {a.categories.slice().sort((x,y)=>(x.score||0)-(y.score||0)).map((cat,i)=>{
              const cb=rband(cat.band);
              const ratio=cat.total?Math.round((cat.passed/cat.total)*100):0;
              return (
                <div key={i}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:5,gap:10}}>
                    <span style={{fontSize:13,color:'var(--text)',fontWeight:600}}>{cat.label}</span>
                    <span style={{fontSize:11.5,color:'var(--text3)',whiteSpace:'nowrap'}}>
                      {cat.passed}/{cat.total} checks · <b style={{color:cb.c,fontSize:13}}>{cat.score}</b>
                    </span>
                  </div>
                  <div style={{height:7,borderRadius:4,background:'var(--surface3)',overflow:'hidden',position:'relative'}}>
                    <div style={{height:'100%',width:(cat.score||0)+'%',background:cb.c,borderRadius:4,transition:'width .4s'}}/>
                    {/* pass-ratio tick: score and pass rate are different things, and a
                        category can score well while most checks only warn. */}
                    <div style={{position:'absolute',left:ratio+'%',top:-2,width:1.5,height:11,background:'var(--text3)',opacity:.55}}
                         title={ratio+'% of checks passed outright'}/>
                  </div>
                </div>
              );})}
          </div>
        </AxPanel>
      )}
      <div style={{height:16}}/>

      <ChartPriorities checks={a.checks}/>

      <ImpactTaxQuality checks={a.checks}/>

      <ImpactGapMap a={a}/>

      <ChartTranslations fields={a.fieldMappings}/>

      {/* Data profile */}
      <div className="card" style={{marginBottom:16}}>
        <div style={{fontWeight:800,fontSize:14,marginBottom:12}}>Data profile <span style={{fontWeight:500,color:'var(--text3)',fontSize:12}}>(sample of {dp.sampled} of {dp.totalInvoices} invoices)</span></div>
        <div style={{display:'flex',gap:10,flexWrap:'wrap',marginBottom:16}}>
          {stat('Total invoices',dp.totalInvoices)}
          {stat('UAE share',(dp.aeSharePct!=null?dp.aeSharePct+'%':'—'))}
          {stat('Est. UAE invoices',(dp.estimatedAeInvoices!=null&&dp.estimatedAeInvoices>=0?dp.estimatedAeInvoices:'—'))}
          {stat('Non-AED share',(dp.nonAedSharePct!=null?dp.nonAedSharePct+'%':'—'))}
          {stat('Invoice lines',dp.lineCount)}
        </div>
        <div style={{fontSize:12,color:'var(--text3)',marginBottom:2}}>Country (DefaultTaxationCountry)</div><ImpactDist data={dp.countryDistribution}/>
        <div style={{fontSize:12,color:'var(--text3)',margin:'10px 0 2px'}}>Currency</div><ImpactDist data={dp.currencyDistribution}/>
        <div style={{fontSize:12,color:'var(--text3)',margin:'10px 0 2px'}}>Tax-rate codes seen</div><ImpactDist data={dp.distinctTaxCodes}/>
        <div style={{fontSize:12,color:'var(--text3)',margin:'10px 0 2px'}}>Units of measure seen</div><ImpactDist data={dp.distinctUoms}/>
      </div>

      <ImpactEntities a={a}/>

      {tracking&&(tracking.notes||[]).some(n=>n.drift==='ORPHANED')&&(
        <div className="card" style={{marginBottom:16,borderLeft:'3px solid var(--warning)'}}>
          <div style={{fontWeight:800,fontSize:14}}>Tracking notes with nothing to attach to</div>
          <div style={{fontSize:12,color:'var(--text3)',marginTop:6,lineHeight:1.5}}>
            These were marked as being worked on, but nothing in this run matches them any more —
            the check may no longer run, or the field may no longer be applicable. Shown rather
            than dropped, because a note quietly disappearing looks exactly like a problem
            quietly solved.
          </div>
          {(tracking.notes||[]).filter(n=>n.drift==='ORPHANED').map(n=>(
            <div key={n.id} style={{display:'flex',gap:10,padding:'9px 0',borderBottom:'1px solid var(--border)'}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:700,fontSize:13}}>{n.subject}</div>
                <div style={{fontSize:11.5,color:'var(--text3)',marginTop:2}}>
                  {n.target}{n.reference?' · '+n.reference:''}{n.owner?' · '+n.owner:''}
                </div>
                {n.note&&<div style={{fontSize:12,color:'var(--text2)',marginTop:3}}>{n.note}</div>}
              </div>
              <button onClick={async()=>{
                try{ await API('DELETE','/api/readiness/remediation/'+n.id); reloadTracking();
                     if(showToast) showToast('Closed','success'); }
                catch(e){ if(showToast) showToast('Could not close: '+e.message,'error'); }
              }} style={{alignSelf:'start',fontSize:11,padding:'3px 10px',borderRadius:6,
                border:'1px solid var(--border2)',background:'transparent',color:'var(--text3)',cursor:'pointer'}}>
                Close
              </button>
            </div>
          ))}
        </div>
      )}

      <ImpactWorklist a={a} answers={answers} connectionId={a.connectionId}
        onAnswered={reloadAnswers} showToast={showToast}/>

      {a.questionnaire&&a.questionnaire.contradictedCount>0&&(
        <div className="card" style={{marginBottom:16,borderLeft:'3px solid var(--error)'}}>
          <div style={{fontWeight:800,fontSize:14}}>
            Answers that disagree with the invoices
          </div>
          <div style={{fontSize:12,color:'var(--text3)',marginTop:6,lineHeight:1.5}}>
            The business told us one thing and the sampled invoices show another. The data stands
            in this report — an answer cannot un-observe an invoice — but the disagreement is
            worth resolving before go-live. More often than not it means the transactions are
            coded wrong, which is a finding in its own right.
          </div>
          {(a.questionnaire.entries||[]).filter(e=>e.disposition==='CONTRADICTED').map((e,i)=>(
            <div key={i} style={{padding:'10px 0',borderBottom:'1px solid var(--border)'}}>
              <div style={{fontWeight:700,fontSize:13}}>{e.conditionLabel}</div>
              <div style={{fontSize:12,color:'var(--text2)',marginTop:3,lineHeight:1.45}}>{e.effect}</div>
              <div style={{fontSize:11.5,color:'var(--text3)',marginTop:3}}>
                Answered {String(e.answer).toLowerCase()}
                {e.answeredBy?' by '+e.answeredBy:''}
                {e.answeredAt?' on '+String(e.answeredAt).slice(0,10):''}
                {e.source?' · '+String(e.source).toLowerCase():''}
              </div>
            </div>
          ))}
        </div>
      )}

      <ImpactScenarios a={a}/>

      <ImpactFieldMatrix fields={a.fieldMappings}/>

      <ImpactOptionalFields a={a}/>

      {(a.fieldMappings&&a.fieldMappings.length)?<ImpactBaselineCompare assessmentId={a.id}/>:null}

      {/* Checks */}
      {groups.map((g,i)=>(
        <div key={i} className="card" style={{marginBottom:14}}>
          <div style={{fontWeight:800,fontSize:14,marginBottom:4}}>{g.cat}</div>
          {g.items.map((c,j)=>(
            <ImpactCheckRow key={j} c={c} note={noteFor('check:'+c.id)} connectionId={a.connectionId}
              onTracked={reloadTracking} showToast={showToast}/>
          ))}
        </div>
      ))}
    </div>
  );
}

function ImpactRunModal({connections,onClose,onRun,running}){
  const eligible=(connections||[]).filter(c=>c.type==='ORACLE_FUSION'||(c.adapter&&String(c.adapter).toLowerCase().includes('fusion'))
    ||c.type==='SAP'||(c.adapter&&String(c.adapter).toLowerCase().includes('sap')));
  const [connId,setConnId]=useState(eligible[0]?eligible[0].id:'');
  const [windowDays,setWindowDays]=useState(90);
  const [sampleSize,setSampleSize]=useState(200);
  const [businessUnit,setBusinessUnit]=useState('');
  const [legalEntity,setLegalEntity]=useState('');
  const [taxationCountry,setTaxationCountry]=useState('AE');
  return (
    <div className="modal-overlay" onClick={e=>{if(e.target===e.currentTarget&&!running)onClose();}}>
      <div className="modal modal-md">
        <div className="modal-header">
          <div className="modal-title">Run Impact Analysis</div>
          <button className="modal-close-btn" onClick={onClose} disabled={running} aria-label="Close"><CloseIcon size={16}/></button>
        </div>
        <div className="modal-body">
          {eligible.length===0?(
            <div style={{padding:'16px',background:'var(--warning-bg)',border:'1px solid var(--warning-border)',borderRadius:10,color:'var(--text2)',fontSize:13}}>
              No Oracle Fusion or SAP connection found. Create one under <b>Connections</b> first, then return here.
            </div>
          ):(
            <>
              <div style={{marginBottom:14}}>
                <label style={{fontSize:12,fontWeight:600,color:'var(--text2)',display:'block',marginBottom:5}}>Source connection</label>
                <select className="form-select" value={connId} onChange={e=>setConnId(e.target.value)}>
                  {eligible.map(c=><option key={c.id} value={c.id}>{c.name} ({c.type==='SAP'?'SAP':'Fusion'})</option>)}
                </select>
              </div>
              <div style={{display:'flex',gap:12,marginBottom:14}}>
                <div style={{flex:1}}>
                  <label style={{fontSize:12,fontWeight:600,color:'var(--text2)',display:'block',marginBottom:5}}>Window (days)</label>
                  <input className="form-input" type="number" min="1" value={windowDays} onChange={e=>setWindowDays(e.target.value)}/>
                </div>
                <div style={{flex:1}}>
                  <label style={{fontSize:12,fontWeight:600,color:'var(--text2)',display:'block',marginBottom:5}}>Sample size</label>
                  <input className="form-input" type="number" min="1" max="500" value={sampleSize} onChange={e=>setSampleSize(e.target.value)}/>
                </div>
              </div>
              <div style={{display:'flex',gap:12,marginBottom:14}}>
                <div style={{flex:1}}>
                  <label style={{fontSize:12,fontWeight:600,color:'var(--text2)',display:'block',marginBottom:5}}>Taxation country</label>
                  <input className="form-input" placeholder="AE" value={taxationCountry} onChange={e=>setTaxationCountry(e.target.value.toUpperCase())}/>
                  <div style={{fontSize:11,color:'var(--text3)',marginTop:4}}>ISO code. Blank = every country.</div>
                </div>
                <div style={{flex:1}}>
                  <label style={{fontSize:12,fontWeight:600,color:'var(--text2)',display:'block',marginBottom:5}}>Business unit <span style={{fontWeight:400,color:'var(--text3)'}}>(optional)</span></label>
                  <input className="form-input" placeholder="all business units" value={businessUnit} onChange={e=>setBusinessUnit(e.target.value)}/>
                </div>
                <div style={{flex:1}}>
                  <label style={{fontSize:12,fontWeight:600,color:'var(--text2)',display:'block',marginBottom:5}}>Legal entity <span style={{fontWeight:400,color:'var(--text3)'}}>(optional)</span></label>
                  <input className="form-input" placeholder="all legal entities" value={legalEntity} onChange={e=>setLegalEntity(e.target.value)}/>
                </div>
              </div>
              <div style={{fontSize:12,color:'var(--text3)',lineHeight:1.5,padding:'10px 12px',background:'var(--surface2)',borderRadius:8,border:'1px solid var(--border)'}}>
                Runs <b>read-only</b> probes against the live ERP (Fusion REST API or SAP CPI iFlow). No data is
                written. It runs in the background and typically takes a minute or two, so you can close this and
                carry on — the list shows its progress and updates itself when it finishes.
                Leave the scope fields empty to assess the whole tenant — every business unit is still rated separately in the report.
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={running}>Cancel</button>
          <button className="btn btn-primary" disabled={running||!connId} onClick={()=>onRun(connId,Number(windowDays),Number(sampleSize),businessUnit.trim(),legalEntity.trim(),taxationCountry.trim())}>{running?'Starting…':'Run Assessment'}</button>
        </div>
      </div>
    </div>
  );
}

/* Assessing a system we cannot call.

   Most of the ERPs this has to cover have no endpoint reachable from here, and an adapter per
   vendor would mean inventing attribute names. All of them export invoices, and both of the
   manual assessments this module is measured against were built from extracts — so the file is
   the realistic integration point, not a fallback.

   Two phases, and the second is the point. The mapper deliberately refuses to guess at a
   column, which is only a virtue if settling what it declined takes half a minute. So the file
   is read first and nothing is assessed until somebody has seen what bound, what did not, and
   what two terms both claimed. Skip that step and the refusal becomes friction, and the next
   person adds the dangerous synonym back. */
function ImpactExtractModal({connections,onClose,onAssessed,showToast}){
  const [file,setFile]=React.useState(null);
  const [erp,setErp]=React.useState('');
  const [connId,setConnId]=React.useState('');
  const [preview,setPreview]=React.useState(null);
  const [overrides,setOverrides]=React.useState({});
  const [busy,setBusy]=React.useState(false);
  const [err,setErr]=React.useState(null);
  const [profiles,setProfiles]=React.useState([]);
  const [profileId,setProfileId]=React.useState('');
  const [saveAs,setSaveAs]=React.useState('');
  const inputRef=React.useRef(null);

  React.useEffect(()=>{
    API('GET','/api/readiness/extract-profiles')
      .then(r=>setProfiles(r.profiles||[]))
      .catch(()=>{});
  },[]);

  const post=async(path,extra)=>{
    const fd=new FormData();
    fd.append('file',file);
    if(Object.keys(overrides).length) fd.append('columns',JSON.stringify(overrides));
    if(profileId) fd.append('profileId',profileId);
    Object.entries(extra||{}).forEach(([k,v])=>{ if(v) fd.append(k,v); });
    const res=await fetch(path,{method:'POST',body:fd,headers:await authHeaders()});
    const body=await res.json();
    if(!res.ok) throw new Error(body.error||('HTTP '+res.status));
    return body;
  };

  const doPreview=async()=>{
    if(!file) return;
    setBusy(true); setErr(null);
    try{ setPreview(await post('/api/readiness/extract-preview')); }
    catch(e){ setErr(e.message); setPreview(null); }
    finally{ setBusy(false); }
  };

  const doRun=async()=>{
    setBusy(true); setErr(null);
    try{
      if(saveAs.trim()){
        // Saved before the run, because the run is the thing that might fail and the binding
        // someone just worked out is the part worth keeping either way.
        const mapped={...((preview&&preview.columns&&preview.columns.mapped)||{})};
        const cols={};
        Object.entries(mapped).forEach(([key,col])=>{ cols[col]=key; });
        Object.entries(overrides).forEach(([col,key])=>{ cols[col]=key; });
        await API('POST','/api/readiness/extract-profiles',
          {name:saveAs.trim(),erp:erp.trim()||null,connectionId:connId||null,columns:cols});
      }
      const a=await post('/api/readiness/run-from-extract',{erp:erp.trim(),connectionId:connId});
      if(showToast) showToast('Assessed '+(a.erp||'extract'),'success');
      onAssessed(a);
    }catch(e){ setErr(e.message); }
    finally{ setBusy(false); }
  };

  const cols=(preview&&preview.columns)||{};
  const mapped=cols.mapped||{};
  const keys=(preview&&preview.keys)||[];
  const unmapped=cols.unmappedColumns||[];
  const ambiguous=cols.ambiguous||[];
  const inp={fontSize:12.5,padding:'6px 9px',borderRadius:7,border:'1px solid var(--border2)',
    background:'var(--surface)',color:'var(--text)',width:'100%'};

  return (
    <div className="modal-backdrop" onClick={()=>!busy&&onClose()}>
      <div className="modal" style={{maxWidth:820}} onClick={e=>e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div style={{fontWeight:800,fontSize:15}}>Assess from an invoice extract</div>
            <div style={{fontSize:12,color:'var(--text3)',marginTop:3,lineHeight:1.45}}>
              For a system with no endpoint we can reach. A CSV or spreadsheet of invoices —
              one row per line, header values repeated — from Opera, Yardi, Dynamics, Jonas,
              PACT, Dolphin, E-Business Suite or anything else that exports.
            </div>
          </div>
        </div>

        <div className="modal-body" style={{display:'flex',flexDirection:'column',gap:12}}>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',gap:10}}>
            <label style={{fontSize:11,color:'var(--text3)'}}>System name
              <input value={erp} onChange={e=>setErp(e.target.value)} placeholder="Yardi, Opera, MSD…" style={inp}/>
            </label>
            <label style={{fontSize:11,color:'var(--text3)'}}>Group with a connection (optional)
              <select value={connId} onChange={e=>setConnId(e.target.value)} style={inp}>
                <option value="">— none —</option>
                {(connections||[]).map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            {profiles.length>0&&(
              <label style={{fontSize:11,color:'var(--text3)'}}>Saved column binding
                <select value={profileId} onChange={e=>{setProfileId(e.target.value);setPreview(null);}} style={inp}>
                  <option value="">— none —</option>
                  {profiles.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </label>
            )}
          </div>
          <div style={{fontSize:11,color:'var(--text3)',lineHeight:1.45}}>
            The connection only groups this with that tenant's existing answers and notes. An
            extract needs none — that is the point.
          </div>

          <div style={{display:'flex',gap:9,alignItems:'center',flexWrap:'wrap'}}>
            <input ref={inputRef} type="file" accept=".csv,.xlsx" style={{display:'none'}}
              onChange={e=>{setFile(e.target.files&&e.target.files[0]);setPreview(null);setOverrides({});}}/>
            <button className="btn" style={{...TAXLAB_LIGHT_STYLE,fontSize:12.5}}
              onClick={()=>inputRef.current&&inputRef.current.click()} disabled={busy}>
              {file?'Choose a different file':'Choose a file'}
            </button>
            {file&&<span style={{fontSize:12,color:'var(--text2)'}}>{file.name}</span>}
            {file&&!preview&&(
              <button className="btn btn-primary" style={{...TAXLAB_CTA_STYLE,fontSize:12.5}}
                onClick={doPreview} disabled={busy}>{busy?'Reading…':'Read the columns'}</button>
            )}
          </div>

          {err&&(
            <div style={{padding:'9px 11px',borderRadius:8,background:'var(--error-bg)',
              border:'1px solid var(--error-border)',color:'var(--error)',fontSize:12.5}}>{err}</div>
          )}

          {preview&&(
            <div style={{display:'flex',flexDirection:'column',gap:11}}>
              <div style={{fontSize:12.5,color:'var(--text2)'}}>
                <b>{preview.rowsRead}</b> row(s) → <b>{preview.invoices}</b> invoice(s),
                {' '}<b>{cols.mappedCount||0}</b> column(s) bound
                {unmapped.length>0&&<span>, <b>{unmapped.length}</b> unrecognised</span>}
              </div>
              {(preview.notes||[]).map((n,i)=>(
                <div key={i} style={{fontSize:11.5,color:'var(--text3)'}}>↳ {n}</div>
              ))}

              {!preview.readyToRun&&(
                <div style={{padding:'9px 11px',borderRadius:8,background:'var(--warning-bg)',
                  border:'1px solid var(--warning-border)',fontSize:12.5,lineHeight:1.45}}>
                  <b style={{color:'var(--warning)'}}>Nothing can be assessed from this yet.</b>{' '}
                  Rows are grouped by document number, so one column has to supply it. Bind it
                  below — running without it would produce a confident-looking nothing.
                </div>
              )}

              {ambiguous.length>0&&(
                <div style={{padding:'9px 11px',borderRadius:8,background:'var(--warning-bg)',
                  border:'1px solid var(--warning-border)',fontSize:12,lineHeight:1.45}}>
                  <b style={{color:'var(--warning)'}}>Bound to nothing on purpose</b>
                  {ambiguous.map((x,i)=>(
                    <div key={i} style={{marginTop:4,color:'var(--text2)'}}>
                      <code style={{fontSize:11}}>{x.column}</code> — {x.reason}
                    </div>
                  ))}
                </div>
              )}

              {unmapped.length>0&&(
                <div>
                  <div style={{fontWeight:700,fontSize:12.5,marginBottom:3}}>
                    Columns nothing claimed ({unmapped.length})
                  </div>
                  <div style={{fontSize:11.5,color:'var(--text3)',marginBottom:7,lineHeight:1.45}}>
                    Left unbound rather than guessed at. Bind any that carry a term — a guess
                    here puts a wrong number in a coverage figure, where nobody can see it.
                  </div>
                  <div style={{display:'flex',flexDirection:'column',gap:6,maxHeight:200,overflowY:'auto'}}>
                    {unmapped.map(col=>(
                      <div key={col} style={{display:'flex',gap:8,alignItems:'center'}}>
                        <code style={{fontSize:11.5,flex:'0 0 190px',overflow:'hidden',
                          textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{col}</code>
                        <select value={overrides[col]||''} style={{...inp,flex:1}}
                          onChange={e=>{
                            const v=e.target.value;
                            setOverrides(o=>{const n={...o}; if(v) n[col]=v; else delete n[col]; return n;});
                          }}>
                          <option value="">— leave unbound —</option>
                          {keys.map(k=><option key={k} value={k}>{k}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                  {Object.keys(overrides).length>0&&(
                    <button className="btn" style={{...TAXLAB_LIGHT_STYLE,fontSize:12,marginTop:8}}
                      onClick={doPreview} disabled={busy}>Re-read with these bindings</button>
                  )}
                </div>
              )}

              {Object.keys(mapped).length>0&&(
                <details>
                  <summary style={{cursor:'pointer',fontSize:12.5,fontWeight:700}}>
                    What bound ({Object.keys(mapped).length})
                  </summary>
                  <div style={{marginTop:6,maxHeight:190,overflowY:'auto',fontSize:11.5}}>
                    {Object.entries(mapped).map(([key,col])=>(
                      <div key={key} style={{display:'flex',gap:8,padding:'2px 0',color:'var(--text2)'}}>
                        <span style={{flex:'0 0 230px'}}>{key}</span>
                        <span style={{color:'var(--text3)'}}>← {col}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {(preview.inconsistencies||[]).length>0&&(
                <div style={{padding:'9px 11px',borderRadius:8,background:'var(--warning-bg)',
                  border:'1px solid var(--warning-border)',fontSize:12,lineHeight:1.45}}>
                  <b style={{color:'var(--warning)'}}>
                    {preview.inconsistencies.length} document(s) disagree with themselves
                  </b>
                  <div style={{color:'var(--text2)',marginTop:3}}>
                    A header value differs between rows of the same document. The first is used.
                    Usually it means the export joined something it should not have.
                  </div>
                </div>
              )}

              <label style={{fontSize:11,color:'var(--text3)'}}>Save this binding for next time (optional)
                <input value={saveAs} onChange={e=>setSaveAs(e.target.value)}
                  placeholder="e.g. DAMAC Yardi leasing export" style={inp}/>
              </label>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn" style={TAXLAB_LIGHT_STYLE} onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" style={TAXLAB_CTA_STYLE} disabled={busy||!preview||!preview.readyToRun}
            onClick={doRun}>{busy?'Assessing…':'Assess this extract'}</button>
        </div>
      </div>
    </div>
  );
}

function ImpactAnalysisPage({showToast,connections,confirm}){
  const [assessments,setAssessments]=useState([]);
  const [loading,setLoading]=useState(true);
  const [running,setRunning]=useState(false);
  const [showRun,setShowRun]=useState(false);
  const [showExtract,setShowExtract]=useState(false);
  const [current,setCurrent]=useState(null);

  const load=useCallback(async()=>{
    setLoading(true);
    try{ const d=await API('GET','/api/readiness/assessments'); setAssessments(Array.isArray(d)?d:[]); }
    catch(e){ showToast('Failed to load assessments: '+e.message,'error'); }
    finally{ setLoading(false); }
  },[showToast]);
  useEffect(()=>{ load(); },[load]);

  const anyRunning=(assessments||[]).some(a=>a.status==='RUNNING');
  useEffect(()=>{
    if(!anyRunning) return;
    const t=setInterval(()=>{
      // Refresh quietly: the loading flag would blank the list every few seconds.
      API('GET','/api/readiness/assessments')
        .then(d=>setAssessments(Array.isArray(d)?d:[]))
        .catch(()=>{});
    },3000);
    return ()=>clearInterval(t);
  },[anyRunning]);

  const openReport=useCallback(async(id)=>{
    try{ const a=await API('GET','/api/readiness/assessments/'+id); setCurrent(a); }
    catch(e){ showToast('Failed to open report: '+e.message,'error'); }
  },[showToast]);

  const runAssessment=async(connectionId,windowDays,sampleSize,businessUnit,legalEntity,taxationCountry)=>{
    setRunning(true);
    try{
      // The server answers 202 with a RUNNING record. Let go of the modal immediately: the
      // probes take a minute or more, and holding a dialog open over them means a user who
      // cannot navigate away and loses the result if they do.
      const a=await API('POST','/api/readiness/run',{connectionId,windowDays,sampleSize,businessUnit,legalEntity,taxationCountry});
      setShowRun(false);
      showToast('Assessment started — it runs in the background, this list updates itself.','success');
      load();
    }catch(e){ showToast('Could not start the assessment: '+e.message,'error'); }
    finally{ setRunning(false); }
  };

  const remove=async(id,e)=>{
    e.stopPropagation();
    const _c=await confirm('Delete this assessment?');if(!_c)return;
    try{ await API('DELETE','/api/readiness/assessments/'+id); showToast('Deleted','success'); load(); }
    catch(err){ showToast(err.message,'error'); }
  };

  if(current) return <ImpactReport a={current} onBack={()=>setCurrent(null)} showToast={showToast}/>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Impact Analysis</h1>
          <div className="page-subtitle">Pre-project e-invoicing readiness — probe a source ERP and score how ready it is to emit FTA-compliant invoices.</div>
        </div>
        <div className="page-actions">
          <button className="btn btn-refresh" style={TAXLAB_LIGHT_STYLE} onClick={load}><BuilderRefreshIcon size={18}/> Refresh</button>
          <button className="btn btn-primary" style={TAXLAB_CTA_STYLE} onClick={()=>setShowRun(true)}>
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0}}>
              <path fill="currentColor" d="M12.079 2.25c-4.794 0-8.734 3.663-9.118 8.333H2a.75.75 0 0 0-.528 1.283l1.68 1.666a.75.75 0 0 0 1.056 0l1.68-1.666a.75.75 0 0 0-.528-1.283h-.893c.38-3.831 3.638-6.833 7.612-6.833a7.66 7.66 0 0 1 6.537 3.643a.75.75 0 1 0 1.277-.786A9.16 9.16 0 0 0 12.08 2.25m8.761 8.217a.75.75 0 0 0-1.054 0L18.1 12.133a.75.75 0 0 0 .527 1.284h.899c-.382 3.83-3.651 6.833-7.644 6.833a7.7 7.7 0 0 1-6.565-3.644a.75.75 0 1 0-1.277.788a9.2 9.2 0 0 0 7.842 4.356c4.808 0 8.765-3.66 9.15-8.333H22a.75.75 0 0 0 .527-1.284z"></path>
            </svg>
            Run Assessment
          </button>
          <button className="btn" style={TAXLAB_LIGHT_STYLE} onClick={()=>setShowExtract(true)}
            title="For a system with no endpoint we can reach">
            From an extract
          </button>
        </div>
      </div>

      {showRun&&<ImpactRunModal connections={connections} running={running} onClose={()=>!running&&setShowRun(false)} onRun={runAssessment}/>}

      {showExtract&&<ImpactExtractModal connections={connections} showToast={showToast}
        onClose={()=>setShowExtract(false)}
        onAssessed={a=>{ setShowExtract(false); load(); setCurrent(a); }}/>}

      {loading?(
        <div style={{textAlign:'center',padding:48,color:'var(--text3)'}}>Loading…</div>
      ):assessments.length===0?(
        <div className="empty-state" style={{textAlign:'center',padding:'56px 20px'}}>
          <EmptyStateIcon tone="teal"><AssessmentClipIcon/></EmptyStateIcon>
          <p style={{margin:'12px 0 6px',fontWeight:700,color:'var(--text)',fontSize:16}}>No assessments yet</p>
          <p style={{margin:'0 auto 18px',maxWidth:520,color:'var(--text2)',fontSize:13,lineHeight:1.5}}>
            Run a readiness assessment against an Oracle Fusion connection. It probes connectivity, supplier &amp; buyer TRN coverage, status DFFs, tax/UOM code mapping, country scope and line-data completeness — then scores readiness and lists exactly what to fix in Fusion.
          </p>
          <button className="btn btn-primary" style={TAXLAB_CTA_STYLE} onClick={()=>setShowRun(true)}>
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0}}>
              <path fill="currentColor" d="M12.079 2.25c-4.794 0-8.734 3.663-9.118 8.333H2a.75.75 0 0 0-.528 1.283l1.68 1.666a.75.75 0 0 0 1.056 0l1.68-1.666a.75.75 0 0 0-.528-1.283h-.893c.38-3.831 3.638-6.833 7.612-6.833a7.66 7.66 0 0 1 6.537 3.643a.75.75 0 1 0 1.277-.786A9.16 9.16 0 0 0 12.08 2.25m8.761 8.217a.75.75 0 0 0-1.054 0L18.1 12.133a.75.75 0 0 0 .527 1.284h.899c-.382 3.83-3.651 6.833-7.644 6.833a7.7 7.7 0 0 1-6.565-3.644a.75.75 0 1 0-1.277.788a9.2 9.2 0 0 0 7.842 4.356c4.808 0 8.765-3.66 9.15-8.333H22a.75.75 0 0 0 .527-1.284z"></path>
            </svg>
            Run your first assessment
          </button>
        </div>
      ):(
        <>
        <ImpactSummaryStrip assessments={assessments}/>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(330px,1fr))',gap:16}}>
          {assessments.map(a=>{ const band=rband(a.band); const inFlight=a.status==='RUNNING'; return (
            <div key={a.id} className="card" style={{cursor:inFlight?'default':'pointer',display:'flex',flexDirection:'column',gap:10,opacity:inFlight?0.92:1}} onClick={()=>{ if(!inFlight) openReport(a.id); }}>
              <div style={{display:'flex',alignItems:'center',gap:12}}>
                {inFlight?(
                  <div className="spin" style={{width:54,height:54,borderRadius:'50%',border:'2px solid var(--border)',borderTopColor:'var(--info)',flexShrink:0}}/>
                ):(
                <div style={{width:54,height:54,borderRadius:'50%',background:`conic-gradient(${band.c} ${(a.overallScore||0)*3.6}deg, var(--surface3) 0)`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  <AxScoreDial score={a.overallScore} color={band.c}/>
                </div>
                )}
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:700,fontSize:14,color:'var(--text)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{a.connectionName}</div>
                  {inFlight?(
                    <span style={{display:'inline-block',marginTop:4,padding:'2px 9px',borderRadius:20,background:'var(--surface3)',color:'var(--text2)',border:'1px solid var(--border)',fontWeight:700,fontSize:11}}>Running</span>
                  ):(
                    <span style={{display:'inline-block',marginTop:4,padding:'2px 9px',borderRadius:20,background:band.bg,color:band.c,border:'1px solid '+band.bd,fontWeight:700,fontSize:11}}>{band.label}</span>
                  )}
                </div>
                <button className="btn btn-danger row-icon-action" onClick={e=>remove(a.id,e)} title="Delete" aria-label="Delete" style={{flexShrink:0}}><TrashIcon size={17}/></button>
              </div>
              <div style={{fontSize:12.5,color:'var(--text2)',lineHeight:1.45,minHeight:34}}>
                {inFlight?(a.stage?a.stage+'…':'Probing the source ERP…'):a.headline}
              </div>
              <div style={{display:'flex',gap:12,fontSize:11.5,color:'var(--text3)',flexWrap:'wrap'}}>
                {inFlight?(
                  <span>Started {a.startedAt?new Date(a.startedAt).toLocaleTimeString():''} · this list updates itself</span>
                ):(<>
                <span style={{color:'var(--success)'}}>{a.checksPassed}✓</span>
                <span style={{color:'var(--warning)'}}>{a.checksWarned}!</span>
                <span style={{color:'var(--error)'}}>{a.checksFailed}✕</span>
                {a.criticalGaps>0&&<span style={{color:'var(--error)',fontWeight:700}}>{a.criticalGaps} critical</span>}
                </>)}
                <span style={{flex:1}}/>
                <span>{a.startedAt?new Date(a.startedAt).toLocaleDateString():''}</span>
              </div>
            </div>
          );})}
        </div>
        </>
      )}
    </div>
  );
}

/* Portfolio strip above the assessment cards.
 *
 * The list previously opened straight into a wall of cards, so "how ready are we,
 * overall" could only be answered by reading every card. These four read the same
 * summary rows the cards do — no extra request — and deliberately report the LATEST
 * completed score rather than an average: averaging readiness across unrelated ERPs
 * produces a number that describes nothing. Running assessments are excluded from
 * every figure; a half-finished probe has no score to contribute. */
function ImpactSummaryStrip({assessments}){
  const done=(assessments||[]).filter(a=>a.status!=='RUNNING'&&a.overallScore!=null);
  const running=(assessments||[]).filter(a=>a.status==='RUNNING').length;
  const latest=done.slice().sort((a,b)=>String(b.startedAt||'').localeCompare(String(a.startedAt||'')))[0];
  const passed=done.reduce((n,a)=>n+(a.checksPassed||0),0);
  const warned=done.reduce((n,a)=>n+(a.checksWarned||0),0);
  const failed=done.reduce((n,a)=>n+(a.checksFailed||0),0);
  const gaps=done.reduce((n,a)=>n+(a.criticalGaps||0),0);
  const totalChecks=passed+warned+failed;
  const band=latest?rband(latest.band):null;

  if(!done.length&&!running) return null;

  return(
    <div style={{display:'grid',gridTemplateColumns:'minmax(0,1fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1.3fr)',
                 gap:14,marginBottom:18}}>
      <AxStatCard label="Assessments" value={done.length}
        sub={running?running+' running now':'all complete'}
        tint="rgba(37,99,235,.12)" icon="◷"/>
      <AxStatCard label="Latest score" value={latest?latest.overallScore:'—'}
        sub={latest?(band.label+' · '+(latest.connectionName||'')):'no completed run'}
        tint="rgba(255,86,48,.12)" icon="◎"/>
      <AxStatCard label="Critical gaps" value={gaps}
        sub={gaps?'must be fixed before go-live':'none outstanding'}
        tint={gaps?'rgba(220,38,38,.12)':'rgba(22,163,74,.12)'} icon={gaps?'⚠':'✓'}/>
      <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',padding:'16px 18px'}}>
        <div style={{fontSize:13,fontWeight:600,color:'var(--text2)',marginBottom:10}}>Checks across all runs</div>
        {totalChecks?(
          <>
            <div style={{display:'flex',height:7,borderRadius:4,overflow:'hidden',background:'var(--surface3)',marginBottom:9}}>
              <div style={{width:(passed/totalChecks*100)+'%',background:'var(--success)'}}/>
              <div style={{width:(warned/totalChecks*100)+'%',background:'var(--warning)'}}/>
              <div style={{width:(failed/totalChecks*100)+'%',background:'var(--error)'}}/>
            </div>
            <div style={{display:'flex',gap:14,fontSize:11.5,flexWrap:'wrap'}}>
              <span style={{color:'var(--success)',fontWeight:700}}>{passed} passed</span>
              <span style={{color:'var(--warning)',fontWeight:700}}>{warned} warned</span>
              <span style={{color:'var(--error)',fontWeight:700}}>{failed} failed</span>
              <span style={{color:'var(--text3)',marginLeft:'auto'}}>{totalChecks} total</span>
            </div>
          </>
        ):(
          <div style={{fontSize:12,color:'var(--text3)'}}>No completed checks yet</div>
        )}
      </div>
    </div>
  );
}

// Full-page login experience rendered when no session exists. Mirrors the
// sign-in form, fills the viewport, adds a hero panel with the brand
// and an SSO hint, and disables browser autocomplete tricks so it feels like a
// proper "sign-in wall" rather than a popup.
function LoginPage({onLogin}){
  const [username,setUsername]=useState('');
  const [password,setPassword]=useState('');
  const [loading,setLoading]=useState(false);
  // A failed return from Keycloak's login page lands here (see completeKeycloakLogin).
  const [error,setError]=useState(()=>{const e=window.__ssoError||null;window.__ssoError=null;return e;});
  const [redirecting,setRedirecting]=useState(false);
  const signInWithKeycloak=async()=>{
    if(redirecting)return;
    setRedirecting(true);
    setError(null);
    try{ await startKeycloakLogin(); }
    catch(e){ setError(e.message||'Keycloak is not reachable'); setRedirecting(false); }
  };

  const submit=async(ev)=>{
    ev.preventDefault();
    if(loading)return;
    setLoading(true);
    setError(null);
    try{
      // The form is Axon's own login: the local account, checked by Axon (BreakglassService).
      // Federated users sign in through the Keycloak button below instead.
      await onLogin(username,password,'local');
    }catch(e){
      setError(e.message||'Login failed');
    }finally{
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-page-hero">
        <img className="login-page-logo" src="logo.png" alt="TaxLab"/>
        <div className="login-page-tag">Axon Integration Platform</div>
        <h1 className="login-page-headline">Sign in to your workspace</h1>
        <p className="login-page-sub">Manage integrations, monitor executions, and orchestrate invoice flows — secured by Keycloak SSO.</p>
        <ul className="login-page-bullets">
          <li><span className="dot dot-success"/>SSO via Keycloak · 7-min refresh</li>
          <li><span className="dot dot-info"/>Role-based access across all API endpoints</li>
          <li><span className="dot dot-warning"/>Audit trail on every config change</li>
        </ul>
      </div>
      <div className="login-page-panel">
        <form className="login-page-form" onSubmit={submit} autoComplete="on">
          <h2 className="login-page-title">Welcome back</h2>
          <p className="login-page-help">Enter your Axon credentials to continue.</p>
          {error && <div className="login-page-error">{error}</div>}
          <label className="login-page-field">
            <span>Username or email</span>
            <input
              type="text"
              value={username}
              autoComplete="username"
              required
              autoFocus
              onChange={e=>setUsername(e.target.value)}
            />
          </label>
          <label className="login-page-field">
            <span>Password</span>
            <input
              type="password"
              value={password}
              autoComplete="current-password"
              required
              onChange={e=>setPassword(e.target.value)}
            />
          </label>
          <button
            className="btn btn-primary login-page-submit"
            type="submit"
            disabled={loading||!username||!password}
          >
            {loading?'Signing in…':'Sign in'}
          </button>
          <div style={{display:'flex',alignItems:'center',gap:10,margin:'14px 0 10px',color:'var(--text3)',fontSize:12}}>
            <span style={{flex:1,height:1,background:'var(--border)'}}/>or<span style={{flex:1,height:1,background:'var(--border)'}}/>
          </div>
          <button
            className="btn btn-secondary"
            type="button"
            style={{width:'100%',justifyContent:'center'}}
            onClick={signInWithKeycloak}
            disabled={redirecting||loading}
          >
            {redirecting?'Opening Keycloak…':'Sign in with Keycloak'}
          </button>
          {/* Customers: the developer portal, signed in with their own TaxLab identity
              through the axon-portal client. They never see the console. */}
          <button type="button" className="btn btn-ghost" style={{width:'100%',marginTop:8}}
            onClick={async()=>{if(redirecting)return;setRedirecting(true);setError(null);
              try{await startKeycloakLogin('axon-portal');}catch(e){setError(e.message||'Keycloak is not reachable');setRedirecting(false);}}}
            disabled={redirecting||loading}>
            TaxLab customer? Sign in to the developer portal
          </button>
          <div className="login-page-foot">
            <span>Issues signing in?</span>
            <a href="mailto:support@taxlab.ai">Contact support</a>
          </div>
        </form>
      </div>
    </div>
  );
}

// React 18 unmounts the entire tree when a render error reaches the root with
// nothing to catch it — the app does not show an error, it shows an empty
// <div id="root">. That is the "every page goes blank" symptom: the cause is a
// one-line bug in one component, but the blast radius is the whole UI.
//
// Catching at the content level keeps the shell — sidebar, header, nav — mounted,
// so a broken page is a card the user can read and click away from rather than a
// dead tab needing a reload. Used with key={tab} so switching pages remounts the
// boundary and clears the error without one.
class PageErrorBoundary extends React.Component{
  constructor(props){ super(props); this.state={err:null}; }
  static getDerivedStateFromError(err){ return {err}; }
  componentDidCatch(err,info){ console.error('[page error]',err,info&&info.componentStack); }
  render(){
    if(!this.state.err) return this.props.children;
    const msg=String((this.state.err&&this.state.err.message)||this.state.err);
    return (
      <div className="card" style={{padding:24}}>
        <h3 style={{margin:'0 0 8px',fontSize:15,color:'var(--error)'}}>This page failed to render</h3>
        <p style={{margin:'0 0 14px',color:'var(--text3)',fontSize:13}}>
          The rest of the app is still running — pick another page from the sidebar, or reload.
        </p>
        <pre style={{whiteSpace:'pre-wrap',wordBreak:'break-word',fontSize:12,color:'var(--text3)',
                     background:'var(--surface2)',border:'1px solid var(--border)',
                     padding:10,borderRadius:6,margin:'0 0 14px'}}>{msg}</pre>
        <button className="btn btn-sm" onClick={()=>this.setState({err:null})}>Try again</button>
      </div>
    );
  }
}

/* Grafana, Prometheus and Zipkin are reachable only through the
 * console, with the console's own sign-in. nginx lets a request under /grafana/,
 * /prometheus/ or /zipkin/ through only with a valid axon_tools
 * cookie, which Axon issues in exchange for this tab's token (ToolsSessionController).
 * An iframe cannot send a bearer token, hence the cookie.
 *
 * ToolFrame gets that cookie before it mounts the iframe (so the first load is not a
 * 401 page) and renews it every 4 minutes while open -- the cookie lives 10. Every tool
 * iframe must go through it; a bare <iframe src="/grafana/..."> shows "sign in through
 * Axon" instead of the tool. */
const TOOLS_RENEW_MS=4*60*1000;
async function openToolsSession(){
  const r=await fetch('/api/auth/tools-session',{method:'POST',headers:await authHeaders()});
  if(!r.ok)throw new Error(r.status===401||r.status===403?'Your role does not include the observability tools.':'Could not open the tools session ('+r.status+')');
}
function ToolFrame({src,title,style}){
  const [state,setState]=useState('opening');   // opening | ready | error
  const [err,setErr]=useState('');
  useEffect(()=>{
    let live=true;
    const renew=()=>openToolsSession()
      .then(()=>{if(live)setState('ready');})
      .catch(e=>{if(live){setErr(e.message);setState('error');}});
    renew();
    const t=setInterval(renew,TOOLS_RENEW_MS);
    return()=>{live=false;clearInterval(t);};
  },[]);
  const box={width:'100%',height:'calc(100vh - 120px)',border:'none',borderRadius:8,...(style||{})};
  if(state==='ready')return <iframe src={src} title={title} style={box}/>;
  return (
    <div style={{...box,display:'flex',alignItems:'center',justifyContent:'center',background:'var(--surface2)'}}>
      <span className={'text-sm '+(state==='error'?'':'text-muted')} style={state==='error'?{color:'#dc2626'}:null}>
        {state==='error'?err:'Opening '+title+'…'}
      </span>
    </div>
  );
}

// The three Grafana dashboards, behind one nav leaf. They are iframes into the
// SINGLE axon-grafana instance on :3000 — not three servers.
//
// The views stay separate rather than being merged into one Grafana dashboard
// because their template variables would collide: the APISIX dashboard has its
// own, the Camel ones use job/context/route/processor, and a single `route`
// variable would have to mean an APISIX route AND a Camel route at once. That
// ambiguity is what forced the "Route Metrics" rename on 2026-10-02. Merging the
// NAVIGATION is fine — the view labels still say which is which; merging the
// DASHBOARDS is not.
//
// Each dashboard's source of truth is a provisioned JSON under
// deploy/monitoring/grafana/provisioning/dashboards/; editing one in the Grafana
// UI is lost on the next container restart.
const METRIC_VIEWS=[
  {id:'gateway',label:'Gateway',src:'/grafana/d/apisix-overview/gateway-overview',title:'Gateway Metrics',
   hint:'Gateway — RPS, latency, status codes, upstream health'},
  {id:'integration',label:'Integration',src:'/grafana/d/camel-routes/camel-routes',title:'Camel Integration Metrics',
   hint:'Camel, per route — throughput, failures, redeliveries, per-route totals'},
  {id:'context',label:'Context',src:'/grafana/d/camel-context/camel-context-view',title:'Camel Context Metrics',
   hint:'Camel runtime — routes running vs added, JVM, CPU, per-processor timings'},
  {id:'consumers',label:'Consumers',src:'/grafana/d/api-consumers/api-consumers',title:'API Consumers',
   hint:'Gateway traffic per customer organisation, app and API product'},
  {id:'ai',label:'AI Gateway',src:'/grafana/d/ai-gateway/ai-gateway',title:'AI Gateway',
   hint:'AI products — calls by outcome, spend, tokens, cache, guardrails, budgets, Redis'},
];
// Deep links from when these were three leaves (?tab=gateway-metrics etc).
// access-usage was the separate Consumer Usage leaf (2026-10-17), folded into the Consumers view.
const LEGACY_METRIC_TABS={'gateway-metrics':'gateway','integration-metrics':'integration','context-metrics':'context','access-usage':'consumers'};

function MetricsPage(){
  const [view,setView]=useState(()=>{
    try{
      const p=new URLSearchParams(window.location.search);
      const v=p.get('view');
      if(v&&METRIC_VIEWS.some(x=>x.id===v)) return v;
      const legacy=LEGACY_METRIC_TABS[p.get('tab')];
      if(legacy) return legacy;
    }catch(e){}
    return 'gateway';
  });
  const cur=METRIC_VIEWS.find(v=>v.id===view)||METRIC_VIEWS[0];
  // One iframe at a time, deliberately: keeping all three mounted would load
  // three Grafana dashboards on every visit to this page.
  return (
    <div>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10,flexWrap:'wrap'}}>
        {METRIC_VIEWS.map(v=>(
          <button key={v.id} className={'um-tab'+(view===v.id?' active':'')}
                  onClick={()=>setView(v.id)} aria-current={view===v.id?'page':undefined}>{v.label}</button>
        ))}
        <span style={{fontSize:12,color:'var(--text-dim)',marginLeft:4}}>{cur.hint}</span>
      </div>
      <ToolFrame key={cur.id} src={cur.src} title={cur.title} style={{height:'calc(100vh - 170px)'}}/>
    </div>
  );
}

/* CamelBee — native topology graph + live flow (Observability -> Route Topology).
 *
 * We use CamelBee as the ENGINE, not as the UI. Its /routes payload is the only source
 * here that carries each route's ordered outputs with real endpoint URIs — our own
 * /api/admin/routes returns {id,status,description} only, which cannot draw a graph.
 * Everything below is drawn by us from that data, fetched through
 * /api/admin/camelbee/** (Keycloak-gated, credential stays server-side), so the graph
 * needs no second login and no nginx exposure.
 *
 * Live flow polls the same delta cursor CamelBee's own console uses
 * (index/addVersion/resetVersion) — its bundle has no EventSource or WebSocket either,
 * so this is the same fidelity, not a downgrade. Nothing flows unless the tracer is on,
 * and the tracer self-disarms after 5 idle minutes. */

/* Unwrap Camel's node descriptions down to a bare URI.
 *
 *   From[seda:fusion-cmk-invoice?concurrentConsumers=2]  -> seda:fusion-cmk-invoice
 *   to[/api/invoice-endpoints/documents?httpMethod=POST] -> /api/invoice-endpoints/documents
 *   DynamicTo[toD[https://host/path?a=b]]                -> https://host/path
 *
 * The wrappers NEST — a toD renders as DynamicTo[toD[...]] — so strip repeatedly rather
 * than once. Stripping once left "toD[https://…" which matched neither the http nor the
 * "/" test, so every dynamic endpoint silently vanished from the graph: the AR route, the
 * two TaxLab handlers and both SAP posters appeared to have no outputs at all.
 *
 * A URI that is still a placeholder after unwrapping (${exchangeProperty.sapCpiUrl}) is
 * returned as-is and filtered out by the caller — the target genuinely is not known until
 * runtime, so inventing a node for it would be a lie. */
function cbUri(raw){
  if(!raw) return '';
  let t=String(raw).trim();
  for(let i=0;i<4;i++){
    const m=t.match(/^[A-Za-z]+\[(.*)\]$/);
    if(!m) break;
    t=m[1].trim();
  }
  const q=t.indexOf('?');
  if(q>=0) t=t.slice(0,q);
  return t.trim();
}
function cbShort(uri){
  if(!uri) return '';
  const cut=(t)=>t.length>30?t.slice(0,29)+'…':t;
  /* For an http endpoint show host + last path segment. Slicing at the first ':' (the old
     behaviour) produced "//fa-eucb-test-saasfaprod…" for every Fusion endpoint — the scheme
     slashes eating the only characters that identify it. */
  if(uri.indexOf('http')===0){
    try{
      const u=new URL(uri);
      const segs=u.pathname.split('/').filter(Boolean);
      const tail=segs.length?'/'+segs[segs.length-1]:'';
      const full=u.hostname+tail;
      /* A long tenant host (fa-eucb-test-saasfaprod1.fa.ocs.oraclecloud.com) eats the whole
         budget and truncates away the path — which is the part that says WHICH endpoint it
         is. Drop to the first host label so the path survives. */
      if(full.length>30&&tail) return cut(u.hostname.split('.')[0]+tail);
      return cut(full);
    }catch(e){/* fall through */}
  }
  if(uri.charAt(0)==='/'){
    const segs=uri.split('?')[0].split('/').filter(Boolean);
    return cut(segs.length?'/'+segs.slice(-2).join('/'):uri);
  }
  /* timer://sample -> sample, not "//sample": strip the scheme AND its slashes. */
  const i=uri.indexOf(':');
  return cut((i>=0?uri.slice(i+1):uri).replace(/^\/+/,''));
}

/* ${exchangeProperty.sapCpiUrl} -> sapCpiUrl.  The previous regex was greedy and captured
   a single trailing character, so dynamic nodes were labelled "i", "d", "h", "l". */
function cbDynLabel(uri){
  const m=String(uri||'').match(/\$\{([^}]+)\}/);
  if(!m) return 'runtime';
  const parts=m[1].split('.');
  return parts[parts.length-1]||'runtime';
}

/* Build nodes+edges. Route-to-route edges come from matching a route's to[...] URI
   against another route's From[...] URI — that is what turns the four CMK routes into
   one connected chain instead of four unrelated boxes. */
function cbBuildGraph(routes){
  const byInput={};
  routes.forEach(r=>{const u=cbUri(r.input); if(u) byInput[u]=r.id;});
  const nodes=routes.map(r=>({id:r.id,kind:'route',label:r.id,input:cbUri(r.input)}));
  const extern={}, edges=[];
  routes.forEach(r=>{
    (r.outputs||[]).forEach(o=>{
      const u=cbUri(o.description);
      if(!u) return;
      const target=byInput[u];
      if(target&&target!==r.id){
        if(!edges.some(e=>e.from===r.id&&e.to===target)) edges.push({from:r.id,to:target,uri:u,internal:true});
        return;
      }
      if(target) return;
      /* A ${...} target is a Simple expression evaluated per exchange — there is no static
         value, so it gets its own dashed node rather than being dropped. Silently omitting
         it made routes look like dead ends: sap-ar-invoice-poll and sap-ap-journal-entry-post
         both post to ${exchangeProperty.sapCpiUrl} and appeared to send nowhere at all. */
      const dyn=o.dynamic==='true'||u.indexOf('${')>=0;
      const addressable=u.indexOf('http')===0||u.charAt(0)==='/';
      if(addressable){
        /* Addressable even when it carries a ${...} segment — the Fusion handlers build a
           per-message path against a fixed host, so showing the host and dashing the edge
           is far more useful than collapsing it to an expression name. */
        const eid='ext:'+u;
        if(!extern[eid]) extern[eid]={id:eid,kind:'extern',label:cbShort(u),input:u,dyn:dyn};
        if(!edges.some(e=>e.from===r.id&&e.to===eid)) edges.push({from:r.id,to:eid,uri:u,dynamic:dyn});
      }else if(dyn){
        const eid='dyn:'+u;
        if(!extern[eid]) extern[eid]={id:eid,kind:'dynamic',label:cbDynLabel(u),input:u};
        if(!edges.some(e=>e.from===r.id&&e.to===eid)) edges.push({from:r.id,to:eid,uri:u,dynamic:true});
      }
    });
  });
  const all=nodes.concat(Object.values(extern));
  // layer by longest path from a node with no internal inbound edge
  const depth={}; all.forEach(n=>depth[n.id]=0);
  for(let pass=0;pass<6;pass++){
    edges.forEach(e=>{ if(depth[e.to]<depth[e.from]+1) depth[e.to]=depth[e.from]+1; });
  }
  const cols={};
  all.forEach(n=>{const d=depth[n.id]||0;(cols[d]=cols[d]||[]).push(n);});
  const COLW=250,ROWH=62;
  Object.keys(cols).forEach(d=>{
    cols[d].forEach((n,i)=>{ n.x=20+Number(d)*COLW; n.y=20+i*ROWH; });
  });
  // Math.max() of nothing is -Infinity, which the browser rejects as an SVG size (console
  // error on an empty topology); clamp so an empty graph is just a small blank canvas.
  const height=Math.max(0,...all.map(n=>n.y))+70;
  const width=Math.max(0,...all.map(n=>n.x))+220;
  return {nodes:all,edges,width,height};
}

function CamelBeePage({showToast}){
  const [routes,setRoutes]=useState(null);
  const [tracer,setTracer]=useState({state:'loading'});
  const [busy,setBusy]=useState(false);
  const [selected,setSelected]=useState(null);
  const [live,setLive]=useState(false);
  const [activity,setActivity]=useState({});
  const cursor=useRef({addVersion:-1,resetVersion:-1});

  /* Topology comes from OUR endpoint, not CamelBee's.
   *
   * CamelBee caches its route list on first access and never rebuilds it, and every real
   * route here is created dynamically ~60s after boot by IntegrationBootstrap. A page load
   * inside that window pinned its topology to the single static route for the life of the
   * JVM — one box, no links, no warning. /api/admin/topology walks the live CamelContext
   * per request instead, so a restart or a newly activated integration shows up on the
   * next refresh. CamelBee still provides the tracer below. */
  const loadRoutes=useCallback(async()=>{
    try{const r=await API('GET','/api/admin/topology');setRoutes(r.routes||[]);}
    catch(e){setRoutes({error:e.message});}
  },[]);

  const loadMessages=useCallback(async()=>{
    try{
      const m=await API('GET','/api/admin/camelbee/messages?index=0');
      if(m.info){cursor.current={addVersion:m.info.addVersion,resetVersion:m.info.resetVersion};}
      const msgs=m.messages||[];
      setTracer({state:'ready',count:(m.info&&m.info.count)||0,messages:msgs});
      const now=Date.now(),act={};
      msgs.forEach(x=>{
        const k=x.routeId||x.endpoint; if(!k) return;
        const ts=Number(x.timeStamp)||0;
        if(!act[k]||ts>act[k].ts) act[k]={ts,age:now-ts};
      });
      setActivity(act);
    }catch(e){
      setTracer(String(e.message).includes('tracer')?{state:'boot-disabled'}:{state:'error',error:e.message});
    }
  },[]);

  useEffect(()=>{loadRoutes();loadMessages();},[loadRoutes,loadMessages]);
  useEffect(()=>{
    if(!live) return;
    const t=setInterval(loadMessages,2000);
    return()=>clearInterval(t);
  },[live,loadMessages]);

  const toggle=async(status)=>{
    setBusy(true);
    try{
      await API('POST','/api/admin/camelbee/tracer/status',{status});
      showToast('Tracer '+status,'success');
      setLive(status==='ACTIVE');
      await loadMessages();
    }catch(e){showToast('Tracer toggle failed: '+e.message,'error');}
    finally{setBusy(false);}
  };
  const wipe=async()=>{
    setBusy(true);
    try{await API('DELETE','/api/admin/camelbee/messages');showToast('Buffer wiped','success');setActivity({});await loadMessages();}
    catch(e){showToast('Wipe failed: '+e.message,'error');}
    finally{setBusy(false);}
  };

  const graph=React.useMemo(()=>Array.isArray(routes)?cbBuildGraph(routes):null,[routes]);
  const nodeById={}; if(graph) graph.nodes.forEach(n=>nodeById[n.id]=n);
  const NODEW=200,NODEH=38;
  const isHot=(id)=>{const a=activity[id];return a&&(Date.now()-a.ts)<15000;};

  const rows=(tracer.messages||[]).filter(m=>!selected||m.routeId===selected||m.endpoint===selected);

  return(
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Route Topology</h1>
          <div className="page-subtitle">Live route graph and message tracing</div>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          {live&&<span style={{fontSize:11,fontWeight:600,color:'var(--success)'}}>● LIVE</span>}
          <button className="btn btn-secondary" onClick={()=>{loadRoutes();loadMessages();}}>Refresh</button>
        </div>
      </div>

      {/* ── Topology graph, drawn by us from CamelBee's route data ── */}
      <div className="card" style={{padding:16,marginBottom:14}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
          <div style={{fontWeight:600}}>Topology{selected?' — filtered: '+selected:''}</div>
          {selected&&<button className="btn btn-secondary" onClick={()=>setSelected(null)}>Clear filter</button>}
        </div>
        {routes===null&&<div style={{color:'var(--text3)'}}>Loading…</div>}
        {routes&&routes.error&&<div style={{color:'var(--danger)'}}>Failed to load topology: {routes.error}</div>}
        {graph&&(
          <div style={{overflowX:'auto'}}>
            <svg width={graph.width} height={graph.height} style={{maxWidth:'100%'}}>
              <defs>
                <marker id="cbarrow" markerWidth="9" markerHeight="9" refX="8" refY="4.5" orient="auto">
                  <path d="M0,0 L9,4.5 L0,9 z" fill="var(--text3)"/>
                </marker>
                <marker id="cbarrowhot" markerWidth="9" markerHeight="9" refX="8" refY="4.5" orient="auto">
                  <path d="M0,0 L9,4.5 L0,9 z" fill="var(--success)"/>
                </marker>
              </defs>
              {graph.edges.map((e,i)=>{
                const a=nodeById[e.from],b=nodeById[e.to];
                if(!a||!b) return null;
                const x1=a.x+NODEW,y1=a.y+NODEH/2,x2=b.x,y2=b.y+NODEH/2;
                const mx=(x1+x2)/2;
                const hot=isHot(e.from)&&live;
                return(
                  <path key={i} d={'M'+x1+','+y1+' C'+mx+','+y1+' '+mx+','+y2+' '+x2+','+y2}
                        fill="none" stroke={hot?'var(--success)':'var(--border)'}
                        strokeWidth={hot?2:1.2}
                        strokeDasharray={e.dynamic&&!hot?'4 3':undefined}
                        markerEnd={hot?'url(#cbarrowhot)':'url(#cbarrow)'}>
                    {hot&&<animate attributeName="stroke-dasharray" values="0,80;80,0" dur="1.2s" repeatCount="indefinite"/>}
                  </path>
                );
              })}
              {graph.nodes.map(n=>{
                const hot=isHot(n.id)&&live;
                const sel=selected===n.id;
                return(
                  <g key={n.id} onClick={()=>setSelected(sel?null:n.id)} style={{cursor:'pointer'}}>
                    <rect x={n.x} y={n.y} width={NODEW} height={NODEH} rx="7"
                          fill={n.kind==='route'?'var(--surface)':'var(--surface2)'}
                          stroke={sel?'var(--brand)':hot?'var(--success)':'var(--border)'}
                          strokeDasharray={(n.kind==='dynamic'||n.dyn)?'5 3':undefined}
                          strokeWidth={sel||hot?2:1.2}/>
                    <text x={n.x+10} y={n.y+16} fontSize="11" fontWeight="600" fill="var(--text)">
                      {n.label.length>28?n.label.slice(0,27)+'…':n.label}
                    </text>
                    <text x={n.x+10} y={n.y+30} fontSize="9" fill="var(--text3)">
                      {n.kind==='extern'?(n.dyn?'external · path per message':'external endpoint'):n.kind==='dynamic'?'resolved per message':cbShort(n.input)}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>
        )}
        {graph&&(
          <div style={{fontSize:11,color:'var(--text3)',marginTop:8}}>
            {graph.nodes.filter(n=>n.kind==='route').length} routes · {graph.nodes.filter(n=>n.kind==='extern').length} external
            endpoints · {graph.nodes.filter(n=>n.kind==='dynamic').length} dynamic (dashed — target resolved per
            message) · {graph.edges.length} links. Click a node to filter the capture table.
            {live?' Edges pulse green as messages flow.':' Start tracing to see live flow.'}
          </div>
        )}
      </div>

      {/* ── Tracer ── */}
      <div className="card" style={{padding:16,marginBottom:14}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
          <div style={{fontWeight:600}}>Message tracer</div>
          {tracer.state==='ready'&&(
            <div style={{display:'flex',gap:8}}>
              <button className="btn btn-secondary" disabled={busy} onClick={()=>toggle('ACTIVE')}>Start tracing</button>
              <button className="btn btn-secondary" disabled={busy} onClick={()=>toggle('INACTIVE')}>Stop</button>
              <button className="btn btn-secondary" disabled={busy} onClick={wipe}>Wipe buffer</button>
            </div>
          )}
        </div>
        {tracer.state==='loading'&&<div style={{color:'var(--text3)'}}>Checking…</div>}
        {tracer.state==='boot-disabled'&&(
          <div style={{color:'var(--text2)',lineHeight:1.6}}>
            Tracer disabled at boot (<code>camelbee.tracer-enabled=false</code>) — its endpoints do not
            exist until enabled. Set it in Settings → Configuration and restart. Capture holds message
            bodies in memory and self-disables after 5 idle minutes; the durable per-hop record is the
            execution log at DEBUG.
          </div>
        )}
        {tracer.state==='error'&&<div style={{color:'var(--danger)'}}>{tracer.error}</div>}
        {tracer.state==='ready'&&(
          <div>
            <div style={{fontSize:12,color:'var(--text3)',marginBottom:8}}>
              {tracer.count} in buffer{selected?' · showing '+rows.length+' for '+selected:''} ·
              bodies held in memory only, never persisted
            </div>
            {rows.length>0&&(
              <table className="data-table" style={{width:'100%',fontSize:12}}>
                <thead><tr><th>Time</th><th>Route / endpoint</th><th>Type</th><th>Event</th><th>ms</th></tr></thead>
                <tbody>
                  {rows.slice(-25).reverse().map((m,i)=>(
                    <tr key={i}>
                      <td>{m.timeStamp?new Date(Number(m.timeStamp)).toLocaleTimeString():''}</td>
                      <td>{m.routeId||m.endpoint}</td>
                      <td>{m.messageType}</td>
                      <td>{m.exchangeEventType}</td>
                      <td>{m.timeTaken}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

    </div>
  );
}

function App(){
  const [tab,setTab]=useState(()=>{
    try{
      const t=new URLSearchParams(window.location.search).get('tab');
      // These two are now tabs inside API Gateway, not standalone leaves.
      if(t==='apisix-plugins') return 'api-gateway';
      // API Consumers merged into Organisations (2026-10-17).
      if(t==='api-consumers') return 'access-orgs';
      // Swagger UI leaf removed — per-product OpenAPI lives in the Developer Portal.
      if(t==='api-docs') return 'api-products';
      // The three Grafana leaves became views inside one Metrics leaf (2026-10-03).
      // MetricsPage reads the original ?tab= value to pick the matching view.
      if(LEGACY_METRIC_TABS[t]) return 'metrics';
      // Access Requests leaf retired: grants are approved on the organisation's credentials.
      if(t==='access-requests') return 'access-orgs';
      // Request Inspector retired (2026-10-18): it showed gateway config, not traffic.
      // Its nearest live neighbour is Distributed Tracing.
      if(t==='gateway-inspector') return 'gateway-tracing';
      if(t) return t;
    }catch(e){}
    return 'dashboard';
  });
  // Once the Designer has been opened, keep its iframe mounted and just hide it.
  // Kaoto is a 6.3 MB bundle plus a 15 MB Camel catalog that it parses on boot;
  // unmounting on tab-switch meant paying the whole download and parse again on
  // every return. Latched rather than derived from `tab` so leaving the tab does
  // not tear it down, and never set until the leaf is actually visited — nobody
  // who avoids the Designer should fetch 21 MB for it.
  const [designerMounted,setDesignerMounted]=useState(false);
  useEffect(()=>{ if(tab==='designer') setDesignerMounted(true); },[tab]);
  const [toast,setToast]=useState(null);
  const [confirmModal,setConfirmModal]=useState(null); // {message, onConfirm}
  const themeApi = useTheme();
  const [themePopOpen,setThemePopOpen]=useState(false);
  const [profileOpen,setProfileOpen]=useState(false);
  const [profileTab,setProfileTab]=useState('profile');
  const [profileEditing,setProfileEditing]=useState(false);
  const [mfaEnabled,setMfaEnabled]=useState(true);
  const [connections,setConnections]=useState([]);
  const [integrations,setIntegrations]=useState([]);
  const [executions,setExecutions]=useState([]);
  const [projects,setProjects]=useState([]);
  const [integrationsFilter,setIntegrationsFilter]=useState(null);
  const [sidebarCollapsed,setSidebarCollapsed]=useState(()=>{
    try { return localStorage.getItem('sidebar.collapsed') === '1'; } catch { return false; }
  });
  const toggleSidebar=useCallback(()=>{
    setSidebarCollapsed(prev=>{
      const next=!prev;
      try { localStorage.setItem('sidebar.collapsed', next?'1':'0'); } catch{}
      return next;
    });
  },[]);

  // ── Auth state ─────────────────────────────────────────────────────────────
  // One of: {status:'loading'} | {status:'authenticated',user:{...}} | {status:'guest'}
  const [auth,setAuth]=useState(()=>({status:'loading'}));

  const setAuthenticated=useCallback((user)=>{
    // Mirror the session into window.__authData. ensureValidToken() and
    // authHeaders() read ONLY that global — React state is invisible to them.
    // saveAuthData() sets it on a fresh login, but the boot path restores a
    // cached token straight into React state, so without this line every
    // reload of a signed-in session left the global null: the app rendered as
    // authenticated and then the first secured call on every page found no
    // token and bounced the user to sign in. That was the "sign in" prompt
    // appearing on a working session after nothing more than F5.
    window.__authData={
      token:user.token||null,
      refreshToken:user.refreshToken||localStorage.getItem('kc_refresh'),
      username:user.username,
      fullName:user.fullName||user.username||'User',
      email:user.email||'',
      roles:user.roles||[],
    };
    setAuth({status:'authenticated',user});
  },[setAuth]);

  const markGuest=useCallback(()=>{
    setAuth({status:'guest', user: null});
  },[setAuth]);

  // Session probe on first mount: prefer cached token, then call /api/auth/me
  useEffect(()=>{
    let cancelled=false;
    (async()=>{
      try{
        const sso=await completeKeycloakLogin();
        if(sso){
          if(cancelled)return;
          const u=parseJwt(sso.access_token);
          setAuthenticated({token:sso.access_token,refreshToken:sso.refresh_token||null,username:u.preferred_username,fullName:u.name||u.preferred_username,email:u.email||'',roles:u.realm_access&&u.realm_access.roles?u.realm_access.roles:[]});
          return;
        }
      }catch(e){ window.__ssoError=e.message||'Keycloak sign-in failed'; }
      const cached=parseAuthUser();
      if(cached){
        // Local check first (cheap), then confirm with the server. Both must
        // pass: exp>60 alone let a key-rotated token through, which rendered the
        // app and then 401'd every page behind a login modal. See
        // serverAcceptsToken().
        const payload=parseJwt(cached.token);
        const exp=(payload.exp||0)-Date.now()/1000;
        if(exp>60&&await serverAcceptsToken(cached.token)){
          if(cancelled)return;
          setAuthenticated({token:cached.token,refreshToken:cached.refreshToken,username:cached.username,fullName:payload.name||cached.username,email:payload.email||'',roles:payload.realm_access&&payload.realm_access.roles?payload.realm_access.roles:[]});
          return;
        }
        if(cached.refreshToken){
          try{
            const resp=await fetch('/auth/realms/axon/protocol/openid-connect/token',{
              method:'POST',headers:{'Content-Type':'application/x-www-form-urlencoded'},
              body:new URLSearchParams({grant_type:'refresh_token',client_id:authClientId(),refresh_token:cached.refreshToken})
            });
            if(resp.ok&&!cancelled){
              const data=await resp.json();
              const u=parseJwt(data.access_token);
              setAuthenticated({token:data.access_token,refreshToken:data.refresh_token||cached.refreshToken,username:u.preferred_username,fullName:u.name||u.preferred_username,email:u.email||'',roles:u.realm_access&&u.realm_access.roles?u.realm_access.roles:[]});
              return;
            }
          }catch{}
        }
        // refresh failed → clear and fall through to login
        clearAuthData();
      }
      // No cached token — probe the session endpoint (supports SSO cookie if configured)
      try{
        const resp=await fetch('/api/auth/me',{method:'GET',headers:{Accept:'application/json'},credentials:'omit'});
        if(resp.ok&&!cancelled){
          const u=await resp.json();
          // Probe always answers 200 — the body says whether a session exists.
          if(u.authenticated){
            setAuthenticated({token:u.token||null,refreshToken:null,username:u.username||u.name||'user',fullName:u.fullName||u.name||u.username||'User',email:u.email||'',roles:u.roles||[]});
            return;
          }
        }
      }catch{}
      if(!cancelled) markGuest();
    })();
    return()=>{cancelled=true;};
  },[]);

  // Sidebar group expanded state — persisted across reloads.
  const [groupOpen,setGroupOpen]=useState(()=>{
    try {
      const raw = localStorage.getItem('sidebar.groupOpen');
      if (raw) return JSON.parse(raw);
    } catch{}
    // Default: open every group so first-time users see the structure.
    const def={};
    NAV_ITEMS.filter(n=>n.group).forEach(g=>def[g.id]=true);
    return def;
  });
  const toggleGroup=useCallback((id)=>{
    setGroupOpen(prev=>{
      const next={...prev,[id]:!prev[id]};
      try { localStorage.setItem('sidebar.groupOpen', JSON.stringify(next)); } catch{}
      return next;
    });
  },[]);

  // Sidebar leaf clicked: route directly to the matching tab id.
  const navigateTo=useCallback((leafId)=>{
    setTab(leafId);
  },[]);

  // Auto-expand the parent group whenever the active tab is one of its children.
  useEffect(()=>{
    const parent = NAV_ITEMS.find(n=>n.children && n.children.some(c=>c.id===tab));
    if (parent && !groupOpen[parent.id]) {
      setGroupOpen(prev=>({...prev,[parent.id]:true}));
    }
    // The "audit" leaf sits under Observability but routes to Settings — keep
    // its parent group highlighted while the user is on the audit screen.
  },[tab]);

  const goToIntegrations=useCallback((opts)=>{
    setIntegrationsFilter(opts||null);
    setTab('integrations');
  },[]);

  // Topbar title + group-active styling helpers
  const activeLeafId = tab;
  const activeLeaf = findNavLeaf(activeLeafId);
  const groupHasActive = (g) => g.children && g.children.some(c=>c.id===activeLeafId);
  const [profileForm,setProfileForm]=useState({
    fullName:'Venkadesh Sivalingam',
    jobTitle:'Chief Architect',
    email:'venkadesh.sivalingam@acmetrading.ae',
    phone:'+971 50 123 4567',
    language:'English',
    timeZone:'GST (UTC+4)',
  });
  const profileInitials = useMemo(()=>{
    return (profileForm.fullName||'')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0,2)
      .map(s=>{const c=s[0];return c?c.toUpperCase():'';})
      .join('') || 'U';
  },[profileForm.fullName]);

  const showToast=useCallback((msg,type='info')=>{
    setToast({msg,type,id:Date.now()});
  },[]);

  const confirm=useCallback((message)=>{
    return new Promise(resolve=>{
      setConfirmModal({message,onConfirm:()=>{setConfirmModal(null);resolve(true);},onCancel:()=>{setConfirmModal(null);resolve(false);}});
    });
  },[]);

  const handleLogin=useCallback(async(username,password)=>{
    const data=await loginWithCredentials(username,password);
    const user=parseJwt(data.access_token);
    const a={token:data.access_token,refreshToken:data.refresh_token||null,username:user.preferred_username,fullName:user.name||user.preferred_username,email:user.email||'',roles:user.realm_access&&user.realm_access.roles?user.realm_access.roles:[]};
    saveAuthData(data);
    setAuthenticated(a);
    showToast('Signed in as '+user.preferred_username,'success');
  },[setAuthenticated,showToast]);
  const handleLogout=useCallback(()=>{
    logout();
    markGuest();
  },[markGuest]);

  // Login from the sign-in wall (LoginPage), shown when no session exists or
  // After successful login, closes the modal and (best effort) retries the
  // caller by re-issuing the original request. The original caller hangs on
  // an unresolvable Promise while auth is pending, so we re-fire the same
  // path by reloading the active tab's data — pages that listen on `tab`
  // refresh their state automatically via the bootstrap effect.
  const handleProxyLogin=useCallback(async(username,password,mode)=>{
    // 'local': the form's credentials, checked by Axon itself. Anything else: Keycloak direct grant.
    const local=mode==='local'||mode==='breakglass';
    const data=local?await loginBreakglass(username,password):await loginWithCredentials(username,password);
    const user=parseJwt(data.access_token);
    const a={
      token:data.access_token,
      refreshToken:data.refresh_token||null,
      username:user.preferred_username,
      fullName:user.name||user.preferred_username,
      email:user.email||'',
      roles:(user.realm_access&&user.realm_access.roles)||[],
    };
    saveAuthData(data);
    setAuthenticated(a);
    showToast(local
      ?'Signed in as '+user.preferred_username+' (local account) — session ends in '+Math.round((data.expires_in||3600)/60)+' min'
      :'Signed in as '+user.preferred_username,'success');
    // Refresh the active page so any in-flight UI re-fetches data with the
    // new token. Cheaper than tracking every pending caller.
    const ev=new CustomEvent('axon:auth-changed',{detail:{user:a}});
    window.dispatchEvent(ev);
  },[setAuthenticated,showToast]);
  // Expose confirm for external scripts (api-mgmt.js)
  // Expose login-modal trigger so the API() wrapper can surface a modal
  // when a secured call fails with 401 / returns no token.
  const [dashLoaded,setDashLoaded]=useState(false);
  const [metrics,setMetrics]=useState({});

  useEffect(()=>{
    window.__confirm=confirm;
    // One dead session => one sign-in wall, however many calls were in flight.
    // clearAuthData() is idempotent and markGuest() is a state set, so repeat
    // calls from concurrent 401s collapse rather than stacking modals.
    window.__sessionExpired=()=>{ clearAuthData(); markGuest(); };
    return()=>{delete window.__confirm; delete window.__sessionExpired;};
  },[confirm,markGuest]);

  useEffect(()=>{
    /* These four feed the Dashboard's props. One of them failing must not blank the
       other three — see loadAll.
     *
     * dashLoaded gates the Dashboard's tiles. Until the first load returns, the four
     * useState([]) defaults make every tile read 0 / 0 / 0 / 0% with "No executions yet"
     * — which is indistinguishable from an empty platform and reads as fact rather than
     * as "not yet known". Observed 2026-10-10: the overview showed all zeros while the
     * API held 5 connections, 12 integrations and 31,877 executions; the four calls
     * returned 200 moments later. Same trap loadAll itself was written for. */
    /* Wait for the session before firing these.
     *
     * The session probe is async, so on first mount there is no token yet, and
     * API() answers a missing session with a promise that NEVER SETTLES — it
     * hands off to the sign-in wall rather than rejecting. loadAll therefore
     * never resolves, setDashLoaded(true) never runs, and every tile sits on
     * "loading…" forever. Worse, it stays stuck after a successful sign-in:
     * neither `tab` nor `showToast` changes, so this effect never re-runs and
     * nothing ever retries. Observed 2026-10-13 — the overview held "loading…"
     * indefinitely while all five endpoints answered in under 0.6s.
     *
     * auth.status in the deps is what makes it fire again once the probe (or a
     * sign-in) completes. */
    if(auth.status!=='authenticated') return;
    let cancelled=false;
    setDashLoaded(false);
    loadAll([
      ['Connections',API('GET','/api/connections'),[]],
      ['Integrations',API('GET','/api/integrations'),[]],
      ['Executions',API('GET','/api/executions?limit=50'),[]],
      ['Projects',API('GET','/api/projects'),[]],
      ['Metrics',API('GET','/api/metrics'),{}],
    ],showToast).then(([c,i,e,p,mt])=>{
      if(cancelled) return;
      setConnections(c);setIntegrations(i);setExecutions(e);setProjects(p);setMetrics(mt||{});setDashLoaded(true);
    });
    return()=>{cancelled=true;};
  },[tab,showToast,auth.status]);
  useEffect(()=>{
    if(!profileOpen) return;
    const onKey=(ev)=>{ if(ev.key==='Escape') setProfileOpen(false); };
    window.addEventListener('keydown',onKey);
    return ()=>window.removeEventListener('keydown',onKey);
  },[profileOpen]);

  return(
    <div style={{position:'relative',minHeight:'100vh'}}>
      {auth.status==='loading'&&(
        <div style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'100vh',background:'var(--bg,#f8fafc)'}}>
          <div style={{textAlign:'center',color:'var(--text2)'}}><div style={{fontSize:24,marginBottom:8}}>⏳</div>Loading…</div>
        </div>
      )}
      {/* Sign-in wall. The management API is .authenticated() server-side, so
          a guest reaching the app would fire a dozen calls that all 401 and
          each pop the login modal. LoginPage existed but was never rendered —
          guests fell straight through to a UI whose every page then failed. */}
      {auth.status==='guest'&&(
        <LoginPage onLogin={handleProxyLogin}/>
      )}
      {auth.status==='authenticated'&&isCustomerSession()&&(
        <>
          <CustomerPortalApp user={auth.user} onLogout={handleLogout} showToast={showToast}/>
          {toast&&<Toast key={toast.id} msg={toast.msg} type={toast.type} onClose={()=>setToast(null)}/>}
        </>
      )}
      {auth.status==='authenticated'&&!isCustomerSession()&&(
      <>
      <div id="sidebar" className={sidebarCollapsed?'collapsed':''}>
        <button className="sidebar-toggle" onClick={toggleSidebar} title={sidebarCollapsed?'Expand sidebar':'Collapse sidebar'} aria-label="Toggle sidebar">‹</button>
        <div className="sidebar-brand">
          <img className="sidebar-brand-logo" src="logo.png" alt="TaxLab"/>
          <div className="sidebar-brand-caption">Axon Integration Platform</div>
        </div>
        <div className="sidebar-divider"/>
        <nav className="sidebar-nav">
          {NAV_ITEMS.map(n=>{
            if(n.group){
              // When the sidebar is collapsed, force-expand groups so children
              // are still reachable as icons (otherwise the user has to expand
              // the sidebar back out just to switch tabs).
              const open = sidebarCollapsed ? true : !!groupOpen[n.id];
              const isActive = groupHasActive(n);
              return (
                <div key={n.id} className={`nav-group${isActive?' has-active':''}`}>
                  <button type="button" className="nav-group-header" onClick={()=>!sidebarCollapsed && toggleGroup(n.id)} aria-expanded={open} title={sidebarCollapsed?n.label:undefined}>
                    <span className="nav-icon">{renderNavIcon(n.icon)}</span>
                    <span className="nav-label">{n.label}</span>
                    <span className={`nav-group-chev${open?' open':''}`} aria-hidden="true">
                      <ChevronDownIcon size={17} color="rgb(99,115,129)"/>
                    </span>
                  </button>
                  {open && (
                    <div className="nav-group-children">
                      {n.children.map(c=>(
                        <button key={c.id} className={`nav-item nav-item-sub${activeLeafId===c.id?' active':''}`} onClick={()=>navigateTo(c.id)} title={sidebarCollapsed?c.label:undefined}>
                          <span className="nav-icon">{renderNavIcon(c.icon)}</span>
                          <span className="nav-label">{c.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <button key={n.id} className={`nav-item nav-item-top${tab===n.id?' active':''}`} onClick={()=>navigateTo(n.id)} title={sidebarCollapsed?n.label:undefined}>
                <span className="nav-icon">{renderNavIcon(n.icon)}</span>
                <span className="nav-label">{n.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          <div className="sidebar-status">
            <span className="status-pulse"/>
            <div>
              <div className="sidebar-status-title">All Systems Online</div>
              <div className="sidebar-status-subtitle">© 2025 TaxLab.ai</div>
            </div>
          </div>
          <button
            className="sidebar-profile-launcher"
            title="Profile & settings"
            onClick={()=>{
              setThemePopOpen(false);
              setProfileTab('profile');
              setProfileEditing(false);
              setProfileOpen(true);
            }}
          >
            <div className="sidebar-profile-avatar">{profileInitials}</div>
            <div className="sidebar-profile-meta">
              <div className="sidebar-profile-name">{profileForm.fullName}</div>
              <div className="sidebar-profile-role">{profileForm.jobTitle}</div>
            </div>
            <svg className="sidebar-profile-gear" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
              <g fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M13.765 2.152C13.398 2 12.932 2 12 2s-1.398 0-1.765.152a2 2 0 0 0-1.083 1.083c-.092.223-.129.484-.143.863a1.62 1.62 0 0 1-.79 1.353a1.62 1.62 0 0 1-1.567.008c-.336-.178-.579-.276-.82-.308a2 2 0 0 0-1.478.396C4.04 5.79 3.806 6.193 3.34 7s-.7 1.21-.751 1.605a2 2 0 0 0 .396 1.479c.148.192.355.353.676.555c.473.297.777.803.777 1.361s-.304 1.064-.777 1.36c-.321.203-.529.364-.676.556a2 2 0 0 0-.396 1.479c.052.394.285.798.75 1.605c.467.807.7 1.21 1.015 1.453a2 2 0 0 0 1.479.396c.24-.032.483-.13.819-.308a1.62 1.62 0 0 1 1.567.008c.483.28.77.795.79 1.353c.014.38.05.64.143.863a2 2 0 0 0 1.083 1.083C10.602 22 11.068 22 12 22s1.398 0 1.765-.152a2 2 0 0 0 1.083-1.083c.092-.223.129-.483.143-.863c.02-.558.307-1.074.79-1.353a1.62 1.62 0 0 1 1.567-.008c.336.178.579.276.819.308a2 2 0 0 0 1.479-.396c.315-.242.548-.646 1.014-1.453s.7-1.21.751-1.605a2 2 0 0 0-.396-1.479c-.148-.192-.355-.353-.676-.555A1.62 1.62 0 0 1 19.562 12c0-.558.304-1.064.777-1.36c.321-.203.529-.364.676-.556a2 2 0 0 0 .396-1.479c-.052-.394-.285-.798-.75-1.605c-.467-.807-.7-1.21-1.015-1.453a2 2 0 0 0-1.479-.396c-.24.032-.483.13-.82.308a1.62 1.62 0 0 1-1.566-.008a1.62 1.62 0 0 1-.79-1.353c-.014-.38-.05-.64-.143-.863a2 2 0 0 0-1.083-1.083Z"></path>
              </g>
            </svg>
          </button>
        </div>
      </div>

      {/* Main */}
      <div id="main" className={sidebarCollapsed?'with-collapsed-sidebar':''}>
        <div id="topbar">
          <div style={{flex:1}}>
            <div className="topbar-title">{(activeLeaf && activeLeaf.label) || (NAV_ITEMS.find(n=>n.id===tab)||{}).label}</div>
          </div>
          <div className="flex items-center gap-3" style={{position:'relative'}}>
            <div className="topbar-search">
              <span className="search-field-icon"><SearchIcon size={20} color="rgb(99,115,129)"/></span>
              <input className="topbar-search-input" aria-label="Search documents and suppliers" placeholder="Search document or supplier…" value="" readOnly/>
              <span style={{marginLeft:8,padding:'1px 6px',background:'var(--surface3)',borderRadius:4,fontSize:11,color:'var(--text3)',border:'1px solid var(--border2)'}}>⌘K</span>
            </div>
            <span className="badge badge-success"><span className="dot dot-success"/>Live</span>
            <button className="theme-picker-btn" onClick={()=>setThemePopOpen(p=>!p)} title="Theme & accent color" aria-label="Theme picker">
              <ThemeModeIcon theme={themeApi.theme} size={18}/>
            </button>
            <div className="user-avatar">{(auth.user ? (auth.user.fullName||auth.user.username||'?') : '?').split(' ').filter(Boolean).slice(0,2).map(w=>w[0]).join('')||'?'}</div>
            <button className="btn btn-ghost btn-sm" onClick={handleLogout} title="Sign out" style={{fontSize:12,padding:'4px 10px',borderRadius:6}}>Sign out</button>
            {themePopOpen && <ThemePickerPopover {...themeApi} onClose={()=>setThemePopOpen(false)}/>}
          </div>
        </div>
        <div id="content">
          <PageErrorBoundary key={tab}>
          {tab==='dashboard'&&<Dashboard connections={connections} integrations={integrations} executions={executions} metrics={metrics} loaded={dashLoaded}/>}
          {tab==='impact'&&<ImpactAnalysisPage showToast={showToast} connections={connections} confirm={confirm}/>}
          {tab==='recipes'&&<RecipesPage showToast={showToast} goToIntegrations={goToIntegrations}/>}
          {tab==='connections'&&<ConnectionsPage showToast={showToast} confirm={confirm}/>}
          {tab==='integrations'&&<IntegrationsPage showToast={showToast} projects={projects} navFilter={integrationsFilter} clearNavFilter={()=>setIntegrationsFilter(null)} confirm={confirm}/>}
          {tab==='monitoring'&&<MonitoringPage showToast={showToast} confirm={confirm}/>}
          {tab==='logs'&&<LogViewer showToast={showToast}/>}
          {tab==='projects'&&<ProjectsPage showToast={showToast} goToIntegrations={goToIntegrations} confirm={confirm}/>}
          {tab==='lookups'&&<LookupTablesPage showToast={showToast} confirm={confirm}/>}
          {tab==='schedule'&&<FutureRunsPage showToast={showToast} goToIntegrations={goToIntegrations}/>}
          {tab==='audit'&&<AuditSettings showToast={showToast}/>}
          {tab==='api-products'&&<ApiProductsPage showToast={showToast} confirm={confirm}/>}
          {tab==='access-products'&&<AccessProductsPage showToast={showToast} confirm={confirm}/>}
          {tab==='access-orgs'&&<AccessOrgsPage showToast={showToast} confirm={confirm}/>}
          {tab==='api-portal'&&<DeveloperPortalPage showToast={showToast} confirm={confirm}/>}
          {tab==='api-gateway'&&<ApisixGatewayPage showToast={showToast}/>}
          {tab==='api-environments'&&<GatewayEnvironmentsPage showToast={showToast} confirm={confirm}/>}
          {tab==='ai-providers'&&<AiProvidersPage showToast={showToast} confirm={confirm}/>}
          {tab==='ai-usage'&&<AiUsagePage showToast={showToast}/>}
          {tab==='prometheus-metrics'&&<ToolFrame src="/prometheus/targets" title="Prometheus Targets"/>}
          {/* One leaf, three Grafana dashboards — see METRIC_VIEWS above MetricsPage
              for why the navigation is merged but the dashboards are not. */}
          {tab==='metrics'&&<MetricsPage/>}
          {tab==='camelbee'&&<CamelBeePage showToast={showToast}/>}
          {/* Standalone Kaoto, not the ?embed=1 variant the route editor mounts.
              Standalone keeps its own "Routes in Axon" picker, fetches the
              integration list itself and saves straight back to Axon, which is
              the whole point of having it as a nav leaf rather than only inside
              the builder modal. It lists YAML routes only — Kaoto edits
              camelRoute, and a Java DSL integration has none — and says so in
              the picker when the list comes back empty. */}
          {designerMounted&&<iframe src="/kaoto/index.html" style={{width:'100%',height:'calc(100vh - 120px)',border:'none',borderRadius:8,display:tab==='designer'?'block':'none'}} title="Kaoto Designer"/>}
          {/* The Java DSL counterpart. Not an iframe and not latched: it is a
              plain page whose only heavy dependency is Monaco, which the shell
              already loads — none of what made Kaoto worth keeping mounted
              (6.3 MB bundle, ~20 MB catalog parse) applies here. */}
          {tab==='java-designer'&&<JavaDesignerPage showToast={showToast}/>}
          {/* Trailing slash matters: /zipkin 301s to /zipkin/, and making the
              iframe follow a redirect on every open is a needless round trip. */}
          {tab==='gateway-tracing'&&<ToolFrame src="/zipkin/" title="Distributed Tracing"/>}
          {tab==='api-webhooks'&&<WebhookDeliveriesPage showToast={showToast} confirm={confirm}/>}
          {tab==='settings-general'&&<SettingsGeneral showToast={showToast}/>}
          {tab==='settings-appearance'&&<SettingsAppearance/>}
          {tab==='settings-performance'&&<SettingsPerformance/>}
          {tab==='settings-logging'&&<LoggingSettings showToast={showToast}/>}
          {tab==='settings-security'&&<SettingsSecurity showToast={showToast}/>}
          {tab==='settings-keycloak'&&<SettingsKeycloak showToast={showToast}/>}
          {tab==='settings-notifications'&&<SettingsNotifications showToast={showToast}/>}
          {tab==='settings-users'&&<UserManagementPage showToast={showToast} confirm={confirm}/>}
          {tab==='settings-team'&&<UserManagementPage showToast={showToast}/>}
          {tab==='settings-about'&&<SettingsAbout showToast={showToast}/>}
          </PageErrorBoundary>
        </div>
      </div>

      {toast&&<Toast key={toast.id} msg={toast.msg} type={toast.type} onClose={()=>setToast(null)}/>}
      {confirmModal && <ConfirmModal open={!!confirmModal} message={(confirmModal||{}).message} onConfirm={(confirmModal||{}).onConfirm} onCancel={(confirmModal||{}).onCancel}/>}
      <div className={`profile-drawer-overlay${profileOpen?' open':''}`} onClick={()=>setProfileOpen(false)}>
        <aside className="profile-drawer" onClick={e=>e.stopPropagation()}>
          <div className="profile-drawer-head">
            <div className="profile-drawer-avatar">{profileInitials}</div>
            <div className="profile-drawer-ident">
              <div className="profile-drawer-name">{profileForm.fullName}</div>
              <div className="profile-drawer-role">{profileForm.jobTitle}</div>
            </div>
            <button className="profile-drawer-close" onClick={()=>setProfileOpen(false)} aria-label="Close profile"><CloseIcon size={20}/></button>
          </div>
          <div className="profile-drawer-tabs">
            <button
              className={`profile-drawer-tab${profileTab==='profile'?' active':''}`}
              onClick={()=>{
                setProfileTab('profile');
                setProfileEditing(false);
              }}
            >
              <svg className="profile-tab-icon" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="currentColor" d="M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12S6.477 2 12 2s10 4.477 10 10" opacity=".5"></path>
                <path fill="currentColor" d="M16.807 19.011A8.46 8.46 0 0 1 12 20.5a8.46 8.46 0 0 1-4.807-1.489c-.604-.415-.862-1.205-.51-1.848C7.41 15.83 8.91 15 12 15s4.59.83 5.318 2.163c.35.643.093 1.433-.511 1.848M12 12a3 3 0 1 0 0-6a3 3 0 0 0 0 6"></path>
              </svg>
              Profile
            </button>
            <button
              className={`profile-drawer-tab${profileTab==='security'?' active':''}`}
              onClick={()=>{
                setProfileTab('security');
                setProfileEditing(false);
              }}
            >
              <svg className="profile-tab-icon" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="currentColor" d="M2 16c0-2.828 0-4.243.879-5.121C3.757 10 5.172 10 8 10h8c2.828 0 4.243 0 5.121.879C22 11.757 22 13.172 22 16s0 4.243-.879 5.121C20.243 22 18.828 22 16 22H8c-2.828 0-4.243 0-5.121-.879C2 20.243 2 18.828 2 16" opacity=".5"></path>
                <path fill="currentColor" d="M12 18a2 2 0 1 0 0-4a2 2 0 0 0 0 4M6.75 8a5.25 5.25 0 0 1 10.5 0v2.004c.567.005 1.064.018 1.5.05V8a6.75 6.75 0 0 0-13.5 0v2.055a24 24 0 0 1 1.5-.051z"></path>
              </svg>
              Security
            </button>
          </div>
          <div className="profile-drawer-body">
            {profileTab==='profile'&&(
              <>
                <div className="profile-section-head">
                  <span>Personal details</span>
                  <button
                    className="profile-edit-btn"
                    onClick={()=>{
                      if(profileEditing){
                        setProfileEditing(false);
                        showToast('Profile details updated locally','success');
                      }else{
                        setProfileEditing(true);
                      }
                    }}
                  >
                    {profileEditing?(
                      <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                        <path fill="currentColor" d="M9.55 17.95 4.5 12.9l1.4-1.4 3.65 3.65 8.55-8.55 1.4 1.4z"></path>
                      </svg>
                    ):(
                      <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                        <path fill="currentColor" d="m11.4 18.161l7.396-7.396a10.3 10.3 0 0 1-3.326-2.234a10.3 10.3 0 0 1-2.235-3.327L5.839 12.6c-.577.577-.866.866-1.114 1.184a6.6 6.6 0 0 0-.749 1.211c-.173.364-.302.752-.56 1.526l-1.362 4.083a1.06 1.06 0 0 0 1.342 1.342l4.083-1.362c.775-.258 1.162-.387 1.526-.56q.647-.308 1.211-.749c.318-.248.607-.537 1.184-1.114m9.448-9.448a3.932 3.932 0 0 0-5.561-5.561l-.887.887l.038.111a8.75 8.75 0 0 0 2.092 3.32a8.75 8.75 0 0 0 3.431 2.13z"></path>
                      </svg>
                    )}
                    {profileEditing?'Save':'Edit'}
                  </button>
                </div>
                {profileEditing?(
                  <div className="profile-form-grid">
                    <label className="profile-field"><span>Full name</span><input className="form-input" value={profileForm.fullName} onChange={e=>setProfileForm(v=>({...v,fullName:e.target.value}))}/></label>
                    <label className="profile-field"><span>Job title</span><input className="form-input" value={profileForm.jobTitle} onChange={e=>setProfileForm(v=>({...v,jobTitle:e.target.value}))}/></label>
                    <label className="profile-field"><span>Email</span><input className="form-input" value={profileForm.email} onChange={e=>setProfileForm(v=>({...v,email:e.target.value}))}/></label>
                    <label className="profile-field"><span>Phone</span><input className="form-input" value={profileForm.phone} onChange={e=>setProfileForm(v=>({...v,phone:e.target.value}))}/></label>
                    <label className="profile-field"><span>Language</span><input className="form-input" value={profileForm.language} onChange={e=>setProfileForm(v=>({...v,language:e.target.value}))}/></label>
                    <label className="profile-field"><span>Time zone</span><input className="form-input" value={profileForm.timeZone} onChange={e=>setProfileForm(v=>({...v,timeZone:e.target.value}))}/></label>
                  </div>
                ):(
                  <div className="profile-grid">
                    <div className="profile-item"><div className="profile-item-label">Full name</div><div className="profile-item-value">{profileForm.fullName}</div></div>
                    <div className="profile-item"><div className="profile-item-label">Job title</div><div className="profile-item-value">{profileForm.jobTitle}</div></div>
                    <div className="profile-item"><div className="profile-item-label">Email</div><div className="profile-item-value">{profileForm.email}</div></div>
                    <div className="profile-item"><div className="profile-item-label">Phone</div><div className="profile-item-value">{profileForm.phone}</div></div>
                    <div className="profile-item"><div className="profile-item-label">Language</div><div className="profile-item-value">{profileForm.language}</div></div>
                    <div className="profile-item"><div className="profile-item-label">Time zone</div><div className="profile-item-value">{profileForm.timeZone}</div></div>
                  </div>
                )}
              </>
            )}
            {profileTab==='security'&&(
              <div className="security-stack">
                <div className="security-section">
                  <div className="security-section-title">Multi-factor authentication</div>
                  <div className="security-box">
                    <div className="security-row">
                      <div className="security-row-main">
                        <svg className="security-row-icon" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                          <path fill="currentColor" d="M7 2h10a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2m5 18a1.25 1.25 0 1 0 0-2.5A1.25 1.25 0 0 0 12 20"></path>
                        </svg>
                        <div className="security-row-text">
                          <div className="security-row-title">Authenticator app</div>
                          <div className="security-row-sub">{mfaEnabled?'Enabled · TOTP (6-digit code)':'Disabled · setup required'}</div>
                        </div>
                      </div>
                      <button
                        className={`security-switch${mfaEnabled?' on':''}`}
                        onClick={()=>setMfaEnabled(v=>!v)}
                        aria-label={mfaEnabled?'Disable MFA':'Enable MFA'}
                      >
                        <span className="security-switch-handle"/>
                      </button>
                    </div>
                    <div className="security-actions">
                      <button className="security-mini-btn" onClick={()=>showToast('Recovery codes flow not wired yet','info')}>
                        <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                          <path fill="currentColor" d="M3 12a9 9 0 1 1 2.64 6.36L3 21l2.64-2.64A8.96 8.96 0 0 1 3 12m5.5-.75a.75.75 0 1 0 0 1.5h7a.75.75 0 0 0 0-1.5zm0-3a.75.75 0 1 0 0 1.5h4a.75.75 0 0 0 0-1.5zm0 6a.75.75 0 1 0 0 1.5h5a.75.75 0 0 0 0-1.5z"></path>
                        </svg>
                        Recovery codes
                      </button>
                      <button className="security-mini-btn" onClick={()=>showToast('Re-configure MFA flow not wired yet','info')}>
                        <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                          <path fill="currentColor" d="M12 4a8 8 0 0 1 7.75 6h-2.3A6 6 0 1 0 18 12h2a8 8 0 1 1-8-8m.5 1l3 3l-3 3V9H8V7h4.5z"></path>
                        </svg>
                        Re-configure
                      </button>
                    </div>
                  </div>
                </div>

                <div className="security-section">
                  <div className="security-section-title">Password</div>
                  <div className="security-box">
                    <div className="security-row">
                      <div className="security-row-main">
                        <svg className="security-row-icon" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                          <path fill="currentColor" d="M17 8h-1V6a4 4 0 1 0-8 0v2H7a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2m-7-2a2 2 0 1 1 4 0v2h-4z"></path>
                        </svg>
                        <div className="security-row-text">
                          <div className="security-row-title">Password</div>
                          <div className="security-row-sub">Last changed 24 days ago</div>
                        </div>
                      </div>
                      <button className="security-mini-btn" onClick={()=>showToast('Wire this to your password reset endpoint','info')}>Change</button>
                    </div>
                  </div>
                </div>

                <div className="security-section">
                  <div className="security-section-title">Active sessions</div>
                  <div className="security-box">
                    <div className="security-row">
                      <div className="security-row-main">
                        <svg className="security-row-icon" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                          <path fill="currentColor" d="M4 4h16a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-6v2h3v2H7v-2h3v-2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2"></path>
                        </svg>
                        <div className="security-row-text">
                          <div className="security-row-title">This device · Chrome · Dubai</div>
                        </div>
                      </div>
                      <span className="security-badge">Current</span>
                    </div>
                    <div className="security-row">
                      <div className="security-row-main">
                        <svg className="security-row-icon" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                          <path fill="currentColor" d="M7 2h10a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2m5 18a1.25 1.25 0 1 0 0-2.5A1.25 1.25 0 0 0 12 20"></path>
                        </svg>
                        <div className="security-row-text">
                          <div className="security-row-title">iPhone · Safari · Dubai</div>
                        </div>
                      </div>
                      <button className="security-mini-btn security-mini-btn-danger" onClick={()=>showToast('Sign out session endpoint not connected yet','info')}>Sign out</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="profile-drawer-footer">
            <button className="profile-signout-btn" onClick={handleLogout}>Sign out</button>
          </div>
        </aside>
      </div>
      </>
    )}
    </div>
    );
  }

/* ── API Products — Apigee-style product management ───────────────────────
 *
 * A product bundles related gateway operations (e.g. all six Jurist routes) into
 * one publishable, metered, subscribable unit. Access is a three-way binding:
 * the product says what may be called, a Keycloak group says who, and an APISIX
 * consumer group is where that is enforced (ACL + quota).
 *
 * Tabs: Overview · Operations · Access & Quota · Subscriptions · Docs · Spec
 *
 * NOTE: this must stay the ONLY definition of ApiProductsPage. Every static
 * script shares one global scope, so a second `function ApiProductsPage` in
 * another file silently shadows whichever loads first.
 */
const PRODUCT_COLOR_SWATCHES=['#0891b2','#6366f1','#8b5cf6','#ec4899','#f59e0b','#10b981','#ef4444','#3b82f6','#14b8a6','#f97316'];
const LIFECYCLE_BADGE={PUBLISHED:'success',DEPRECATED:'warning',DRAFT:'gray',RETIRED:'error'};
const SUB_STATUS_BADGE={APPROVED:'success',PENDING:'warning',SUSPENDED:'warning',REJECTED:'gray',REVOKED:'error'};
const QUOTA_UNITS=['SECOND','MINUTE','HOUR','DAY','MONTH'];

function quotaLabel(q){
  if(!q||!q.enabled)return 'Unmetered';
  return q.limit.toLocaleString()+' / '+(q.interval>1?q.interval+' ':'')+q.unit.toLowerCase()+(q.interval>1?'s':'');
}

/* The one-time credential reveal. Deliberately modal and deliberately blunt:
   the secret is not stored in plaintext anywhere, so if it is missed here the
   only remedy is rotation. */
function OneTimeSecretModal({payload,onClose}){
  const [copied,setCopied]=useState('');
  if(!payload)return null;
  const copy=(what,value)=>{
    navigator.clipboard.writeText(value).then(()=>{setCopied(what);setTimeout(()=>setCopied(''),1500);});
  };
  return(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:560}}>
        <div className="modal-header">
          <div className="modal-title">Axon credentials issued</div>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body"><div className="modal-body-scroll">
          <div className="badge badge-warning" style={{marginBottom:14,display:'inline-block'}}>
            Shown once — copy it now
          </div>
          <p style={{fontSize:13,color:'var(--text2)',marginTop:0}}>
            {payload.warning||'This is the only time this secret is shown.'}
          </p>
          <div className="form-group" style={{marginBottom:12}}>
            <label className="form-label">Client ID</label>
            <div style={{display:'flex',gap:8}}>
              <input className="form-input" readOnly value={payload.clientId||''} style={{fontFamily:'monospace',fontSize:12}}/>
              <button className="btn btn-sm btn-secondary" onClick={()=>copy('id',payload.clientId)}>
                {copied==='id'?'Copied':'Copy'}
              </button>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Client secret</label>
            <div style={{display:'flex',gap:8}}>
              <input className="form-input" readOnly value={payload.clientSecret||''} style={{fontFamily:'monospace',fontSize:12}}/>
              <button className="btn btn-sm btn-secondary" onClick={()=>copy('secret',payload.clientSecret)}>
                {copied==='secret'?'Copied':'Copy'}
              </button>
            </div>
          </div>
          <p style={{fontSize:12,color:'var(--text3)',marginBottom:0}}>
            These are <strong>Axon</strong> credentials. The customer uses them at the
            product's token endpoint; the gateway attaches the upstream provider
            credentials itself, so the customer never holds them.
          </p>
        </div></div>
        <div className="modal-footer">
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

function ApiProductsPage({showToast,confirm}){
  const [products,setProducts]=useState([]);
  const [loading,setLoading]=useState(true);
  const [selectedId,setSelectedId]=useState(null);
  const [detail,setDetail]=useState(null);
  const [showCreate,setShowCreate]=useState(false);
  const [secretPayload,setSecretPayload]=useState(null);

  const reload=useCallback(async()=>{
    try{
      setLoading(true);
      const list=await API('GET','/api/admin/api-products');
      setProducts(Array.isArray(list)?list:[]);
    }catch(e){showToast&&showToast('Failed to load API products: '+e.message,'error');}
    finally{setLoading(false);}
  },[showToast]);

  const loadDetail=useCallback(async(id)=>{
    if(!id){setDetail(null);return;}
    try{setDetail(await API('GET','/api/admin/api-products/'+id));}
    catch(e){showToast&&showToast(e.message,'error');}
  },[showToast]);

  useEffect(()=>{reload();},[reload]);
  useEffect(()=>{loadDetail(selectedId);},[selectedId,loadDetail]);

  const afterChange=useCallback(async()=>{await reload();await loadDetail(selectedId);},[reload,loadDetail,selectedId]);

  const create=async(form)=>{
    try{
      const created=await API('POST','/api/admin/api-products',Object.assign({lifecycle:'DRAFT'},form));
      showToast&&showToast('Product created as a draft','success');
      setShowCreate(false);
      await reload();
      setSelectedId(created.id);
    }catch(e){showToast&&showToast(e.message,'error');}
  };

  const remove=async(p)=>{
    const ok=confirm?await confirm('Delete "'+p.name+'"? Its Keycloak group and all subscriptions go with it.'):window.confirm('Delete '+p.name+'?');
    if(!ok)return;
    try{
      await API('DELETE','/api/admin/api-products/'+p.id);
      showToast&&showToast('Product deleted','success');
      if(selectedId===p.id)setSelectedId(null);
      await reload();
    }catch(e){showToast&&showToast(e.message,'error');}
  };

  return(
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">APIs</h1>
          <div className="page-subtitle">Versioned API definitions and their gateway routes. Organisations are granted access through API products.</div>
        </div>
        <button className="btn btn-primary" style={TAXLAB_CTA_STYLE} onClick={()=>setShowCreate(true)}>
          New Product
        </button>
      </div>

      {loading&&<div className="card"><div className="card-body">Loading…</div></div>}

      {!loading&&products.length===0&&(
        <div className="card"><div className="card-body empty-state" style={{textAlign:'center',padding:48}}>
          <h3 style={{margin:'0 0 8px'}}>No API products yet</h3>
          <p style={{color:'var(--text3)',margin:0}}>
            A product groups gateway operations, sets their quota, and controls who can subscribe.
          </p>
          <button className="btn btn-primary" style={{marginTop:16}} onClick={()=>setShowCreate(true)}>
            Create your first product
          </button>
        </div></div>
      )}

      {!loading&&products.length>0&&(
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(340px,1fr))',gap:16,marginBottom:20}}>
          {products.map(p=>(
            <div key={p.id} className="card"
                 onClick={()=>setSelectedId(selectedId===p.id?null:p.id)}
                 style={{cursor:'pointer',borderColor:selectedId===p.id?(p.color||'var(--brand-start)'):undefined}}>
              <div className="card-body">
                <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:8}}>
                  <div style={{minWidth:0}}>
                    <div style={{fontWeight:600,fontSize:15,display:'flex',alignItems:'center',gap:8}}>
                      <span style={{width:10,height:10,borderRadius:3,background:p.color||'var(--brand-start)',flex:'none'}}></span>
                      {p.name}
                      {p.type==='AI'&&<span className="badge badge-info" style={{fontSize:10,flex:'none'}}>AI</span>}
                    </div>
                    <div style={{fontSize:12,color:'var(--text3)',marginTop:4}}>{p.description}</div>
                  </div>
                  <span className={'badge badge-'+(LIFECYCLE_BADGE[p.lifecycle]||'gray')} style={{fontSize:10,flex:'none'}}>
                    {p.lifecycle||'DRAFT'}
                  </span>
                </div>
                <div style={{display:'flex',gap:8,marginTop:10,flexWrap:'wrap',alignItems:'center'}}>
                  <ProductVersionChip product={p}/>
                </div>
                <div style={{display:'flex',gap:14,marginTop:10,fontSize:11,color:'var(--text3)',flexWrap:'wrap'}}>
                  <span>v{p.version||'1.0.0'}</span>
                  {p.type==='AI'
                    ?<span>{(p.aiModels||[]).length} model{(p.aiModels||[]).length===1?'':'s'}</span>
                    :<span>{p.memberCount} operation{p.memberCount===1?'':'s'}</span>}
                  <span>{p.subscriptionCount} subscriber{p.subscriptionCount===1?'':'s'}</span>
                  <span>{p.type==='AI'&&p.aiTokenQuota?aiTokenQuotaLabel(p.aiTokenQuota):quotaLabel(p.quota)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {detail&&(
        <ProductDetailPanel
          product={detail}
          onChanged={afterChange}
          onSelectProduct={setSelectedId}
          onClose={()=>setSelectedId(null)}
          onDelete={()=>remove(detail)}
          onSecret={setSecretPayload}
          showToast={showToast}
          confirm={confirm}/>
      )}

      {showCreate&&<CreateProductModal onCancel={()=>setShowCreate(false)} onCreate={create}/>}
      <OneTimeSecretModal payload={secretPayload} onClose={()=>setSecretPayload(null)}/>
    </div>
  );
}

function CreateProductModal({onCancel,onCreate}){
  const [name,setName]=useState('');
  const [description,setDescription]=useState('');
  const [version,setVersion]=useState('1.0.0');
  const [color,setColor]=useState(PRODUCT_COLOR_SWATCHES[0]);
  const [type,setType]=useState('REST');
  const [saving,setSaving]=useState(false);
  const submit=async()=>{setSaving(true);await onCreate({name,description,version,color,type});setSaving(false);};
  return(
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:560}}>
        <div className="modal-header">
          <div className="modal-title">New API product</div>
          <button className="modal-close-btn" onClick={onCancel}>×</button>
        </div>
        <div className="modal-body"><div className="modal-body-scroll">
          <div className="form-group">
            <label className="form-label">Name <span className="req">*</span></label>
            <input className="form-input" value={name} onChange={e=>setName(e.target.value)} placeholder="Jurist API"/>
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-input" rows={3} value={description} onChange={e=>setDescription(e.target.value)}
                      placeholder="What this product lets a customer do"/>
          </div>
          <div className="form-group">
            <label className="form-label">Kind <span className="req">*</span></label>
            <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
              {[['REST','REST API','Proxies HTTP operations to an upstream service.'],
                ['AI','AI models','Routes to LLM providers with token budgets and guardrails.']].map(([id,label,hint])=>(
                <button key={id} type="button" onClick={()=>setType(id)}
                  style={{flex:'1 1 200px',textAlign:'left',padding:'10px 12px',borderRadius:8,cursor:'pointer',
                          background:'var(--surface)',
                          border:type===id?'2px solid var(--brand-start)':'1px solid var(--border)'}}>
                  <div style={{fontWeight:600,fontSize:13}}>{label}</div>
                  <div style={{fontSize:11,color:'var(--text3)',marginTop:2}}>{hint}</div>
                </button>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Version</label>
            <input className="form-input" value={version} onChange={e=>setVersion(e.target.value)}/>
          </div>
          <div className="form-group">
            <label className="form-label">Colour</label>
            <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
              {PRODUCT_COLOR_SWATCHES.map(c=>(
                <button key={c} onClick={()=>setColor(c)} title={c}
                  style={{width:26,height:26,borderRadius:6,background:c,cursor:'pointer',
                          border:color===c?'2px solid var(--text)':'2px solid transparent'}}/>
              ))}
            </div>
          </div>
          <p style={{fontSize:12,color:'var(--text3)'}}>
            New products start as a <strong>draft</strong> — invisible in the developer portal
            until you publish them.
          </p>
        </div></div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving||!name}>
            {saving?'Creating…':'Create product'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProductDetailPanel({product,onChanged,onClose,onDelete,onSecret,onSelectProduct,showToast,confirm}){
  const [tab,setTab]=useState('overview');
  const isAi=product.type==='AI';
  // An AI product has no HTTP upstream and no per-operation methods to edit, so the
  // Operations and Spec tabs would be empty for it; its gateway configuration and its
  // token consumption take their place.
  const TABS=isAi?[
    ['overview','Overview'],['ai','AI Gateway'],['access','Access & Quota'],
    ['versions','Versions'],['subscriptions','Subscriptions'],['usage','Usage'],['docs','Docs']
  ]:[
    ['overview','Overview'],['operations','Operations'],['access','Access & Quota'],
    ['versions','Versions'],['subscriptions','Subscriptions'],['docs','Docs'],['spec','Spec']
  ];
  return(
    <div className="card">
      <div className="card-header" style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:12}}>
        <div className="card-title" style={{display:'flex',alignItems:'center',gap:10}}>
          <span style={{width:10,height:10,borderRadius:3,background:product.color||'var(--brand-start)'}}></span>
          {product.name}
          {isAi&&<span className="badge badge-info" style={{fontSize:10}}>AI</span>}
          <span className={'badge badge-'+(LIFECYCLE_BADGE[product.lifecycle]||'gray')} style={{fontSize:10}}>
            {product.lifecycle||'DRAFT'}
          </span>
          <span className="badge badge-gray" style={{fontSize:10}}>v{product.version||'1.0.0'}</span>
          {product.apiVersion&&(
            <span className="badge badge-gray" style={{fontSize:10,fontFamily:'monospace'}}>{product.apiVersion}</span>
          )}
        </div>
        <div style={{display:'flex',gap:8}}>
          <button className="btn btn-sm btn-danger" onClick={onDelete}>Delete</button>
          <button className="btn btn-sm btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
      <div className="card-body">
        <div className="um-tabs" style={{marginBottom:16}}>
          {TABS.map(([id,label])=>(
            <button key={id} className={'um-tab'+(tab===id?' active':'')} onClick={()=>setTab(id)}>{label}</button>
          ))}
        </div>
        {tab==='overview'&&<ProductOverviewTab product={product} onChanged={onChanged} showToast={showToast}/>}
        {tab==='ai'&&<ProductAiTab product={product} onChanged={onChanged} showToast={showToast}/>}
        {tab==='usage'&&<ProductAiUsageTab product={product} showToast={showToast}/>}
        {tab==='operations'&&<ProductOperationsTab product={product} onChanged={onChanged} showToast={showToast}/>}
        {tab==='access'&&<ProductAccessTab product={product} onChanged={onChanged} showToast={showToast}/>}
        {tab==='versions'&&<ProductVersionsTab product={product} onChanged={onChanged}
                                               onSelectProduct={onSelectProduct}
                                               showToast={showToast} confirm={confirm}/>}
        {tab==='subscriptions'&&<ProductSubscriptionsTab product={product} onChanged={onChanged}
                                                        onSecret={onSecret} showToast={showToast} confirm={confirm}/>}
        {tab==='docs'&&<ProductDocsTab product={product} onChanged={onChanged} showToast={showToast}/>}
        {tab==='spec'&&<ProductSpecTab product={product} showToast={showToast}/>}
      </div>
    </div>
  );
}

function ProductOverviewTab({product,onChanged,showToast}){
  const [form,setForm]=useState({
    name:product.name||'',description:product.description||'',version:product.version||'1.0.0',
    color:product.color||PRODUCT_COLOR_SWATCHES[0],visibility:product.visibility||'PUBLIC',
    contactName:product.contactName||'',contactEmail:product.contactEmail||'',termsUrl:product.termsUrl||''
  });
  const [saving,setSaving]=useState(false);
  const set=(k,v)=>setForm(f=>Object.assign({},f,{[k]:v}));

  const save=async()=>{
    setSaving(true);
    try{await API('PUT','/api/admin/api-products/'+product.id,form);showToast&&showToast('Saved','success');await onChanged();}
    catch(e){showToast&&showToast(e.message,'error');}
    finally{setSaving(false);}
  };

  const transition=async(to)=>{
    try{
      await API('PUT','/api/admin/api-products/'+product.id+'/lifecycle',{lifecycle:to});
      showToast&&showToast('Product is now '+to.toLowerCase(),'success');
      await onChanged();
    }catch(e){showToast&&showToast(e.message,'error');}
  };

  const current=product.lifecycle||'DRAFT';
  const NEXT={DRAFT:['PUBLISHED','RETIRED'],PUBLISHED:['DEPRECATED','RETIRED'],
              DEPRECATED:['PUBLISHED','RETIRED'],RETIRED:[]};

  return(
    <div>
      <div className="form-row" style={{display:'flex',gap:16,flexWrap:'wrap'}}>
        <div className="form-group" style={{flex:'1 1 260px'}}>
          <label className="form-label">Name</label>
          <input className="form-input" value={form.name} onChange={e=>set('name',e.target.value)}/>
        </div>
        <div className="form-group" style={{flex:'0 0 140px'}}>
          <label className="form-label">Version</label>
          <input className="form-input" value={form.version} onChange={e=>set('version',e.target.value)}/>
        </div>
        <div className="form-group" style={{flex:'0 0 160px'}}>
          <label className="form-label">Visibility</label>
          <select className="form-input" value={form.visibility} onChange={e=>set('visibility',e.target.value)}>
            <option value="PUBLIC">Public — listed in the portal</option>
            <option value="PRIVATE">Private — subscribers only</option>
          </select>
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Description</label>
        <textarea className="form-input" rows={2} value={form.description} onChange={e=>set('description',e.target.value)}/>
      </div>
      <div className="form-row" style={{display:'flex',gap:16,flexWrap:'wrap'}}>
        <div className="form-group" style={{flex:'1 1 200px'}}>
          <label className="form-label">Support contact</label>
          <input className="form-input" value={form.contactName} onChange={e=>set('contactName',e.target.value)}/>
        </div>
        <div className="form-group" style={{flex:'1 1 200px'}}>
          <label className="form-label">Support email</label>
          <input className="form-input" value={form.contactEmail} onChange={e=>set('contactEmail',e.target.value)}/>
        </div>
        <div className="form-group" style={{flex:'1 1 200px'}}>
          <label className="form-label">Terms URL</label>
          <input className="form-input" value={form.termsUrl} onChange={e=>set('termsUrl',e.target.value)}/>
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Colour</label>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          {PRODUCT_COLOR_SWATCHES.map(c=>(
            <button key={c} onClick={()=>set('color',c)} title={c}
              style={{width:24,height:24,borderRadius:6,background:c,cursor:'pointer',
                      border:form.color===c?'2px solid var(--text)':'2px solid transparent'}}/>
          ))}
        </div>
      </div>

      <div style={{display:'flex',gap:8,alignItems:'center',marginTop:18,flexWrap:'wrap'}}>
        <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving…':'Save changes'}</button>
        <span style={{flex:1}}></span>
        <span style={{fontSize:12,color:'var(--text3)'}}>Lifecycle:</span>
        {NEXT[current].map(to=>(
          <button key={to} className="btn btn-sm btn-secondary" onClick={()=>transition(to)}>
            {to==='PUBLISHED'&&current==='DEPRECATED'?'Un-deprecate':'Move to '+to.toLowerCase()}
          </button>
        ))}
        {NEXT[current].length===0&&<span style={{fontSize:12,color:'var(--text3)'}}>retired — terminal</span>}
      </div>
    </div>
  );
}

function ProductOperationsTab({product,onChanged,showToast}){
  const members=product.members||[];
  const [busy,setBusy]=useState('');

  const toggleMethod=async(m,method)=>{
    const has=(m.methods||[]).some(x=>x.toUpperCase()===method);
    const next=has?(m.methods||[]).filter(x=>x.toUpperCase()!==method):(m.methods||[]).concat(method);
    setBusy(m.integrationId);
    try{
      await API('PUT','/api/admin/api-products/'+product.id+'/members/'+m.integrationId,{methods:next});
      await onChanged();
    }catch(e){showToast&&showToast(e.message,'error');}
    finally{setBusy('');}
  };

  const toggleDeprecated=async(m)=>{
    setBusy(m.integrationId);
    try{
      await API('PUT','/api/admin/api-products/'+product.id+'/members/'+m.integrationId,{deprecated:!m.deprecated});
      await onChanged();
    }catch(e){showToast&&showToast(e.message,'error');}
    finally{setBusy('');}
  };

  if(members.length===0){
    return <div className="empty-state" style={{padding:32,textAlign:'center',color:'var(--text3)'}}>
      This product has no operations yet.
    </div>;
  }

  return(
    <div>
      <p style={{fontSize:12,color:'var(--text3)',marginTop:0}}>
        Unticking a verb removes it from the published spec and refuses it at the gateway.
        The token endpoint is never gated — it is what issues the credential.
      </p>
      <div style={{overflowX:'auto'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
          <thead>
            <tr style={{textAlign:'left',color:'var(--text3)',fontSize:11,textTransform:'uppercase'}}>
              <th style={{padding:'8px 6px'}}>Operation</th>
              <th style={{padding:'8px 6px'}}>Path</th>
              <th style={{padding:'8px 6px'}}>Methods</th>
              <th style={{padding:'8px 6px'}}>Route</th>
              <th style={{padding:'8px 6px'}}>State</th>
            </tr>
          </thead>
          <tbody>
            {members.map(m=>(
              <tr key={m.integrationId} style={{borderTop:'1px solid var(--border)',opacity:busy===m.integrationId?0.5:1}}>
                <td style={{padding:'10px 6px'}}>
                  <div style={{fontWeight:500}}>{m.integrationName}</div>
                  <div style={{fontSize:11,color:'var(--text3)'}}>{m.summary||m.operationId||''}</div>
                </td>
                <td style={{padding:'10px 6px',fontFamily:'monospace',fontSize:11}}>{m.publicPath}</td>
                <td style={{padding:'10px 6px'}}>
                  {m.authEndpoint
                    ? <span className="badge badge-gray" style={{fontSize:10}}>POST</span>
                    : ['GET','POST','PUT','DELETE'].map(v=>(
                        <label key={v} style={{marginRight:10,fontSize:11,cursor:'pointer',whiteSpace:'nowrap'}}>
                          <input type="checkbox" style={{marginRight:4}}
                                 checked={(m.methods||[]).some(x=>x.toUpperCase()===v)}
                                 onChange={()=>toggleMethod(m,v)}/>{v}
                        </label>
                      ))}
                </td>
                <td style={{padding:'10px 6px',fontFamily:'monospace',fontSize:11,color:'var(--text3)'}}>{m.apisixRouteId||'—'}</td>
                <td style={{padding:'10px 6px'}}>
                  {m.authEndpoint
                    ? <span className="badge badge-info" style={{fontSize:10}}>token</span>
                    : <label style={{fontSize:11,cursor:'pointer',whiteSpace:'nowrap'}}>
                        <input type="checkbox" style={{marginRight:4}} checked={!!m.deprecated}
                               onChange={()=>toggleDeprecated(m)}/>deprecated
                      </label>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProductAccessTab({product,onChanged,showToast}){
  const q=product.quota||{limit:10000,interval:1,unit:'DAY',enabled:true};
  const rl=product.rateLimit||{rate:100,burst:200,enabled:true};
  const [quota,setQuota]=useState(q);
  const [rate,setRate]=useState(rl);
  const [approvalMode,setApprovalMode]=useState(product.approvalMode||'MANUAL');
  const [scopes,setScopes]=useState((product.scopes||[]).join(', '));
  const [sync,setSync]=useState(null);
  const [busy,setBusy]=useState('');

  const saveQuota=async()=>{
    setBusy('quota');
    try{
      const res=await API('PUT','/api/admin/api-products/'+product.id+'/quota',quota);
      setSync(res.sync||null);
      showToast&&showToast('Quota updated and pushed to the gateway','success');
      await onChanged();
    }catch(e){showToast&&showToast(e.message,'error');}
    finally{setBusy('');}
  };

  const saveRate=async()=>{
    setBusy('rate');
    try{
      await API('PUT','/api/admin/api-products/'+product.id+'/rate-limit',rate);
      showToast&&showToast('Rate limit saved — run Sync to apply it to the routes','success');
      await onChanged();
    }catch(e){showToast&&showToast(e.message,'error');}
    finally{setBusy('');}
  };

  const savePolicy=async()=>{
    setBusy('policy');
    try{
      await API('PUT','/api/admin/api-products/'+product.id,
        {approvalMode:approvalMode,scopes:scopes.split(',').map(s=>s.trim()).filter(Boolean)});
      showToast&&showToast('Subscription policy saved','success');
      await onChanged();
    }catch(e){showToast&&showToast(e.message,'error');}
    finally{setBusy('');}
  };

  const runSync=async()=>{
    setBusy('sync');
    try{
      const res=await API('POST','/api/admin/api-products/'+product.id+'/sync-apisix');
      setSync(res);
      showToast&&showToast(res.ok?'Gateway is in sync':'Sync finished with errors',res.ok?'success':'error');
    }catch(e){showToast&&showToast(e.message,'error');}
    finally{setBusy('');}
  };

  return(
    <div>
      <div className="card" style={{marginBottom:16}}>
        <div className="card-body">
          <div style={{fontWeight:600,marginBottom:4}}>Identity bindings</div>
          <p style={{fontSize:12,color:'var(--text3)',marginTop:0}}>
            Read-only. A subscriber's service account joins the Keycloak group; the APISIX
            consumer group is where the quota and the ACL are actually enforced.
          </p>
          <div style={{display:'flex',gap:24,flexWrap:'wrap',fontSize:12}}>
            <div>
              <div style={{color:'var(--text3)'}}>Keycloak group</div>
              <div style={{fontFamily:'monospace'}}>{product.keycloakGroupName||'—'}</div>
            </div>
            <div>
              <div style={{color:'var(--text3)'}}>Gateway consumer group</div>
              <div style={{fontFamily:'monospace'}}>{product.apisixConsumerGroupId||'—'}</div>
            </div>
            <div>
              <div style={{color:'var(--text3)'}}>Gateway base path</div>
              <div style={{fontFamily:'monospace'}}>{product.gatewayBasePath||'—'}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card" style={{marginBottom:16}}>
        <div className="card-body">
          <div style={{fontWeight:600,marginBottom:4}}>Quota</div>
          <p style={{fontSize:12,color:'var(--text3)',marginTop:0}}>
            The billing allowance, counted per subscriber. Lives on the consumer group, so
            changing it here applies to every subscriber at once.
          </p>
          <div style={{display:'flex',gap:12,alignItems:'flex-end',flexWrap:'wrap'}}>
            <label style={{fontSize:12,display:'flex',alignItems:'center',gap:6}}>
              <input type="checkbox" checked={quota.enabled}
                     onChange={e=>setQuota(Object.assign({},quota,{enabled:e.target.checked}))}/>
              Metered
            </label>
            <div className="form-group" style={{margin:0,width:130}}>
              <label className="form-label">Calls</label>
              <input className="form-input" type="number" value={quota.limit} disabled={!quota.enabled}
                     onChange={e=>setQuota(Object.assign({},quota,{limit:parseInt(e.target.value||'0',10)}))}/>
            </div>
            <div className="form-group" style={{margin:0,width:90}}>
              <label className="form-label">Per</label>
              <input className="form-input" type="number" value={quota.interval} disabled={!quota.enabled}
                     onChange={e=>setQuota(Object.assign({},quota,{interval:parseInt(e.target.value||'1',10)}))}/>
            </div>
            <div className="form-group" style={{margin:0,width:130}}>
              <label className="form-label">Unit</label>
              <select className="form-input" value={quota.unit} disabled={!quota.enabled}
                      onChange={e=>setQuota(Object.assign({},quota,{unit:e.target.value}))}>
                {QUOTA_UNITS.map(u=><option key={u} value={u}>{u.toLowerCase()}</option>)}
              </select>
            </div>
            <button className="btn btn-primary btn-sm" onClick={saveQuota} disabled={busy==='quota'}>
              {busy==='quota'?'Applying…':'Apply quota'}
            </button>
          </div>
        </div>
      </div>

      <div className="card" style={{marginBottom:16}}>
        <div className="card-body">
          <div style={{fontWeight:600,marginBottom:4}}>Rate limit</div>
          <p style={{fontSize:12,color:'var(--text3)',marginTop:0}}>
            Short-window burst protection, applied per route. Separate from the quota above.
          </p>
          <div style={{display:'flex',gap:12,alignItems:'flex-end',flexWrap:'wrap'}}>
            <label style={{fontSize:12,display:'flex',alignItems:'center',gap:6}}>
              <input type="checkbox" checked={rate.enabled}
                     onChange={e=>setRate(Object.assign({},rate,{enabled:e.target.checked}))}/>
              Enabled
            </label>
            <div className="form-group" style={{margin:0,width:130}}>
              <label className="form-label">Requests/sec</label>
              <input className="form-input" type="number" value={rate.rate} disabled={!rate.enabled}
                     onChange={e=>setRate(Object.assign({},rate,{rate:parseInt(e.target.value||'0',10)}))}/>
            </div>
            <div className="form-group" style={{margin:0,width:130}}>
              <label className="form-label">Burst</label>
              <input className="form-input" type="number" value={rate.burst} disabled={!rate.enabled}
                     onChange={e=>setRate(Object.assign({},rate,{burst:parseInt(e.target.value||'0',10)}))}/>
            </div>
            <button className="btn btn-secondary btn-sm" onClick={saveRate} disabled={busy==='rate'}>
              {busy==='rate'?'Saving…':'Save rate limit'}
            </button>
          </div>
        </div>
      </div>

      <div className="card" style={{marginBottom:16}}>
        <div className="card-body">
          <div style={{fontWeight:600,marginBottom:4}}>Subscription policy</div>
          <div style={{display:'flex',gap:20,margin:'10px 0',flexWrap:'wrap'}}>
            <label style={{fontSize:13,cursor:'pointer'}}>
              <input type="radio" name="approvalMode" style={{marginRight:6}}
                     checked={approvalMode==='MANUAL'} onChange={()=>setApprovalMode('MANUAL')}/>
              Manual — requests wait for an operator
            </label>
            <label style={{fontSize:13,cursor:'pointer'}}>
              <input type="radio" name="approvalMode" style={{marginRight:6}}
                     checked={approvalMode==='AUTO'} onChange={()=>setApprovalMode('AUTO')}/>
              Automatic — credentials issued on request
            </label>
          </div>
          <div className="form-group">
            <label className="form-label">Scopes (comma separated)</label>
            <input className="form-input" value={scopes} onChange={e=>setScopes(e.target.value)}
                   placeholder="documents:read, documents:write"/>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={savePolicy} disabled={busy==='policy'}>
            {busy==='policy'?'Saving…':'Save policy'}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div style={{fontWeight:600,marginBottom:4}}>Gateway sync</div>
          <p style={{fontSize:12,color:'var(--text3)',marginTop:0}}>
            Pushes this product's consumer group, routes and subscriber consumers to APISIX.
            Routes are only written from here — never automatically on restart, so a redeploy
            cannot overwrite gateway configuration you have changed by hand.
          </p>
          <button className="btn btn-primary btn-sm" onClick={runSync} disabled={busy==='sync'}>
            {busy==='sync'?'Syncing…':'Sync to gateway'}
          </button>
          {sync&&(
            <div style={{marginTop:12,fontSize:12}}>
              {(sync.applied||[]).map((a,i)=><div key={'a'+i} style={{color:'var(--success,#10b981)'}}>✓ {a}</div>)}
              {(sync.skipped||[]).map((s,i)=><div key={'s'+i} style={{color:'var(--text3)'}}>– {s}</div>)}
              {(sync.errors||[]).map((e,i)=><div key={'e'+i} style={{color:'var(--error,#ef4444)'}}>✕ {e}</div>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ProductSubscriptionsTab({product,onChanged,onSecret,showToast,confirm}){
  const [subs,setSubs]=useState([]);
  const [loading,setLoading]=useState(true);
  const [busy,setBusy]=useState('');

  const reload=useCallback(async()=>{
    setLoading(true);
    try{
      const [s]=await loadAll([
        ['Subscriptions',API('GET','/api/admin/api-products/'+product.id+'/subscriptions'),[]],
      ],showToast);
      setSubs(Array.isArray(s)?s:[]);
    }catch(e){showToast&&showToast(e.message,'error');}
    finally{setLoading(false);}
  },[product.id,showToast]);
  useEffect(()=>{reload();},[reload]);

  const act=async(sub,action,body)=>{
    if(action==='revoke'){
      const ok=confirm?await confirm('Revoke '+sub.consumerName+"'s access? Their token stops working immediately."):window.confirm('Revoke?');
      if(!ok)return;
    }
    setBusy(sub.id);
    try{
      const res=await API('POST','/api/admin/api-products/'+product.id+'/subscriptions/'+sub.id+'/'+action,body||{});
      if(res&&res.clientSecret)onSecret(res);
      showToast&&showToast('Subscription '+action+'d','success');
      await reload();await onChanged();
    }catch(e){showToast&&showToast(e.message,'error');}
    finally{setBusy('');}
  };

  return(
    <div>
      <div className="alert" style={{marginBottom:16,fontSize:13}}>
        Access is no longer granted per API. Add this API to an <b>API product</b>, then grant the product to a
        customer under <b>API Management → Organisations</b> → the organisation → its app → <b>New credential</b>.
        The subscriptions below predate that and keep working.
      </div>

      {loading&&<div style={{color:'var(--text3)',fontSize:13}}>Loading…</div>}

      {!loading&&subs.length===0&&(
        <div className="empty-state" style={{padding:32,textAlign:'center',color:'var(--text3)'}}>
          No subscribers yet.
        </div>
      )}

      {!loading&&subs.length>0&&(
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead>
              <tr style={{textAlign:'left',color:'var(--text3)',fontSize:11,textTransform:'uppercase'}}>
                <th style={{padding:'8px 6px'}}>Customer</th>
                <th style={{padding:'8px 6px'}}>App</th>
                <th style={{padding:'8px 6px'}}>Status</th>
                <th style={{padding:'8px 6px'}}>Axon client ID</th>
                <th style={{padding:'8px 6px'}}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {subs.map(s=>(
                <tr key={s.id} style={{borderTop:'1px solid var(--border)',opacity:busy===s.id?0.5:1}}>
                  <td style={{padding:'10px 6px'}}>{s.consumerName}</td>
                  <td style={{padding:'10px 6px'}}>{s.appName}</td>
                  <td style={{padding:'10px 6px'}}>
                    <span className={'badge badge-'+(SUB_STATUS_BADGE[s.status]||'gray')} style={{fontSize:10}}>{s.status}</span>
                  </td>
                  <td style={{padding:'10px 6px',fontFamily:'monospace',fontSize:11}}>{s.axonClientId||'—'}</td>
                  <td style={{padding:'10px 6px',whiteSpace:'nowrap'}}>
                    {s.status==='PENDING'&&<>
                      <button className="btn btn-sm btn-primary" style={{marginRight:6}} onClick={()=>act(s,'approve')}>Approve</button>
                      <button className="btn btn-sm btn-ghost" onClick={()=>act(s,'reject')}>Reject</button>
                    </>}
                    {s.status==='APPROVED'&&<>
                      <button className="btn btn-sm btn-secondary" style={{marginRight:6}} onClick={()=>act(s,'rotate-secret')}>Rotate</button>
                      <button className="btn btn-sm btn-ghost" style={{marginRight:6}} onClick={()=>act(s,'suspend')}>Suspend</button>
                      <button className="btn btn-sm btn-danger" onClick={()=>act(s,'revoke')}>Revoke</button>
                    </>}
                    {s.status==='SUSPENDED'&&
                      <button className="btn btn-sm btn-primary" onClick={()=>act(s,'resume')}>Resume</button>}
                    {(s.status==='REVOKED'||s.status==='REJECTED')&&
                      <span style={{fontSize:11,color:'var(--text3)'}}>{s.notes||'—'}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ProductDocsTab({product,onChanged,showToast}){
  const [md,setMd]=useState(product.documentation||'');
  const [saving,setSaving]=useState(false);
  const save=async()=>{
    setSaving(true);
    try{
      await API('PUT','/api/admin/api-products/'+product.id+'/documentation',{markdown:md});
      showToast&&showToast('Documentation saved','success');
      await onChanged();
    }catch(e){showToast&&showToast(e.message,'error');}
    finally{setSaving(false);}
  };
  const preview=()=>{
    try{return{__html:window.marked?window.marked.parse(md||''):''};}
    catch(e){return{__html:''};}
  };
  return(
    <div>
      <p style={{fontSize:12,color:'var(--text3)',marginTop:0}}>
        Markdown. Shown on the product's developer-portal page and used as the description
        in its generated OpenAPI document.
      </p>
      <div style={{display:'flex',gap:16,flexWrap:'wrap'}}>
        <div style={{flex:'1 1 340px'}}>
          <textarea className="form-input" rows={22} value={md} onChange={e=>setMd(e.target.value)}
                    style={{fontFamily:'monospace',fontSize:12,width:'100%'}}/>
        </div>
        <div style={{flex:'1 1 340px',minWidth:0}}>
          <div className="card" style={{height:'100%'}}>
            <div className="card-body markdown-body" style={{maxHeight:460,overflowY:'auto'}}
                 dangerouslySetInnerHTML={preview()}/>
          </div>
        </div>
      </div>
      <button className="btn btn-primary" style={{marginTop:12}} onClick={save} disabled={saving}>
        {saving?'Saving…':'Save documentation'}
      </button>
    </div>
  );
}

function ProductSpecTab({product,showToast}){
  const [spec,setSpec]=useState('');
  const [format,setFormat]=useState('json');
  const [loading,setLoading]=useState(true);

  useEffect(()=>{
    let cancelled=false;
    (async()=>{
      setLoading(true);
      try{
        const r=await fetch('/api/admin/api-products/'+product.id+'/openapi?format='+format);
        const text=await r.text();
        if(!cancelled)setSpec(format==='json'?JSON.stringify(JSON.parse(text),null,2):text);
      }catch(e){
        if(!cancelled)setSpec('Could not generate the spec: '+e.message);
      }finally{ if(!cancelled)setLoading(false); }
    })();
    return()=>{cancelled=true;};
  },[product.id,format]);

  const download=()=>{
    const blob=new Blob([spec],{type:format==='json'?'application/json':'application/yaml'});
    const a=document.createElement('a');
    a.href=URL.createObjectURL(blob);
    a.download=(product.slug||product.id)+'-openapi.'+format;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return(
    <div>
      <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:12,flexWrap:'wrap'}}>
        <select className="form-input" style={{width:120}} value={format} onChange={e=>setFormat(e.target.value)}>
          <option value="json">JSON</option>
          <option value="yaml">YAML</option>
        </select>
        <button className="btn btn-sm btn-secondary" onClick={download} disabled={loading}>Download</button>
        <span style={{fontSize:12,color:'var(--text3)'}}>
          Scoped to this product's operations, with the Axon token endpoint as its only auth flow.
        </span>
      </div>
      <pre style={{background:'var(--surface2)',padding:14,borderRadius:8,maxHeight:480,
                   overflow:'auto',fontSize:11,margin:0}}>{loading?'Generating…':spec}</pre>
    </div>
  );
}

function EditProductForm({product,onSave,onCancel}){
  const[name,setName]=useState(product.name);
  const[desc,setDesc]=useState(product.description);
  const[icon,setIcon]=useState(product.icon||'⚖');
  const[color,setColor]=useState(product.color||'#0891b2');
  const handleSave=()=>{onSave({name,description:desc,icon,color})};
  return(<div style={{marginTop:8}}>
    <div style={{display:'flex',gap:8,marginBottom:8}}>
      <input className="form-control" style={{flex:1,fontSize:13,padding:'4px 8px'}} value={name} onChange={e=>setName(e.target.value)}/>
      <input className="form-control" style={{width:40,fontSize:13,padding:'4px 8px'}} value={icon} onChange={e=>setIcon(e.target.value)}/>
      <input type="color" value={color} onChange={e=>setColor(e.target.value)} style={{width:36,height:30,border:'1px solid var(--border)',borderRadius:4,cursor:'pointer',padding:2}}/>
    </div>
    <textarea className="form-control" rows={2} value={desc} onChange={e=>setDesc(e.target.value)} style={{fontSize:13,marginBottom:8}}/>
    <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
      <button className="btn btn-sm btn-secondary" onClick={onCancel}>Cancel</button>
      <button className="btn btn-sm btn-primary" onClick={handleSave}>Save</button>
    </div>
  </div>);
}

// ─── APISIX Gateway Management (full CRUD) ──────────────────────────────────

function getEmptyForm(tab){
  if(tab==='routes')return {name:'',uri:'',uriPattern:'',priority:0,methods:[],hosts:[],remoteAddrs:[],enableWebsocket:false,plugins:{cors:{allow_origins:'*',allow_methods:'GET,POST,OPTIONS',allow_headers:'Authorization,Content-Type',max_age:3600,allow_credential:false},'limit-req':{rate:100,burst:200,key_type:'var'}},upstream:{type:'roundrobin',nodes:{}},serviceId:'',labels:{}};
  if(tab==='services')return {name:'',hosts:[],plugins:{},timeout:{connect:6,send:60,read:60}};
  if(tab==='upstreams')return {name:'',type:'roundrobin',nodes:{},pass_host:'pass',timeout:{connect:6,send:60,read:60}};
  if(tab==='consumers')return {username:'',plugins:{}};
  if(tab==='ssl')return {sni:'',sns:[],key:''};
  return {};
}

// Tabs that render a whole sub-page instead of the generic APISIX CRUD table.
// Function declarations hoist, so referencing these before their definitions is safe.
// API Consumers was a tab here until 2026-10-17: the customer record is the organisation
// now, and its TaxLab credential is set on it (API Management -> Organisations).
const SUB_TABS={'apisix-plugins':ApisixPluginsPage};

// APISIX 3.x renamed the SSL collection to `ssls`. The tab id stays `ssl`
// because the columns, form and validation all key off it — only the URL
// segment differs, so map it at the call sites rather than renaming the tab.
const APISIX_COLLECTION={ssl:'ssls'};
const coll=(t)=>APISIX_COLLECTION[t]||t;
// tab.slice(0,-1) yields "ss" for ssl and is wrong for any non-plural id.
/* Display labels only. The tab ids stay as the gateway's own nouns ('routes'),
   because they key the fetch path, COLSPAN and every tab=== branch — only what
   the operator reads changes. An APISIX route is presented as a PROXY, which is
   also what its id has always said: proxy-taxlab-documents-v1. */
const ADD_LABEL={routes:'route',services:'service',upstreams:'upstream',consumers:'consumer',ssl:'SSL cert'};
const EMPTY_LABEL={routes:'proxies',services:'services',upstreams:'upstreams',consumers:'consumers',ssl:'certificates'};

function ApisixGatewayPage({showToast}){
  const[tab,setTab]=useState('routes');
  const[items,setItems]=useState([]);
  const[loading,setLoading]=useState(true);
  const[error,setError]=useState(null);
  const[modal,setModal]=useState(null); // null | {mode:'create'|'edit', data, tab}
  const[form,setForm]=useState(getEmptyForm('routes'));
  const[formErrors,setFormErrors]=useState({});
  const[saving,setSaving]=useState(false);
  const[search,setSearch]=useState('');

  // A Response body is a one-shot stream. The previous version called r.text()
  // twice and then r.json(), so every non-2xx surfaced as "body stream already
  // read" instead of the real APISIX error. Read once, parse from the string.
  const api=useCallback(async(path,opts={})=>{
    try{
      // Token merged last-but-one so an explicit opts.headers can still add to
      // it, but cannot accidentally drop Authorization and 401 the whole page.
      const r=await fetch('/api/apisix-admin'+path,{...opts,headers:{'Content-Type':'application/json',...(opts.headers||{}),...(await authHeaders())}});
      const raw=await r.text();
      let data=null;
      if(raw){ try{ data=JSON.parse(raw); }catch{ /* non-JSON body */ } }
      if(!r.ok){
        const msg=(data&&(data.error_msg||data.error||data.message))||raw||(r.status+' '+r.statusText);
        throw new Error(msg);
      }
      return data;
    }catch(e){ setError(e.message||String(e)); throw e; }
  },[]);

  // APISIX Admin API v3 wraps every entry as {key, value:{...}, createdIndex}.
  // Reading the envelope directly left id/name/uri/status undefined, which is
  // why routes rendered blank and every route showed as "Disabled".
  // APISIX returns "list": {} — an object, not an array — for an empty
  // collection, so guard on Array.isArray rather than truthiness.
  const unwrap=(list)=>(Array.isArray(list)?list:[]).map(it=>{
    if(!it) return it;
    if(!it.value) return it;                       // already flat (older APISIX)
    const v={...it.value};
    if(v.id==null&&it.key) v.id=String(it.key).split('/').pop();
    v._key=it.key;
    return v;
  });

  const load=useCallback(async()=>{
    if(SUB_TABS[tab]){setItems([]);setLoading(false);setError(null);return;}
    setLoading(true);setError(null);
    try{const r=await api('/'+coll(tab)+'?page=1&page_size=200');setItems(unwrap(r&&r.list))}catch(e){}
    setLoading(false);
  },[tab,api]);

  useEffect(()=>{load()},[load]);

  const openCreate=()=>{setForm(getEmptyForm(tab));setFormErrors({});setModal({mode:'create',data:null,tab})};
  const openEdit=(item)=>{const f=getEmptyForm(tab);Object.keys(f).forEach(k=>{if(item[k]!=null)f[k]=item[k]});setForm(f);setFormErrors({});setModal({mode:'edit',data:item,tab})};
  const closeModal=()=>{setModal(null)};

  const validate=()=>{const e={};if(tab==='routes'&&!form.uri)e.uri='URI is required';if(tab==='services'&&!form.name)e.name='Name is required';if(tab==='upstreams'&&!form.name)e.name='Name is required';if(tab==='consumers'&&!form.username)e.username='Username is required';if(tab==='ssl'&&!form.sni)e.sni='SNI is required';setFormErrors(e);return Object.keys(e).length===0};

  const handleSave=async()=>{if(!validate())return;setSaving(true);try{const payload={...form};if(tab==='upstreams'&&form.nodes&&typeof form.nodes==='string'){try{payload.nodes=JSON.parse(form.nodes)}catch{payload.nodes={[form.nodes]:1}}}if(tab==='routes'){if(!payload.name)payload.name=payload.uri;if(!payload.methods)payload.methods=['GET'];if(!payload.priority==null)payload.priority=10;if(!payload.plugins){payload.plugins={cors:{allow_origins:'*',allow_methods:'GET,POST,OPTIONS',allow_headers:'Authorization,Content-Type',max_age:3600},'limit-req':{rate:100,burst:200}}}}if(modal?.mode==='edit'&&modal.data?.id){await api('/'+coll(tab)+'/'+modal.data.id,{method:'PUT',body:JSON.stringify(payload)})}else{await api('/'+coll(tab),{method:'POST',body:JSON.stringify(payload)})}showToast(modal?.mode==='edit'?'Updated':'Created','success');closeModal();load()}catch(e){showToast(e.message||'Failed','error')}setSaving(false)};

  const handleDelete=async(item)=>{if(!confirm('Delete '+item.name||item.id+'?'))return;try{await api('/'+coll(tab)+'/'+item.id,{method:'DELETE'});showToast('Deleted','success');load()}catch(e){showToast(e.message||'Delete failed','error')}};
  const handleToggle=async(item)=>{const newStatus=item.status===1?0:1;try{await api('/'+coll('routes')+'/'+item.id,{method:'PATCH',body:JSON.stringify({status:newStatus})});showToast(newStatus===1?'Enabled':'Disabled','success');load()}catch(e){showToast(e.message||'Toggle failed','error')}};

  const TABS=[{id:'routes',label:'Routes',icon:'endpoints'},{id:'services',label:'Services',icon:'cloud'},{id:'upstreams',label:'Upstreams',icon:'database'},{id:'consumers',label:'Consumers',icon:'users'},{id:'ssl',label:'SSL',icon:'security'},{id:'apisix-plugins',label:'Plugin Catalog',icon:'extension'}];

  const fmt=(v)=>{if(v==null||v===undefined)return '-';if(typeof v==='object')return JSON.stringify(v);return String(v)};

  /* Column count per tab, including the always-present ID, Name and Actions.
     The empty-state row spans these, so it must track the headers above. */
  const COLSPAN={routes:7,services:5,upstreams:6,consumers:5,ssl:5};

  /* HTTP verbs carry meaning, so they get colour rather than a JSON dump.
     Reuses the existing .badge variants so they retone with every theme. */
  const METHOD_BADGE={GET:'badge-info',POST:'badge-success',PUT:'badge-warning',PATCH:'badge-warning',DELETE:'badge-error'};
  const renderMethods=(v)=>{
    const list=Array.isArray(v)?v:(v==null?[]:[v]);
    if(!list.length)return <span className="text-muted">ANY</span>;
    return <span className="gw-methods">
      {list.map(m=>(<span key={m} className={'badge '+(METHOD_BADGE[String(m).toUpperCase()]||'badge-gray')}>{m}</span>))}
    </span>;
  };

  /* 16px line icons, matching the sidebar's visual language. */
  const ICON={
    pause:<svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M9 4.5a1 1 0 0 0-1 1v13a1 1 0 0 0 2 0v-13a1 1 0 0 0-1-1m6 0a1 1 0 0 0-1 1v13a1 1 0 0 0 2 0v-13a1 1 0 0 0-1-1"/></svg>,
    play:<svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M8.5 5.2a1 1 0 0 1 1.52-.85l9 6.8a1 1 0 0 1 0 1.7l-9 6.8A1 1 0 0 1 8.5 18.8z"/></svg>,
    edit:<svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M3.5 17.4 14.9 6a1 1 0 0 1 1.4 0l2.3 2.3a1 1 0 0 1 0 1.4L7.2 21.1a1 1 0 0 1-.5.3l-3.4.8a.8.8 0 0 1-1-1l.8-3.4a1 1 0 0 1 .4-.4m14.2-14a1 1 0 0 1 1.4 0l1.5 1.5a1 1 0 0 1 0 1.4l-1.2 1.2-2.9-2.9z"/></svg>,
    trash:<svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M9.3 2.8a1 1 0 0 0-1 1V5H4.8a.9.9 0 0 0 0 1.8h14.4a.9.9 0 0 0 0-1.8h-3.5V3.8a1 1 0 0 0-1-1zM5.9 8.5h12.2l-.7 10.4a2.6 2.6 0 0 1-2.6 2.4H9.2a2.6 2.6 0 0 1-2.6-2.4zm3.6 2.7a.8.8 0 0 0-.8.8v5.6a.8.8 0 0 0 1.6 0V12a.8.8 0 0 0-.8-.8m5 0a.8.8 0 0 0-.8.8v5.6a.8.8 0 0 0 1.6 0V12a.8.8 0 0 0-.8-.8"/></svg>
  };

  return (<div className="gw-page">
    {/* The page had no heading at all — the only "API Gateway" was in the shell's
        topbar, so the document outline started at the tab buttons. */}
    <header className="gw-head">
      <div className="gw-head__text">
        {/* The eyebrow names the nav group this leaf sits in. "Axon" would be
            redundant — the whole portal is Axon. */}
        <span className="gw-eyebrow"><span className="gw-eyebrow__dot" aria-hidden="true"></span>API Management</span>
        <h1 className="gw-title">API Gateway</h1>
        <p className="gw-sub">Routes, services, upstreams, consumers and certificates — read live from the gateway. Routes owned by an API product are rewritten by its next sync; change those on the product.</p>
      </div>
      {!SUB_TABS[tab]&&<button className="btn btn-primary gw-cta" onClick={openCreate}>+ Add {ADD_LABEL[tab]||tab.slice(0,-1)}</button>}
    </header>

    {/* One segmented control, not seven loose buttons. */}
    <div className="gw-tabs" role="tablist" aria-label="Gateway objects">
      {TABS.map(t=>(
        <button key={t.id} role="tab" aria-selected={tab===t.id} onClick={()=>{setTab(t.id);setSearch('')}}
                className={'gw-tab'+(tab===t.id?' is-active':'')}>
          <span className="gw-tab-icon" aria-hidden="true">{renderNavIcon(t.icon)}</span>{t.label}
        </button>
      ))}
    </div>

    {error&&<div className="alert alert-error" style={{marginBottom:12}}>{error}</div>}

    {SUB_TABS[tab]&&React.createElement(SUB_TABS[tab],{showToast})}

    {!SUB_TABS[tab]&&tab==='routes'&&(<div className="gw-search">
      <label className="sr-only" htmlFor="gw-route-search">Search routes</label>
      <input id="gw-route-search" className="form-input" placeholder="Search routes…" value={search} onChange={e=>setSearch(e.target.value)}/>
      {search&&<span className="gw-search__count">{items.length} match{items.length!==1?'es':''}</span>}
    </div>)}

    {!SUB_TABS[tab]&&(loading?<div style={{textAlign:'center',padding:40,color:'var(--text3)'}}>Loading...</div>:(<>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th className="gw-col-id">ID</th>
              <th className="gw-col-name">Name</th>
              {tab==='routes'&&<><th className="gw-col-uri">URI</th><th className="gw-col-methods">Methods</th><th>Upstream</th><th>Status</th></>}
              {tab==='services'&&<><th>Hosts</th><th>Timeout</th></>}
              {tab==='upstreams'&&<><th>Type</th><th>Nodes</th><th>Pass Host</th></>}
              {tab==='consumers'&&<><th>Username</th><th>Plugins</th></>}
              {tab==='ssl'&&<><th>SNI</th><th>Status</th></>}
              <th style={{textAlign:'right',width:130}}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length===0&&<tr><td colSpan={COLSPAN[tab]||5} style={{textAlign:'center',padding:32,color:'var(--text3)'}}>No {EMPTY_LABEL[tab]||tab} configured</td></tr>}
            {(search?items.filter(it=>(it.name||it.id||'').toLowerCase().includes(search.toLowerCase())||(tab==='routes'&&(it.uri||'').includes(search))):items).map(item=>(
              <tr key={item.id} style={{cursor:'pointer'}} onClick={()=>openEdit(item)}>
                <td className="gw-mono gw-col-id">{item.id||item.key}</td>
                <td className="font-semibold gw-col-name"><span className="gw-clamp">{item.name||item.username||item.sni||item.id}</span></td>
                {tab==='routes'&&<><td className="gw-mono gw-col-uri" title={item.uri}>{item.uri}</td><td className="gw-col-methods">{renderMethods(item.methods)}</td><td className="gw-mono">{fmt(item.upstream_id||(item.upstream&&item.upstream.nodes?Object.keys(item.upstream.nodes).join(', '):''))}</td><td><span className={'badge '+(item.status===1?'badge-success':'badge-gray')}>{item.status===1?'Enabled':'Disabled'}</span></td></>}
                {tab==='services'&&<><td className="gw-mono">{fmt(item.hosts)}</td><td>{item.timeout?fmt(item.timeout):'-'}</td></>}
                {tab==='upstreams'&&<><td>{item.type}</td><td className="gw-mono">{fmt(item.nodes)}</td><td>{item.pass_host}</td></>}
                {tab==='consumers'&&<><td>{item.username}</td><td>{item.plugins?Object.keys(item.plugins).join(', '):'-'}</td></>}
                {tab==='ssl'&&<><td>{item.sni}</td><td><span className={'badge '+(item.status?'badge-success':'badge-gray')}>{item.status?'Enabled':'Disabled'}</span></td></>}
                <td style={{textAlign:'right',whiteSpace:'nowrap'}}>
                  {tab==='routes'&&<button className="btn btn-sm btn-ghost gw-act" onClick={e=>{e.stopPropagation();handleToggle(item)}} title={item.status===1?'Disable':'Enable'} aria-label={(item.status===1?'Disable':'Enable')+' '+(item.name||item.id)} style={{color:item.status===1?'var(--warning)':'var(--success)'}}>{item.status===1?ICON.pause:ICON.play}</button>}
                  <button className="btn btn-sm btn-ghost gw-act" onClick={e=>{e.stopPropagation();openEdit(item)}} title="Edit" aria-label={'Edit '+(item.name||item.id)}>{ICON.edit}</button>
                  <button className="btn btn-sm btn-ghost gw-act gw-act-danger" onClick={e=>{e.stopPropagation();handleDelete(item)}} title="Delete" aria-label={'Delete '+(item.name||item.id)}>{ICON.trash}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>))}

    {modal&&(<div className="modal-overlay" onClick={closeModal}>
      <div className="modal" style={{maxWidth:640}} onClick={e=>e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{modal.mode==='create'?'New':'Edit'} {ADD_LABEL[modal.tab]||modal.tab.slice(0,-1)}</div>
          <button className="modal-close-btn" onClick={closeModal} aria-label="Close">×</button>
        </div>
        <div className="modal-body"><div className="modal-body-scroll gw-form">
          {modal.tab==='routes'&&modal.mode==='create'&&(
            <div className="text-sm text-muted" style={{marginBottom:14,padding:'10px 12px',background:'var(--surface2)',borderRadius:8}}>
              A route owned by an API product is rewritten by that product's next sync — change those on the product. Use this for routes nothing else owns.
            </div>)}
          <FormFields tab={modal.tab} form={form} setForm={setForm} errors={formErrors}/>
          {formErrors._global&&<div className="alert alert-error" style={{marginBottom:12}}>{formErrors._global}</div>}
        </div></div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving?'Saving…':(modal.mode==='create'?'Create':'Save changes')}</button>
        </div>
      </div>
    </div>)}
  </div>);
}

// ── API Consumers: manage per-consumer TaxLab credentials ────────────────────
function ApisixPluginsPage({showToast}){
  const[plugins,setPlugins]=useState([]);
  const[loading,setLoading]=useState(true);
  const[search,setSearch]=useState('');
  const[categoryFilter,setCategoryFilter]=useState('all');
  const[selected,setSelected]=useState(null);

  const load=async()=>{
    setLoading(true);
    try{
      const r=await API('GET','/api/admin/apisix-plugins');
      setPlugins(r.plugins||[]);
    }catch(e){showToast?.(e.message,'error')}
    setLoading(false);
  };
  useEffect(()=>{load()},[]);

  const categories=['all',...new Set(plugins.map(p=>p.category).filter(Boolean).sort())];

  const filtered=plugins.filter(p=>{
    if(categoryFilter!=='all'&&p.category!==categoryFilter)return false;
    if(search){
      const q=search.toLowerCase();
      return(p.name||'').toLowerCase().includes(q)||(p.description||'').toLowerCase().includes(q);
    }
    return true;
  });

  const catIcon=(cat)=>{
    switch(cat){
      case 'Authentication': return '🔐';
      case 'Authorization': return '🛡️';
      case 'Security': return '🔒';
      case 'Traffic Control': return '🚦';
      case 'Rewrite & Transform': return '🔄';
      case 'Observability': return '📊';
      case 'Logging': return '📝';
      case 'Protocol': return '🌐';
      case 'Serverless': return '⚡';
      case 'Utility': return '🔧';
      default: return '📦';
    }
  };

  if(loading) return <div className="card"><div className="card-body"><p>Loading...</p></div></div>;

  return(
    <div style={{padding:24}}>
      <div style={{marginBottom:24}}>
        <h2 style={{margin:0}}>Plugin Catalog</h2>
        <p style={{color:'var(--text3)',margin:'4px 0 0'}}>All available gateway plugins. Click any plugin to view details.</p>
      </div>

      <div style={{display:'flex',gap:12,marginBottom:20,flexWrap:'wrap',alignItems:'center'}}>
        <input placeholder="Search plugins..." value={search} onChange={e=>setSearch(e.target.value)} style={{padding:'8px 14px',borderRadius:8,border:'1px solid var(--border)',background:'var(--surface)',color:'var(--text)',width:300,fontSize:14}}/>
        <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
          {categories.map(c=>(
            <button key={c} onClick={()=>setCategoryFilter(c)} className={'btn btn-sm '+(categoryFilter===c?'btn-primary':'btn-ghost')}>{c!=='all'?catIcon(c)+' ':''}{c}</button>
          ))}
        </div>
        <span style={{marginLeft:'auto',fontSize:12,color:'var(--text3)'}}>{filtered.length} plugin{filtered.length!==1?'s':''}</span>
      </div>

      {selected?(
        <div>
          <button className="btn btn-sm btn-ghost" onClick={()=>setSelected(null)} style={{marginBottom:12}}>← Back to catalog</button>
          <PluginDetail plugin={selected} showToast={showToast}/>
        </div>
      ):(
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))',gap:16}}>
          {filtered.map(p=>(
            <div key={p.name} className="card" style={{cursor:'pointer'}} onClick={()=>setSelected(p)}>
              <div className="card-body">
                <div style={{display:'flex',alignItems:'flex-start',gap:10,marginBottom:8}}>
                  <span style={{fontSize:20}}>{catIcon(p.category)}</span>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:600,fontSize:14,marginBottom:2}}>{p.name}</div>
                    <span className="badge badge-neutral" style={{fontSize:10}}>{p.category}</span>
                  </div>
                </div>
                <p style={{margin:0,fontSize:13,color:'var(--text2)',lineHeight:1.5}}>{p.description||'No description available'}</p>
                {p.configurable&&<span style={{fontSize:11,color:'var(--brand-start)',marginTop:6,display:'inline-block'}}>⚙ Configurable</span>}
              </div>
            </div>
          ))}
          {filtered.length===0&&<div style={{gridColumn:"1 / -1",textAlign:'center',padding:40,color:'var(--text3)'}}>No plugins match your search.</div>}
        </div>
      )}
    </div>
  );
}

function PluginDetail({plugin,showToast}){
  const[config,setConfig]=useState('{}');
  const[saving,setSaving]=useState(false);

  return(
    <div className="card">
      <div className="card-header" style={{display:'flex',alignItems:'center',gap:12}}>
        <span style={{fontSize:24}}>{plugin.category==='Authentication'?'🔐':plugin.category==='Authorization'?'🛡️':plugin.category==='Security'?'🔒':plugin.category==='Traffic Control'?'🚦':plugin.category==='Observability'?'📊':plugin.category==='Logging'?'📝':plugin.category==='Serverless'?'⚡':'📦'}</span>
        <div>
          <h3 className="card-title" style={{margin:0}}>{plugin.name}</h3>
          <span className="badge badge-neutral">{plugin.category}</span>
        </div>
      </div>
      <div className="card-body">
        <p style={{margin:'0 0 16px',color:'var(--text2)',lineHeight:1.6}}>{plugin.description||'No description available.'}</p>

        {plugin.configurable&&(
          <div style={{marginBottom:20}}>
            <label style={{fontSize:13,color:'var(--text2)',marginBottom:6,display:'block',fontWeight:600}}>Plugin Configuration (JSON)</label>
            <textarea className="form-input" rows={8} value={config} onChange={e=>setConfig(e.target.value)} style={{fontFamily:'monospace',fontSize:13,width:'100%',borderRadius:8}}/>
            <div style={{marginTop:8,display:'flex',gap:8}}>
              <button className="btn btn-sm btn-primary" disabled={saving} onClick={async()=>{
                setSaving(true);
                try{
                  showToast?.('Configuration saved (demo)','success');
                }catch(e){showToast?.(e.message,'error')}
                setSaving(false);
              }}>{saving?'Saving...':'Save Configuration'}</button>
            </div>
          </div>
        )}

        <div style={{background:'var(--surface2)',borderRadius:8,padding:16,fontSize:13}}>
          <div style={{fontWeight:600,marginBottom:8}}>How to apply this plugin</div>
          <ol style={{margin:0,paddingLeft:20,color:'var(--text2)',lineHeight:1.8}}>
            <li>Go to <strong>API Gateway → Routes</strong></li>
            <li>Select the route you want to add the plugin to</li>
            <li>In the route editor, find the <strong>Plugins</strong> section</li>
            <li>Click <strong>Add Plugin</strong> and select <code>{plugin.name}</code></li>
            <li>Configure the plugin parameters</li>
            <li>Save — the plugin takes effect immediately</li>
          </ol>
        </div>
      </div>
    </div>
  );
}

function FormFields({tab,form,setForm,errors}){
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const setNested=(k1,k2,v)=>setForm(f=>({...f,[k1]:{...f[k1],[k2]:v}}));
  const toggleMethod=(m)=>{const ms=new Set(form.methods||[]);if(ms.has(m))ms.delete(m);else ms.add(m);set('methods',[...ms])};

  if(tab==='routes')return(<>
    <Field label="Name" err={errors.name}><input value={form.name} onChange={e=>set('name',e.target.value)} placeholder="My Route"/></Field>
    <Field label="URI" err={errors.uri} req><input value={form.uri} onChange={e=>set('uri',e.target.value)} placeholder="/api/my-endpoint/*"/></Field>
    <Field label="Priority"><input type="number" value={form.priority} onChange={e=>set('priority',+e.target.value)}/></Field>
    <Field label="Methods">
      <div style={{display:'flex',gap:6}}>{['GET','POST','PUT','DELETE','PATCH','HEAD','OPTIONS'].map(m=><button key={m} type="button" className={'btn btn-sm '+(form.methods?.includes(m)?'btn-primary':'btn-ghost')} onClick={()=>toggleMethod(m)}>{m}</button>)}</div>
    </Field>
    <Field label="Hosts (optional)"><input value={form.hosts?.join(',')||''} onChange={e=>set('hosts',e.target.value.split(',').map(s=>s.trim()).filter(Boolean))} placeholder="host1,host2"/></Field>
    <Field label="Remote Addrs (optional)"><input value={form.remoteAddrs?.join(',')||''} onChange={e=>set('remoteAddrs',e.target.value.split(',').map(s=>s.trim()).filter(Boolean))} placeholder="CIDR list"/></Field>
    <Field label="Service ID (optional)"><input value={form.serviceId||''} onChange={e=>set('serviceId',e.target.value)} placeholder="service-xxx"/></Field>
    <Field label="Labels (JSON, optional)"><textarea rows={2} value={form.labels?JSON.stringify(form.labels,null,2):''} onChange={e=>{try{set('labels',JSON.parse(e.target.value))}catch{}}} placeholder='{"team":"api"}'/></Field>
    <Field label="CORS">
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
        <input value={form.plugins?.cors?.allow_origins||''} onChange={e=>setNested('plugins.cors','allow_origins',e.target.value)} placeholder="allow_origins (default: *)"/>
        <input value={form.plugins?.cors?.allow_methods||''} onChange={e=>setNested('plugins.cors','allow_methods',e.target.value)} placeholder="allow_methods"/>
        <input value={form.plugins?.cors?.allow_headers||''} onChange={e=>setNested('plugins.cors','allow_headers',e.target.value)} placeholder="allow_headers"/>
        <input type="number" value={form.plugins?.cors?.max_age||3600} onChange={e=>setNested('plugins.cors','max_age',+e.target.value)} placeholder="max_age"/>
      </div>
    </Field>
    <Field label="Rate Limit">
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
        <input type="number" value={form.plugins?.['limit-req']?.rate||''} onChange={e=>setNested('plugins.limit-req','rate',+e.target.value)} placeholder="Rate (req/sec)"/>
        <input type="number" value={form.plugins?.['limit-req']?.burst||''} onChange={e=>setNested('plugins.limit-req','burst',+e.target.value)} placeholder="Burst"/>
      </div>
    </Field>
  </>);

  if(tab==='services')return(<>
    <Field label="Name *" err={errors.name} req><input value={form.name} onChange={e=>set('name',e.target.value)} placeholder="my-service"/></Field>
    <Field label="Hosts">
      <textarea rows={2} value={form.hosts?.join('\n')||''} onChange={e=>set('hosts',e.target.value.split('\n').map(s=>s.trim()).filter(Boolean))} placeholder="host1&#10;host2:80"/>
    </Field>
    <Field label="Connect Timeout (s)"><input type="number" value={form.timeout?.connect||6} onChange={e=>setNested('timeout','connect',+e.target.value)}/></Field>
    <Field label="Send Timeout (s)"><input type="number" value={form.timeout?.send||60} onChange={e=>setNested('timeout','send',+e.target.value)}/></Field>
    <Field label="Read Timeout (s)"><input type="number" value={form.timeout?.read||60} onChange={e=>setNested('timeout','read',+e.target.value)}/></Field>
  </>);

  if(tab==='upstreams')return(<>
    <Field label="Name *" err={errors.name} req><input value={form.name} onChange={e=>set('name',e.target.value)} placeholder="my-upstream"/></Field>
    <Field label="Type">
      <select value={form.type} onChange={e=>set('type',e.target.value)}>
        <option value="roundrobin">roundrobin</option>
        <option value="chash">chash</option>
        <option value="ewma">ewma</option>
        <option value="least_conn">least_conn</option>
        <option value="consistent_hash">consistent_hash</option>
      </select>
    </Field>
    <Field label="Nodes (JSON or host:port)">
      <textarea rows={3} value={typeof form.nodes==='string'?form.nodes:JSON.stringify(form.nodes,null,2)} onChange={e=>set('nodes',e.target.value)} placeholder='{"backend.example.com": 1}'/>
      <small style={{color:'var(--text3)',fontSize:11}}>JSON object {"{host: weight}"} or plain "host:port"</small>
    </Field>
    <Field label="Pass Host">
      <select value={form.pass_host} onChange={e=>set('pass_host',e.target.value)}>
        <option value="pass">pass</option>
        <option value="node">node</option>
      </select>
    </Field>
  </>);

  if(tab==='consumers')return(<>
    <Field label="Username *" err={errors.username} req><input value={form.username} onChange={e=>set('username',e.target.value)} placeholder="consumer-name"/></Field>
    <Field label="Plugins (JSON)">
      <textarea rows={4} value={form.plugins?JSON.stringify(form.plugins,null,2):''} onChange={e=>{try{set('plugins',JSON.parse(e.target.value))}catch{console.warn('invalid JSON')}}} placeholder='{"jwt-auth": {"key": "my-key"}}'/>
      <small style={{color:'var(--text3)',fontSize:11}}>Common: jwt-auth, basic-auth, key-auth, hmac-auth, wolf-rbac</small>
    </Field>
  </>);

  if(tab==='ssl')return(<>
    <Field label="SNI *" err={errors.sni} req><input value={form.sni} onChange={e=>set('sni',e.target.value)} placeholder="*.example.com"/></Field>
    <Field label="Certificate (PEM)">
      <textarea rows={5} value={form.key||''} onChange={e=>set('key',e.target.value)} placeholder="-----BEGIN CERTIFICATE-----"/>
    </Field>
    <Field label="Sans (comma-separated)">
      <input value={form.sns?.join(',')||''} onChange={e=>set('sns',e.target.value.split(',').map(s=>s.trim()).filter(Boolean))} placeholder="*.example.com, example.com"/>
    </Field>
  </>);

  return null;
}

function Field({label,err,req,children}){
  return(<div className="form-group">
    <label className="form-label">{label}{req&&<span className="req"> *</span>}</label>
    {children}
    {err&&<div style={{color:'var(--error)',fontSize:11,marginTop:4}}>{err}</div>}
  </div>);
}

const root=ReactDOM.createRoot(document.getElementById('root'));
root.render(<App/>);
